export default {
  quarter: 1,
  phase: '4',
  theme: 'Sound + Sight = Reading',
  weeks: 13,
  description: 'Blending sounds, building sight-word fluency, and reading simple words and sentences with growing confidence',
  lessons: [

[
  // ========= LESSON 1 (V2) =========
  {
    id: 1,
    phase: '4',
    quarter: 1,
    week: 1,
    weekIndex: 0,
    dayInWeek: 1,
    title: 'Lesson 1: Meet the A-Team',
    theme: 'CVC Mastery: Short A',
    skillRecurrenceTags: ['cvc-decoding', 'short-vowels', 'blending'],
    relatedToPreviousSkills: ['letter-sounds', 'phoneme-awareness'],
    duration: '10-15 min',
    version: 2,
    banner: 'Grab paper and something to write with. A crayon is perfect.',
    steps: [
      {
        stepNumber: 1,
        name: 'The Sound That Starts It All',
        duration: '2–3 min',
        hook: 'Your child already knows letter sounds. Today, those sounds click together into a word for the first time.',
        sayThis: [
          '“Remember the sound A makes? /a/! Like apple!”',
          '“Today, A is joining a team. Letters work together to make WORDS.”',
          '“Watch this: /c/... /a/... /t/. Say it fast — CAT!”'
        ],
        thenDoThis: [
          'Review the /a/ sound — have them say it with you, mouths wide open',
          'Write CAT in large letters on paper where they can see it',
          'Point to each letter slowly: “/c/... /a/... /t/” — pause between each, then blend: “CAT!”',
          'Do it again. This time, point and have THEM try the sounds while you point',
          'Blend it together 3x — slow, medium, fast. They’ll hear it click on the fast one.'
        ],
        watchFor: 'The moment they stop looking at you and look at the word — that’s the reading instinct activating. They’re trying to decode. It might not happen today. That’s okay — the neural pathway is forming either way.',
        whyThisWorks: 'Ehri (2001) showed that systematic letter-sound blending — hearing sounds separately, then pushed together — is how the brain builds its first sight words.',
        transition: 'They heard sounds become a word. Now let’s build more.'
      },
      {
        stepNumber: 2,
        name: 'Build the A-Team',
        duration: '4 min',
        hook: 'Same idea, more words. The more they sound out, the easier it gets.',
        sayThis: [
          '“Let’s build more words! Every word has three sounds.”',
          '“H... A... T. Put them together — HAT! You wear a hat!”',
          '“M... A... P. Push them — MAP! A map shows you where to go!”',
          '“What sounds do you hear in CAT? C... A... T! You got it!”'
        ],
        thenDoThis: [
          'Write each word BIG on paper: HAT, MAP, SAT, BAG',
          'Point under each letter as you say its sound — your finger is their guide',
          'After sounding out, blend it fast and connect to meaning: “HAT — you put it on your head!”',
          'Have them try: point to a word, let them sound it out with you, then alone',
          'Do each word 2x — once together, once with them leading',
          'If they struggle, slow down — go back to two sounds (/a/... /t/) before adding the first consonant'
        ],
        watchFor: 'Them moving their finger under the letters without being told — that’s self-directed decoding. It’s the most important signal in early reading.',
        whyThisWorks: 'National Reading Panel (2000) confirmed explicit phonics instruction — sounding out and blending — is the most effective method for beginning readers, across all studies reviewed.',
        transition: 'They can build words. Let’s prove they know what those words mean.'
      },
      {
        stepNumber: 3,
        name: 'Match the Word',
        duration: '3 min',
        hook: 'They can sound out words. Now let’s see if they know what those words actually mean.',
        sayThis: [
          '“I’m going to say a word. You find it!”',
          '“CAT! Where’s CAT? Point to it!”',
          '“Now YOU be the teacher — pick a word and I’ll find it!”',
          '“Read it to me — what does this one say?”'
        ],
        thenDoThis: [
          'Lay out 3–4 written words (from Step 2) spread apart on the table',
          'Say a word — let them scan and point to the match',
          'When they find it, have them sound it out: “Read it to me!”',
          'Go through all the words, then shuffle and do a second round',
          'Swap roles — let them pick a word for you to find. Make a silly wrong guess first: “Is it... PIZZA?” — they’ll correct you and love it'
        ],
        watchFor: 'Sounding out the word BEFORE pointing to it — that means they’re decoding, not memorizing position. If they’re grabbing by position, shuffle the papers.',
        whyThisWorks: 'Scarborough (2001) — the Reading Rope model — shows that word recognition must connect to meaning for true comprehension.',
        transition: 'They can read words and know what they mean. One more game to seal it in.'
      },
      {
        stepNumber: 4,
        name: 'Speed Round',
        duration: '2 min',
        hook: 'Point to a word, they read it. Start slow, speed up. This builds the speed that turns sounding-out into real reading.',
        sayThis: [
          '“Let’s play the fast game! I point, you read!”',
          '“Ready? GO!” (point to CAT)',
          '“YES! Next!” (point to HAT)',
          '“You’re SO fast! One more!” (point to MAP)'
        ],
        thenDoThis: [
          'Point to written words one at a time — start slow, speed up as they succeed',
          'Go through all words once slow, then again faster',
          'Cheer after every word, keep energy high',
          'If they need to sound it out, that’s fine — speed comes later, accuracy comes first',
          'End by letting them “quiz” you — they point, you read (throw in one intentional mistake for them to catch)'
        ],
        watchFor: 'Any word they recognize instantly without sounding out — that’s their first sight word forming. Name it: “You didn’t even have to sound that out! You just KNEW it!”',
        whyThisWorks: 'Gough & Tunmer (1986) showed that reading fluency develops when decoding becomes automatic — repeated exposure to the same words in short bursts is what builds that automaticity.',
        transition: 'Look at what you two just did. This deserves a real celebration.'
      },
      {
        stepNumber: 5,
        name: 'You Just Taught Reading',
        duration: '1 min',
        hook: 'Your child just read their first words. Not from a screen. Not from an app. From you.',
        sayThis: [
          '“Do you know what you just did? You READ. Real words!”',
          '“CAT. HAT. MAP. You read ALL of those.”',
          '“Tomorrow we’re reading even more. You’re a reader now.”'
        ],
        thenDoThis: [
          'Have them pick their favorite word and read it one more time — that’s their word now',
          'Write it on a piece of paper and stick it on the fridge — they’ll read it every time they walk by',
          'Tell them the truth: “Not every kid your age can do what you just did.” — because it’s true, and they should hear it'
        ],
        watchFor: 'Pride. Not yours — theirs. When they stand a little taller or want to show someone, that’s intrinsic motivation forming. Feed it.',
        whyThisWorks: 'CVC decoding is the foundational skill of independent reading. Ehri’s phase model shows this “partial alphabetic” stage — sounding out simple words — is the gateway to all fluent reading that follows.',
        transition: null
      }
    ]
  },

  // ========= LESSON 2 =========
  {
    id: 2,
    phase: '4',
    quarter: 1,
    week: 1,
    weekIndex: 0,
    dayInWeek: 2,
    title: 'Lesson 2: Tap & Blend — A Words',
    theme: 'CVC Mastery: Short A',
    skillRecurrenceTags: ['cvc-decoding', 'blending', 'phoneme-segmentation'],
    relatedToPreviousSkills: ['cvc-decoding', 'short-vowels'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Tap & Blend!',
        duration: '1 min',
        content: {
          goal: 'Use physical tapping to help your child segment sounds, then blend them into complete words.',
          parentGuide: [
            'Tapping each sound makes decoding physical and memorable.',
            'This multisensory approach (touch + sound + sight) strengthens neural connections.',
            'Go slowly at first — speed comes with practice.',
            'Celebrate blending even if pronunciation isn\'t perfect yet.'
          ],
          materials: ['Your hands and fingers', 'Paper and pencil', 'Optional: small objects to tap (blocks, coins)'],
          tip: 'Use three fingers or three taps on the table — one tap per sound keeps it clear and structured.',
          researchNote: 'Lonigan et al. (2000) - Phonological awareness activities, including segmenting and blending, are strong predictors of reading success.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Three-Finger Tap',
        duration: '3-4 min',
        focus: 'Phoneme Segmentation',
        researchBasis: 'Anthony & Francis (2005) - Explicit segmentation training improves phonemic awareness and early decoding skills.',
        content: {
          goal: 'Teach your child to "tap out" each sound in a CVC word using their fingers.',
          whatToSay: [
            '"Let\'s tap out the sounds in \'cat.\' Use three fingers!"',
            '"First sound: /c/ — tap your pointer finger!"',
            '"Middle sound: /a/ — tap your middle finger!"',
            '"Last sound: /t/ — tap your ring finger!"',
            '"Now let\'s say them all together: cat!"'
          ],
          whatToDo: [
            'Demonstrate by tapping your three fingers on the table as you say each sound.',
            'Say the word "cat" — tap finger 1 for /c/, finger 2 for /a/, finger 3 for /t/.',
            'Have your child copy you with their own hand.',
            'Practice with 4-5 words (map, hat, bag, jam, ran).',
            'Go slowly at first, then speed up as they gain confidence.'
          ],
          examples: [
            'Tap and say: "/m/ (tap) /a/ (tap) /p/ (tap) — map!"'
          ],
          watchFor: [
            'Child tapping in sync with sounds.',
            'Clear pronunciation of each individual sound.',
            'Beginning to anticipate the tapping pattern.'
          ],
          parentTip: 'If three fingers feel tricky, tap on the table or use small objects — the physical motion is what matters!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Tap Then Blend',
        duration: '4-5 min',
        focus: 'Sound Blending',
        researchBasis: 'Ehri (2020) - Blending is a core phonemic awareness skill that directly supports word reading and spelling development.',
        content: {
          goal: 'Move from segmented sounds to smooth blending — the heart of decoding.',
          whatToSay: [
            '"Now let\'s tap the sounds and then say the whole word fast!"',
            '"Ready? /c/ /a/ /t/ — cat!"',
            '"Your turn! Tap it out, then blend it together!"'
          ],
          whatToDo: [
            'Write a word (e.g., "bat") on paper.',
            'Point to each letter while your child taps and says each sound.',
            'After tapping all three sounds, say "Now blend!" and say the word together quickly.',
            'Repeat with 5-6 words (sat, pan, dam, wag, tap).',
            'Let your child try independently on the last 2-3 words.'
          ],
          examples: [
            'Point and tap: "/s/ /a/ /t/" — pause — "Now blend: sat!"'
          ],
          watchFor: [
            'Smooth transition from segmented to blended sounds.',
            'Child able to say the whole word without pausing.',
            'Confidence growing with each repetition.'
          ],
          parentTip: 'Blending is like a bridge — first you walk across slowly (tap each sound), then you run across (say it fast)!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Tap & Write Challenge',
        duration: '3-4 min',
        focus: 'Encoding Practice',
        researchBasis: 'National Reading Panel (2000) - Writing and spelling reinforce phonemic awareness and solidify sound-letter connections.',
        content: {
          goal: 'Reverse the process — hear a word, tap it out, then write the sounds.',
          whatToSay: [
            '"Now I\'ll say a word, and you tap out the sounds!"',
            '"After you tap, try writing the letters you hear!"',
            '"Ready? The word is \'cap.\' Tap it out!"'
          ],
          whatToDo: [
            'Say a short-A CVC word aloud (e.g., "cap").',
            'Have your child tap out each sound on their fingers.',
            'Then have them write each letter on paper as they say the sound.',
            'Repeat with 3-4 words (mat, lap, tag, van).',
            'Offer help if they get stuck, but let them lead.'
          ],
          watchFor: [
            'Correct sound segmentation.',
            'Attempting to write letters that match sounds.',
            'Growing independence in the process.'
          ],
          parentTip: 'Spelling at this stage is about hearing sounds, not perfect handwriting — celebrate every attempt!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '👏 Amazing tapping and blending!',
            'You practiced:',
            '✓ Tapping out sounds in CVC words',
            '✓ Blending sounds together smoothly',
            '✓ Writing sounds you hear'
          ],
          reviewQuestions: [
            'Could your child tap out sounds confidently?',
            'Were they able to blend smoothly after tapping?',
            'Did they attempt to write the letters they heard?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Tapping makes decoding physical and memorable. This multisensory technique helps struggling readers "feel" the sounds as they learn to read.'
          },
          nextSteps: [
            'Use finger-tapping whenever your child gets stuck on a word.',
            'Practice tap-and-blend during car rides or waiting times.',
            'Let your child tap out their name or favorite words!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 3 =========
  {
    id: 3,
    phase: '4',
    quarter: 1,
    week: 1,
    weekIndex: 0,
    dayInWeek: 3,
    title: 'Lesson 3: Word Builder Hands',
    theme: 'CVC Mastery: Short A',
    skillRecurrenceTags: ['cvc-decoding', 'phoneme-manipulation', 'kinesthetic-learning'],
    relatedToPreviousSkills: ['cvc-decoding', 'blending', 'phoneme-segmentation'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Word Builder Hands!',
        duration: '1 min',
        content: {
          goal: 'Use fingers as "sound pieces" to build, manipulate, and decode CVC words physically.',
          parentGuide: [
            'This lesson makes abstract sounds concrete by assigning each sound to a finger.',
            'Kinesthetic learning strengthens memory — children remember what they do with their bodies.',
            'This technique helps children "feel" the structure of words.',
            'Perfect for active learners who need to move while learning.'
          ],
          materials: ['Your hands and your child\'s hands', 'Paper and pencil (optional)', 'Your enthusiasm!'],
          tip: 'Make it playful — wiggle fingers, make sound effects, and celebrate each word built!',
          researchNote: 'Diamond (1990) - Motor activity enhances memory formation in young children, particularly when paired with language learning.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Meet Your Sound Fingers',
        duration: '3-4 min',
        focus: 'Phoneme Assignment',
        researchBasis: 'Anthony & Francis (2005) - Multisensory phonics instruction improves phonological awareness and decoding skills.',
        content: {
          goal: 'Assign each sound in a CVC word to a specific finger, making decoding tangible.',
          whatToSay: [
            '"Your fingers are going to be word builders today!"',
            '"Your thumb is the first sound, your pointer finger is the middle sound, and your middle finger is the last sound!"',
            '"Let\'s build the word \'cat\' with our fingers!"'
          ],
          whatToDo: [
            'Hold up your hand with three fingers extended (thumb, pointer, middle).',
            'Touch your thumb and say "/c/".',
            'Touch your pointer finger and say "/a/".',
            'Touch your middle finger and say "/t/".',
            'Have your child copy you with their own hand.',
            'Practice with 3-4 words (map, hat, bag, jam).'
          ],
          examples: [
            'Touch each finger in sequence: "Thumb: /c/, Pointer: /a/, Middle: /t/ — cat!"'
          ],
          watchFor: [
            'Child touching the correct finger for each sound.',
            'Clear pronunciation of individual phonemes.',
            'Building confidence with the finger pattern.'
          ],
          parentTip: 'Go slowly at first! Once they master the pattern, they\'ll speed up naturally.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Change One Sound',
        duration: '4-5 min',
        focus: 'Phoneme Manipulation',
        researchBasis: 'National Reading Panel (2000) - Phoneme manipulation is a higher-level skill that significantly predicts reading ability.',
        content: {
          goal: 'Help your child change one sound at a time to create new words, understanding word flexibility.',
          whatToSay: [
            '"Let\'s change just ONE sound and make a new word!"',
            '"We have \'cat\' — /c/ /a/ /t/. What if we change the first sound to /b/?"',
            '"Now touch your thumb and say /b/ instead! What word do we get?"'
          ],
          whatToDo: [
            'Start with "cat" using three fingers.',
            'Say: "Let\'s change /c/ to /b/. Touch your thumb and say /b/!"',
            'Have child say the new word: "bat!"',
            'Repeat with different changes:',
            '  - cat → hat (change /c/ to /h/)',
            '  - cat → can (change /t/ to /n/)',
            '  - cat → cut (change /a/ to /u/)',
            'Practice 5-6 transformations total.'
          ],
          examples: [
            '"Cat becomes bat! We just changed one finger — the thumb!"',
            '"Hat becomes had! We changed the last finger!"'
          ],
          watchFor: [
            'Child recognizing which finger/sound changes.',
            'Successfully producing the new word.',
            'Understanding that one sound change = new word.'
          ],
          parentTip: 'This is hard at first! If they struggle, go back to saying all three sounds each time before changing one.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Build Your Own Words',
        duration: '3-4 min',
        focus: 'Independent Word Construction',
        researchBasis: 'Ehri (2001) - Active word construction supports decoding automaticity and phonemic awareness development.',
        content: {
          goal: 'Let your child independently choose sounds and build their own CVC words.',
          whatToSay: [
            '"Now it\'s your turn to be the word builder!"',
            '"Pick any three sounds and build a word with your fingers!"',
            '"It can be a real word or a silly made-up word — let\'s hear it!"'
          ],
          whatToDo: [
            'Let your child choose any three sounds.',
            'Have them assign each sound to a finger.',
            'They say each sound while touching each finger.',
            'Then blend the sounds together into a word.',
            'Celebrate real words AND silly nonsense words!',
            'Try 3-5 child-created words.'
          ],
          examples: [
            'Child says: "/p/ /a/ /n/ — pan!" or "/z/ /a/ /m/ — zam!"'
          ],
          watchFor: [
            'Child independently assigning sounds to fingers.',
            'Successful blending of their chosen sounds.',
            'Creativity and enthusiasm in word-building.'
          ],
          parentTip: 'Nonsense words are GREAT practice! They prove your child is truly decoding, not just memorizing.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '✋ You\'re a word-building champion!',
            'You practiced:',
            '✓ Using fingers as sound builders',
            '✓ Changing one sound to make new words',
            '✓ Creating your own CVC words'
          ],
          reviewQuestions: [
            'Could your child assign sounds to fingers easily?',
            'Were they able to change one sound to make a new word?',
            'Did they enjoy creating their own words?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Kinesthetic learning makes abstract phonics concrete. Using the body to decode helps struggling readers "feel" the structure of language.'
          },
          nextSteps: [
            'Use finger-building whenever your child encounters a tricky word.',
            'Practice sound-swapping during transitions (bath time, car rides).',
            'Challenge them: "Can you build 3 words with /a/ in the middle?"'
          ]
        }
      }
    ]
  },

  // ========= LESSON 4 =========
  {
    id: 4,
    phase: '4',
    quarter: 1,
    week: 1,
    weekIndex: 0,
    dayInWeek: 4,
    title: 'Lesson 4: Draw the Word',
    theme: 'CVC Mastery: Short A',
    skillRecurrenceTags: ['cvc-decoding', 'word-meaning', 'visual-literacy'],
    relatedToPreviousSkills: ['cvc-decoding', 'short-vowels', 'word-building'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Draw the Word!',
        duration: '1 min',
        content: {
          goal: 'Connect decoded words to their meanings through drawing and labeling activities.',
          parentGuide: [
            'This lesson bridges word recognition and comprehension — reading must connect to meaning.',
            'Drawing helps children visualize what they\'re reading.',
            'Labeling drawings reinforces spelling and letter formation.',
            'This is where decoding becomes real reading!'
          ],
          materials: ['Paper', 'Crayons, markers, or colored pencils', 'Pencil for writing'],
          tip: 'Focus on the CONNECTION between picture and word, not artistic skill. Stick figures are perfect!',
          researchNote: 'Scarborough (2001) - The Reading Rope shows that word recognition must intertwine with language comprehension for true literacy.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Decode and Draw',
        duration: '4-5 min',
        focus: 'Word-to-Meaning Connection',
        researchBasis: 'Whitehurst & Lonigan (1998) - Connecting print to meaning is essential for emergent literacy and reading comprehension.',
        content: {
          goal: 'Have your child decode a word, then draw a picture that represents its meaning.',
          whatToSay: [
            '"I\'m going to write a word, and you\'re going to read it and draw it!"',
            '"Let\'s try \'cat\' first. Can you read this word?"',
            '"Great! Now draw a cat — any kind of cat you like!"'
          ],
          whatToDo: [
            'Write the word "cat" clearly on paper.',
            'Have your child decode it by tapping or blending.',
            'Once they say "cat," ask them to draw a picture of a cat.',
            'Repeat with 3-4 more words:',
            '  - hat (draw someone wearing a hat)',
            '  - map (draw a simple map)',
            '  - bag (draw a bag)',
            '  - jam (draw a jar of jam)'
          ],
          examples: [
            'Child reads "hat" → draws a stick figure with a hat on their head.'
          ],
          watchFor: [
            'Child decoding the word before drawing.',
            'Drawing shows understanding of word meaning.',
            'Engagement and creativity in illustrations.'
          ],
          parentTip: 'Simple drawings are perfect! The goal is meaning connection, not art class.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Label Your Pictures',
        duration: '4-5 min',
        focus: 'Encoding and Spelling',
        researchBasis: 'National Reading Panel (2000) - Writing practice reinforces phonemic awareness and strengthens the reading-writing connection.',
        content: {
          goal: 'Have your child write labels for their drawings, reinforcing spelling and letter formation.',
          whatToSay: [
            '"Now let\'s label your pictures! Can you write the word \'cat\' under your cat?"',
            '"Remember: /c/ /a/ /t/ — three sounds, three letters!"',
            '"If you need help, sound it out with your fingers first!"'
          ],
          whatToDo: [
            'Point to the first picture your child drew (cat).',
            'Ask them to write the word below the picture.',
            'If needed, help them segment the sounds first.',
            'Have them write labels for all their drawings.',
            'Celebrate attempts even if spelling isn\'t perfect.'
          ],
          examples: [
            'Child writes "cat" under their cat drawing, "hat" under the hat picture, etc.'
          ],
          watchFor: [
            'Child attempting to write independently.',
            'Correct sound-to-letter matching.',
            'Letter formation improving with practice.'
          ],
          parentTip: 'Invented spelling is normal and healthy! Focus on phonetic accuracy (cat vs. kat) rather than perfection.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Picture Dictionary',
        duration: '2-3 min',
        focus: 'Review and Reflection',
        researchBasis: 'Bus & van IJzendoorn (1995) - Shared reading and reviewing personalized content enhances literacy development.',
        content: {
          goal: 'Review all the words and pictures together, celebrating your child\'s mini picture dictionary.',
          whatToSay: [
            '"Look at all the words you read and drew today!"',
            '"Let\'s read through your picture dictionary together!"',
            '"Point to each word and read it to me!"'
          ],
          whatToDo: [
            'Review each picture and label together.',
            'Have your child point to and read each word aloud.',
            'Ask: "What\'s your favorite picture?"',
            'Optional: Add one more word of their choice.',
            'Save the page as their first "dictionary page."'
          ],
          examples: [
            'Child points to each drawing and reads: "Cat, hat, map, bag, jam!"'
          ],
          watchFor: [
            'Confident reading of all labeled words.',
            'Pride in their created work.',
            'Ability to recognize words out of sequence.'
          ],
          parentTip: 'Keep these pages! Building a personal picture dictionary over weeks shows visible progress and builds confidence.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎨 You\'re an amazing word artist!',
            'You practiced:',
            '✓ Reading and drawing CVC words',
            '✓ Writing labels for your pictures',
            '✓ Creating your own picture dictionary'
          ],
          reviewQuestions: [
            'Which word was your child most excited to draw?',
            'Could they label their pictures independently?',
            'Did they understand the meaning of each word they read?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Reading without meaning is just sound-making. Connecting words to pictures ensures your child comprehends what they decode — the heart of true literacy.'
          },
          nextSteps: [
            'Start a "Word of the Day" drawing journal.',
            'Label objects around the house with short-A words.',
            'Read simple books and have your child draw their favorite page.'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 5 =========
  {
    id: 5,
    phase: '4',
    quarter: 1,
    week: 1,
    weekIndex: 0,
    dayInWeek: 5,
    title: 'Lesson 5: Short A Story Sketch',
    theme: 'CVC Mastery: Short A',
    skillRecurrenceTags: ['cvc-decoding', 'listening-comprehension', 'story-sequencing'],
    relatedToPreviousSkills: ['cvc-decoding', 'word-meaning', 'visual-literacy'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Story Sketch!',
        duration: '1 min',
        content: {
          goal: 'Listen to a simple short-A story and sketch what happens, connecting decoding to narrative comprehension.',
          parentGuide: [
            'This lesson integrates all Week 1 skills into meaningful story context.',
            'Stories make isolated words memorable and meaningful.',
            'Sketching while listening enhances comprehension and retention.',
            'This prepares children for reading connected text, not just isolated words.'
          ],
          materials: ['Paper', 'Crayons or markers', 'Your storytelling voice'],
          tip: 'Read slowly and with expression! Pause after each sentence to let your child sketch.',
          researchNote: 'Bus & van IJzendoorn (1995) - Shared reading experiences significantly predict literacy development and reading comprehension.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Listen to the Story',
        duration: '4-5 min',
        focus: 'Listening Comprehension',
        researchBasis: 'National Early Literacy Panel (2008) - Oral language comprehension is a foundational skill for reading comprehension.',
        content: {
          goal: 'Read a simple short-A story while your child listens and follows along.',
          whatToSay: [
            '"I\'m going to read you a special story! All the important words have the short-A sound!"',
            '"Listen carefully, and when I pause, you can draw what you hear!"',
            '"Ready? Here we go!"'
          ],
          whatToDo: [
            'Read this story slowly and clearly:',
            '',
            '"The Cat and the Hat"',
            'A cat sat on a mat.',
            'The cat had a hat.',
            'A rat ran to the cat.',
            'The cat and the rat sat on the mat.',
            '',
            'Pause after each sentence.',
            'Emphasize short-A words slightly.',
            'Ask: "What happened in that sentence?"'
          ],
          examples: [
            'After "A cat sat on a mat," ask: "What did the cat do?"'
          ],
          watchFor: [
            'Child listening attentively.',
            'Recognition of short-A words when emphasized.',
            'Ability to recall what happened in each sentence.'
          ],
          parentTip: 'Read it twice! First time for listening, second time for sketching.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Sketch the Story',
        duration: '5-6 min',
        focus: 'Visual Story Representation',
        researchBasis: 'Whitehurst & Lonigan (1998) - Active engagement with stories, including drawing, enhances emergent literacy skills.',
        content: {
          goal: 'Have your child draw simple pictures for each sentence of the story.',
          whatToSay: [
            '"Now let\'s draw the story! I\'ll read it again, and you draw what happens!"',
            '"First sentence: A cat sat on a mat. Can you draw that?"',
            '"Great! Next sentence: The cat had a hat..."'
          ],
          whatToDo: [
            'Fold the paper into 4 sections (or use 4 separate small papers).',
            'Read one sentence at a time.',
            'Let your child draw a simple sketch for each sentence.',
            'Don\'t rush — give 1-2 minutes per sketch.',
            'Encourage stick figures and simple shapes.',
            'Label key items together if time allows (cat, mat, hat, rat).'
          ],
          examples: [
            'Section 1: Cat sitting on mat',
            'Section 2: Cat wearing hat',
            'Section 3: Rat running toward cat',
            'Section 4: Cat and rat sitting together on mat'
          ],
          watchFor: [
            'Child capturing the main action of each sentence.',
            'Including key story elements (cat, mat, hat, rat).',
            'Understanding story sequence.'
          ],
          parentTip: 'The drawings don\'t need to be perfect! Focus on whether they capture the story events.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Retell with Words',
        duration: '2-3 min',
        focus: 'Story Retelling',
        researchBasis: 'National Reading Panel (2000) - Retelling stories strengthens comprehension and oral language development.',
        content: {
          goal: 'Have your child retell the story using their drawings and their own words.',
          whatToSay: [
            '"Now YOU tell ME the story! Use your pictures to help you remember!"',
            '"What happened first?"',
            '"And then what happened?"'
          ],
          whatToDo: [
            'Point to the first picture.',
            'Ask: "What\'s happening here?"',
            'Encourage your child to use complete sentences.',
            'If they get stuck, prompt: "What did the cat do?"',
            'Celebrate when they use short-A words from the story.',
            'Have them point to and "read" any labels you wrote together.'
          ],
          examples: [
            'Child says: "The cat sat on the mat. Then the cat got a hat. A rat came. They sat together!"'
          ],
          watchFor: [
            'Child retelling events in correct sequence.',
            'Using short-A vocabulary from the story.',
            'Confidence in storytelling.'
          ],
          parentTip: 'If they struggle, retell it together: you say one sentence, they say the next!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '📖 You\'re a story artist AND storyteller!',
            'You practiced:',
            '✓ Listening to a short-A story',
            '✓ Drawing story events in sequence',
            '✓ Retelling the story in your own words'
          ],
          reviewQuestions: [
            'Could your child follow the story sequence?',
            'Did they include the main characters and events in their drawings?',
            'Were they able to retell the story independently?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Stories give isolated words purpose and context. This lesson shows your child that reading isn\'t just about sounding out words — it\'s about understanding and enjoying stories.'
          },
          nextSteps: [
            'Create your own short-A stories together!',
            'Act out the cat and rat story with stuffed animals.',
            'Challenge: "Can you add one more sentence to the story?"'
          ]
        }
      }
    ]
  },

  // ========= LESSON 6 =========
  {
    id: 6,
    phase: '4',
    quarter: 1,
    week: 2,
    weekIndex: 1,
    dayInWeek: 1,
    title: 'Lesson 6: Echo the E',
    theme: 'CVC Mastery: Short E',
    skillRecurrenceTags: ['cvc-decoding', 'short-vowels', 'vowel-discrimination'],
    relatedToPreviousSkills: ['cvc-decoding', 'short-vowels'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Echo the E!',
        duration: '1 min',
        content: {
          goal: 'Introduce the short-E sound and begin decoding short-E CVC words (bed, leg, hen).',
          parentGuide: [
            'This lesson introduces a new vowel sound while building on Week 1 skills.',
            'Short E sounds like "eh" — the sound you make when you\'re puzzled.',
            'Children often confuse short A and short E — that\'s normal and expected.',
            'Repetition and contrast will help distinguish the two vowels over time.'
          ],
          materials: ['Paper and pencil (or marker/crayon)', 'Household objects or quick drawings (a bed pillow, a toy hen, a red crayon — or sketch them!)', 'Your voice'],
          tip: 'Exaggerate the difference between /a/ (mouth wide) and /e/ (mouth less open, more relaxed).',
          researchNote: 'Ehri (2020) - Systematic vowel instruction, including explicit contrast between vowels, improves decoding accuracy.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Meet Short E',
        duration: '3-4 min',
        focus: 'Vowel Sound Introduction',
        researchBasis: 'National Reading Panel (2000) - Explicit instruction in vowel sounds is critical for phonics mastery.',
        content: {
          goal: 'Introduce the short-E sound clearly and connect it to familiar words.',
          whatToSay: [
            '"Today we\'re meeting a new vowel friend — the letter E!"',
            '"E says /e/ — like in egg or elephant!"',
            '"Let\'s say it together: /e/ /e/ /e/!"',
            '"Your mouth is a little more relaxed than /a/. Try it!"'
          ],
          whatToDo: [
            'Show the letter E clearly written.',
            'Make the /e/ sound, showing your mouth position.',
            'Have your child repeat the sound 4-5 times.',
            'Compare to short A: "/a/ /e/ /a/ /e/ — do they sound different?"',
            'Point to objects or quick drawings: an egg from the fridge, a toy elephant, or your own elbow!',
            'Say each word emphasizing /e/: "/e/-gg, /e/-lephant."'
          ],
          examples: [
            '"Feel your mouth: /a/ is wide like you\'re surprised! /e/ is more relaxed, like \'eh?\'"'
          ],
          watchFor: [
            'Child producing a clear /e/ sound.',
            'Noticing the difference between /a/ and /e/.',
            'Interest in the new vowel sound.'
          ],
          parentTip: 'If they struggle with the sound, try: "Say \'egg\' and freeze on the first sound!"'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Decode Short-E Words',
        duration: '4-5 min',
        focus: 'CVC Decoding with Short E',
        researchBasis: 'Gough & Tunmer (1986) - Decoding practice with varied vowels builds word recognition automaticity.',
        content: {
          goal: 'Practice decoding CVC words with the short-E sound in the middle.',
          whatToSay: [
            '"Let\'s read some words with /e/ in the middle!"',
            '"First word: b-e-d. Let\'s tap it out together!"',
            '"What word did we make?"'
          ],
          whatToDo: [
            'Write "bed" in large letters.',
            'Point to each letter: "/b/ /e/ /d/".',
            'Have your child tap and blend with you.',
            'Once they say "bed," show a picture of a bed.',
            'Repeat with 5-6 words:',
            '  - leg',
            '  - hen',
            '  - pen',
            '  - red',
            '  - ten',
            'Emphasize the /e/ sound each time.'
          ],
          examples: [
            'Point and decode: "/l/ /e/ /g/ — leg! That\'s a part of your body!"'
          ],
          watchFor: [
            'Child producing the short-E sound correctly.',
            'Successful blending of three sounds.',
            'Connecting words to their meanings.'
          ],
          parentTip: 'Use hand motions! Touch your leg when you say "leg," point to a pen for "pen" — make it concrete!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Echo Game',
        duration: '3-4 min',
        focus: 'Auditory Discrimination',
        researchBasis: 'Lonigan et al. (2000) - Phonological awareness activities, including sound discrimination, predict reading success.',
        content: {
          goal: 'Play an echo game where your child repeats short-E words, strengthening sound memory.',
          whatToSay: [
            '"Now we\'re going to play Echo! I\'ll say a word, and you echo it back to me!"',
            '"Listen carefully for the /e/ sound!"',
            '"Ready? Bed!"'
          ],
          whatToDo: [
            'Say a short-E word clearly.',
            'Your child echoes it back.',
            'After they echo, ask: "What sound is in the middle?"',
            'Practice with 8-10 words:',
            '  - bed, leg, hen, pen, red, ten, wet, net, pet, set',
            'Alternate between saying words and having them decode written words.',
            'Celebrate every correct echo!'
          ],
          examples: [
            'You: "Hen!" Child: "Hen!" You: "What\'s the middle sound?" Child: "/e/!"'
          ],
          watchFor: [
            'Clear repetition of words.',
            'Correct identification of the middle vowel sound.',
            'Growing confidence with short-E words.'
          ],
          parentTip: 'Make it silly! Use different voices — robot voice, mouse voice, giant voice — to keep it fun!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎯 Amazing work with short E!',
            'You practiced:',
            '✓ The short-E sound (/e/)',
            '✓ Reading CVC words (bed, leg, hen, pen)',
            '✓ Echoing and identifying vowel sounds'
          ],
          reviewQuestions: [
            'Could your child produce the short-E sound clearly?',
            'Did they successfully decode short-E words?',
            'Can they tell the difference between /a/ and /e/?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Learning multiple vowels shows your child that decoding is flexible. The same pattern (CVC) works with different vowels — unlocking thousands of words!'
          },
          nextSteps: [
            'Point out short-E words during the day: "red car," "wet hands," "my leg."',
            'Play the echo game with household objects.',
            'Practice writing 2-3 short-E words each day.'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 7 =========
  {
    id: 7,
    phase: '4',
    quarter: 1,
    week: 2,
    weekIndex: 1,
    dayInWeek: 2,
    title: 'Lesson 7: Stretch & Snap',
    theme: 'CVC Mastery: Short E',
    skillRecurrenceTags: ['cvc-decoding', 'blending', 'phoneme-segmentation'],
    relatedToPreviousSkills: ['cvc-decoding', 'blending', 'short-vowels'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Stretch & Snap!',
        duration: '1 min',
        content: {
          goal: 'Use stretching and snapping motions to practice segmenting and blending short-E words.',
          parentGuide: [
            'This lesson makes blending physical through rubber-band-like stretching motions.',
            'Stretching sounds slowly helps children hear each phoneme clearly.',
            'Snapping sounds together builds blending automaticity.',
            'Movement + sound = stronger memory connections.'
          ],
          materials: ['Your hands and arms', 'Paper and pencil', 'Optional: rubber band to demonstrate'],
          tip: 'Make big, exaggerated stretching motions — the more dramatic, the more memorable!',
          researchNote: 'Ehri (2020) - Blending and segmenting are reciprocal skills that together build strong decoding ability.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Stretch the Sounds',
        duration: '3-4 min',
        focus: 'Phoneme Segmentation with Movement',
        researchBasis: 'Anthony & Francis (2005) - Kinesthetic approaches to phonemic awareness improve segmentation skills in young readers.',
        content: {
          goal: 'Teach your child to "stretch" words slowly, saying each sound while pulling their hands apart.',
          whatToSay: [
            '"Let\'s stretch the word \'bed\' like a rubber band!"',
            '"Start with your hands together and pull them apart as you say each sound!"',
            '"Ready? Hands together... /b/... (pull) /e/... (pull) /d/!"'
          ],
          whatToDo: [
            'Demonstrate: hold hands together in front of you.',
            'Say "/b/" and pull hands slightly apart.',
            'Say "/e/" and pull hands further apart.',
            'Say "/d/" and pull hands to full stretch.',
            'Have your child copy the motion with you.',
            'Practice with 5-6 words:',
            '  - bed',
            '  - leg',
            '  - pen',
            '  - red',
            '  - wet',
            '  - ten'
          ],
          examples: [
            'Hands together → "/l/" (pull) → "/e/" (pull) → "/g/" (full stretch) = "leg"'
          ],
          watchFor: [
            'Child coordinating hand movements with sounds.',
            'Clear pronunciation of each individual phoneme.',
            'Understanding that words are made of separate sounds.'
          ],
          parentTip: 'If they rush, slow them down: "Let\'s really stretch it out — super slow!"'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Snap It Together',
        duration: '4-5 min',
        focus: 'Sound Blending',
        researchBasis: 'National Reading Panel (2000) - Explicit blending instruction significantly improves word reading accuracy.',
        content: {
          goal: 'After stretching sounds apart, "snap" them back together quickly to form the whole word.',
          whatToSay: [
            '"Now we stretched the word apart — let\'s snap it back together!"',
            '"Start stretched out: /b/... /e/... /d/... now CLAP and say it fast: bed!"',
            '"Let\'s try another one!"'
          ],
          whatToDo: [
            'Start with hands fully stretched (from Step 1).',
            'Say the three sounds slowly while stretched.',
            'Clap hands together and say the whole word quickly.',
            'Repeat the full sequence:',
            '  - Stretch: "/b/ /e/ /d/"',
            '  - Snap: "bed!"',
            'Practice with 6-7 words:',
            '  - hen, net, pet, set, jet, vet, get',
            'Let your child lead on the last 2-3 words.'
          ],
          examples: [
            'Stretch: "/h/ /e/ /n/" (hands apart) → Snap: "hen!" (clap hands together)'
          ],
          watchFor: [
            'Smooth transition from segmented to blended sounds.',
            'Child able to say the whole word without hesitation.',
            'Excitement about the "snap" motion.'
          ],
          parentTip: 'The clap makes blending satisfying! It\'s like solving a puzzle — the pieces come together!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Stretch, Snap, Write',
        duration: '3-4 min',
        focus: 'Encoding Practice',
        researchBasis: 'National Reading Panel (2000) - Writing activities reinforce phonemic awareness and letter-sound knowledge.',
        content: {
          goal: 'Combine stretching, snapping, and writing to fully encode short-E words.',
          whatToSay: [
            '"Now I\'ll say a word, you stretch it, snap it, then write it!"',
            '"Ready? The word is \'pet.\'"',
            '"Stretch it out first... good! Now snap it... great! Now write it!"'
          ],
          whatToDo: [
            'Say a short-E word aloud (e.g., "pet").',
            'Have your child stretch the sounds with hand motions.',
            'Have them snap/clap and say the whole word.',
            'Then have them write the word on paper.',
            'Repeat with 4-5 words:',
            '  - pet, wet, jet, net, set',
            'Celebrate each successful stretch-snap-write!'
          ],
          examples: [
            'Word: "wet" → Stretch: "/w/ /e/ /t/" → Snap: "wet!" → Write: "wet"'
          ],
          watchFor: [
            'Correct segmentation during stretching.',
            'Successful blending during snapping.',
            'Accurate letter writing for each sound.'
          ],
          parentTip: 'If they get stuck writing, have them stretch it again — the motion helps them remember the sounds!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎯 You\'re a stretch and snap champion!',
            'You practiced:',
            '✓ Stretching sounds apart slowly',
            '✓ Snapping sounds together quickly',
            '✓ Writing words you decoded'
          ],
          reviewQuestions: [
            'Could your child stretch sounds clearly?',
            'Were they able to snap sounds together smoothly?',
            'Did they successfully write the words they decoded?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'The stretch-and-snap technique gives children a physical tool for decoding. When they encounter a tricky word, they can "stretch it out" to figure it out independently.'
          },
          nextSteps: [
            'Use stretch-and-snap whenever your child gets stuck on a word.',
            'Practice with objects: "Find something in the room you can stretch and snap!"',
            'Try it with their name or family members\' names!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 8 =========
  {
    id: 8,
    phase: '4',
    quarter: 1,
    week: 2,
    weekIndex: 1,
    dayInWeek: 3,
    title: 'Lesson 8: A or E? Sound Sorting',
    theme: 'CVC Mastery: Short E',
    skillRecurrenceTags: ['vowel-discrimination', 'phoneme-awareness', 'cvc-decoding'],
    relatedToPreviousSkills: ['cvc-decoding', 'short-vowels'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Sound Sorting!',
        duration: '1 min',
        content: {
          goal: 'Help your child distinguish between short-A and short-E vowel sounds through comparison and sorting.',
          parentGuide: [
            'Vowel discrimination is challenging but critical for accurate reading.',
            'Short A and short E are often confused because they\'re similar sounds.',
            'Direct comparison helps children notice the subtle differences.',
            'This lesson builds phonemic awareness — hearing the difference between similar sounds.'
          ],
          materials: ['Paper divided into two columns (A side, E side)', 'Pencil for writing', 'Household objects or quick sketches for each word'],
          tip: 'Exaggerate the vowel sounds when you say words — make the difference obvious at first!',
          researchNote: 'Lonigan et al. (2000) - Phoneme discrimination tasks, especially with minimal pairs, strengthen phonological awareness.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Review Both Vowels',
        duration: '2-3 min',
        focus: 'Vowel Sound Contrast',
        researchBasis: 'Ehri (2001) - Explicit vowel instruction with direct comparison improves decoding accuracy.',
        content: {
          goal: 'Review both short-A and short-E sounds side by side, highlighting the difference.',
          whatToSay: [
            '"Let\'s remember our two vowel friends!"',
            '"Short A says /a/ — your mouth is wide open, like you\'re surprised!"',
            '"Short E says /e/ — your mouth is more relaxed, like you\'re saying \'eh?\'"',
            '"Let\'s practice both: /a/ /e/ /a/ /e/!"'
          ],
          whatToDo: [
            'Show the letter A and make the /a/ sound.',
            'Show the letter E and make the /e/ sound.',
            'Alternate between the two: "/a/ /e/ /a/ /e/".',
            'Have your child mirror your mouth position.',
            'Point out the difference: "Feel how your mouth changes!"',
            'Practice with example words: "cat" vs. "bet".'
          ],
          examples: [
            '"Cat has /a/ — wide mouth! Bet has /e/ — relaxed mouth!"'
          ],
          watchFor: [
            'Child noticing the mouth position difference.',
            'Clear production of both vowel sounds.',
            'Interest in comparing the sounds.'
          ],
          parentTip: 'Use a mirror! Let them see their own mouth changing between /a/ and /e/.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Sort the Words',
        duration: '5-6 min',
        focus: 'Auditory Discrimination',
        researchBasis: 'Anthony & Francis (2005) - Sorting activities strengthen phonological awareness and support reading development.',
        content: {
          goal: 'Listen to words and sort them into "A words" or "E words" based on the middle vowel sound.',
          whatToSay: [
            '"I\'m going to say some words, and you tell me: A or E?"',
            '"Listen for the middle sound!"',
            '"First word: cat. Is that A or E?"'
          ],
          whatToDo: [
            'Draw a line down the middle of a paper.',
            'Write "A" on the left side, "E" on the right side.',
            'Say words one at a time (mix of short-A and short-E).',
            'Have your child point to the correct side.',
            'Then write the word on that side together.',
            'Use these words:',
            '  - cat (A), bed (E), hat (A), leg (E)',
            '  - map (A), pen (E), bag (A), ten (E)',
            '  - tap (A), wet (E), sad (A), red (E)'
          ],
          examples: [
            'You: "Bed — A or E?" Child: "E!" You: "Yes! Let\'s write it on the E side!"'
          ],
          watchFor: [
            'Correct identification of vowel sounds.',
            'Ability to distinguish similar-sounding words.',
            'Growing confidence with each word.'
          ],
          parentTip: 'If they struggle, exaggerate: "Bed: b-/EEEE/-d! That\'s E!" Then try again normally.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Minimal Pairs Challenge',
        duration: '3-4 min',
        focus: 'Fine Discrimination',
        researchBasis: 'National Reading Panel (2000) - Minimal pair practice improves phonemic awareness and decoding precision.',
        content: {
          goal: 'Practice with "minimal pairs" — words that differ by only one sound (bat vs. bet).',
          whatToSay: [
            '"Now for a tricky challenge! These words are almost the same — but not quite!"',
            '"Listen: bat... bet. What\'s different?"',
            '"That\'s right — the middle sound changes!"'
          ],
          whatToDo: [
            'Present word pairs that differ only in the vowel.',
            'Say both words: "bat... bet".',
            'Ask: "Which one has /a/? Which has /e/?"',
            'Practice with these pairs:',
            '  - bat / bet',
            '  - pan / pen',
            '  - man / men',
            '  - sad / sed (nonsense word — good practice!)',
            'Have your child decode both words if they can.',
            'Point out: "Just one sound changed the whole word!"'
          ],
          examples: [
            '"Listen: pan... pen. Pan is something you cook with (/a/). Pen is something you write with (/e/)!"'
          ],
          watchFor: [
            'Child hearing the vowel difference.',
            'Ability to identify which word has which vowel.',
            'Understanding that one sound changes meaning.'
          ],
          parentTip: 'Minimal pairs are hard! If they struggle, come back to this activity in a few days.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '👂 Your ears are super sharp!',
            'You practiced:',
            '✓ Comparing short-A and short-E sounds',
            '✓ Sorting words by vowel sound',
            '✓ Hearing differences in minimal pairs'
          ],
          reviewQuestions: [
            'Could your child tell the difference between /a/ and /e/?',
            'Were they able to sort words correctly?',
            'Did they notice the difference in minimal pairs?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Vowel discrimination is one of the hardest skills in early reading. Mastering it unlocks accurate decoding and prevents common reading errors like reading "bat" as "bet."'
          },
          nextSteps: [
            'Play "A or E?" during daily routines: "Is \'bed\' A or E?"',
            'Point out vowels in books: "Look, this word has short-E!"',
            'Make it a game: "I spy something with short-A in its name!"'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 9 =========
  {
    id: 9,
    phase: '4',
    quarter: 1,
    week: 2,
    weekIndex: 1,
    dayInWeek: 4,
    title: 'Lesson 9: Write the Missing Sound',
    theme: 'CVC Mastery: Short E',
    skillRecurrenceTags: ['cvc-decoding', 'phoneme-awareness', 'encoding'],
    relatedToPreviousSkills: ['cvc-decoding', 'phoneme-segmentation', 'short-vowels'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Missing Sound Detective!',
        duration: '1 min',
        content: {
          goal: 'Strengthen phonemic awareness by identifying and writing missing sounds in CVC words.',
          parentGuide: [
            'This lesson teaches children to analyze word structure — where each sound belongs.',
            'Finding missing sounds is harder than reading complete words — it requires deeper thinking.',
            'This builds encoding (spelling) skills alongside decoding (reading).',
            'Children learn that every position in a word matters: beginning, middle, end.'
          ],
          materials: ['Paper and pencil', 'Objects or drawings (optional)', 'Your encouragement!'],
          tip: 'Start with missing ending sounds (easiest), then beginning, then middle vowels (hardest).',
          researchNote: 'National Reading Panel (2000) - Phoneme manipulation activities, including deletion and substitution, strengthen overall phonemic awareness.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Find the Missing Beginning Sound',
        duration: '3-4 min',
        focus: 'Initial Sound Identification',
        researchBasis: 'Anthony & Francis (2005) - Phoneme identification tasks improve reading and spelling development.',
        content: {
          goal: 'Identify and write the missing beginning sound in short-E words.',
          whatToSay: [
            '"We\'re going to be sound detectives today!"',
            '"I\'ll show you a word with a missing sound, and you figure out what\'s missing!"',
            '"Here\'s your first mystery: _ed. What sound is missing at the beginning?"'
          ],
          whatToDo: [
            'Write "_ed" on paper with a blank line for the first letter.',
            'Say the word slowly: "b-e-d".',
            'Ask: "What sound comes first?"',
            'Have your child say "/b/" and write the letter B.',
            'Celebrate: "You solved it! B-E-D!"',
            'Practice with 4-5 words:',
            '  - _ed (bed)',
            '  - _et (pet, net, wet — accept any!)',
            '  - _en (hen, pen, ten)',
            '  - _eg (leg, peg)',
            '  - _ed (red)'
          ],
          examples: [
            'Write "_en" → Child says "/h/" → Writes "H" → "Hen!"'
          ],
          watchFor: [
            'Child correctly identifying the missing sound.',
            'Accurate letter writing for the sound.',
            'Understanding that the beginning sound changes the word.'
          ],
          parentTip: 'If they struggle, say the whole word slowly and ask them to repeat just the first sound.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Find the Missing Ending Sound',
        duration: '3-4 min',
        focus: 'Final Sound Identification',
        researchBasis: 'Ehri (2020) - Attention to all phoneme positions in words improves spelling and decoding accuracy.',
        content: {
          goal: 'Identify and write the missing ending sound in short-E words.',
          whatToSay: [
            '"Now let\'s find missing sounds at the END of words!"',
            '"Here\'s a tricky one: be_. The word is \'bed.\' What sound is missing?"',
            '"That\'s right — /d/! Write the letter D!"'
          ],
          whatToDo: [
            'Write "be_" on paper with a blank line for the last letter.',
            'Say the word: "bed".',
            'Ask: "What sound comes at the end?"',
            'Have your child say "/d/" and write the letter D.',
            'Practice with 5-6 words:',
            '  - be_ (bed)',
            '  - pe_ (pet, pen)',
            '  - le_ (leg, let)',
            '  - we_ (wet, web)',
            '  - re_ (red)',
            '  - te_ (ten)'
          ],
          examples: [
            'Write "le_" → Say "leg" → Child identifies "/g/" → Writes "G"'
          ],
          watchFor: [
            'Child isolating the final sound.',
            'Correct letter choice for the ending sound.',
            'Confidence building with each word.'
          ],
          parentTip: 'Ending sounds can be tricky! Emphasize them strongly when you say the word: "bed-D-D-D!"'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Find the Missing Middle Sound',
        duration: '3-4 min',
        focus: 'Medial Vowel Identification',
        researchBasis: 'Gough & Tunmer (1986) - Vowel identification is critical for reading accuracy and spelling development.',
        content: {
          goal: 'Identify and write the missing middle vowel sound — the trickiest position!',
          whatToSay: [
            '"This is the detective challenge! The middle sound is missing!"',
            '"Here\'s the word: b_d. Listen: bed. What sound is in the middle?"',
            '"Yes! /e/! Write the letter E!"'
          ],
          whatToDo: [
            'Write "b_d" on paper with a blank for the vowel.',
            'Say the word clearly: "bed".',
            'Ask: "What vowel sound do you hear in the middle?"',
            'Have your child identify /e/ and write E.',
            'Practice with 5-6 words:',
            '  - b_d (bed)',
            '  - p_t (pet)',
            '  - l_g (leg)',
            '  - w_t (wet)',
            '  - h_n (hen)',
            '  - r_d (red)',
            'This is harder — give lots of support!'
          ],
          examples: [
            'Write "p_n" → Say "pen" → Child identifies "/e/" → Writes "E"'
          ],
          watchFor: [
            'Child correctly hearing the vowel sound.',
            'Distinguishing between /a/ and /e/ if needed.',
            'Successful completion even with support.'
          ],
          parentTip: 'Middle sounds are hardest! If they struggle, stretch it out: "p-eeeee-t — what sound is in the middle?"'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🔍 You\'re an amazing sound detective!',
            'You practiced:',
            '✓ Finding missing beginning sounds',
            '✓ Finding missing ending sounds',
            '✓ Finding missing middle vowel sounds'
          ],
          reviewQuestions: [
            'Which position was easiest for your child (beginning, middle, or end)?',
            'Could they identify the missing vowel sounds?',
            'Did they understand how each sound position works?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Finding missing sounds requires deeper phonemic awareness than just reading words. This skill directly transfers to spelling — children learn to think about where each sound belongs in a word.'
          },
          nextSteps: [
            'Play "missing sound" games during transitions.',
            'When spelling new words, ask: "What sound comes first? Next? Last?"',
            'Create your own missing-sound puzzles together!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 10 =========
  {
    id: 10,
    phase: '4',
    quarter: 1,
    week: 2,
    weekIndex: 1,
    dayInWeek: 5,
    title: 'Lesson 10: Short E Mini Tale',
    theme: 'CVC Mastery: Short E',
    skillRecurrenceTags: ['cvc-decoding', 'comprehension', 'story-retelling'],
    relatedToPreviousSkills: ['cvc-decoding', 'short-vowels', 'listening-comprehension'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Mini Tale Time!',
        duration: '1 min',
        content: {
          goal: 'Listen to and retell a short story featuring short-E words, connecting decoding to comprehension.',
          parentGuide: [
            'This lesson wraps up Week 2 by putting short-E words into meaningful story context.',
            'Retelling stories strengthens comprehension and oral language skills.',
            'Using pictures or gestures helps children organize their thoughts.',
            'This shows reading is about understanding stories, not just saying words.'
          ],
          materials: ['Paper and crayons', 'Your storytelling voice', 'Optional: simple props or toys'],
          tip: 'Use expression and drama when reading — make the story come alive!',
          researchNote: 'Whitehurst & Lonigan (1998) - Story retelling is a strong predictor of reading comprehension development.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Listen to the Story',
        duration: '4-5 min',
        focus: 'Listening Comprehension',
        researchBasis: 'National Early Literacy Panel (2008) - Oral language comprehension forms the foundation for reading comprehension.',
        content: {
          goal: 'Read a simple short-E story while your child listens attentively.',
          whatToSay: [
            '"I have a special story for you about a hen and her friends!"',
            '"Listen carefully to all the short-E words!"',
            '"Ready? Here we go!"'
          ],
          whatToDo: [
            'Read this story with expression:',
            '',
            '"The Red Hen and Her Friends"',
            'A red hen met a pet dog.',
            'The dog was wet from the rain.',
            'The hen said, "Get in my pen!"',
            'They sat on a bed of hay.',
            'Ten chicks came to see them.',
            'The dog said, "This is the best day!"',
            '',
            'Read slowly and clearly.',
            'Emphasize short-E words slightly.',
            'Pause after each sentence to check understanding.',
            'Ask simple questions: "What color was the hen?" "How many chicks came?"'
          ],
          examples: [
            'After "The dog was wet," ask: "Why was the dog wet?"'
          ],
          watchFor: [
            'Child listening attentively.',
            'Engagement with the story.',
            'Ability to answer simple comprehension questions.'
          ],
          parentTip: 'Read it twice! First for enjoyment, second for retelling preparation.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Act It Out',
        duration: '4-5 min',
        focus: 'Physical Story Retelling',
        researchBasis: 'Hirsh-Pasek et al. (2017) - Active, engaged play enhances language development and comprehension.',
        content: {
          goal: 'Have your child retell the story using gestures, movements, or simple props.',
          whatToSay: [
            '"Now it\'s your turn to tell the story!"',
            '"You can use your hands, your body, or these toys to show what happened!"',
            '"What happened first in the story?"'
          ],
          whatToDo: [
            'Encourage your child to act out the story.',
            'They can use:',
            '  - Hand gestures (flapping for hen, panting for dog)',
            '  - Body movements (getting "wet," sitting on hay)',
            '  - Simple toys or stuffed animals',
            '  - Drawings from previous lessons',
            'Prompt if they get stuck: "What did the hen do next?"',
            'Celebrate any retelling, even if incomplete.',
            'Focus on story sequence: beginning, middle, end.'
          ],
          examples: [
            'Child flaps arms: "The red hen met a dog!" Pretends to shake off water: "The dog was wet!"'
          ],
          watchFor: [
            'Child retelling events in correct sequence.',
            'Using short-E vocabulary from the story.',
            'Engaging physically with the narrative.'
          ],
          parentTip: 'Join in! Act out the story together — you be the dog, they be the hen!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Draw and Label',
        duration: '3-4 min',
        focus: 'Visual Story Summary',
        researchBasis: 'Scarborough (2001) - Connecting visual representation to text strengthens comprehension and word recognition.',
        content: {
          goal: 'Have your child draw their favorite part of the story and label it with short-E words.',
          whatToSay: [
            '"What was your favorite part of the story?"',
            '"Let\'s draw that part and label it!"',
            '"Can you write some of the short-E words from the story?"'
          ],
          whatToDo: [
            'Let your child choose their favorite scene.',
            'Have them draw it (simple sketch is fine!).',
            'Help them label key elements:',
            '  - red hen',
            '  - wet dog',
            '  - pen',
            '  - bed',
            '  - ten chicks',
            'Encourage writing 2-4 labels independently.',
            'Celebrate their story art!'
          ],
          examples: [
            'Child draws hen and dog sitting together, labels: "red hen" "wet dog" "bed"'
          ],
          watchFor: [
            'Child selecting a meaningful scene.',
            'Attempting to write short-E words.',
            'Connecting their drawing to the story.'
          ],
          parentTip: 'This drawing becomes a memory tool! They can use it to retell the story again later.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '📖 You\'re a wonderful storyteller!',
            'You practiced:',
            '✓ Listening to a short-E story',
            '✓ Retelling the story with actions',
            '✓ Drawing and labeling your favorite part',
            '',
            '🎉 Week 2 Complete! You\'ve mastered short-E words!'
          ],
          reviewQuestions: [
            'Could your child retell the story in sequence?',
            'Did they use short-E vocabulary from the story?',
            'Did they enjoy acting out and drawing the story?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Week 2 wrapped up with story comprehension — the ultimate goal of reading! Your child can now decode short-A AND short-E words and understand them in context. That\'s huge progress!'
          },
          nextSteps: [
            'Reread "The Red Hen" story throughout the week.',
            'Create your own short-E stories together!',
            'Get ready for Week 3: Sound Blending Mastery across all vowels!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 11 =========
  {
    id: 11,
    phase: '4',
    quarter: 1,
    week: 3,
    weekIndex: 2,
    dayInWeek: 1,
    title: 'Lesson 11: Robot to Reader',
    theme: 'Sound Blending Mastery',
    skillRecurrenceTags: ['blending', 'cvc-decoding', 'phoneme-awareness'],
    relatedToPreviousSkills: ['blending', 'cvc-decoding', 'short-vowels'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Robot to Reader!',
        duration: '1 min',
        content: {
          goal: 'Transform choppy "robot talk" into smooth, fluent reading by blending segmented sounds.',
          parentGuide: [
            'This lesson strengthens blending across ALL vowels learned so far (A, E, I, O, U).',
            'Robot talk (segmented sounds) mimics how beginning readers often sound.',
            'The goal is to move from choppy to smooth — from robot to reader!',
            'This builds automaticity — the ability to decode quickly without thinking.'
          ],
          materials: ['Your voice (robot and regular!)', 'Paper and pencil', 'Optional: toy robot for fun'],
          tip: 'Make robot voice fun and exaggerated! Stiff movements and mechanical sounds make it memorable.',
          researchNote: 'Ehri (2020) - Automaticity in blending is essential for reading fluency and comprehension.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Talk Like a Robot',
        duration: '3-4 min',
        focus: 'Segmented Speech Recognition',
        researchBasis: 'National Reading Panel (2000) - Explicit blending practice improves word recognition and reading fluency.',
        content: {
          goal: 'Introduce "robot talk" where words are broken into individual sounds with pauses.',
          whatToSay: [
            '"Today we\'re going to talk like robots!"',
            '"Robots say words in a choppy way: /c/... /a/... /t/!"',
            '"Can you guess what word the robot is saying?"',
            '"That\'s right — cat! Now YOU say it like a robot!"'
          ],
          whatToDo: [
            'Demonstrate robot voice with stiff movements.',
            'Say a word in robot talk: "/d/... /o/... /g/".',
            'Pause between each sound (1-2 seconds).',
            'Have your child guess the word: "dog!"',
            'Then have THEM say words in robot talk.',
            'Practice with 5-6 words:',
            '  - cat, dog, pig, sun, bed, hat',
            'Switch roles — you guess their robot words!'
          ],
          examples: [
            'Robot says: "/p/... /i/... /g/" Child: "Pig!" Child robot: "/s/... /u/... /n/" You: "Sun!"'
          ],
          watchFor: [
            'Child understanding the concept of segmented speech.',
            'Ability to blend robot talk into words.',
            'Enjoyment of the playful robot character.'
          ],
          parentTip: 'Make it silly! Walk stiffly, use a monotone voice, and pretend to "power down" between words!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Robot to Reader Challenge',
        duration: '5-6 min',
        focus: 'Blending Fluency',
        researchBasis: 'Lonigan et al. (2000) - Repeated blending practice develops phonological awareness and reading automaticity.',
        content: {
          goal: 'Practice turning robot talk into smooth, fluent reading across multiple vowels.',
          whatToSay: [
            '"Now let\'s help the robot become a reader!"',
            '"I\'ll say words in robot talk, and you say them smoothly like a reader!"',
            '"Ready? Robot says: /b/... /e/... /d/. Reader says...?"',
            '"Bed! Perfect! You turned robot talk into reading!"'
          ],
          whatToDo: [
            'Say words in robot talk (segmented).',
            'Your child responds with smooth blending.',
            'Start with short pauses, then make pauses longer (harder).',
            'Use words with different vowels:',
            '  Short A: /c/... /a/... /t/ (cat)',
            '  Short E: /l/... /e/... /g/ (leg)',
            '  Short I: /p/... /i/... /g/ (pig)',
            '  Short O: /d/... /o/... /g/ (dog)',
            '  Short U: /b/... /u/... /g/ (bug)',
            'Practice 10-12 words total.',
            'Celebrate each successful blend!'
          ],
          examples: [
            'Robot: "/h/... /o/... /p/" Child: "Hop!" You: "You\'re becoming a reading expert!"'
          ],
          watchFor: [
            'Child blending smoothly without hesitation.',
            'Success with different vowel sounds.',
            'Growing confidence with longer pauses.'
          ],
          parentTip: 'If they struggle, reduce the pause length. Speed = automaticity building!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Speed Round!',
        duration: '3-4 min',
        focus: 'Blending Automaticity',
        researchBasis: 'Ehri (2001) - Timed reading activities build automaticity and prepare children for fluent reading.',
        content: {
          goal: 'Challenge your child to blend words as quickly as possible, building speed and confidence.',
          whatToSay: [
            '"Now for the speed challenge! How fast can you turn robot talk into words?"',
            '"I\'ll say words fast, and you blend them super quick!"',
            '"Ready, set, blend!"'
          ],
          whatToDo: [
            'Say words in robot talk but with shorter pauses.',
            'Your child blends as quickly as they can.',
            'Use a timer (optional) for extra motivation.',
            'Practice 8-10 words:',
            '  - /m/... /a/... /p/ (map)',
            '  - /r/... /e/... /d/ (red)',
            '  - /s/... /i/... /t/ (sit)',
            '  - /t/... /o/... /p/ (top)',
            '  - /c/... /u/... /p/ (cup)',
            '  - /b/... /a/... /g/ (bag)',
            '  - /p/... /e/... /t/ (pet)',
            '  - /w/... /i/... /n/ (win)',
            'Celebrate their speed!',
            'Optional: Try again to "beat their record!"'
          ],
          examples: [
            '"Ready? /r/-/u/-/n/" Child: "Run!" You: "Wow, so fast!"'
          ],
          watchFor: [
            'Rapid blending without pausing.',
            'Accuracy maintained even at speed.',
            'Excitement about the challenge.'
          ],
          parentTip: 'Speed builds automaticity, but accuracy comes first! It\'s okay if they\'re not super fast yet.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🤖 You transformed from robot to reader!',
            'You practiced:',
            '✓ Understanding robot talk (segmented sounds)',
            '✓ Blending words smoothly across all vowels',
            '✓ Speed blending challenge'
          ],
          reviewQuestions: [
            'Could your child blend robot talk easily?',
            'Did they succeed with different vowels (A, E, I, O, U)?',
            'Were they able to blend quickly in the speed round?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Blending is the core skill of reading. The faster and smoother your child can blend sounds, the more fluent their reading will become. Robot talk makes this hard skill fun and playful!'
          },
          nextSteps: [
            'Play robot talk during car rides or transitions.',
            'Take turns being the robot — let your child segment words for you!',
            'Gradually increase the pause length to keep it challenging.'
          ]
        }
      }
    ]
  },

  // ========= LESSON 12 =========
  {
    id: 12,
    phase: '4',
    quarter: 1,
    week: 3,
    weekIndex: 2,
    dayInWeek: 2,
    title: 'Lesson 12: Blend Train',
    theme: 'Sound Blending Mastery',
    skillRecurrenceTags: ['blending', 'sequencing', 'cvc-decoding'],
    relatedToPreviousSkills: ['blending', 'cvc-decoding', 'phoneme-awareness'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Blend Train!',
        duration: '1 min',
        content: {
          goal: 'Use the visual metaphor of a train to practice blending sounds into words.',
          parentGuide: [
            'This lesson makes blending concrete through a train metaphor.',
            'Each sound is a "train car" that connects to form a complete word.',
            'Visual and kinesthetic learners especially benefit from this approach.',
            'The train can be drawn, acted out, or imagined — flexibility is key!'
          ],
          materials: ['Paper and crayons', 'Optional: toy train or blocks to represent cars', 'Your imagination!'],
          tip: 'Make train sounds! "Choo-choo" between words keeps energy high and makes it memorable.',
          researchNote: 'Anthony & Francis (2005) - Multisensory approaches to phonemic awareness strengthen learning and retention.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Build the Train',
        duration: '4-5 min',
        focus: 'Visual Sound Sequencing',
        researchBasis: 'National Reading Panel (2000) - Visual representation of phonemes supports blending development.',
        content: {
          goal: 'Teach your child to see each sound as a separate train car that connects to form a word.',
          whatToSay: [
            '"Let\'s build a word train! Each sound is a train car!"',
            '"The word is \'cat.\' How many train cars do we need?"',
            '"That\'s right — three! One for /c/, one for /a/, one for /t/!"',
            '"Let\'s draw them!"'
          ],
          whatToDo: [
            'Draw three connected rectangles (train cars).',
            'Write one letter in each car: C - A - T.',
            'Point to each car and say its sound.',
            'Then "connect" the cars by running your finger along them.',
            'Say the sounds smoothly as you connect: "/c/-/a/-/t/ — cat!"',
            'Draw 4-5 more word trains:',
            '  - dog (D-O-G)',
            '  - pig (P-I-G)',
            '  - bed (B-E-D)',
            '  - sun (S-U-N)',
            '  - map (M-A-P)',
            'Let your child draw some trains independently.'
          ],
          examples: [
            'Draw three cars: [P][I][G] → Point and say: "/p/ /i/ /g/" → Swipe across: "pig!"'
          ],
          watchFor: [
            'Child understanding each sound as a separate unit.',
            'Ability to connect the sounds smoothly.',
            'Engagement with the visual representation.'
          ],
          parentTip: 'Use colors! Each vowel can be a different color train car to help them stand out.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Connect the Cars',
        duration: '4-5 min',
        focus: 'Blending Practice',
        researchBasis: 'Ehri (2020) - Sequential phoneme processing with visual support improves decoding accuracy.',
        content: {
          goal: 'Practice "connecting train cars" by blending sounds into words, emphasizing smooth transitions.',
          whatToSay: [
            '"Now we\'re going to connect the train cars and make words!"',
            '"Watch as I point to each car — when I reach the end, the train is complete!"',
            '"Your turn! Point to each car and say the sound, then blend them together!"'
          ],
          whatToDo: [
            'Draw a new word train (e.g., "dog").',
            'Have your child point to each letter.',
            'They say each sound as they point.',
            'When they reach the end, they say the whole word.',
            'Practice connecting 6-8 trains:',
            '  - run (R-U-N)',
            '  - wet (W-E-T)',
            '  - hop (H-O-P)',
            '  - sit (S-I-T)',
            '  - pen (P-E-N)',
            '  - bag (B-A-G)',
            '  - leg (L-E-G)',
            '  - cup (C-U-P)',
            'Make train sounds between words: "Choo-choo!"'
          ],
          examples: [
            'Point: [R] "/r/" → [U] "/u/" → [N] "/n/" → Swipe: "Run! The train is complete!"'
          ],
          watchFor: [
            'Smooth pointing and sound production.',
            'Successful blending at the end.',
            'Independence in the process.'
          ],
          parentTip: 'Let them be the conductor! They can "drive" the train by pointing and blending.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Mystery Train',
        duration: '3-4 min',
        focus: 'Auditory Blending',
        researchBasis: 'Lonigan et al. (2000) - Auditory blending without visual support strengthens phonological awareness.',
        content: {
          goal: 'Challenge your child to blend "mystery trains" — words you say aloud without showing them.',
          whatToSay: [
            '"Now for the mystery train challenge!"',
            '"I\'ll tell you what sounds are in each train car, and you guess the word!"',
            '"Ready? The train has three cars: /h/... /o/... /t/. What word is it?"',
            '"Hot! You figured out the mystery train!"'
          ],
          whatToDo: [
            'Say the sounds of a word separately.',
            'Your child blends them and says the word.',
            'No visual support — pure auditory blending!',
            'Start with easier words, then increase difficulty.',
            'Practice 8-10 mystery trains:',
            '  - /c/... /a/... /n/ (can)',
            '  - /b/... /e/... /d/ (bed)',
            '  - /f/... /i/... /t/ (fit)',
            '  - /m/... /o/... /p/ (mop)',
            '  - /r/... /u/... /g/ (rug)',
            '  - /t/... /a/... /p/ (tap)',
            '  - /n/... /e/... /t/ (net)',
            '  - /z/... /i/... /p/ (zip)',
            'Celebrate each correct answer!'
          ],
          examples: [
            'You: "The mystery train is /p/... /e/... /n/!" Child: "Pen!" You: "Yes! Mystery solved!"'
          ],
          watchFor: [
            'Child blending without visual cues.',
            'Success across different vowels.',
            'Confidence in auditory-only blending.'
          ],
          parentTip: 'This is harder without pictures! If they struggle, go back to drawing the train first.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🚂 All aboard the reading train!',
            'You practiced:',
            '✓ Building word trains with sound cars',
            '✓ Connecting sounds smoothly',
            '✓ Solving mystery trains (auditory blending)'
          ],
          reviewQuestions: [
            'Could your child visualize sounds as train cars?',
            'Were they able to blend smoothly when connecting the cars?',
            'Did they succeed at mystery trains without visual support?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'The train metaphor makes abstract blending concrete. Children can "see" how individual sounds connect to form words, which helps them decode independently when reading.'
          },
          nextSteps: [
            'Draw word trains for any new vocabulary!',
            'Play "mystery train" during waiting times.',
            'Let your child create their own word trains and teach YOU!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 13 =========
  {
    id: 13,
    phase: '4',
    quarter: 1,
    week: 3,
    weekIndex: 2,
    dayInWeek: 3,
    title: 'Lesson 13: Mystery Word Bag (Hands Only)',
    theme: 'Sound Blending Mastery',
    skillRecurrenceTags: ['blending', 'kinesthetic-learning', 'auditory-processing'],
    relatedToPreviousSkills: ['blending', 'cvc-decoding', 'phoneme-awareness'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Mystery Word Bag!',
        duration: '1 min',
        content: {
          goal: 'Blend sounds into words, then act them out using only gestures and movement.',
          parentGuide: [
            'This lesson combines auditory blending with kinesthetic expression.',
            'Children decode words by listening, then show understanding through movement.',
            'Movement reinforces memory and makes abstract sounds concrete.',
            'No materials needed — just voices, hands, and imagination!'
          ],
          materials: ['Your voice', 'Your hands and body', 'Space to move and gesture'],
          tip: 'Model dramatic gestures! Over-exaggerate movements to make words memorable and fun.',
          researchNote: 'Anthony & Francis (2005) - Kinesthetic learning enhances phonological awareness and word retention.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Decode the Mystery',
        duration: '3-4 min',
        focus: 'Auditory Blending',
        researchBasis: 'Lonigan et al. (2000) - Auditory blending without visual cues strengthens phonological processing.',
        content: {
          goal: 'Practice blending sounds into words through listening alone, building auditory decoding skills.',
          whatToSay: [
            '"I have mystery words hiding in my word bag!"',
            '"I\'ll say the sounds, and you blend them into a word!"',
            '"Ready? First mystery word: /j/... /u/... /m/... /p/!"',
            '"What word is it?"',
            '"Jump! Yes! Now let\'s act it out!"'
          ],
          whatToDo: [
            'Say a word slowly in segmented sounds.',
            'Pause briefly between each sound (1 second).',
            'Your child blends and says the complete word.',
            'Once decoded, move to acting it out (next step).',
            'Start with 5-6 action words:',
            '  - /j/... /u/... /m/... /p/ (jump)',
            '  - /r/... /u/... /n/ (run)',
            '  - /h/... /o/... /p/ (hop)',
            '  - /s/... /i/... /t/ (sit)',
            '  - /c/... /l/... /a/... /p/ (clap)',
            '  - /s/... /p/... /i/... /n/ (spin)',
            'Celebrate each successful blend!'
          ],
          examples: [
            'You: "/r/... /u/... /n/" Child: "Run!" You: "Perfect! Now show me running!"'
          ],
          watchFor: [
            'Child blending sounds without visual support.',
            'Accurate decoding across different vowels.',
            'Readiness to connect words to movement.'
          ],
          parentTip: 'Keep pauses short! Longer pauses make blending harder for beginners.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Act It Out!',
        duration: '4-5 min',
        focus: 'Kinesthetic Word Connection',
        researchBasis: 'Hirsh-Pasek et al. (2017) - Movement-based learning enhances memory and comprehension in young children.',
        content: {
          goal: 'Connect decoded words to physical actions, reinforcing meaning and memory through movement.',
          whatToSay: [
            '"Now for the fun part — let\'s ACT OUT the words you decoded!"',
            '"When you figure out the word, show me with your body!"',
            '"Mystery word: /w/... /a/... /v/... /e/!"',
            '"Wave! Yes! Now show me waving!"'
          ],
          whatToDo: [
            'Say a word in segmented sounds.',
            'Child decodes the word.',
            'Child immediately acts it out with gestures.',
            'Join in! Mirror their movements.',
            'Practice 8-10 action/object words:',
            '  Actions:',
            '    - /d/... /a/... /n/... /c/... /e/ (dance)',
            '    - /s/... /t/... /o/... /p/ (stop - freeze pose)',
            '    - /w/... /a/... /l/... /k/ (walk)',
            '    - /k/... /i/... /c/... /k/ (kick)',
            '  Objects (gesture showing them):',
            '    - /c/... /u/... /p/ (cup - pretend to drink)',
            '    - /b/... /o/... /o/... /k/ (book - pretend to read)',
            '    - /h/... /a/... /t/ (hat - touch head)',
            '    - /b/... /a/... /l/... /l/ (ball - throw motion)',
            'Make it playful and energetic!'
          ],
          examples: [
            'You: "/h/... /o/... /p/" Child: "Hop!" (starts hopping) You: "Great hopping! I\'ll hop too!"'
          ],
          watchFor: [
            'Child connecting words to appropriate actions.',
            'Enthusiastic physical participation.',
            'Immediate response (showing automaticity).'
          ],
          parentTip: 'Let them be creative! If their gesture is different from yours, that\'s perfect — they\'re showing comprehension their way.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Reverse Mystery',
        duration: '3-4 min',
        focus: 'Segmentation Practice',
        researchBasis: 'Ehri (2020) - Bidirectional phoneme work (blending AND segmenting) strengthens decoding skills.',
        content: {
          goal: 'Reverse the game — child acts out a word, parent guesses, then child segments the sounds.',
          whatToSay: [
            '"Now YOU pick a mystery word!"',
            '"Act it out, and I\'ll guess what it is!"',
            '"Then we\'ll say it in robot talk together!"',
            '"Show me your first mystery word!"'
          ],
          whatToDo: [
            'Child thinks of an action or object word.',
            'Child acts it out silently.',
            'You guess the word: "Jump!"',
            'Then TOGETHER, segment it: "/j/... /u/... /m/... /p/"',
            'Switch roles every 2-3 words.',
            'Suggested word bank for child to choose from:',
            '  - sit, run, hop, clap, spin, wave',
            '  - cat, dog, cup, pen, hat, bed',
            'Practice 6-8 words total.',
            'If child struggles to segment, you model first.'
          ],
          examples: [
            'Child: (pretends to sleep) You: "Sleep!" Both: "/s/... /l/... /ee/... /p/" (or simpler: /s/-/l/-/p/ for CVC)'
          ],
          watchFor: [
            'Child\'s ability to segment words into sounds.',
            'Creativity in choosing and acting words.',
            'Turn-taking and engagement.'
          ],
          parentTip: 'If segmenting is hard, focus on CVC words (3 sounds). Longer words come later!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎭 You became a word detective AND an actor!',
            'You practiced:',
            '✓ Blending mystery words from sounds',
            '✓ Acting out words with movement',
            '✓ Breaking words back into sounds (segmenting)'
          ],
          reviewQuestions: [
            'Could your child blend words without seeing them written?',
            'Did they connect words to appropriate actions?',
            'Were they able to segment words back into sounds?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Movement makes abstract sounds concrete. When children physically experience words, they remember them better and understand them more deeply. This multisensory approach accelerates reading comprehension.'
          },
          nextSteps: [
            'Play "Mystery Word" during transitions (car rides, waiting in line).',
            'Let your child create their own mystery words for family members to guess.',
            'Add emotion words: "Can you act out /s/... /a/... /d/?"'
          ]
        }
      }
    ]
  },

  // ========= LESSON 14 =========
  {
    id: 14,
    phase: '4',
    quarter: 1,
    week: 3,
    weekIndex: 2,
    dayInWeek: 4,
    title: 'Lesson 14: Build, Blend, Break',
    theme: 'Sound Blending Mastery',
    skillRecurrenceTags: ['blending', 'segmentation', 'phoneme-manipulation'],
    relatedToPreviousSkills: ['blending', 'cvc-decoding', 'phoneme-awareness'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Build, Blend, Break!',
        duration: '1 min',
        content: {
          goal: 'Master the complete phoneme awareness cycle: building words from sounds, blending them, then breaking them apart.',
          parentGuide: [
            'This lesson develops bidirectional phoneme manipulation — a critical reading skill.',
            'Children practice moving between sounds and whole words flexibly.',
            'This "reversible thinking" is essential for both reading AND spelling.',
            'The three-step cycle mirrors how skilled readers process words.'
          ],
          materials: ['Paper and pencil', 'Drawing supplies', 'Optional: letter tiles or cards'],
          tip: 'Use hand motions! Bring hands together for "blend," pull apart for "break."',
          researchNote: 'National Reading Panel (2000) - Phoneme manipulation (blending and segmentation) is the strongest predictor of reading success.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Build It',
        duration: '4-5 min',
        focus: 'Sound Assembly',
        researchBasis: 'Ehri (2001) - Constructing words from individual phonemes develops phonological awareness and orthographic mapping.',
        content: {
          goal: 'Build words by assembling individual sounds, creating a visual and auditory foundation.',
          whatToSay: [
            '"Let\'s be word builders! We\'ll put sounds together like building blocks!"',
            '"I\'ll give you three sounds, and you put them together to BUILD a word!"',
            '"First word: /c/... /a/... /t/. Can you build it?"',
            '"Yes! Cat! Now let\'s draw three boxes and write one sound in each!"'
          ],
          whatToDo: [
            'Draw three connected boxes on paper (sound boxes).',
            'Say three separate sounds.',
            'Child writes one letter in each box as you say the sounds.',
            'Say the sounds again, pointing to each box.',
            'Practice building 6-8 words:',
            '  - /d/... /o/... /g/ (dog)',
            '  - /s/... /u/... /n/ (sun)',
            '  - /p/... /i/... /g/ (pig)',
            '  - /b/... /e/... /d/ (bed)',
            '  - /h/... /a/... /t/ (hat)',
            '  - /r/... /u/... /g/ (rug)',
            '  - /p/... /e/... /n/ (pen)',
            '  - /m/... /o/... /p/ (mop)',
            'Let child draw and fill boxes for some words independently.'
          ],
          examples: [
            'You: "/m/... /a/... /p/" Child writes: [M][A][P] You: "Perfect! You built MAP!"'
          ],
          watchFor: [
            'Child correctly matching sounds to letters.',
            'Understanding that each box = one sound.',
            'Growing independence in sound assembly.'
          ],
          parentTip: 'If they write wrong letters, say the sound again and ask, "Which letter makes that sound?"'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Blend It',
        duration: '3-4 min',
        focus: 'Sound Synthesis',
        researchBasis: 'Lonigan et al. (2000) - Repeated blending practice builds automaticity and prepares children for fluent reading.',
        content: {
          goal: 'Take the built words and blend them smoothly into whole words, practicing fluency.',
          whatToSay: [
            '"Now that we\'ve BUILT words, let\'s BLEND them!"',
            '"Point to each box and say its sound, then swipe across and say the whole word!"',
            '"Ready? Point to the first box in CAT!"'
          ],
          whatToDo: [
            'Use the words built in Step 1.',
            'Child points to each sound box.',
            'Child says each sound: "/c/... /a/... /t/"',
            'Child swipes finger across all boxes.',
            'Child says the blended word: "Cat!"',
            'Practice with ALL words from Step 1.',
            'Gradually increase speed:',
            '  - Round 1: Slow and careful',
            '  - Round 2: A bit faster',
            '  - Round 3: Quick and smooth!',
            'Celebrate smooth blending!'
          ],
          examples: [
            'Child: (points) "/d/... /o/... /g/" (swipes) "Dog!" You: "Perfect blend!"'
          ],
          watchFor: [
            'Smooth transitions between sounds.',
            'Finger movement coordinating with speech.',
            'Increasing speed without losing accuracy.'
          ],
          parentTip: 'The swipe motion is key! It helps children feel the sounds connecting together.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Break It',
        duration: '3-4 min',
        focus: 'Sound Segmentation',
        researchBasis: 'Ehri (2020) - Segmentation (breaking words into phonemes) develops spelling ability and deepens phonological awareness.',
        content: {
          goal: 'Take whole words and break them back into individual sounds, completing the cycle.',
          whatToSay: [
            '"Now let\'s BREAK words apart like we\'re word scientists!"',
            '"I\'ll say a whole word, and you break it into separate sounds!"',
            '"The word is RUN. Can you break it apart?"',
            '"Yes! /r/... /u/... /n/! You broke it perfectly!"'
          ],
          whatToDo: [
            'Say a complete word.',
            'Child breaks it into individual sounds.',
            'Use hand motions: hands together (whole word), pull apart (separate sounds).',
            'Practice with 8-10 words:',
            '  - cat → /c/... /a/... /t/',
            '  - dog → /d/... /o/... /g/',
            '  - sun → /s/... /u/... /n/',
            '  - bed → /b/... /e/... /d/',
            '  - hop → /h/... /o/... /p/',
            '  - sit → /s/... /i/... /t/',
            '  - leg → /l/... /e/... /g/',
            '  - cup → /c/... /u/... /p/',
            'Optional: Draw boxes as they segment.',
            'Switch roles — child says words, you break them!'
          ],
          examples: [
            'You: "PIG" Child: (pulls hands apart) "/p/... /i/... /g/" You: "You broke it! Great job!"'
          ],
          watchFor: [
            'Accurate segmentation of all three sounds.',
            'Appropriate pauses between sounds.',
            'Ability to segment different vowel patterns.'
          ],
          parentTip: 'Breaking is harder than blending! If they struggle, go back to building and blending first.'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Build-Blend-Break Challenge',
        duration: '2-3 min',
        focus: 'Integrated Practice',
        researchBasis: 'National Reading Panel (2000) - Integrated phoneme manipulation activities strengthen overall reading ability.',
        content: {
          goal: 'Complete the full cycle with multiple words rapidly, building automaticity.',
          whatToSay: [
            '"Now for the ultimate challenge — Build, Blend, AND Break!"',
            '"I\'ll give you sounds, you build the word, blend it, then break it back apart!"',
            '"Ready for the triple challenge?"'
          ],
          whatToDo: [
            'Say three sounds: "/b/... /a/... /g/"',
            'Child builds: writes B-A-G',
            'Child blends: "Bag!"',
            'Child breaks: "/b/... /a/... /g/"',
            'Complete the cycle with 4-5 words:',
            '  - /m/... /a/... /p/ → MAP → /m/-/a/-/p/',
            '  - /r/... /e/... /d/ → RED → /r/-/e/-/d/',
            '  - /w/... /i/... /n/ → WIN → /w/-/i/-/n/',
            '  - /t/... /o/... /p/ → TOP → /t/-/o/-/p/',
            '  - /b/... /u/... /s/ → BUS → /b/-/u/-/s/',
            'Time them (optional): "Can you do it in 30 seconds?"',
            'Celebrate completing the full cycle!'
          ],
          examples: [
            'You: "/h/... /a/... /t/" Child: (writes HAT) "Hat!" (breaks) "/h/-/a/-/t/" You: "Amazing cycle!"'
          ],
          watchFor: [
            'Fluency through the complete cycle.',
            'Confidence with all three skills.',
            'Speed AND accuracy.'
          ],
          parentTip: 'This is advanced! If it\'s overwhelming, practice each step separately for a few more days.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🔨 You\'re a master word builder!',
            'You practiced:',
            '✓ Building words from individual sounds',
            '✓ Blending sounds into whole words',
            '✓ Breaking words back into sounds',
            '✓ Completing the full Build-Blend-Break cycle'
          ],
          reviewQuestions: [
            'Could your child build words from sounds successfully?',
            'Did they blend smoothly and accurately?',
            'Were they able to break words back into sounds?',
            'Did they complete the full cycle with confidence?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'The Build-Blend-Break cycle develops flexible phoneme awareness — the ability to move fluidly between sounds and words. This flexibility is the foundation of both reading AND spelling. Children who master this cycle become confident, independent readers and writers.'
          },
          nextSteps: [
            'Practice one step at a time throughout the day (build during breakfast, blend during car rides, break at bedtime).',
            'Let your child teach the cycle to a stuffed animal or sibling.',
            'Gradually introduce 4-sound words (e.g., flag, stop) as they gain confidence.'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 15 =========
  {
    id: 15,
    phase: '4',
    quarter: 1,
    week: 3,
    weekIndex: 2,
    dayInWeek: 5,
    title: 'Lesson 15: Blending Storytime',
    theme: 'Sound Blending Mastery',
    skillRecurrenceTags: ['blending', 'reading-fluency', 'comprehension'],
    relatedToPreviousSkills: ['blending', 'cvc-decoding', 'story-sequencing'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Blending Storytime!',
        duration: '1 min',
        content: {
          goal: 'Apply blending skills in context by decoding words within a simple story.',
          parentGuide: [
            'This lesson bridges isolated word practice to real reading.',
            'Children decode 6-8 CVC words embedded in a meaningful story.',
            'Context helps children check if their blending makes sense.',
            'This is the transition from "learning to read" to "reading to learn."'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Your storytelling voice!'],
          tip: 'Make it dramatic! Use different voices for characters and pause before decodable words.',
          researchNote: 'Scarborough (2001) - Reading comprehension requires both decoding AND language comprehension working together (The Reading Rope).'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Story Preview',
        duration: '2-3 min',
        focus: 'Story Context & Vocabulary',
        researchBasis: 'National Reading Panel (2000) - Pre-reading activities improve comprehension and prepare children for decoding success.',
        content: {
          goal: 'Build background knowledge and excitement before reading, setting children up for success.',
          whatToSay: [
            '"Today we\'re reading a special story together!"',
            '"It\'s called \'Sam the Cat.\' What do you think it might be about?"',
            '"Let\'s look at what we might see in the story!"',
            '"There might be words like: cat, mat, sat, hat. Let\'s practice those!"'
          ],
          whatToDo: [
            'Introduce the story title and main character.',
            'Ask prediction questions: "What do you think will happen?"',
            'Pre-teach 4-5 key words from the story:',
            '  - cat',
            '  - mat',
            '  - sat',
            '  - hat',
            '  - fat',
            'Point out the word family pattern (-at).',
            'Have child read each word once before the story.',
            'Show enthusiasm about reading together!',
            'Optional: Draw a quick picture of Sam the Cat together.'
          ],
          examples: [
            'You: "Here\'s one word we\'ll see: C-A-T. Can you blend it?" Child: "Cat!" You: "Perfect! Sam IS a cat!"'
          ],
          watchFor: [
            'Child making predictions (shows engagement).',
            'Successful pre-reading of key words.',
            'Excitement about the upcoming story.'
          ],
          parentTip: 'Front-load success! When kids know some words before reading, they feel confident and capable.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Read the Story Together',
        duration: '5-6 min',
        focus: 'Contextual Decoding',
        researchBasis: 'Ehri (2020) - Reading words in context strengthens orthographic mapping and builds reading fluency.',
        content: {
          goal: 'Read a simple story where your child decodes target CVC words while you provide the connecting text.',
          whatToSay: [
            '"I\'ll read most of the story, and YOU read the special words!"',
            '"When I point to a word, that\'s your turn to blend and read it!"',
            '"Ready? Here we go!"'
          ],
          whatToDo: [
            'Write or read this simple story, pausing at bolded words for child to decode:',
            '',
            '**Sam the Cat**',
            'Sam is a **cat**.',
            'Sam sat on a **mat**.',
            'Sam wore a **hat**.',
            'The hat was on the **mat**.',
            'Sam is a **fat** cat!',
            'Sam had a **nap** on the mat.',
            'The **sun** was hot.',
            'Sam is a happy cat!',
            '',
            'Parent reads connecting text smoothly.',
            'Point to each bolded word for child to decode.',
            'Wait patiently as they blend (5-10 seconds).',
            'If stuck, provide first sound as a hint.',
            'Celebrate each decoded word: "Yes! Sam sat on a mat!"',
            'Read with expression and enthusiasm!'
          ],
          examples: [
            'You: "Sam is a..." (point to CAT) Child: "Cat!" You: "Yes! Sam is a cat!"'
          ],
          watchFor: [
            'Child successfully decoding target words.',
            'Using context to self-correct mistakes.',
            'Understanding the story meaning, not just decoding.'
          ],
          parentTip: 'Reading is thinking! Ask "Does that make sense?" if they misread a word.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Story Talk',
        duration: '2-3 min',
        focus: 'Comprehension Check',
        researchBasis: 'Gough & Tunmer (1986) - Reading comprehension = Decoding × Language Comprehension (Simple View of Reading).',
        content: {
          goal: 'Ensure your child understood the story meaning, not just decoded words.',
          whatToSay: [
            '"You read those words so well! Now let\'s talk about the story!"',
            '"Who was the story about?"',
            '"Where did Sam sit?"',
            '"What did Sam wear?"',
            '"How do you think Sam felt?"'
          ],
          whatToDo: [
            'Ask simple comprehension questions:',
            '  - Who: "Who is the main character?" (Sam)',
            '  - What: "What did Sam do?" (sat, wore a hat, napped)',
            '  - Where: "Where did Sam sit?" (on a mat)',
            '  - Feeling: "How did Sam feel at the end?" (happy)',
            'Accept answers in any form (pointing, gestures, words).',
            'Reread key sentences if needed.',
            'Ask a prediction: "What do you think Sam will do next?"',
            'Celebrate understanding: "You\'re not just reading — you\'re thinking!"'
          ],
          examples: [
            'You: "Where did Sam sit?" Child: "On the mat!" You: "Yes! You understood the story!"'
          ],
          watchFor: [
            'Child recalling story details.',
            'Understanding cause and effect.',
            'Making personal connections ("I have a cat too!").'
          ],
          parentTip: 'Comprehension is the goal! If they decode but don\'t understand, read it again together.'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Draw the Story',
        duration: '2-3 min',
        focus: 'Story Retelling',
        researchBasis: 'Whitehurst & Lonigan (1998) - Visual representation of stories strengthens comprehension and recall.',
        content: {
          goal: 'Have your child draw a scene from the story, reinforcing comprehension through art.',
          whatToSay: [
            '"Now let\'s draw your favorite part of the story!"',
            '"What part did you like best?"',
            '"Let\'s draw Sam on the mat with his hat!"',
            '"Can you label the picture with the words you read?"'
          ],
          whatToDo: [
            'Child chooses their favorite story moment.',
            'Child draws the scene.',
            'Encourage labeling with decoded words:',
            '  - Draw Sam → label "cat"',
            '  - Draw the mat → label "mat"',
            '  - Draw the hat → label "hat"',
            'Parent can help with spelling if needed.',
            'Child retells the story using their drawing.',
            'Display the artwork proudly!',
            'Optional: Create a mini story book by stapling pages together.'
          ],
          examples: [
            'Child draws cat on mat. You: "Can you write the word \'cat\' under Sam?" Child writes: CAT'
          ],
          watchFor: [
            'Child\'s drawing representing story events.',
            'Attempt to label with words.',
            'Ability to retell using their illustration.'
          ],
          parentTip: 'Art doesn\'t have to be perfect! Stick figures are great — the goal is retelling and labeling.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '📖 You read a real story!',
            'You practiced:',
            '✓ Decoding words in a story context',
            '✓ Understanding what you read (comprehension)',
            '✓ Answering questions about the story',
            '✓ Drawing and retelling the story'
          ],
          reviewQuestions: [
            'Could your child decode the target words in the story?',
            'Did they understand the story meaning?',
            'Were they able to answer comprehension questions?',
            'Did they enjoy the reading experience?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'This is real reading! When children decode words in meaningful context, they experience the joy and purpose of reading. Context helps them self-monitor ("Does this make sense?") and builds the connection between decoding and comprehension — the ultimate goal of reading.'
          },
          nextSteps: [
            'Reread "Sam the Cat" tomorrow — rereading builds fluency!',
            'Create new stories using the same word family (-at words).',
            'Let your child "read" the story to a stuffed animal or family member.',
            'Find simple decodable books at the library or online.'
          ]
        }
      }
    ]
  },

  // ========= LESSON 16 =========
  {
    id: 16,
    phase: '4',
    quarter: 1,
    week: 4,
    weekIndex: 3,
    dayInWeek: 1,
    title: 'Lesson 16: CVC Relay',
    theme: 'Integration Week: CVC Across Contexts',
    skillRecurrenceTags: ['cvc-decoding', 'movement', 'fluency-building'],
    relatedToPreviousSkills: ['cvc-decoding', 'blending', 'kinesthetic-learning'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to CVC Relay!',
        duration: '1 min',
        content: {
          goal: 'Combine reading practice with physical movement, making decoding active and fun.',
          parentGuide: [
            'This is Week 4 — Integration Week! We apply all blending skills in play.',
            'Movement + reading = engagement + memory.',
            'Physical activity energizes the brain and reinforces learning.',
            'This lesson is perfect for high-energy kids who learn through movement.'
          ],
          materials: ['Paper and markers', '6-8 word cards (you\'ll make them)', 'Space to move!'],
          tip: 'Make it a game, not a test! Cheer loudly and celebrate every decoded word.',
          researchNote: 'Hirsh-Pasek et al. (2017) - Active, playful learning enhances engagement and improves learning outcomes in young children.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Make the Word Cards',
        duration: '3-4 min',
        focus: 'Word Recognition Preparation',
        researchBasis: 'Ehri (2001) - Visual exposure to written words supports orthographic mapping and word recognition.',
        content: {
          goal: 'Create simple word cards together, involving your child in the preparation.',
          whatToSay: [
            '"Let\'s make word cards for our relay race!"',
            '"We\'ll write one word on each card — you can help me!"',
            '"What word should we make first? How about CAT?"',
            '"Let\'s write it in BIG letters so we can see it from far away!"'
          ],
          whatToDo: [
            'Cut or tear paper into 8 cards (about 4x6 inches).',
            'Write one CVC word on each card in large, clear letters.',
            'Use different colors for variety.',
            'Suggested words (mix vowels):',
            '  - cat',
            '  - dog',
            '  - sun',
            '  - bed',
            '  - pig',
            '  - hop',
            '  - run',
            '  - cup',
            'Let child help write letters or decorate cards.',
            'Practice reading each word once as you make it.',
            'Place cards in a pile, face down.'
          ],
          examples: [
            'You: "Let\'s write DOG. What letter does dog start with?" Child: "D!" You: "Yes! Let\'s write D first!"'
          ],
          watchFor: [
            'Child participation in card creation.',
            'Letter recognition as you write.',
            'Excitement about the upcoming game.'
          ],
          parentTip: 'Making the cards together builds ownership and anticipation!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Relay Race Rules',
        duration: '1-2 min',
        focus: 'Game Setup',
        researchBasis: 'Hirsh-Pasek et al. (2017) - Clear, simple game rules support playful learning and child autonomy.',
        content: {
          goal: 'Explain the relay race rules simply and demonstrate one round.',
          whatToSay: [
            '"Here\'s how the relay works!"',
            '"You\'ll pick a card, read the word, then RUN to the finish line!"',
            '"When you get there, you read the word again!"',
            '"Then run back and grab another card!"',
            '"Let\'s try one together!"'
          ],
          whatToDo: [
            'Set up the space:',
            '  - Starting line: where word cards are',
            '  - Finish line: 10-15 feet away (mark with tape, pillow, or stuffed animal)',
            'Demonstrate one round:',
            '  1. Pick up a card',
            '  2. Read the word aloud',
            '  3. Run/hop/skip to finish line',
            '  4. Read the word again at finish line',
            '  5. Run back to start',
            '  6. Repeat with next card',
            'Emphasize: Accuracy over speed!',
            'Explain you\'ll cheer and celebrate each word.',
            'Ask: "Ready to try?"'
          ],
          examples: [
            'You: "I pick up... C-A-T... CAT! Now I run!" (run to finish) "CAT! I made it!"'
          ],
          watchFor: [
            'Child understanding the rules.',
            'Enthusiasm about starting.',
            'No anxiety about performance.'
          ],
          parentTip: 'Keep it light! If they misdecode, gently help and celebrate the correction.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Play the Relay!',
        duration: '5-6 min',
        focus: 'Active Decoding Practice',
        researchBasis: 'Lonigan et al. (2000) - Repeated word reading practice builds automaticity and reading fluency.',
        content: {
          goal: 'Child completes the relay, reading words in an active, playful context.',
          whatToSay: [
            '"On your mark... get set... READ!"',
            '"You got it! Now run!"',
            '"Read it again at the finish line!"',
            '"Great job! Back for the next one!"'
          ],
          whatToDo: [
            'Child picks first card from pile.',
            'Child decodes word aloud.',
            'If correct: "Go, go, go!"',
            'If stuck: Give first sound hint, then cheer.',
            'Child runs/hops/skips to finish line.',
            'Child reads word again at finish line.',
            'Child returns to start for next card.',
            'Continue through all 8 cards.',
            'Variations to keep it fresh:',
            '  - Hop like a bunny',
            '  - Tiptoe like a mouse',
            '  - Stomp like a giant',
            '  - Skip or gallop',
            'Cheer enthusiastically throughout!',
            'Optional: Time them and try to "beat the record" in round 2.'
          ],
          examples: [
            'Child: "B-E-D... bed!" You: "YES! Run to the finish!" Child runs. Child: "Bed!" You: "Perfect!"'
          ],
          watchFor: [
            'Accurate decoding at both start and finish.',
            'Confidence growing with each word.',
            'Joy and energy during the activity.'
          ],
          parentTip: 'This is about fun + practice! If they need help, help immediately — keep the momentum going!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Challenge Round',
        duration: '2-3 min',
        focus: 'Fluency Building',
        researchBasis: 'National Reading Panel (2000) - Repeated reading improves fluency, accuracy, and reading confidence.',
        content: {
          goal: 'Replay the relay with added challenge: faster decoding or special movements.',
          whatToSay: [
            '"You did amazing! Want to try a challenge round?"',
            '"This time, try to read even faster!"',
            '"Or... we can try SILLY movements! Crab walk? Bear crawl?"',
            '"You choose!"'
          ],
          whatToDo: [
            'Offer challenge options:',
            '  Option 1: Speed challenge (read words faster)',
            '  Option 2: Movement challenge (silly ways to move)',
            '  Option 3: Parent joins (race together!)',
            '  Option 4: Backward relay (start at finish, read at start)',
            'Let child choose the challenge.',
            'Shuffle cards and play again.',
            'Celebrate improvement: "You read those so much faster!"',
            'Emphasize growth, not perfection.',
            'End with high-fives and celebration!'
          ],
          examples: [
            'You: "Last time you read \'cat\' slowly. This time you just KNEW it! That\'s fluency!"'
          ],
          watchFor: [
            'Increased reading speed and confidence.',
            'Child choosing challenges (shows autonomy).',
            'Maintained accuracy even at faster pace.'
          ],
          parentTip: 'Repeated practice = fluency! Don\'t worry if round 2 looks the same as round 1 — they\'re building neural pathways!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🏃 You completed the CVC Relay!',
            'You practiced:',
            '✓ Reading CVC words quickly',
            '✓ Moving and reading at the same time',
            '✓ Reading words multiple times (building fluency)',
            '✓ Having FUN while learning!'
          ],
          reviewQuestions: [
            'Could your child decode the words successfully?',
            'Did movement enhance their engagement?',
            'Did you notice faster reading in round 2?',
            'Was this a positive, joyful experience?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Movement-based learning leverages children\'s natural energy to build reading skills. The relay format provides repeated practice (essential for fluency) in a format that feels like play, not work. When kids move while learning, they remember better and enjoy the process more.'
          },
          nextSteps: [
            'Play CVC Relay with new words tomorrow!',
            'Create relay races for other skills (letter names, sight words).',
            'Let your child be the "coach" and set up the relay for you.',
            'Try outdoor relay races with chalk-written words on the sidewalk.'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 17 =========
  {
    id: 17,
    phase: '4',
    quarter: 1,
    week: 4,
    weekIndex: 3,
    dayInWeek: 2,
    title: 'Lesson 17: Silly Sentences',
    theme: 'Integration Week: CVC Across Contexts',
    skillRecurrenceTags: ['cvc-decoding', 'sentence-reading', 'comprehension'],
    relatedToPreviousSkills: ['cvc-decoding', 'blending', 'word-meaning'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Silly Sentences!',
        duration: '1 min',
        content: {
          goal: 'Practice reading CVC words in complete sentences while having fun with silly, playful combinations.',
          parentGuide: [
            'This lesson introduces sentence-level reading with familiar CVC words.',
            'Silly combinations make reading memorable and engaging.',
            'Children learn that sentences communicate complete ideas.',
            'Humor captures attention and reduces reading anxiety.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Your silliest imagination!'],
          tip: 'Act out the silly sentences! Physical comedy makes them unforgettable.',
          researchNote: 'Whitehurst & Lonigan (1998) - Playful language activities build literacy skills while maintaining motivation and joy.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: What Makes a Sentence Silly?',
        duration: '2-3 min',
        focus: 'Sentence Awareness',
        researchBasis: 'National Reading Panel (2000) - Understanding sentence structure supports reading comprehension and fluency.',
        content: {
          goal: 'Introduce the concept of sentences and explore what makes them "silly" versus "normal."',
          whatToSay: [
            '"A sentence tells us a complete idea about someone or something!"',
            '"Let\'s make some sentences about a cat. Normal sentence: The cat sat."',
            '"Now a SILLY sentence: The cat ran in a cup!"',
            '"That\'s silly because cats don\'t fit in cups! Can you think of something else silly?"'
          ],
          whatToDo: [
            'Explain what a sentence is (a complete thought).',
            'Give examples of normal vs. silly sentences:',
            '  Normal: "The dog ran."',
            '  Silly: "The dog sat in a hat!"',
            '  Normal: "The pig is fat."',
            '  Silly: "The fat pig can hop!"',
            'Ask child to identify WHY it\'s silly.',
            'Brainstorm silly ideas together:',
            '  - Animals doing unusual things',
            '  - Things in wrong places',
            '  - Impossible combinations',
            'Laugh together at silly ideas!',
            'Build excitement: "Let\'s READ silly sentences now!"'
          ],
          examples: [
            'You: "A bug in a mug! Is that silly?" Child: "Yes! Bugs don\'t drink from mugs!" You: "Exactly!"'
          ],
          watchFor: [
            'Child understanding what a sentence is.',
            'Engagement with silly vs. normal concept.',
            'Creative thinking about silly combinations.'
          ],
          parentTip: 'Silliness = engagement! Don\'t skip this step — it sets the playful tone for reading.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Read Silly Sentences',
        duration: '5-6 min',
        focus: 'Sentence Decoding',
        researchBasis: 'Ehri (2020) - Reading connected text builds fluency and reinforces word recognition in meaningful contexts.',
        content: {
          goal: 'Child reads pre-written silly sentences, decoding CVC words in context.',
          whatToSay: [
            '"I wrote some silly sentences for you to read!"',
            '"Let\'s read them together and see how silly they are!"',
            '"Point to each word as you read. Ready?"'
          ],
          whatToDo: [
            'Write 6-8 silly sentences using CVC words:',
            '',
            '1. The cat sat in a hat.',
            '2. A pig can dig in a wig!',
            '3. The bug is in my mug.',
            '4. A fat rat ran to the mat.',
            '5. The dog got a mop on his top.',
            '6. A fox in a box can hop!',
            '7. The hen has a pen in the den.',
            '8. A bat sat on a red bed.',
            '',
            'Child points to each word while reading.',
            'Help with unfamiliar words if needed.',
            'Pause after each sentence: "Is that silly? Why?"',
            'Act out the silly sentence together!',
            'Celebrate successful reading.',
            'Reread 2-3 favorites for fluency practice.'
          ],
          examples: [
            'Child reads: "The bug is in my mug." You: "Oh no! A bug in your mug! That\'s so silly!" (pretend to drink from mug and look surprised)'
          ],
          watchFor: [
            'Child pointing to words while reading (one-to-one correspondence).',
            'Successful decoding of CVC words.',
            'Understanding the silly meaning.',
            'Willingness to reread.'
          ],
          parentTip: 'If they skip words, gently redirect: "Point to each word as you read." This builds tracking skills!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Create Your Own Silly Sentence',
        duration: '4-5 min',
        focus: 'Sentence Composition',
        researchBasis: 'Gough & Tunmer (1986) - Active composition reinforces decoding skills and deepens understanding of language structure.',
        content: {
          goal: 'Child creates their own silly sentence using CVC words, then writes or dictates it.',
          whatToSay: [
            '"Now it\'s YOUR turn to make a silly sentence!"',
            '"Think of an animal. What silly thing could it do?"',
            '"Let\'s use words we can read — like cat, dog, pig, run, sit, hop!"',
            '"Tell me your silly sentence, and we\'ll write it down!"'
          ],
          whatToDo: [
            'Help child brainstorm a silly sentence.',
            'Guide with prompts:',
            '  - "What animal?" (cat, dog, pig, bug, fox)',
            '  - "What is it doing?" (sit, run, hop, dig)',
            '  - "Where or what?" (hat, mat, cup, box)',
            'Example process:',
            '  Child: "A cat!"',
            '  You: "What is the cat doing?"',
            '  Child: "Jumping in a pot!"',
            '  You: "Great! Let\'s write: A cat can hop in a pot!"',
            'Write the sentence together on paper.',
            'Child can write letters they know; you fill in the rest.',
            'Child reads their sentence aloud.',
            'Draw a picture to illustrate it.',
            'Display proudly!',
            'Optional: Make multiple silly sentences.'
          ],
          examples: [
            'Child: "A pig in a wig!" You: "Perfect! Let\'s write: The pig has a wig!" Child draws pig wearing wig.'
          ],
          watchFor: [
            'Child\'s creative sentence ideas.',
            'Use of familiar CVC words.',
            'Pride in their creation.',
            'Ability to read their own sentence.'
          ],
          parentTip: 'Their sentence doesn\'t have to be perfectly written — focus on the fun of creating and reading!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Silly Sentence Show',
        duration: '2-3 min',
        focus: 'Performance & Sharing',
        researchBasis: 'Whitehurst & Lonigan (1998) - Sharing literacy creations builds confidence and reinforces learning through repetition.',
        content: {
          goal: 'Child performs their silly sentence for an audience (real or imaginary), building confidence.',
          whatToSay: [
            '"Let\'s have a Silly Sentence Show!"',
            '"You\'ll read your sentence like you\'re on stage!"',
            '"Use your best reading voice and act it out!"',
            '"I\'ll be your audience and clap!"'
          ],
          whatToDo: [
            'Set up a "stage" area (even just standing on a pillow works!).',
            'Child holds their sentence and picture.',
            'Child reads their silly sentence aloud with expression.',
            'Child acts out or shows their illustration.',
            'You (and any family members) cheer and applaud!',
            'Optional additions:',
            '  - Child introduces themself: "I am [name] and my sentence is..."',
            '  - Take a photo of them with their sentence',
            '  - Create a "Silly Sentence Book" by adding to it weekly',
            'Ask: "What was your favorite silly sentence today?"',
            'Celebrate the creativity and reading!'
          ],
          examples: [
            'Child: "The dog got a mop on his top!" (acts out putting mop on head) Everyone: (applause!) "Bravo!"'
          ],
          watchFor: [
            'Child\'s confidence performing.',
            'Expressive reading (using voice).',
            'Pride in their work.',
            'Willingness to share.'
          ],
          parentTip: 'Authentic audience = motivation! Even performing for stuffed animals counts!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '😆 You created silly sentences!',
            'You practiced:',
            '✓ Reading complete sentences',
            '✓ Decoding CVC words in context',
            '✓ Creating your own silly sentence',
            '✓ Performing and sharing your reading'
          ],
          reviewQuestions: [
            'Could your child read the silly sentences successfully?',
            'Did they understand why the sentences were silly?',
            'Were they able to create their own sentence?',
            'Did they enjoy the performance aspect?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Silly Sentences bridge word reading to sentence reading — a critical step toward fluency. When children read sentences (not just isolated words), they begin to understand that reading communicates meaning. The silliness maintains motivation while they practice this more complex skill. Creating their own sentences reinforces both decoding and composition skills.'
          },
          nextSteps: [
            'Create a "Silly Sentence of the Day" routine.',
            'Make a silly sentence book by adding new ones each week.',
            'Challenge family members to read your child\'s silly sentences.',
            'Find silly sentence books at the library (Dr. Seuss is perfect!).'
          ]
        }
      }
    ]
  },

  // ========= LESSON 18 =========
  {
    id: 18,
    phase: '4',
    quarter: 1,
    week: 4,
    weekIndex: 3,
    dayInWeek: 3,
    title: 'Lesson 18: CVC Beatbox',
    theme: 'Integration Week: CVC Across Contexts',
    skillRecurrenceTags: ['cvc-decoding', 'syllable-awareness', 'rhythm'],
    relatedToPreviousSkills: ['cvc-decoding', 'blending', 'phoneme-awareness'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to CVC Beatbox!',
        duration: '1 min',
        content: {
          goal: 'Combine rhythm and reading by clapping or tapping out syllables while decoding CVC words.',
          parentGuide: [
            'This lesson integrates auditory rhythm with visual decoding.',
            'Rhythm helps children "feel" the word structure.',
            'Music and literacy are deeply connected in the brain.',
            'Perfect for children who respond strongly to music and movement.'
          ],
          materials: ['Paper and markers', 'Your hands for clapping!', 'Optional: drum, tambourine, or pots and spoons'],
          tip: 'Make strong, consistent beats! The rhythm should be simple and repetitive.',
          researchNote: 'Trainor et al. (2002) - Musical rhythm training enhances phonological awareness and reading skills in young children.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Feel the Beat',
        duration: '2-3 min',
        focus: 'Rhythm & Syllable Awareness',
        researchBasis: 'Anthony & Francis (2005) - Syllable awareness is a foundational phonological skill that supports word decoding.',
        content: {
          goal: 'Introduce the concept of syllables through clapping and rhythm.',
          whatToSay: [
            '"Every word has a beat — like music!"',
            '"Let\'s clap out words together!"',
            '"My name is [your name]. Let\'s clap it: [Name - clap each syllable]"',
            '"Now your name: [child\'s name]!"',
            '"CVC words have just ONE beat because they\'re short!"'
          ],
          whatToDo: [
            'Introduce syllables through clapping.',
            'Start with names (multi-syllabic):',
            '  - Emily = E-mi-ly (3 claps)',
            '  - Jack = Jack (1 clap)',
            '  - Mommy = Mom-my (2 claps)',
            'Then demonstrate CVC words = 1 syllable:',
            '  - cat (1 clap)',
            '  - dog (1 clap)',
            '  - sun (1 clap)',
            'Practice clapping 5-6 CVC words together.',
            'Emphasize: "One beat, one syllable!"',
            'Try different sounds:',
            '  - Clap',
            '  - Tap table',
            '  - Stomp foot',
            '  - Pat legs',
            'Make it rhythmic and fun!'
          ],
          examples: [
            'You: "Cat! How many beats?" (clap once) Child: "One!" You: "Yes! CAT is one beat!"'
          ],
          watchFor: [
            'Child clapping in time with syllables.',
            'Understanding that CVC words = 1 syllable.',
            'Engagement with rhythm.'
          ],
          parentTip: 'If syllables are confusing, just focus on the rhythm. The concept will develop over time!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Read and Beat',
        duration: '5-6 min',
        focus: 'Synchronized Reading & Rhythm',
        researchBasis: 'Lonigan et al. (2000) - Multi-sensory integration (visual + auditory + kinesthetic) strengthens word recognition.',
        content: {
          goal: 'Child reads CVC words while clapping or tapping the syllable, synchronizing reading with rhythm.',
          whatToSay: [
            '"Now let\'s READ words and CLAP at the same time!"',
            '"When you read the word, give it one big clap!"',
            '"Ready? Here\'s the first word: CAT!"',
            '"Read it and clap!"'
          ],
          whatToDo: [
            'Write or show 10-12 CVC words.',
            'Child reads each word.',
            'Child claps once AS they say the word.',
            'Timing is key: clap and word happen together!',
            'Words to practice:',
            '  - cat (CLAP)',
            '  - dog (CLAP)',
            '  - sun (CLAP)',
            '  - bed (CLAP)',
            '  - pig (CLAP)',
            '  - hop (CLAP)',
            '  - run (CLAP)',
            '  - cup (CLAP)',
            '  - hat (CLAP)',
            '  - net (CLAP)',
            '  - wig (CLAP)',
            '  - box (CLAP)',
            'Increase tempo gradually.',
            'Try different sounds: tap, stomp, snap.',
            'Optional: Parent and child alternate (call-and-response).'
          ],
          examples: [
            'You show: DOG. Child: "Dog!" (CLAP) You: "Perfect timing! Read and beat together!"'
          ],
          watchFor: [
            'Synchronized reading and clapping.',
            'Accurate word decoding.',
            'Maintaining rhythm.',
            'Growing confidence with tempo.'
          ],
          parentTip: 'If timing is hard, YOU clap while THEY read. Then switch to them doing both together.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Sound Breakdown Beat',
        duration: '3-4 min',
        focus: 'Phoneme-Level Rhythm',
        researchBasis: 'Ehri (2020) - Explicit attention to phoneme sequences strengthens decoding and spelling skills.',
        content: {
          goal: 'Break words into individual phonemes with a clap for each sound, deepening phoneme awareness.',
          whatToSay: [
            '"Now let\'s break words into SOUNDS and beat each one!"',
            '"CAT has three sounds: /c/ /a/ /t/. Let\'s clap each sound!"',
            '"Ready? /c/ (clap), /a/ (clap), /t/ (clap)!"',
            '"Now put them together: CAT (one big clap)!"'
          ],
          whatToDo: [
            'Choose a CVC word: CAT',
            'Break it into phonemes: /c/ /a/ /t/',
            'Clap once for each phoneme (3 claps total).',
            'Then clap once for the whole word.',
            'Pattern: 3 small claps → 1 big clap.',
            'Practice with 6-8 words:',
            '  - dog: /d/ /o/ /g/ → DOG',
            '  - sun: /s/ /u/ /n/ → SUN',
            '  - bed: /b/ /e/ /d/ → BED',
            '  - pig: /p/ /i/ /g/ → PIG',
            '  - run: /r/ /u/ /n/ → RUN',
            '  - hat: /h/ /a/ /t/ → HAT',
            'Make it rhythmic: "Clap-clap-clap-CLAP!"',
            'Child can lead some rounds.'
          ],
          examples: [
            'You: "PIG! Let\'s beat the sounds!" Both: (clap) /p/ (clap) /i/ (clap) /g/ (BIG CLAP) "PIG!"'
          ],
          watchFor: [
            'Child identifying all three phonemes.',
            'Maintaining rhythm through segmentation.',
            'Understanding the relationship between parts and whole.'
          ],
          parentTip: 'This is advanced! If it\'s confusing, stick with Step 2 (one clap per word) and come back to this later.'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Beatbox Challenge',
        duration: '2-3 min',
        focus: 'Speed & Fluency',
        researchBasis: 'National Reading Panel (2000) - Repeated, timed practice builds reading automaticity and fluency.',
        content: {
          goal: 'Read a list of words with rhythm as quickly as possible, building speed and confidence.',
          whatToSay: [
            '"Time for the Beatbox Challenge!"',
            '"Let\'s see how many words you can read and beat in 1 minute!"',
            '"Ready? On your mark... get set... READ AND BEAT!"'
          ],
          whatToDo: [
            'Prepare a list of 12-15 CVC words.',
            'Set a timer for 1 minute (optional — can skip timing).',
            'Child reads each word and claps/taps.',
            'Count how many they complete.',
            'Word list:',
            '  cat, dog, sun, bed, pig, hat',
            '  run, cup, mop, wet, sit, top',
            '  bag, leg, win',
            'After first round, celebrate!',
            'Ask: "Want to try to beat your record?"',
            'Second round: Same words, try for more.',
            'Emphasize: Speed WITH accuracy, not just speed!',
            'End with cheers and high-fives.'
          ],
          examples: [
            'Child: "Cat! (clap) Dog! (clap) Sun! (clap)..." You count: "That\'s 3... 4... 5... Great job!"'
          ],
          watchFor: [
            'Maintained accuracy even at speed.',
            'Enjoyment of the challenge.',
            'Visible improvement between rounds.'
          ],
          parentTip: 'If 1 minute feels stressful, skip the timer. Just practice reading with rhythm for fun!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🥁 You became a reading beatboxer!',
            'You practiced:',
            '✓ Feeling syllables through rhythm',
            '✓ Reading words while clapping/tapping',
            '✓ Breaking words into sound beats',
            '✓ Speed reading with rhythm'
          ],
          reviewQuestions: [
            'Could your child synchronize reading with clapping?',
            'Did they understand syllable awareness?',
            'Were they able to break words into phoneme beats?',
            'Did they enjoy the musical aspect of reading?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Rhythm and reading are deeply connected. When children "feel" words through beats, they develop stronger phonological awareness and better word recognition. This multisensory approach is especially powerful for children who learn well through music and movement. The rhythm also makes reading practice feel less like work and more like play!'
          },
          nextSteps: [
            'Add rhythm to any reading practice — clap, tap, or stomp!',
            'Make up beatbox chants with CVC words.',
            'Try reading with background music or creating word raps.',
            'Explore rhythm books and songs at the library.'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 19 =========
  {
    id: 19,
    phase: '4',
    quarter: 1,
    week: 4,
    weekIndex: 3,
    dayInWeek: 4,
    title: 'Lesson 19: Picture-to-Word Match',
    theme: 'Integration Week: CVC Across Contexts',
    skillRecurrenceTags: ['cvc-decoding', 'word-meaning', 'visual-discrimination'],
    relatedToPreviousSkills: ['cvc-decoding', 'blending', 'vocabulary'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Picture-to-Word Match!',
        duration: '1 min',
        content: {
          goal: 'Connect written CVC words to their meanings by matching words with corresponding pictures.',
          parentGuide: [
            'This lesson reinforces that words represent real objects and actions.',
            'Matching activities build visual discrimination and word recognition.',
            'Pictures provide meaning support while children decode.',
            'This bridges decoding (reading the word) and comprehension (knowing what it means).'
          ],
          materials: ['Paper and markers or crayons', 'Scissors (optional)', 'Your drawing skills (stick figures welcome!)'],
          tip: 'Simple drawings work perfectly! Focus on clear, recognizable images.',
          researchNote: 'Gough & Tunmer (1986) - Reading comprehension requires both word recognition AND language comprehension (Simple View of Reading).'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Draw the Pictures',
        duration: '3-4 min',
        focus: 'Visual Representation',
        researchBasis: 'Ehri (2001) - Connecting words to meaningful images strengthens orthographic mapping and word memory.',
        content: {
          goal: 'Draw simple pictures representing CVC words together, building anticipation for the matching game.',
          whatToSay: [
            '"Let\'s draw pictures of things we can read!"',
            '"First, let\'s draw a cat. What does a cat look like?"',
            '"Now let\'s draw a sun!"',
            '"We\'ll make cards for all our pictures!"'
          ],
          whatToDo: [
            'Draw 8-10 simple pictures on separate pieces of paper or cards.',
            'Suggested pictures (CVC words):',
            '  - cat (simple cat face or body)',
            '  - dog (simple dog)',
            '  - sun (circle with rays)',
            '  - bed (rectangle with pillow)',
            '  - hat (basic hat shape)',
            '  - cup (simple cup)',
            '  - pig (pig face or body)',
            '  - bug (6 legs, antennae)',
            'Child can help draw or color.',
            'Keep drawings SIMPLE — stick figures are perfect!',
            'Make pictures large and clear (about 3x3 inches each).',
            'Set pictures aside for now.',
            'Build excitement: "Now we need the WORDS!"'
          ],
          examples: [
            'You: "Let\'s draw a cat! Two eyes, triangle ears, whiskers!" Child colors it in.'
          ],
          watchFor: [
            'Child engagement in drawing process.',
            'Recognition of what each picture represents.',
            'Excitement about upcoming matching game.'
          ],
          parentTip: 'Art quality doesn\'t matter! A circle with a smile can be a "sun" — children use their imagination.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Write the Words',
        duration: '2-3 min',
        focus: 'Word Card Creation',
        researchBasis: 'National Reading Panel (2000) - Visual exposure to printed words supports word recognition development.',
        content: {
          goal: 'Create word cards that match the pictures, preparing for the matching activity.',
          whatToSay: [
            '"Now let\'s write the WORDS to match our pictures!"',
            '"We drew a cat. What letters spell cat?"',
            '"C-A-T! Let\'s write it on a card!"',
            '"We\'ll make word cards for everything we drew!"'
          ],
          whatToDo: [
            'Create word cards on separate paper (same size as pictures).',
            'Write one word per card in LARGE, clear letters.',
            'Words to match your pictures:',
            '  - cat',
            '  - dog',
            '  - sun',
            '  - bed',
            '  - hat',
            '  - cup',
            '  - pig',
            '  - bug',
            'Child can help write letters they know.',
            'Say each word aloud as you write it.',
            'Have child READ each word card after writing.',
            'Mix up the word cards (not in same order as pictures).',
            'Now you have two sets: pictures and words!'
          ],
          examples: [
            'You write: SUN. You: "Can you read this?" Child: "Sun!" You: "Yes! This word says sun!"'
          ],
          watchFor: [
            'Child reading word cards successfully.',
            'Understanding that written words match spoken words.',
            'Letter recognition as you write.'
          ],
          parentTip: 'Let them write letters they know confidently. You can fill in the rest — it\'s all practice!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Match the Words and Pictures',
        duration: '5-6 min',
        focus: 'Word-Meaning Connection',
        researchBasis: 'Lonigan et al. (2000) - Connecting print to meaning strengthens both decoding and comprehension skills.',
        content: {
          goal: 'Child matches each written word to its corresponding picture, reinforcing word meaning.',
          whatToSay: [
            '"Now for the matching game!"',
            '"I\'ll spread out the pictures. The word cards are in a pile."',
            '"Pick a word card, read it, then match it to the right picture!"',
            '"Let\'s try the first one together!"'
          ],
          whatToDo: [
            'Spread your drawings face-up on the table or floor.',
            'Stack word cards face-down in a pile.',
            'Child picks a word card.',
            'Child READS the word aloud.',
            'Child finds the matching picture.',
            'Child places word card ON TOP of picture.',
            'Continue until all words are matched.',
            'Process for each word:',
            '  1. Pick word card',
            '  2. Decode word: "C-A-T... cat!"',
            '  3. Find picture: "Where\'s the cat?"',
            '  4. Match: Place word on picture',
            '  5. Celebrate: "Perfect match!"',
            'If child struggles to decode, help blend.',
            'If child matches wrong picture, ask: "Does this make sense?"',
            'After all matched, review each pair together.'
          ],
          examples: [
            'Child picks: DOG. Child: "D-O-G... dog!" Child: "Here\'s the dog picture!" (places card) You: "Great match!"'
          ],
          watchFor: [
            'Successful decoding of word cards.',
            'Correct matching to pictures.',
            'Self-correction when mistakes happen.',
            'Understanding of word meanings.'
          ],
          parentTip: 'If they guess based on first letter alone, cover the picture and have them fully decode first!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Memory Match Challenge',
        duration: '2-3 min',
        focus: 'Word Recognition Fluency',
        researchBasis: 'Ehri (2020) - Repeated exposure to words in varied contexts builds automatic word recognition.',
        content: {
          goal: 'Play a memory-style game where both pictures and words are face-down, building word recognition speed.',
          whatToSay: [
            '"Let\'s make it harder — Memory Match Challenge!"',
            '"We\'ll flip ALL the cards face-down!"',
            '"Flip two cards. If they match, you keep them!"',
            '"If a word card flips up, you have to READ it!"'
          ],
          whatToDo: [
            'Place ALL cards (pictures AND words) face-down.',
            'Mix them up randomly.',
            'Child flips two cards.',
            'If a WORD card flips: child must READ it.',
            'If cards match (word + picture): child keeps them!',
            'If cards don\'t match: flip back face-down.',
            'Take turns or let child play solo.',
            'Classic memory game rules.',
            'Continue until all pairs are found.',
            'Count matches at the end.',
            'Celebrate: "You found [X] matches!"',
            'Optional: Play again to improve "score."'
          ],
          examples: [
            'Child flips: (picture of cat). Child flips: (word "dog"). You: "Cat and dog — do they match?" Child: "No!" (flips back)'
          ],
          watchFor: [
            'Quick word recognition when flipped.',
            'Memory of card locations.',
            'Matching accuracy.',
            'Sustained engagement with game.'
          ],
          parentTip: 'Start with fewer pairs (4-5) if full set is overwhelming. Add more as skill grows!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎯 You matched pictures and words perfectly!',
            'You practiced:',
            '✓ Drawing pictures together',
            '✓ Reading CVC word cards',
            '✓ Matching words to their meanings',
            '✓ Playing memory match games'
          ],
          reviewQuestions: [
            'Could your child read the word cards successfully?',
            'Did they make correct picture-word matches?',
            'Did they understand that words represent meanings?',
            'Did they enjoy the game format?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Picture-to-word matching explicitly connects decoding (reading the letters) with comprehension (knowing what the word means). This is essential because reading isn\'t just about sounding out words — it\'s about understanding them. When children see that written words represent real things they know, reading becomes meaningful and purposeful.'
          },
          nextSteps: [
            'Keep the cards and replay the game tomorrow!',
            'Create new picture-word pairs with different CVC words.',
            'Hide word cards around the house — child finds and reads them.',
            'Make a matching game for any new vocabulary they\'re learning.'
          ]
        }
      }
    ]
  },

  // ========= UPDATED LESSON 20 =========
  {
    id: 20,
    phase: '4',
    quarter: 1,
    week: 4,
    weekIndex: 3,
    dayInWeek: 5,
    title: 'Lesson 20: CVC Story Theater + Teach',
    theme: 'Integration Week: CVC Across Contexts',
    skillRecurrenceTags: ['cvc-decoding', 'story-comprehension', 'dramatic-play', 'teaching-others'],
    relatedToPreviousSkills: ['cvc-decoding', 'blending', 'story-sequencing'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to CVC Story Theater + Teach!',
        duration: '1 min',
        content: {
          goal: 'Act out a simple story using only CVC words, then TEACH someone else how to perform it.',
          parentGuide: [
            'This is the culminating Integration Week lesson — all skills come together!',
            'NEW: The "teaching" component increases cognitive load significantly.',
            'Teaching others is the highest level of learning (Bloom\'s Taxonomy).',
            'This lesson shows reading as a joyful, creative, sharable experience.'
          ],
          materials: ['Paper and pencil', 'Optional: simple props (stuffed animals, blankets, household items)', 'Your dramatic flair!', 'AUDIENCE: sibling, other parent, grandparent, or stuffed animals'],
          tip: 'The "teach someone else" component is CRITICAL — it shows true mastery!',
          researchNote: 'Whitehurst & Lonigan (1998) - Dramatic story enactment enhances comprehension, vocabulary, and love of reading. Teaching others solidifies learning (protégé effect).'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Read the Story Script',
        duration: '3-4 min',
        focus: 'Story Comprehension',
        researchBasis: 'Scarborough (2001) - Reading comprehension develops through understanding narrative structure and story elements.',
        content: {
          goal: 'Read a simple CVC-based story together, understanding the sequence and characters.',
          whatToSay: [
            '"Today we\'re putting on a PLAY!"',
            '"First, let\'s read the story script together."',
            '"I\'ll read some parts, and YOU read the CVC words!"',
            '"Then we\'ll ACT IT OUT!"',
            '"And here\'s the SPECIAL part: YOU will teach someone else to do it too!"'
          ],
          whatToDo: [
            'Present this story script (parent reads connecting text, child reads bolded CVC words):',
            '',
            '**The Bug and the Mug**',
            'Once there was a little **bug**.',
            'The bug saw a **mug**.',
            'The bug **got** in the mug.',
            'Then a **cat** came.',
            'The cat saw the **bug** in the **mug**.',
            'The bug **ran** fast!',
            'The bug **hid** under a **rug**.',
            'The cat **sat** on the **rug**.',
            'The bug was **sad**.',
            'Then the cat **got** up.',
            'The bug was **glad**!',
            'The end.',
            '',
            'Read through once together.',
            'Child decodes bolded words; parent reads rest.',
            'After reading, discuss:',
            '  "Who are the characters?" (bug, cat)',
            '  "What happened first? Then what?"',
            '  "How did the bug feel at the end?"',
            'NEW: "How would you TEACH someone to act this out?"',
            'Build excitement: "Now let\'s act it out AND teach it!"'
          ],
          examples: [
            'You: "The bug saw a..." Child: "Mug!" You: "Yes! The bug saw a mug! When you teach this later, what will you say?"'
          ],
          watchFor: [
            'Child decoding CVC words successfully.',
            'Understanding story sequence.',
            'Recall of characters and events.',
            'NEW: Beginning to think about HOW to explain to others.'
          ],
          parentTip: 'Read with expression! Your enthusiasm models that reading is enjoyable and worth sharing.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Assign Roles and Props',
        duration: '2 min',
        focus: 'Dramatic Preparation',
        researchBasis: 'Hirsh-Pasek et al. (2017) - Role-play and pretend play support language development and narrative skills.',
        content: {
          goal: 'Decide who plays which character and gather simple props for the performance.',
          whatToSay: [
            '"Who should play the bug — you or me?"',
            '"Who should be the cat?"',
            '"Let\'s find things to be our props!"',
            '"What can we use for the mug? The rug?"'
          ],
          whatToDo: [
            'Assign roles:',
            '  Option 1: Child = bug, Parent = cat',
            '  Option 2: Child = cat, Parent = bug',
            '  Option 3: Child plays BOTH (switches roles mid-story)',
            '  Option 4: Both act together; stuffed animals are characters',
            'Gather simple props:',
            '  - Mug: actual mug or cup',
            '  - Rug: towel, blanket, or paper',
            '  - Bug: finger puppet, toy, or just pretend',
            '  - Cat: stuffed animal or child/parent acting',
            'Set up "stage" area (just a space on floor is fine).',
            'Place props in position.',
            'NEW: As you set up, ASK: "When you teach someone, what will you tell them to use?"',
            'Build anticipation: "Ready for showtime?"'
          ],
          examples: [
            'You: "What should the bug be?" Child: "My finger!" You: "Perfect! When you teach later, what will you say?"'
          ],
          watchFor: [
            'Child\'s creative prop choices.',
            'Understanding of character roles.',
            'NEW: Thinking ahead about teaching process.',
            'Readiness to perform.'
          ],
          parentTip: 'Props are optional! Imagination and movement alone create great theater.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Perform the Story!',
        duration: '4-5 min',
        focus: 'Dramatic Enactment',
        researchBasis: 'National Reading Panel (2000) - Active engagement with text (including dramatization) enhances comprehension and retention.',
        content: {
          goal: 'Act out the story together, with child reading and performing their role.',
          whatToSay: [
            '"Lights, camera, action!"',
            '"Let\'s perform our story!"',
            '"Remember to READ your words AND act them out!"',
            '"Here we go!"'
          ],
          whatToDo: [
            'Parent narrates (or reads from script).',
            'Child reads CVC words when they appear.',
            'Both act out the story as it\'s read.',
            'Story sequence to act:',
            '  1. Bug appears (child wiggles finger or toy)',
            '  2. Bug sees mug (point/look at mug)',
            '  3. Bug gets IN mug (move bug into mug)',
            '  4. Cat comes (cat character enters)',
            '  5. Cat sees bug (cat looks at mug)',
            '  6. Bug RUNS (move bug quickly away)',
            '  7. Bug hides under rug (hide bug)',
            '  8. Cat sits on rug (cat sits)',
            '  9. Bug is sad (show sad face/sounds)',
            '  10. Cat gets up (cat stands)',
            '  11. Bug is glad! (happy movements)',
            'Use BIG movements and expressions.',
            'Encourage child to add dialogue or sound effects.',
            'If they forget words, pause and help them decode.',
            'Celebrate at the end: "BRAVO!"',
            'NEW: After performing once, say: "Now you know it REALLY well! You\'re ready to teach someone!"'
          ],
          examples: [
            'You: "The bug..." Child: "RAN!" (moves bug quickly) "Fast!" You: "Yes! The bug ran fast! Remember how you did that — you\'ll teach it!"'
          ],
          watchFor: [
            'Child reading CVC words during performance.',
            'Appropriate acting for story events.',
            'Understanding of story sequence.',
            'Confidence and enjoyment in performing.',
            'NEW: Internalizing the story well enough to teach it.'
          ],
          parentTip: 'Perfection isn\'t the goal — FUN is! If they forget a line or miss a word, just keep going and enjoy the show!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: TEACH Someone Else (NEW!)',
        duration: '4-5 min',
        focus: 'Teaching and Leadership',
        researchBasis: 'Vygotsky (1978) - Teaching others is a form of self-regulation that deepens understanding. The "protégé effect" shows learners retain more when they teach.',
        content: {
          goal: 'Child becomes the teacher, explaining the story and coaching someone else to perform it.',
          whatToSay: [
            '"Now YOU are the teacher!"',
            '"You\'re going to teach [name/stuffed animal] how to perform this story!"',
            '"Tell them the story first!"',
            '"Then show them how to act it out!"',
            '"You\'re in charge!"'
          ],
          whatToDo: [
            'Identify the "student":',
            '  - Sibling',
            '  - Other parent',
            '  - Grandparent (when visiting)',
            '  - Stuffed animals (if no one else available)',
            '  - YOU (parent plays confused learner)',
            '',
            'Child\'s teaching process:',
            '',
            '  **Step 1: Explain the Story**',
            '    Child: "This story is about a bug and a cat..."',
            '    Child summarizes plot',
            '    You prompt if needed: "What happened first?"',
            '',
            '  **Step 2: Assign Roles**',
            '    Child: "You will be the bug. I\'ll be the cat."',
            '    Or: "I\'ll show you both parts!"',
            '',
            '  **Step 3: Show Props**',
            '    Child: "This is the mug. This is the rug."',
            '    Child explains what each prop represents',
            '',
            '  **Step 4: Teach Key Actions**',
            '    Child demonstrates:',
            '      "When the bug sees the cat, you RUN like this!"',
            '      "The bug hides under the rug like this!"',
            '      "At the end, the bug is GLAD, so you smile!"',
            '',
            '  **Step 5: Perform Together**',
            '    Child coaches student through performance',
            '    Child reads the CVC words for them',
            '    Child guides actions: "Now run! Now hide!"',
            '',
            'Parent\'s role during teaching:',
            '  - BE the confused learner (if you\'re the student)',
            '  - Ask questions: "Where should I stand?" "How do I show sad?"',
            '  - Make small "mistakes" for child to correct',
            '  - Celebrate child\'s teaching: "You explained that so well!"',
            '',
            'After the taught performance:',
            '  Applaud both teacher and student!',
            '  "You were an AMAZING teacher!"',
            '  "[Student name] did great because YOU taught them!"',
            '',
            'Reflection questions:',
            '  "What was hardest to teach?"',
            '  "How did you help them understand?"',
            '  "What makes a good teacher?"'
          ],
          examples: [
            'Child to sibling: "You be the bug. When I say RAN, you go fast like this!" Demonstrates. Sibling copies. Child: "Perfect!"'
          ],
          watchFor: [
            'NEW: Child explaining story coherently.',
            'NEW: Child demonstrating actions clearly.',
            'NEW: Child providing feedback/corrections.',
            'NEW: Patience and encouragement toward "student."',
            'Confidence in leadership role.',
            'Pride in teaching successfully.'
          ],
          parentTip: 'TEACHING is the highest form of learning! When they can teach it, they\'ve truly mastered it. This is MUCH harder than just performing!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎭 You\'re a story theater teacher!',
            'You practiced:',
            '✓ Reading a story script with CVC words',
            '✓ Understanding story sequence and characters',
            '✓ Acting out a story dramatically',
            '✓ Performing for an audience',
            '✓ NEW: TEACHING someone else to perform the story!'
          ],
          reviewQuestions: [
            'Could your child read the CVC words in the story?',
            'Did they understand the story plot and sequence?',
            'Were they engaged in the dramatic performance?',
            'NEW: Were they able to TEACH someone else effectively?',
            'NEW: Did they show patience and clarity when teaching?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Story Theater is the ultimate integration of reading skills. The NEW teaching component elevates this lesson significantly — teaching requires deeper understanding than performing. When children explain, demonstrate, and coach others, they must organize their knowledge, use clear language, and monitor comprehension. This metacognitive work is cognitively demanding and incredibly valuable. The "protégé effect" shows that learners retain more when they teach, and the confidence boost from successful teaching is enormous. This lesson transforms your child from passive learner to active teacher — a major developmental leap!'
          },
          nextSteps: [
            'Create new story scripts using other CVC words.',
            'Let your child write their own mini-play to teach.',
            'Have them teach a different family member or friend.',
            'Celebrate your child\'s teaching—cheer and discuss what they taught!',
            'NEXT WEEK: L-Blends begin — building on this strong CVC foundation!'
          ]
        }
      }
    ]
  },
],

[
  // ========= LESSON 21 =========
  {
    id: 21,
    phase: '4',
    quarter: 1,
    week: 5,
    weekIndex: 4,
    dayInWeek: 1,
    title: 'Lesson 21: Meet the L-Blends',
    theme: 'Beginning Consonant Blends (L-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'blending', 'phoneme-awareness'],
    relatedToPreviousSkills: ['cvc-decoding', 'blending', 'phoneme-awareness'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to L-Blends!',
        duration: '1 min',
        content: {
          goal: 'Introduce consonant blends where two consonants work together at the beginning of words.',
          parentGuide: [
            'L-blends are: bl, cl, fl, gl, pl, sl',
            'These are TWO sounds that blend together smoothly, not one sound.',
            'This is a new challenge — words now have 4 sounds instead of 3!',
            'Example: flag = /f/ /l/ /a/ /g/ (4 phonemes)',
            'Children must hear and blend both consonants before the vowel.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Your voice and hands'],
          tip: 'Emphasize that L-blends are TWO sounds close together, not one new sound!',
          researchNote: 'National Reading Panel (2000) - Explicit instruction in consonant blends improves decoding accuracy and reading fluency.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Hear the L-Sound',
        duration: '2-3 min',
        focus: 'Phoneme Awareness',
        researchBasis: 'Ehri (2020) - Explicit phoneme instruction supports orthographic mapping and word recognition.',
        content: {
          goal: 'Isolate and practice the /l/ sound, preparing children to hear it in blends.',
          whatToSay: [
            '"Today we\'re learning about special letter teams!"',
            '"First, let\'s practice the /l/ sound all by itself."',
            '"Say /l/... /l/... /l/. Feel your tongue touch the top of your mouth!"',
            '"Now let\'s find that /l/ sound in words!"'
          ],
          whatToDo: [
            'Model the /l/ sound clearly.',
            'Have child repeat /l/ several times.',
            'Point out tongue placement (touches roof of mouth).',
            'Practice /l/ in simple words:',
            '  - leg',
            '  - lip',
            '  - log',
            '  - lot',
            '  - lad',
            'Ask: "Do you hear /l/ at the beginning?"',
            'Practice saying /l/ slowly, then quickly.',
            'Make it playful: "Let\'s say /l/ like a robot... now like a mouse!"',
            'Prepare for next step: "Now /l/ will work with a partner!"'
          ],
          examples: [
            'You: "/l/... /l/... leg! Do you hear /l/?" Child: "Yes!" You: "Perfect! That\'s the /l/ sound!"'
          ],
          watchFor: [
            'Clear /l/ sound production.',
            'Ability to hear /l/ in initial position.',
            'Understanding that /l/ is ONE sound.',
            'Readiness to learn blends.'
          ],
          parentTip: 'If /l/ is hard to pronounce, practice in front of a mirror so they can see tongue position!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Build the L-Blends',
        duration: '4-5 min',
        focus: 'Blend Introduction',
        researchBasis: 'Lonigan et al. (2000) - Explicit blending instruction develops phonological awareness and decoding skills.',
        content: {
          goal: 'Introduce each L-blend (bl, cl, fl, gl, pl, sl) and demonstrate how two sounds blend together.',
          whatToSay: [
            '"Now /l/ gets a partner! Let\'s meet the L-blend team!"',
            '"First: /b/ + /l/ = /bl/. Listen: /b/... /l/... /bl/!"',
            '"Your turn! Say /b/... /l/... /bl/!"',
            '"Let\'s try all the L-blends!"'
          ],
          whatToDo: [
            'Introduce each L-blend systematically.',
            'For EACH blend, follow this pattern:',
            '  1. Say first sound slowly: "/b/"',
            '  2. Say second sound: "/l/"',
            '  3. Blend together: "/bl/"',
            '  4. Child repeats',
            '  5. Give example word',
            '',
            'L-Blends to teach:',
            '  **BL:** /b/ + /l/ = /bl/ → block',
            '  **CL:** /c/ + /l/ = /cl/ → clap',
            '  **FL:** /f/ + /l/ = /fl/ → flag',
            '  **GL:** /g/ + /l/ = /gl/ → glad',
            '  **PL:** /p/ + /l/ = /pl/ → plug',
            '  **SL:** /s/ + /l/ = /sl/ → slip',
            '',
            'Use hand motions:',
            '  - Show two fingers for TWO sounds',
            '  - Bring fingers together as sounds blend',
            'Write each blend as you teach it.',
            'Child practices saying each blend 2-3 times.',
            'Emphasize: "Two sounds, working together!"'
          ],
          examples: [
            'You: "/f/... /l/... /fl/!" (bring hands together) "Flag! /fl/-ag!" Child: "/fl/... flag!"'
          ],
          watchFor: [
            'Child blending both consonants smoothly.',
            'No extra vowel inserted (not "buh-luh").',
            'Understanding that it\'s TWO sounds, not one.',
            'Ability to repeat each blend.'
          ],
          parentTip: 'Common mistake: adding "uh" between consonants. Model saying them quickly together: /bl/ not /buh-luh/!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Blend Detective',
        duration: '3-4 min',
        focus: 'Auditory Discrimination',
        researchBasis: 'Anthony & Francis (2005) - Auditory discrimination of phoneme clusters supports reading development.',
        content: {
          goal: 'Practice identifying which L-blend you hear at the beginning of words.',
          whatToSay: [
            '"Now you\'re a Blend Detective!"',
            '"I\'ll say a word, and you tell me which blend you hear!"',
            '"Listen carefully: CLAP. What blend do you hear?"',
            '"CL! Great detective work!"'
          ],
          whatToDo: [
            'Say words with L-blends.',
            'Child identifies the beginning blend.',
            'Practice with 10-12 words:',
            '  - **block** (bl)',
            '  - **clap** (cl)',
            '  - **flag** (fl)',
            '  - **glad** (gl)',
            '  - **plug** (pl)',
            '  - **slip** (sl)',
            '  - **blue** (bl)',
            '  - **clock** (cl)',
            '  - **flop** (fl)',
            '  - **glass** (gl)',
            '  - **plan** (pl)',
            '  - **sled** (sl)',
            'For each word:',
            '  1. You say word clearly',
            '  2. Child identifies blend',
            '  3. Optional: child writes the blend letters',
            'Make it a game: "How many can you solve?"',
            'Celebrate correct answers enthusiastically!'
          ],
          examples: [
            'You: "FLAT!" Child: "FL!" You: "Yes! Detective success! FL like flag and flat!"'
          ],
          watchFor: [
            'Accurate identification of blends.',
            'Hearing BOTH consonants in the blend.',
            'Not confusing blends (bl vs. fl).',
            'Growing confidence with each word.'
          ],
          parentTip: 'If they struggle, isolate the blend: "Listen to just the beginning: /fl/... flag. What did you hear first?"'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Draw and Label L-Blends',
        duration: '2-3 min',
        focus: 'Visual-Verbal Connection',
        researchBasis: 'Ehri (2001) - Connecting phonemes to graphemes through writing strengthens orthographic memory.',
        content: {
          goal: 'Draw pictures of L-blend words and label them, connecting sounds to letters.',
          whatToSay: [
            '"Let\'s draw some L-blend words!"',
            '"Pick your favorite: flag, clock, or block?"',
            '"Let\'s draw it and write the word!"'
          ],
          whatToDo: [
            'Child chooses 2-3 L-blend words to draw.',
            'Suggested words (easy to draw):',
            '  - flag (pole with flag)',
            '  - clock (circle with hands)',
            '  - block (square/rectangle)',
            '  - plug (simple drawing)',
            '  - sled (curved bottom with rails)',
            'Child draws the picture.',
            'Together, write the word below:',
            '  - Say the word',
            '  - Identify the blend: "FL"',
            '  - Write the blend letters',
            '  - Add remaining letters: "A-G"',
            '  - Read the complete word: "FLAG!"',
            'Label clearly with large letters.',
            'Display artwork proudly.',
            'Review: Point to each picture and read the word.'
          ],
          examples: [
            'Child draws clock. You: "What blend?" Child: "CL!" You: "Write C-L!" Child writes: CLOCK'
          ],
          watchFor: [
            'Correct identification of blend.',
            'Proper letter formation.',
            'Ability to spell simple L-blend words.',
            'Pride in their labeled artwork.'
          ],
          parentTip: 'Focus on the BLEND letters — you can help with the rest of the spelling!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎯 You met the L-Blend team!',
            'You practiced:',
            '✓ Hearing the /l/ sound',
            '✓ Building L-blends (bl, cl, fl, gl, pl, sl)',
            '✓ Identifying blends in words (Blend Detective)',
            '✓ Drawing and labeling L-blend words'
          ],
          reviewQuestions: [
            'Can your child say each L-blend clearly (bl, cl, fl, gl, pl, sl)?',
            'Did they identify blends in spoken words?',
            'Do they understand that blends are TWO sounds together?',
            'Were they able to write blend letters?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Consonant blends are a major milestone in reading development. They require children to process two consonant sounds in quick succession before the vowel — a more complex skill than simple CVC words. Mastering L-blends opens the door to hundreds of new words and builds the phonological awareness needed for all consonant blends.'
          },
          nextSteps: [
            'Practice L-blends throughout the day: "I see a CLOCK!"',
            'Hunt for L-blend words in books or around the house.',
            'Make a poster with all 6 L-blends and example words.',
            'Tomorrow we\'ll practice reading complete L-blend words!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 22 =========
  {
    id: 22,
    phase: '4',
    quarter: 1,
    week: 5,
    weekIndex: 4,
    dayInWeek: 2,
    title: 'Lesson 22: Blend Slides',
    theme: 'Beginning Consonant Blends (L-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'blending', 'decoding-fluency'],
    relatedToPreviousSkills: ['consonant-blends', 'cvc-decoding', 'blending'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Blend Slides!',
        duration: '1 min',
        content: {
          goal: 'Practice smooth blending of L-blend words using a sliding motion to connect sounds.',
          parentGuide: [
            'This lesson focuses on FLUENT blending — moving from choppy to smooth.',
            'The "slide" motion helps children feel sounds connecting.',
            'We\'re decoding 4-sound words now (CCVC pattern).',
            'Example: flag = /f/ /l/ /a/ /g/ blended smoothly into "flag"',
            'Visual and kinesthetic cues support auditory blending.'
          ],
          materials: ['Paper and markers', 'Ruler or straight edge (optional)', 'Your sliding finger!'],
          tip: 'The sliding motion is KEY — it helps children feel the smooth connection between sounds!',
          researchNote: 'Ehri (2020) - Kinesthetic-visual-auditory integration accelerates phoneme blending and word recognition.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Review L-Blends',
        duration: '2 min',
        focus: 'Blend Recognition',
        researchBasis: 'National Reading Panel (2000) - Review and repetition strengthen phonological knowledge.',
        content: {
          goal: 'Quickly review the 6 L-blends before practicing full word decoding.',
          whatToSay: [
            '"Let\'s warm up with our L-blends!"',
            '"I\'ll say a blend, you repeat it!"',
            '"Ready? BL!"',
            '"Now let\'s go through all of them!"'
          ],
          whatToDo: [
            'Rapid-fire review of all 6 L-blends:',
            '  - BL (child repeats)',
            '  - CL (child repeats)',
            '  - FL (child repeats)',
            '  - GL (child repeats)',
            '  - PL (child repeats)',
            '  - SL (child repeats)',
            'Go through the list 2-3 times.',
            'Increase speed each round.',
            'Add example words:',
            '  "BL like block!"',
            '  "CL like clap!"',
            '  "FL like flag!"',
            'Make it rhythmic and energetic.',
            'End with: "Great! Now let\'s READ blend words!"'
          ],
          examples: [
            'You: "FL!" Child: "FL!" You: "FL like flag!" Child: "Flag!"'
          ],
          watchFor: [
            'Quick, automatic recall of blends.',
            'Clear pronunciation without extra vowels.',
            'Confidence and readiness to decode.'
          ],
          parentTip: 'Keep this QUICK — it\'s just a warm-up to activate prior knowledge!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: The Sliding Technique',
        duration: '3-4 min',
        focus: 'Blending Strategy',
        researchBasis: 'Lonigan et al. (2000) - Visual tracking supports phoneme blending and word decoding.',
        content: {
          goal: 'Teach the "sliding" technique for smoothly blending L-blend words.',
          whatToSay: [
            '"Today we\'re learning a special trick — Blend Slides!"',
            '"We\'ll slide our finger under the word as we read it!"',
            '"The slide helps the sounds stick together smoothly!"',
            '"Watch me do it first!"'
          ],
          whatToDo: [
            'Write a simple L-blend word in LARGE letters: CLAP',
            'Demonstrate the sliding technique:',
            '  1. Place finger under first letter (C)',
            '  2. Say first sound: "/c/"',
            '  3. WITHOUT lifting finger, slide to L',
            '  4. Say: "/l/"',
            '  5. Continue sliding: "/a/"',
            '  6. Final letter: "/p/"',
            '  7. Now SWEEP finger under whole word: "CLAP!"',
            'Key instruction: "Don\'t stop! Keep sliding smoothly!"',
            'Practice with child:',
            '  - Write: FLAG',
            '  - Child places finger under F',
            '  - Child slides and says: /f/ /l/ /a/ /g/',
            '  - Child sweeps: "FLAG!"',
            'Repeat with 3-4 words:',
            '  - SLIP',
            '  - PLUG',
            '  - GLAD',
            'Emphasize: "Slide = smooth reading!"'
          ],
          examples: [
            'Child slides finger under SLIP: "/s/... /l/... /i/... /p/..." SWEEP: "Slip!" You: "Perfect slide!"'
          ],
          watchFor: [
            'Continuous sliding motion (not stopping).',
            'Smooth sound production as finger moves.',
            'Understanding of the technique.',
            'Growing confidence with each word.'
          ],
          parentTip: 'If they stop between sounds, remind: "Keep your finger moving — slide it smoothly!"'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Blend Slide Practice',
        duration: '5-6 min',
        focus: 'Word Decoding',
        researchBasis: 'Ehri (2001) - Repeated decoding practice builds automaticity and sight word recognition.',
        content: {
          goal: 'Practice reading multiple L-blend words using the sliding technique.',
          whatToSay: [
            '"Now let\'s practice lots of blend words!"',
            '"Use your sliding finger for each one!"',
            '"Ready to slide and read?"'
          ],
          whatToDo: [
            'Write or display 10-12 L-blend words.',
            'Child reads each word using the slide technique.',
            'Words to practice (mix all L-blends):',
            '  **BL words:**',
            '    - bled',
            '    - blob',
            '  **CL words:**',
            '    - clam',
            '    - clip',
            '  **FL words:**',
            '    - flag',
            '    - flat',
            '    - flip',
            '  **GL words:**',
            '    - glad',
            '    - glen',
            '  **PL words:**',
            '    - plan',
            '    - plod',
            '  **SL words:**',
            '    - slab',
            '    - slim',
            '    - slop',
            'For each word:',
            '  1. Child slides and sounds out',
            '  2. Child reads whole word',
            '  3. You celebrate!',
            'If child struggles, help sound it out together.',
            'Notice improvement: "That was faster than last time!"',
            'Optional: Time them reading all words (measure fluency).'
          ],
          examples: [
            'Child: (slides under FLIP) "/f/... /l/... /i/... /p/... flip!" You: "Yes! You read flip!"'
          ],
          watchFor: [
            'Accurate decoding of all phonemes.',
            'Smooth blending using slide technique.',
            'Speed increasing with practice.',
            'Self-correction when mistakes happen.'
          ],
          parentTip: 'BLEND is the hardest part! If they say /f/ /l/ but miss the connection, emphasize: "Those two sounds are best friends — /fl/!"'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Choppy vs. Smooth Challenge',
        duration: '2-3 min',
        focus: 'Fluency Awareness',
        researchBasis: 'National Reading Panel (2000) - Metacognitive awareness of fluency improves reading performance.',
        content: {
          goal: 'Compare choppy reading to smooth reading, helping children hear and feel the difference.',
          whatToSay: [
            '"Let\'s play a game! I\'ll read two ways — choppy and smooth!"',
            '"You tell me which one sounds like a good reader!"',
            '"Ready? Choppy: /f/...  /l/...  /a/...  /g/. Smooth: flag!"',
            '"Which one sounded better?"'
          ],
          whatToDo: [
            'Demonstrate two ways to read the same word:',
            '  **Choppy:** Pause between each sound with robot voice',
            '  **Smooth:** Slide sounds together naturally',
            'Example comparisons:',
            '  - Choppy: "/c/... /l/... /a/... /p/" vs. Smooth: "clap"',
            '  - Choppy: "/s/... /l/... /i/... /p/" vs. Smooth: "slip"',
            '  - Choppy: "/g/... /l/... /a/... /d/" vs. Smooth: "glad"',
            'Child identifies which is smooth.',
            'Then CHILD practices both ways:',
            '  "First read it choppy, then smooth!"',
            'Help them HEAR the difference.',
            'Emphasize: "Smooth reading is our goal!"',
            'Celebrate smooth reading with enthusiasm.'
          ],
          examples: [
            'You: (choppy) "/f/... /l/... /a/... /g/" Child: "That\'s choppy!" You: "Now smooth!" Child: "Flag!" You: "Perfect!"'
          ],
          watchFor: [
            'Child distinguishing choppy from smooth.',
            'Understanding that smooth = fluent.',
            'Actively working toward smoother blending.',
            'Self-monitoring their own reading.'
          ],
          parentTip: 'This builds metacognition — awareness of HOW they\'re reading, not just WHAT they\'re reading!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🛝 You mastered Blend Slides!',
            'You practiced:',
            '✓ Reviewing all L-blends',
            '✓ Using the sliding technique to blend words',
            '✓ Reading L-blend words smoothly',
            '✓ Understanding choppy vs. smooth reading'
          ],
          reviewQuestions: [
            'Could your child use the sliding technique effectively?',
            'Did they decode L-blend words accurately?',
            'Is their blending becoming smoother?',
            'Did they understand the difference between choppy and smooth reading?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'The sliding technique is a powerful scaffold for blending. The kinesthetic motion (finger sliding) mirrors the auditory goal (sounds blending smoothly). As children master this technique, their reading becomes more fluent and automatic. This lesson moves them from laborious sound-by-sound decoding toward natural, smooth reading — the hallmark of skilled readers.'
          },
          nextSteps: [
            'Use the sliding technique for ALL reading practice!',
            'Notice when they naturally start blending without the finger slide — that\'s automaticity!',
            'Create "slide paths" with chalk outside for physical blend sliding.',
            'Tomorrow we\'ll build even more L-blend words!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 23 =========
  {
    id: 23,
    phase: '4',
    quarter: 1,
    week: 5,
    weekIndex: 4,
    dayInWeek: 3,
    title: 'Lesson 23: L-Blend Word Builder',
    theme: 'Beginning Consonant Blends (L-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'word-building', 'spelling-patterns'],
    relatedToPreviousSkills: ['consonant-blends', 'cvc-decoding', 'phoneme-manipulation'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to L-Blend Word Builder!',
        duration: '1 min',
        content: {
          goal: 'Build and manipulate L-blend words by changing beginning blends, vowels, and ending sounds.',
          parentGuide: [
            'This lesson develops phoneme manipulation — a critical pre-spelling skill.',
            'Children learn that changing ONE sound creates a NEW word.',
            'This is more advanced than just reading — it\'s actively constructing words.',
            'Word building strengthens understanding of how sounds map to letters.'
          ],
          materials: ['Paper and pencil', 'Letter cards or tiles (optional)', 'Eraser for word changes'],
          tip: 'Make it playful! Say "Let\'s do word magic — change one sound, get a new word!"',
          researchNote: 'Ehri (2001) - Phoneme manipulation activities strengthen both reading and spelling abilities.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Build Your First Word',
        duration: '3-4 min',
        focus: 'Word Construction',
        researchBasis: 'National Reading Panel (2000) - Explicit word-building activities improve phonemic awareness and decoding.',
        content: {
          goal: 'Construct complete L-blend words sound by sound, understanding the word-building process.',
          whatToSay: [
            '"Let\'s build a word together — like construction workers!"',
            '"We\'ll start with the blend, add a vowel, then add the ending!"',
            '"First word: CLAP. What blend do we need?"',
            '"CL! Great! Now what vowel sound... /a/! Then /p/ at the end!"'
          ],
          whatToDo: [
            'Choose first word to build: CLAP',
            'Build it step by step:',
            '  Step 1: "What blend?" → CL',
            '  Step 2: "What vowel?" → A',
            '  Step 3: "What ending?" → P',
            '  Step 4: Read it: "CLAP!"',
            'Write each letter as you build.',
            'Child can write letters or just participate verbally.',
            'Build 3-4 words completely:',
            '  - **flag:** FL + A + G',
            '  - **slip:** SL + I + P',
            '  - **glad:** GL + A + D',
            '  - **plug:** PL + U + G',
            'For each word:',
            '  - Identify the blend',
            '  - Identify the vowel',
            '  - Identify the ending',
            '  - Read the complete word',
            'Celebrate each completed word!',
            'Say: "You just BUILT that word!"'
          ],
          examples: [
            'You: "Let\'s build FLAG. What blend?" Child: "FL!" You: "Write F-L. What vowel?" Child: "A!" You: "What ending?" Child: "G!" Both: "FLAG!"'
          ],
          watchFor: [
            'Understanding of word structure (blend + vowel + ending).',
            'Correct identification of each component.',
            'Ability to segment words into parts.',
            'Success reading completed words.'
          ],
          parentTip: 'Use boxes or dashes to show word structure: [FL][A][G] helps them see the parts!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Change the Blend',
        duration: '3-4 min',
        focus: 'Beginning Sound Manipulation',
        researchBasis: 'Ehri (2020) - Phoneme substitution develops flexible phonological awareness essential for reading and spelling.',
        content: {
          goal: 'Transform words by changing only the beginning blend, showing how one change creates new words.',
          whatToSay: [
            '"Now for word magic! Let\'s change just the BLEND!"',
            '"We have CLAP. What if we change CL to FL?"',
            '"FLAP! A new word!"',
            '"Let\'s try more word magic!"'
          ],
          whatToDo: [
            'Start with a base word.',
            'Change ONLY the blend, keep vowel and ending same.',
            'Word transformation chains:',
            '',
            '  **Chain 1 (_LAP family):**',
            '  - clap → flap → slap',
            '',
            '  **Chain 2 (_LIP family):**',
            '  - clip → flip → slip',
            '',
            '  **Chain 3 (_LOT family):**',
            '  - blot → clot → plot → slot',
            '',
            '  **Chain 4 (_LAD family):**',
            '  - clad → glad',
            '',
            'For each transformation:',
            '  1. Read the original word',
            '  2. "Change the blend to..."',
            '  3. Erase or cross out old blend',
            '  4. Write new blend',
            '  5. Read new word',
            '  6. Celebrate the transformation!',
            'Emphasize: "ONE change = NEW word!"',
            'Let child suggest some blend changes.'
          ],
          examples: [
            'You have: CLIP. You: "Change CL to FL!" Child erases CL, writes FL. Child reads: "FLIP!" You: "Word magic!"'
          ],
          watchFor: [
            'Understanding that changing the blend changes the word.',
            'Accurate blend substitution.',
            'Successful reading of new words.',
            'Excitement about "word magic."'
          ],
          parentTip: 'If they struggle, do it together: "Let\'s cross out CL and write FL instead. What\'s our new word?"'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Change the Vowel',
        duration: '3-4 min',
        focus: 'Medial Sound Manipulation',
        researchBasis: 'Lonigan et al. (2000) - Medial phoneme manipulation strengthens phonological awareness and decoding flexibility.',
        content: {
          goal: 'Transform words by changing the middle vowel while keeping the blend and ending constant.',
          whatToSay: [
            '"Now let\'s change the MIDDLE — the vowel!"',
            '"We have SLIP. What if we change I to A?"',
            '"SLAP! Another new word!"',
            '"The blend and ending stay the same — just the vowel changes!"'
          ],
          whatToDo: [
            'Start with a blend word.',
            'Change ONLY the vowel.',
            'Vowel transformation chains:',
            '',
            '  **Chain 1 (FL_ P family):**',
            '  - flap → flip → flop',
            '',
            '  **Chain 2 (SL_ P family):**',
            '  - slap → slip → slop',
            '',
            '  **Chain 3 (BL_ D family):**',
            '  - bled → blad (nonsense, skip) → (use: bled → glad instead)',
            '',
            '  **Chain 4 (PL_ N family):**',
            '  - plan → (plin - nonsense) → (use: plan → plug instead)',
            '',
            'Note: Some vowel changes create nonsense words — that\'s okay for practice!',
            'For each change:',
            '  1. Identify current vowel',
            '  2. "Change the vowel to..."',
            '  3. Erase/replace vowel',
            '  4. Read new word',
            '  5. Ask: "Is this a real word?"',
            'This helps children understand vowel importance.',
            'Real vs. nonsense words both provide practice!'
          ],
          examples: [
            'You have: FLIP. You: "Change I to O!" Child: "FLOP!" You: "Yes! Real word! Flop like a fish flops!"'
          ],
          watchFor: [
            'Understanding that vowels change word meaning.',
            'Accurate vowel substitution.',
            'Recognition of real vs. nonsense words.',
            'Flexibility with phoneme changes.'
          ],
          parentTip: 'Nonsense words are GREAT practice! They prove your child is truly decoding, not memorizing!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Word Builder Challenge',
        duration: '2-3 min',
        focus: 'Independent Word Construction',
        researchBasis: 'Ehri (2001) - Independent phoneme manipulation demonstrates mastery and builds confidence.',
        content: {
          goal: 'Child independently builds L-blend words from your verbal instructions, demonstrating mastery.',
          whatToSay: [
            '"Now YOU\'re the word builder!"',
            '"I\'ll tell you what word to build, and you do it!"',
            '"Ready? Build the word GLAD!"',
            '"What blend? What vowel? What ending? Build it!"'
          ],
          whatToDo: [
            'Give child verbal instructions only.',
            'Child builds words independently.',
            'Challenge words to build:',
            '  1. "Build CLAP"',
            '  2. "Build FLAG"',
            '  3. "Build SLIP"',
            '  4. "Build PLUG"',
            '  5. "Build GLAD"',
            '  6. "Build PLOT"',
            'For each word:',
            '  - Child segments sounds',
            '  - Child writes letters',
            '  - Child reads completed word',
            '  - You check and celebrate!',
            'If mistakes happen, guide: "Check the blend — do you hear two sounds?"',
            'Optional: Time them (friendly challenge).',
            'End with: "You\'re an official Word Builder!"',
            'Consider making a "Word Builder Certificate."'
          ],
          examples: [
            'You: "Build FLIP!" Child writes: F-L-I-P. Child: "FLIP!" You: "Perfect building! You\'re a master!"'
          ],
          watchFor: [
            'Independent segmentation of words.',
            'Accurate spelling of L-blend words.',
            'Confidence in the building process.',
            'Self-correction abilities.'
          ],
          parentTip: 'Independence is the goal! Step back and let them work — only help if truly stuck!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🏗️ You\'re a Word Builder expert!',
            'You practiced:',
            '✓ Building L-blend words from scratch',
            '✓ Changing blends to make new words',
            '✓ Changing vowels to transform words',
            '✓ Building words independently'
          ],
          reviewQuestions: [
            'Could your child build L-blend words independently?',
            'Did they understand how changing sounds creates new words?',
            'Were they successful with phoneme manipulation?',
            'Did they enjoy the word-building process?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Word building is the bridge between reading and spelling. When children can manipulate phonemes (change blends, change vowels), they develop deep phonological awareness and understand how the alphabetic code works. This active, hands-on approach to phonics builds both decoding AND encoding (spelling) skills simultaneously. Children who can build words are well on their way to becoming confident readers and writers.'
          },
          nextSteps: [
            'Play word-building games daily: "Can you build...?"',
            'Write letters on small paper scraps — your child can rearrange them to build words.',
            'Challenge: Build as many words as possible with one blend (all FL words!).',
            'Tomorrow we\'ll hunt for L-blends in stories!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 24 =========
  {
    id: 24,
    phase: '4',
    quarter: 1,
    week: 5,
    weekIndex: 4,
    dayInWeek: 4,
    title: 'Lesson 24: Blend Detective',
    theme: 'Beginning Consonant Blends (L-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'listening-comprehension', 'vocabulary'],
    relatedToPreviousSkills: ['consonant-blends', 'auditory-discrimination', 'story-comprehension'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Blend Detective!',
        duration: '1 min',
        content: {
          goal: 'Listen to a story and identify L-blend words, building listening skills and vocabulary.',
          parentGuide: [
            'This lesson integrates L-blends into meaningful context.',
            'Children practice listening comprehension while identifying target sounds.',
            'Finding blends in stories shows that these aren\'t just isolated skills — they\'re everywhere in reading!',
            'This prepares children for independent reading of connected text.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Your storytelling voice'],
          tip: 'Read with enthusiasm and pause slightly before L-blend words to help children notice them!',
          researchNote: 'Scarborough (2001) - Integrating phonics skills into meaningful text supports both word recognition and comprehension (The Reading Rope).'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Detective Training',
        duration: '2-3 min',
        focus: 'Listening Preparation',
        researchBasis: 'National Reading Panel (2000) - Pre-reading activities that focus attention improve comprehension and word recognition.',
        content: {
          goal: 'Prepare children to listen actively for L-blend words in the upcoming story.',
          whatToSay: [
            '"Today you\'re a Blend Detective!"',
            '"Detectives listen VERY carefully and find clues!"',
            '"Your job: find all the L-blend words hiding in our story!"',
            '"Let\'s practice first. Listen: The flag blew in the wind. Did you hear an L-blend?"'
          ],
          whatToDo: [
            'Explain the detective mission.',
            'Practice with sample sentences:',
            '  - "The flag blew in the wind." (flag, blew)',
            '  - "She wore a blue dress." (blue)',
            '  - "The clock went clang!" (clock, clang)',
            '  - "He plans to slide down." (plans, slide)',
            'After each sentence:',
            '  - "Did you hear an L-blend?"',
            '  - Child identifies the word',
            '  - Child identifies the specific blend',
            'Set up listening signal:',
            '  - Clap when you hear an L-blend, OR',
            '  - Raise hand, OR',
            '  - Tap table',
            'Explain: "In the story, signal every time you hear one!"',
            'Build excitement: "Let\'s see how many you can detect!"'
          ],
          examples: [
            'You: "Listen: The glass was full. What blend word?" Child: "Glass!" You: "Yes! GL! Great detecting!"'
          ],
          watchFor: [
            'Understanding of the detective task.',
            'Ability to identify blends in sentences.',
            'Enthusiasm for the listening challenge.',
            'Readiness to focus on the story.'
          ],
          parentTip: 'Make it clear they\'re listening FOR something — this focuses attention and makes listening active, not passive!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Listen to the Detective Story',
        duration: '5-6 min',
        focus: 'Active Listening',
        researchBasis: 'Lonigan et al. (2000) - Targeted listening activities develop phonological awareness and vocabulary simultaneously.',
        content: {
          goal: 'Listen to a story rich with L-blend words while actively identifying them.',
          whatToSay: [
            '"Ready for the story? Remember — signal when you hear L-blends!"',
            '"Listen carefully... here we go!"',
            '"The story is called: Clyde\'s Plan!"'
          ],
          whatToDo: [
            'Read this story aloud (L-blend words in bold — pause slightly before them):',
            '',
            '**Clyde\'s Plan**',
            'Clyde had a **plan**. He wanted to **climb** the big hill.',
            'First, he put on his **blue** shirt.',
            'Then he grabbed his **flag** and his **backpack**.',
            'He started to **climb**. **Slowly**, up the hill.',
            'A bird began to **flap** its wings near him.',
            '"Hello, **bluebird**!" said Clyde.',
            'Clyde kept climbing. He did not **slip**.',
            'At the top, he put up his **flag**.',
            '"I am **glad** I made this **climb**!" said Clyde.',
            'He could see the **clouds** and the **sky**.',
            'Clyde\'s **plan** worked! He felt very **pleased**.',
            'The end.',
            '',
            'As you read:',
            '  - Pause slightly before each bold word',
            '  - Wait for child\'s signal (clap/hand raise)',
            '  - Acknowledge: "Yes! Good detective work!"',
            '  - Continue reading',
            'After the story:',
            '  - "How many L-blends did you find?"',
            '  - List them together on paper',
            '  - Identify each specific blend'
          ],
          examples: [
            'You read: "He wanted to..." (pause) "climb..." Child: (claps!) You: "Yes! CLIMB! CL blend!"'
          ],
          watchFor: [
            'Consistent signaling when blends appear.',
            'Accurate identification of L-blend words.',
            'Sustained attention throughout story.',
            'Understanding of story content (not just hunting for blends).'
          ],
          parentTip: 'If they miss some blends, that\'s okay! This is challenging. You can reread and hunt for more!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Count the Clues',
        duration: '2-3 min',
        focus: 'Blend Categorization',
        researchBasis: 'Ehri (2020) - Categorizing and analyzing phoneme patterns strengthens orthographic knowledge.',
        content: {
          goal: 'Review and categorize all the L-blend words found in the story.',
          whatToSay: [
            '"Let\'s count all the L-blend clues you found!"',
            '"I\'ll write them down as you remember them!"',
            '"Then we\'ll sort them by blend family!"'
          ],
          whatToDo: [
            'Create a list of L-blend words from the story:',
            '  - plan / pleased',
            '  - climb / climb / clouds',
            '  - blue / bluebird',
            '  - flag / flag / flap',
            '  - slowly',
            '  - slip',
            '  - glad',
            'Write each word child identifies.',
            'Count total: "You found [X] L-blend words!"',
            'Sort by blend type:',
            '  **BL:** blue, bluebird',
            '  **CL:** Clyde, climb, climb, clouds',
            '  **FL:** flag, flag, flap',
            '  **GL:** glad',
            '  **PL:** plan, pleased',
            '  **SL:** slowly, slip',
            'Notice patterns: "We found lots of CL words!"',
            'Have child read each word from the list.',
            'Celebrate: "You\'re an expert detective!"'
          ],
          examples: [
            'Child: "Flag!" You write: FLAG. You: "What blend?" Child: "FL!" You: "Yes! We\'ll put it in the FL group!"'
          ],
          watchFor: [
            'Memory of story words.',
            'Correct blend identification.',
            'Ability to sort by blend family.',
            'Success reading the word list.'
          ],
          parentTip: 'Sorting helps children see patterns. "Look how many CL words! CL must be common!"'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Detective Report',
        duration: '2-3 min',
        focus: 'Story Retelling',
        researchBasis: 'Whitehurst & Lonigan (1998) - Story retelling strengthens comprehension and narrative skills.',
        content: {
          goal: 'Retell the story using the L-blend words discovered, integrating comprehension with phonics.',
          whatToSay: [
            '"Now tell me the story of Clyde!"',
            '"Use our L-blend words to help you remember!"',
            '"What happened first? What did Clyde want to do?"'
          ],
          whatToDo: [
            'Guide child in retelling the story.',
            'Prompt with questions:',
            '  - "Who is the story about?" (Clyde)',
            '  - "What was Clyde\'s plan?" (climb the hill)',
            '  - "What did he wear?" (blue shirt)',
            '  - "What did he bring?" (flag, backpack)',
            '  - "Who did he meet?" (bluebird)',
            '  - "How did he feel at the end?" (glad, pleased)',
            'Point to L-blend words on the list as they say them.',
            'Encourage full sentences.',
            'Accept any order or details — focus on comprehension.',
            'Optional: Child draws favorite scene from story.',
            'Celebrate: "You understood the story AND found the blends!"'
          ],
          examples: [
            'Child: "Clyde wanted to climb. He wore blue. He put up a flag." You: "Perfect! You remembered the whole story!"'
          ],
          watchFor: [
            'Understanding of story sequence.',
            'Use of story vocabulary.',
            'Connection between phonics and meaning.',
            'Confidence in retelling.'
          ],
          parentTip: 'Retelling proves they weren\'t just hunting for sounds — they understood the story meaning too!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🔍 You\'re a certified Blend Detective!',
            'You practiced:',
            '✓ Listening for L-blend words in a story',
            '✓ Identifying and counting blend clues',
            '✓ Sorting words by blend families',
            '✓ Retelling the story using blend words'
          ],
          reviewQuestions: [
            'Could your child identify L-blends while listening?',
            'Did they remember and categorize the blend words?',
            'Did they understand the story content?',
            'Were they engaged in the detective mission?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Blend Detective bridges isolated phonics practice to real reading. When children hunt for target sounds in meaningful stories, they develop both phonological awareness AND comprehension. This active listening strengthens their ability to notice phonics patterns in books they read independently. Most importantly, it shows children that the sounds they\'re learning aren\'t abstract — they\'re the building blocks of real, interesting stories!'
          },
          nextSteps: [
            'Play Blend Detective with any book you read together!',
            'Hunt for L-blends in environmental print (signs, packages).',
            'Let your child create their own L-blend story to tell you.',
            'Tomorrow: Review and celebrate all L-blend learning!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 25 =========
  {
    id: 25,
    phase: '4',
    quarter: 1,
    week: 5,
    weekIndex: 4,
    dayInWeek: 5,
    title: 'Lesson 25: Draw & Label L-Blends',
    theme: 'Beginning Consonant Blends (L-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'vocabulary', 'writing'],
    relatedToPreviousSkills: ['consonant-blends', 'word-meaning', 'letter-formation'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Draw & Label L-Blends!',
        duration: '1 min',
        content: {
          goal: 'Create illustrated vocabulary cards for L-blend words, connecting pictures to written words.',
          parentGuide: [
            'This lesson consolidates L-blend learning through art and writing.',
            'Drawing + labeling = deeper word memory.',
            'Visual representation helps children remember word meanings.',
            'This is the culminating L-blend lesson — celebrating the week\'s learning!'
          ],
          materials: ['Paper (6-8 sheets or index cards)', 'Crayons, markers, or colored pencils', 'Pencil for writing'],
          tip: 'Simple drawings are perfect! Focus on clear labeling, not artistic perfection.',
          researchNote: 'Ehri (2001) - Multi-sensory word learning (visual, motor, linguistic) creates stronger orthographic memories.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Choose Your Words',
        duration: '2-3 min',
        focus: 'Word Selection',
        researchBasis: 'National Reading Panel (2000) - Student choice in learning activities increases engagement and motivation.',
        content: {
          goal: 'Select 6-8 L-blend words to illustrate, giving children ownership in their learning.',
          whatToSay: [
            '"This week you learned SO many L-blend words!"',
            '"Let\'s pick your favorites to draw!"',
            '"Which L-blend words do you want to draw?"',
            '"Think of things that are easy to draw — like flag, clock, or plug!"'
          ],
          whatToDo: [
            'Review L-blends learned this week.',
            'Brainstorm drawable L-blend words together.',
            'Suggested word bank (easy to illustrate):',
            '  **BL:** block, blue (color swatch), blanket',
            '  **CL:** clock, clam, cloud, clip',
            '  **FL:** flag, flower, fly (insect)',
            '  **GL:** glass, glove, globe',
            '  **PL:** plug, plant, plate, plum',
            '  **SL:** sled, slide, slug',
            'Child chooses 6-8 words they want to draw.',
            'Write the word list on a separate paper.',
            'Aim for variety: different blends represented.',
            'Get excited: "These will be YOUR special word cards!"',
            'Prepare paper/cards for drawing.'
          ],
          examples: [
            'Child: "I want to draw a flag!" You: "Great choice! FLAG — that\'s FL! What else?"'
          ],
          watchFor: [
            'Child engagement in word selection.',
            'Variety of blend choices.',
            'Understanding that words must be drawable.',
            'Excitement about the project.'
          ],
          parentTip: 'Let them choose! Even if they want all FL words, that\'s fine — ownership matters most!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Draw Your Pictures',
        duration: '5-6 min',
        focus: 'Visual Representation',
        researchBasis: 'Whitehurst & Lonigan (1998) - Visual representation of vocabulary strengthens word learning and retention.',
        content: {
          goal: 'Create clear, simple illustrations for each chosen L-blend word.',
          whatToSay: [
            '"Now let\'s draw your words!"',
            '"Keep drawings SIMPLE — stick figures and shapes work great!"',
            '"Make sure someone could look at your picture and guess the word!"',
            '"Let\'s start with your first word!"'
          ],
          whatToDo: [
            'Set up one paper/card per word.',
            'Draw pictures one at a time.',
            'For each word:',
            '  1. Say the word aloud',
            '  2. Discuss what to draw: "What does a clock look like?"',
            '  3. Child draws (parent can help if needed)',
            '  4. Keep it SIMPLE and clear',
            '  5. Add color if desired',
            'Drawing tips:',
            '  - **flag:** rectangle on a pole',
            '  - **clock:** circle with numbers and hands',
            '  - **block:** square or cube',
            '  - **plug:** rectangle with two prongs',
            '  - **glass:** cup/cylinder shape',
            '  - **sled:** curved bottom with rails',
            '  - **cloud:** fluffy oval shape',
            '  - **plant:** pot with leaves/flower',
            'Encourage creativity but maintain recognizability.',
            'No pressure for perfection!',
            'Celebrate each completed drawing.'
          ],
          examples: [
            'Child draws flag: pole + rectangle with stripes. You: "Perfect! I can tell that\'s a flag!"'
          ],
          watchFor: [
            'Clear, recognizable drawings.',
            'Child engagement in artistic process.',
            'Pictures matching word meanings.',
            'Pride in their artwork.'
          ],
          parentTip: 'If they get stuck, draw together! "Let\'s draw a clock together — you do the circle, I\'ll add numbers!"'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Label Your Cards',
        duration: '4-5 min',
        focus: 'Word Writing',
        researchBasis: 'Ehri (2020) - Writing words while saying them aloud strengthens orthographic mapping and spelling knowledge.',
        content: {
          goal: 'Write the L-blend word below each picture, practicing spelling and letter formation.',
          whatToSay: [
            '"Now let\'s LABEL your pictures!"',
            '"We\'ll write the word under each drawing!"',
            '"Let\'s sound it out as we write!"',
            '"First card: FLAG. What sounds do you hear?"'
          ],
          whatToDo: [
            'For EACH drawing:',
            '  1. Review the word: "This is a flag"',
            '  2. Sound it out: "/f/ /l/ /a/ /g/"',
            '  3. Identify letters: "F-L-A-G"',
            '  4. Child writes word below picture',
            '  5. Say word again: "Flag!"',
            'Writing process:',
            '  - Write in LARGE, clear letters',
            '  - Bottom of card (below picture)',
            '  - Child writes what they can; parent helps as needed',
            '  - Emphasize the BLEND letters especially',
            'As child writes each word:',
            '  - Say sounds aloud',
            '  - Point to blend: "These two letters work together!"',
            '  - Read completed word',
            'Optional: Use different colors for the blend.',
            'Celebrate completed vocabulary cards!',
            'Review all cards together when finished.'
          ],
          examples: [
            'Child writes C-L-O-C-K under clock drawing. You: "Perfect! CL — the blend — then O-C-K. Clock!"'
          ],
          watchFor: [
            'Correct spelling of L-blend words.',
            'Proper letter formation.',
            'Ability to segment words while writing.',
            'Pride in completed cards.'
          ],
          parentTip: 'Let them write independently but help with spelling! This isn\'t a spelling test — it\'s practice!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Review & Play',
        duration: '2-3 min',
        focus: 'Word Recognition Practice',
        researchBasis: 'Lonigan et al. (2000) - Repeated exposure to words in varied formats builds automatic word recognition.',
        content: {
          goal: 'Use the completed cards for review games, reinforcing L-blend word reading.',
          whatToSay: [
            '"Your word cards are amazing!"',
            '"Let\'s use them to play some games!"',
            '"First, let\'s read through ALL your cards!"'
          ],
          whatToDo: [
            'Review all completed cards:',
            '  - Show picture side',
            '  - Child reads word',
            '  - Celebrate each one!',
            'Play quick games:',
            '',
            '  **Game 1: Speed Read**',
            '  - Show the word side only (cover the drawing)',
            '  - Child reads the word',
            '  - Flip to check with the drawing',
            '',
            '  **Game 2: Memory Match**',
            '  - If you made duplicates, play matching',
            '  - Or play "Which card am I thinking of?"',
            '',
            '  **Game 3: Sort by Blend**',
            '  - Sort cards into BL, CL, FL, GL, PL, SL piles',
            '  - Count how many of each blend',
            '',
            '  **Game 4: Story Time**',
            '  - Pick 3-4 cards',
            '  - Make up a silly story using those words',
            '',
            'Store cards in a special place.',
            'Plan: "We can use these cards all week!"',
            'Take a photo of all the cards together.'
          ],
          examples: [
            'Show: CLOCK card. Child: "Clock!" You: "Yes! CL blend!" Show: FLAG card. Child: "Flag!" You: "FL blend!"'
          ],
          watchFor: [
            'Quick word recognition.',
            'Enjoyment of review games.',
            'Willingness to use cards for practice.',
            'Pride in their creation.'
          ],
          parentTip: 'These cards are valuable! Use them daily for quick practice — they\'re personalized learning tools!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎨 You created L-Blend vocabulary cards!',
            'You practiced:',
            '✓ Choosing L-blend words to illustrate',
            '✓ Drawing clear pictures',
            '✓ Writing and spelling L-blend words',
            '✓ Playing review games with your cards',
            '✓ Completing L-Blend Week!'
          ],
          reviewQuestions: [
            'Did your child create 6-8 illustrated word cards?',
            'Were they able to spell L-blend words?',
            'Do they recognize their words quickly?',
            'Are they proud of their vocabulary cards?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Creating personal vocabulary cards is powerful because it combines multiple learning pathways: visual (seeing the picture), motor (drawing and writing), and linguistic (saying and reading the word). When children create their own learning materials, they develop ownership and pride in their reading progress. These cards become tangible evidence of their learning — concrete proof that they\'re becoming readers. Keep these cards and use them regularly for quick, joyful practice sessions.'
          },
          nextSteps: [
            'Use cards daily for 2-minute quick review sessions.',
            'Add new L-blend word cards as you encounter them.',
            'Let your child "teach" someone else using their cards.',
            'Create a special box or folder to store vocabulary cards.',
            'Next week: R-Blends! (br, cr, dr, fr, gr, pr, tr)'
          ]
        }
      }
    ]
  },

  // ========= LESSON 26 =========
  {
    id: 26,
    phase: '4',
    quarter: 1,
    week: 6,
    weekIndex: 5,
    dayInWeek: 1,
    title: 'Lesson 26: R-Blends Roll-In',
    theme: 'Beginning Consonant Blends (R-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'blending', 'phoneme-awareness'],
    relatedToPreviousSkills: ['consonant-blends', 'blending', 'phoneme-awareness'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to R-Blends!',
        duration: '1 min',
        content: {
          goal: 'Introduce R-blends (br, cr, dr, fr, gr, pr, tr) and their distinctive rolling sound quality.',
          parentGuide: [
            'R-blends are: br, cr, dr, fr, gr, pr, tr',
            'The /r/ sound gives these blends a distinctive "rolling" quality.',
            'R-blends can be trickier than L-blends because /r/ is harder to pronounce.',
            'Emphasize the STRONG /r/ sound — it shouldn\'t disappear!',
            'Children who mastered L-blends will find R-blends similar but with new sounds.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Mirror (optional - to see mouth position)'],
          tip: 'Exaggerate the /r/ sound! Make it growly and strong so children can really hear it.',
          researchNote: 'National Reading Panel (2000) - Systematic phonics instruction that builds on prior knowledge improves reading outcomes.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Feel the /r/ Sound',
        duration: '2-3 min',
        focus: 'Phoneme Awareness',
        researchBasis: 'Ehri (2020) - Explicit articulation instruction supports phoneme awareness and word recognition.',
        content: {
          goal: 'Isolate and practice the /r/ sound, emphasizing its unique mouth position and sound quality.',
          whatToSay: [
            '"Today we\'re learning R-blends — and /r/ is a STRONG sound!"',
            '"Let\'s practice /r/ like a tiger growl: /rrrr/!"',
            '"Feel your tongue curl back? That\'s the /r/ position!"',
            '"Let\'s say /r/ lots of times!"'
          ],
          whatToDo: [
            'Model the /r/ sound clearly and strongly.',
            'Point out tongue position:',
            '  - Tongue curls back (retroflex)',
            '  - Doesn\'t touch roof of mouth',
            '  - Makes a "growly" sound',
            'Optional: Use mirror so child can see mouth position.',
            'Practice /r/ in isolation: "/r/... /r/... /r/"',
            'Make it playful:',
            '  - Growl like a tiger: "Rrrrr!"',
            '  - Engine starting: "Rrrr-rrrr-rrrr!"',
            '  - Pirate voice: "Arrrr!" (starts with /r/)',
            'Practice /r/ in simple words:',
            '  - red',
            '  - run',
            '  - rat',
            '  - rug',
            'Ask: "Do you hear /r/ at the beginning?"',
            'Emphasize: "/r/ is STRONG — don\'t let it disappear!"'
          ],
          examples: [
            'You: "/r/... /r/... red!" Child: "/r/... red!" You: "Strong /r/! I can really hear it!"'
          ],
          watchFor: [
            'Attempt at correct tongue position.',
            'Audible /r/ sound (not /w/).',
            'Understanding that /r/ is ONE sound.',
            'Confidence with /r/ production.'
          ],
          parentTip: 'If /r/ is hard, that\'s very common! Practice in front of a mirror and exaggerate the tongue curl.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Build the R-Blends',
        duration: '4-5 min',
        focus: 'Blend Introduction',
        researchBasis: 'Lonigan et al. (2000) - Explicit, systematic blend instruction develops phonological awareness.',
        content: {
          goal: 'Introduce each R-blend (br, cr, dr, fr, gr, pr, tr) and demonstrate smooth blending.',
          whatToSay: [
            '"Now /r/ gets a partner — just like L-blends!"',
            '"First: /b/ + /r/ = /br/. Listen: /b/... /r/... /br/!"',
            '"That /r/ is STRONG! Your turn: /br/!"',
            '"Let\'s meet all the R-blend friends!"'
          ],
          whatToDo: [
            'Introduce each R-blend systematically.',
            'For EACH blend, follow this pattern:',
            '  1. Say first sound: "/b/"',
            '  2. Say second sound (strong!): "/r/"',
            '  3. Blend together: "/br/"',
            '  4. Child repeats',
            '  5. Give example word',
            '',
            'R-Blends to teach:',
            '  **BR:** /b/ + /r/ = /br/ → bring',
            '  **CR:** /c/ + /r/ = /cr/ → crab',
            '  **DR:** /d/ + /r/ = /dr/ → drum',
            '  **FR:** /f/ + /r/ = /fr/ → frog',
            '  **GR:** /g/ + /r/ = /gr/ → grab',
            '  **PR:** /p/ + /r/ = /pr/ → pretzel',
            '  **TR:** /t/ + /r/ = /tr/ → truck',
            '',
            'Use hand motions:',
            '  - Two fingers for TWO sounds',
            '  - Bring together as sounds blend',
            '  - Rolling motion for /r/ emphasis',
            'Write each blend as you teach it.',
            'Child practices each blend 2-3 times.',
            'Emphasize: "Two sounds working together — with STRONG /r/!"'
          ],
          examples: [
            'You: "/f/... /rrr/... /fr/!" Child: "/fr/!" You: "Yes! Frog! /fr/-og!" Child: "Frog!"'
          ],
          watchFor: [
            'Child blending both consonants smoothly.',
            'Strong, audible /r/ sound in blend.',
            'No extra vowel between sounds.',
            'Ability to repeat each blend.'
          ],
          parentTip: 'If /r/ gets lost, isolate it: "Stop after the blend: /br/... /br/... Now add the rest: /br/-ing!"'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: R-Blend Chant',
        duration: '3-4 min',
        focus: 'Rhythmic Practice',
        researchBasis: 'Trainor et al. (2002) - Rhythmic practice enhances phonological memory and retrieval.',
        content: {
          goal: 'Practice all R-blends in a rhythmic chant, building automaticity and memory.',
          whatToSay: [
            '"Let\'s learn the R-Blend Chant!"',
            '"We\'ll say each blend with a clap!"',
            '"Listen first, then join me!"',
            '"BR, CR, DR... FR, GR, PR, TR!"'
          ],
          whatToDo: [
            'Teach this rhythmic chant (clap on each blend):',
            '',
            '**R-Blend Chant:**',
            'BR! (clap) - bring me bread!',
            'CR! (clap) - crunchy crab!',
            'DR! (clap) - bang the drum!',
            'FR! (clap) - funny frog!',
            'GR! (clap) - grab and grin!',
            'PR! (clap) - pretty prize!',
            'TR! (clap) - truck goes trip!',
            'R-blends everywhere! (clap-clap-clap)',
            '',
            'Practice process:',
            '  1. You say line, child repeats',
            '  2. Add clapping',
            '  3. Go through all 7 blends',
            '  4. Repeat entire chant together',
            '  5. Speed up gradually',
            'Make it energetic and fun!',
            'Add movements:',
            '  - March in place',
            '  - Stomp on beats',
            '  - Hand gestures for words',
            'Goal: Memorize the chant by end of activity.',
            'Can be used as daily warm-up all week!'
          ],
          examples: [
            'Both: "BR! (clap) - bring me bread! CR! (clap) - crunchy crab!" (continue through all blends)'
          ],
          watchFor: [
            'Child joining in the chant.',
            'Clear production of each blend.',
            'Rhythm and timing.',
            'Beginning to memorize sequence.'
          ],
          parentTip: 'Chants = memory magic! Rhythm helps children remember the blends automatically.'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Compare L-Blends and R-Blends',
        duration: '2-3 min',
        focus: 'Blend Discrimination',
        researchBasis: 'Anthony & Francis (2005) - Comparing similar phoneme patterns strengthens discrimination skills.',
        content: {
          goal: 'Help children distinguish between L-blends and R-blends, preventing confusion.',
          whatToSay: [
            '"Let\'s think about L-blends and R-blends!"',
            '"What\'s the same? What\'s different?"',
            '"Listen: BL vs. BR. Can you hear the difference?"',
            '"Let\'s practice hearing which is which!"'
          ],
          whatToDo: [
            'Compare similar L and R blends:',
            '  - **BL** vs **BR**: "blue" vs "bring"',
            '  - **CL** vs **CR**: "clap" vs "crab"',
            '  - **FL** vs **FR**: "flag" vs "frog"',
            '  - **GL** vs **GR**: "glad" vs "grab"',
            '  - **PL** vs **PR**: "plan" vs "pretzel"',
            '',
            'For each pair:',
            '  1. Say both blends: "BL... BR"',
            '  2. Say example words: "blue... bring"',
            '  3. Ask: "Do they sound the same?"',
            '  4. Point out: "L is light, R is rolling!"',
            '',
            'Discrimination practice:',
            '  - You say word: "flag"',
            '  - Child identifies: "FL!" or "L-blend!"',
            '  - You say word: "frog"',
            '  - Child identifies: "FR!" or "R-blend!"',
            '',
            'Practice with 6-8 words:',
            '  - clip (CL), crab (CR)',
            '  - glad (GL), grab (GR)',
            '  - blob (BL), bring (BR)',
            'Celebrate accurate identification!'
          ],
          examples: [
            'You: "Listen: glad... grab. Same or different?" Child: "Different!" You: "Yes! GL vs. GR!"'
          ],
          watchFor: [
            'Hearing difference between L and R blends.',
            'Accurate identification.',
            'No confusion between similar blends.',
            'Understanding of the distinction.'
          ],
          parentTip: 'If they confuse them, emphasize: "L is smooth and light. R is strong and rolling!"'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎯 You met the R-Blend team!',
            'You practiced:',
            '✓ Feeling and saying the strong /r/ sound',
            '✓ Building all 7 R-blends (br, cr, dr, fr, gr, pr, tr)',
            '✓ Learning the R-Blend Chant',
            '✓ Comparing L-blends and R-blends'
          ],
          reviewQuestions: [
            'Can your child say each R-blend clearly?',
            'Is the /r/ sound strong and audible?',
            'Did they learn the R-Blend Chant?',
            'Can they distinguish L-blends from R-blends?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'R-blends expand children\'s decoding abilities significantly. The /r/ sound is one of the last sounds children typically master, so R-blend work strengthens articulation as well as phonics. By distinguishing R-blends from L-blends, children develop more sophisticated phonemic awareness — they\'re not just hearing sounds, they\'re analyzing and comparing them. This metacognitive skill is essential for advanced reading and spelling.'
          },
          nextSteps: [
            'Practice the R-Blend Chant daily as a warm-up!',
            'Hunt for R-blend words in books and around the house.',
            'Compare every new word: "Is that an L-blend or R-blend?"',
            'Tomorrow: We\'ll practice reading complete R-blend words!'
          ]
        }
      }
    ]
  }
],

[
  // ========= UPDATED LESSON 27 =========
  {
    id: 27,
    phase: '4',
    quarter: 1,
    week: 6,
    weekIndex: 5,
    dayInWeek: 2,
    title: 'Lesson 27: Blend Chop vs. Smooth + Explain Why',
    theme: 'Beginning Consonant Blends (R-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'blending', 'fluency-building', 'metacognition'],
    relatedToPreviousSkills: ['consonant-blends', 'blending', 'phoneme-awareness'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Blend Chop vs. Smooth + Explain Why!',
        duration: '1 min',
        content: {
          goal: 'Practice blending R-blend words both choppily and smoothly, then EXPLAIN to parent WHY smooth reading is better.',
          parentGuide: [
            'This lesson builds on Week 1\'s "Tap & Blend" but adds R-blends AND metacognition.',
            'NEW: The "explain why" component requires higher-order thinking.',
            'Children must articulate their understanding — not just DO it, but EXPLAIN it.',
            'This builds reading awareness and self-monitoring skills.'
          ],
          materials: ['Paper and pencil', 'Markers or crayons', 'Optional: timer for fluency practice'],
          tip: 'Push for explanations! "Why is smooth better?" forces them to think ABOUT reading, not just read.',
          researchNote: 'National Reading Panel (2000) - Guided repeated oral reading improves fluency, word recognition, and comprehension. Metacognitive awareness (thinking about thinking) accelerates learning (Flavell, 1979).'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: What is Choppy vs. Smooth?',
        duration: '2-3 min',
        focus: 'Fluency Awareness + Comparison',
        researchBasis: 'Ehri (2020) - Metacognitive awareness of reading processes supports self-monitoring and improvement.',
        content: {
          goal: 'Review choppy vs. smooth reading, BUT compare to earlier learning with CVC words.',
          whatToSay: [
            '"Remember when we learned choppy vs. smooth reading back in Week 1?"',
            '"That was with SHORT words like CAT!"',
            '"Now we have LONGER words with R-blends!"',
            '"Let\'s see if choppy and smooth feel different with harder words!"'
          ],
          whatToDo: [
            'RECALL previous learning:',
            '  "Remember \'Tap & Blend\' from Week 1?"',
            '  "We tapped out C-A-T with our fingers!"',
            '  "That was 3 sounds!"',
            '',
            'CONTRAST with current learning:',
            '  "Now look at this word: CRAB"',
            '  "How many sounds? Count with me!"',
            '  Together: "/c/ /r/ /a/ /b/ — FOUR sounds!"',
            '  "That\'s HARDER than CAT!"',
            '',
            'Demonstrate choppy reading:',
            '  - Pause between each phoneme',
            '  - Robot-like, mechanical voice',
            '  - Example: "/d/... /r/... /u/... /m/" = drum',
            'Demonstrate smooth reading:',
            '  - Sounds flow together',
            '  - Natural, conversational voice',
            '  - Example: "drum"',
            '',
            'Compare with several words:',
            '  **Choppy vs Smooth:**',
            '  - "/f/... /r/... /o/... /g/" vs "frog"',
            '  - "/c/... /r/... /a/... /b/" vs "crab"',
            '  - "/t/... /r/... /a/... /p/" vs "trap"',
            '',
            'NEW: Ask comparison questions:',
            '  "Is CRAB harder to read choppy than CAT was?"',
            '  "Why do you think R-blend words need smooth reading MORE?"',
            '  "What happens if you pause too long in CRAB?"',
            '',
            'Make it playful: "Let\'s practice BOTH ways today — then YOU\'LL teach ME which is better and WHY!"'
          ],
          examples: [
            'You: "Which is harder — choppy CAT or choppy CRAB?" Child: "CRAB!" You: "Why?" Child: "More sounds!" You: "Exactly!"'
          ],
          watchFor: [
            'Understanding the difference between choppy and smooth.',
            'Recognition that smooth = fluent.',
            'NEW: Comparing current task to previous learning (Week 1).',
            'NEW: Beginning to articulate WHY smooth is better.',
            'Readiness to practice both.'
          ],
          parentTip: 'The comparison to Week 1 shows them their OWN progress — they\'re tackling harder words now!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Chop It First',
        duration: '3-4 min',
        focus: 'Sound-by-Sound Decoding',
        researchBasis: 'Lonigan et al. (2000) - Explicit phoneme segmentation practice strengthens decoding accuracy.',
        content: {
          goal: 'Practice reading R-blend words in a slow, choppy manner to ensure accuracy.',
          whatToSay: [
            '"First, let\'s read words CHOPPY — sound by sound!"',
            '"This helps us make sure we get every sound right!"',
            '"Ready? First word: DRUM. Let\'s chop it!"',
            '"/d/... /r/... /u/... /m/. Drum!"'
          ],
          whatToDo: [
            'Write 8-10 R-blend words clearly.',
            'Child reads each word CHOPPY first.',
            'Words to practice:',
            '  - **brag:** /b/ /r/ /a/ /g/',
            '  - **crib:** /c/ /r/ /i/ /b/',
            '  - **drag:** /d/ /r/ /a/ /g/',
            '  - **frog:** /f/ /r/ /o/ /g/',
            '  - **grin:** /g/ /r/ /i/ /n/',
            '  - **drop:** /d/ /r/ /o/ /p/',
            '  - **trim:** /t/ /r/ /i/ /m/',
            '  - **trip:** /t/ /r/ /i/ /p/',
            '  - **crab:** /c/ /r/ /a/ /b/',
            '  - **grab:** /g/ /r/ /a/ /b/',
            '',
            'For EACH word:',
            '  1. Point to each letter',
            '  2. Say each sound with pause',
            '  3. Emphasize the R-BLEND: "/b/... /r/"',
            '  4. Complete all sounds',
            '  5. Then say whole word: "brag"',
            'Celebrate accuracy: "You got every sound!"',
            'Don\'t worry about speed — accuracy is the goal.',
            'If they rush, slow them down: "Chop it — sound by sound!"'
          ],
          examples: [
            'Child: "/f/... /r/... /o/... /g/... frog!" You: "Perfect chopping! Every sound was right!"'
          ],
          watchFor: [
            'Accurate identification of all phonemes.',
            'Strong /r/ in the blend.',
            'Systematic left-to-right processing.',
            'Successful blending after segmenting.'
          ],
          parentTip: 'Choppy reading is NOT bad! It\'s the foundation for smooth reading. Accuracy first, speed second!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Now Blend It Smooth',
        duration: '3-4 min',
        focus: 'Fluent Reading',
        researchBasis: 'National Reading Panel (2000) - Repeated reading of the same text improves fluency and automaticity.',
        content: {
          goal: 'Reread the same words smoothly, building fluency through immediate repetition.',
          whatToSay: [
            '"Great choppy reading! Now let\'s make it SMOOTH!"',
            '"Read the same words again, but this time no pauses!"',
            '"The sounds should flow together like music!"',
            '"Ready for smooth reading?"'
          ],
          whatToDo: [
            'Use the SAME word list from Step 2.',
            'Child reads each word SMOOTHLY now.',
            'Process for each word:',
            '  1. Child looks at word',
            '  2. Child reads WITHOUT segmenting',
            '  3. Sounds flow together: "brag"',
            '  4. You celebrate: "Smooth!"',
            '',
            'Compare reading:',
            '  - First time (choppy): slow, careful',
            '  - Second time (smooth): faster, flowing',
            '',
            'Notice improvement together:',
            '  "That was MUCH smoother than the first time!"',
            '  "You\'re getting faster AND accurate!"',
            '',
            'If child struggles with smooth reading:',
            '  - Let them chop it first',
            '  - Then try smooth',
            '  - Some words may need more practice',
            '',
            'Optional: Time the smooth reading.',
            '  - "Let\'s see how fast you can read all 10 words!"',
            '  - First attempt = baseline',
            '  - Try again to "beat the record"',
            '',
            'Celebrate fluency gains!'
          ],
          examples: [
            'Child: "frog" (smoothly, no pauses). You: "Beautiful! That was SO smooth compared to the first time!"'
          ],
          watchFor: [
            'Smoother, faster reading on second pass.',
            'Maintained accuracy.',
            'Confidence growing.',
            'Natural-sounding reading.'
          ],
          parentTip: 'If smooth reading is hard, that\'s okay! Some children need many repetitions. Keep it positive and celebrate progress!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: YOU Explain Why (NEW!)',
        duration: '3-4 min',
        focus: 'Metacognitive Explanation',
        researchBasis: 'Flavell (1979) - Metacognition (thinking about thinking) is critical for reading development. Vygotsky (1978) - Verbal explanation consolidates learning.',
        content: {
          goal: 'Child explains to parent WHY smooth reading is better than choppy reading.',
          whatToSay: [
            '"You just read both ways — choppy AND smooth!"',
            '"Now YOU are the teacher!"',
            '"Explain to ME: Why is smooth reading better?"',
            '"Teach me like I\'m learning for the first time!"'
          ],
          whatToDo: [
            'Child becomes the explainer/teacher.',
            '',
            'Prompt questions to guide explanation:',
            '',
            '  **Question 1: Speed**',
            '    You: "Which way was faster — choppy or smooth?"',
            '    Child: "Smooth!"',
            '    You: "Why does faster help us read better?"',
            '    Guide them to: "Faster means we understand the story better!"',
            '',
            '  **Question 2: Sound**',
            '    You: "Which way sounded more like talking — choppy or smooth?"',
            '    Child: "Smooth!"',
            '    You: "Why is that important?"',
            '    Guide them to: "Real reading sounds like talking!"',
            '',
            '  **Question 3: Understanding**',
            '    You: "Listen to this: /t/... /r/... /i/... /p/. What word is that?"',
            '    Child: "Trip!"',
            '    You: "Now listen: trip. Which way helped you understand faster?"',
            '    Child: "The smooth way!"',
            '    You: "Explain why!"',
            '    Guide them to: "Choppy makes you forget the beginning by the time you get to the end!"',
            '',
            '  **Question 4: Hard Words**',
            '    You: "Are R-blend words HARDER than CAT?"',
            '    Child: "Yes!"',
            '    You: "So do we need smooth reading even MORE with hard words?"',
            '    Child: "Yes!"',
            '    You: "Why?"',
            '    Guide them to: "More sounds to remember — smooth keeps them together!"',
            '',
            'Encourage full explanations:',
            '  Don\'t accept one-word answers',
            '  Push for "because" statements:',
            '    "Smooth is better BECAUSE..."',
            '    "Choppy is hard BECAUSE..."',
            '',
            'Role reversal exercise:',
            '  You: "Pretend I\'m your student. I read choppy."',
            '  You read: "/c/... /r/... /a/... /b/"',
            '  You: "Should I read like that?"',
            '  Child: "No!"',
            '  You: "Why not? Teach me!"',
            '  Child explains and demonstrates smooth reading',
            '',
            'Celebrate their explanation:',
            '  "You explained that SO clearly!"',
            '  "You understand WHY smooth reading works!"',
            '  "You\'re not just a good reader — you understand HOW reading works!"',
            '',
            'Summary statement together:',
            '  "Smooth reading is better because..."',
            '  Child completes: "...it\'s faster, sounds like talking, and helps us understand!"'
          ],
          examples: [
            'You: "Why is smooth better?" Child: "Because you can understand the word faster!" You: "GREAT explanation! You\'re teaching me!"'
          ],
          watchFor: [
            'NEW: Child articulating reasons clearly.',
            'NEW: Using "because" statements.',
            'NEW: Making comparisons (choppy vs. smooth).',
            'NEW: Demonstrating to illustrate explanation.',
            'Confidence in teacher role.',
            'Deep understanding, not just surface knowledge.'
          ],
          parentTip: 'EXPLANATION is harder than DOING! This is advanced cognitive work. If they struggle, model the explanation first, then have them repeat in their own words.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🏁 You mastered choppy AND smooth reading!',
            'You practiced:',
            '✓ Understanding choppy vs smooth reading',
            '✓ Reading R-blend words carefully (choppy)',
            '✓ Reading the same words fluently (smooth)',
            '✓ NEW: EXPLAINING why smooth reading is better!',
            '✓ NEW: Teaching parent about fluency!'
          ],
          reviewQuestions: [
            'Could your child read words accurately when choppy?',
            'Was their smooth reading faster and more fluent?',
            'Did they understand the difference between the two styles?',
            'NEW: Were they able to EXPLAIN why smooth is better?',
            'NEW: Did they use clear reasoning ("because" statements)?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'The journey from choppy to smooth reading is the essence of developing fluency. By explicitly practicing both styles AND explaining the difference, children develop metacognitive awareness — they\'re not just reading, they\'re thinking ABOUT reading. This self-awareness helps them monitor their own fluency and self-correct. The explanation component is NEW and significantly more challenging than Week 1\'s lesson. When children can articulate WHY smooth reading works, they\'ve moved from unconscious skill to conscious expertise. This deeper understanding accelerates their progress because they can now coach THEMSELVES toward fluency!'
          },
          nextSteps: [
            'Practice any new words both ways: choppy first, then smooth!',
            'Ask them to EXPLAIN other reading concepts: "Why do we sound out words?"',
            'Notice when your child naturally reads smoothly — celebrate it!',
            'Have them teach a sibling or family member about choppy vs. smooth!',
            'Tomorrow: Blend Match-Up — word building with R-blends!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 28 =========
  {
    id: 28,
    phase: '4',
    quarter: 1,
    week: 6,
    weekIndex: 5,
    dayInWeek: 3,
    title: 'Lesson 28: Blend Match-Up',
    theme: 'Beginning Consonant Blends (R-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'word-families', 'phoneme-manipulation'],
    relatedToPreviousSkills: ['consonant-blends', 'word-families', 'decoding'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Blend Match-Up!',
        duration: '1 min',
        content: {
          goal: 'Match R-blend beginnings with word endings (rimes) to create complete words and explore word families.',
          parentGuide: [
            'This lesson teaches word building using onset-rime patterns.',
            'Onset = beginning blend (br, cr, dr, etc.)',
            'Rime = ending chunk (-ab, -ag, -ip, etc.)',
            'This approach builds both decoding AND spelling skills.',
            'Word families help children recognize patterns and read more efficiently.'
          ],
          materials: ['Paper and scissors', 'Markers or crayons', 'Tape or glue (optional)'],
          tip: 'Make it hands-on! Moving physical pieces to create words makes the learning concrete.',
          researchNote: 'Ehri (2001) - Onset-rime awareness accelerates word reading and spelling development.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Make Blend Cards',
        duration: '3-4 min',
        focus: 'Material Preparation',
        researchBasis: 'National Reading Panel (2000) - Hands-on phonics materials support active learning and engagement.',
        content: {
          goal: 'Create cards with R-blends and ending chunks to use for word building.',
          whatToSay: [
            '"Today we\'re making a word-building game!"',
            '"We\'ll make cards with R-blends and ending chunks!"',
            '"Then we\'ll match them to make real words!"',
            '"Let\'s start by making our cards!"'
          ],
          whatToDo: [
            'Cut paper into cards (about 2x3 inches each).',
            'Make two sets:',
            '',
            '**Set 1: R-Blend Cards (Onsets)**',
            'Make one card for each blend:',
            '  - BR',
            '  - CR',
            '  - DR',
            '  - FR',
            '  - GR',
            '  - PR',
            '  - TR',
            '(7 cards total)',
            '',
            '**Set 2: Ending Cards (Rimes)**',
            'Make cards with common endings:',
            '  - -ab',
            '  - -ag',
            '  - -ap',
            '  - -ed',
            '  - -im',
            '  - -ip',
            '  - -og',
            '  - -op',
            '  - -ug',
            '(9 cards total)',
            '',
            'Write LARGE, clear letters.',
            'Optional: Use different colors for blends vs. endings.',
            'Child can help write or decorate.',
            'Keep blend cards in one pile, ending cards in another.',
            'Say: "Now we have our word-building kit!"'
          ],
          examples: [
            'You write: BR on one card, -ag on another. Child: "What will those make?" You: "Let\'s find out!"'
          ],
          watchFor: [
            'Child engagement in card creation.',
            'Clear, legible writing.',
            'Understanding that blends and endings are separate.',
            'Excitement about upcoming matching game.'
          ],
          parentTip: 'Laminate cards if possible — you\'ll use them again and again!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Test the Matches',
        duration: '5-6 min',
        focus: 'Word Building',
        researchBasis: 'Lonigan et al. (2000) - Active word construction develops phonological awareness and orthographic knowledge.',
        content: {
          goal: 'Experiment with matching blend cards to ending cards to create words, discovering which combinations are real words.',
          whatToSay: [
            '"Let\'s see what words we can make!"',
            '"Pick a blend card and an ending card!"',
            '"Put them together and read the word!"',
            '"Is it a real word or a silly word?"'
          ],
          whatToDo: [
            'Model the matching process:',
            '  1. Pick blend card: "BR"',
            '  2. Pick ending card: "-ag"',
            '  3. Place side-by-side: "BR" + "ag"',
            '  4. Read: "brag"',
            '  5. Ask: "Is brag a real word?"',
            '  6. Yes! Keep the match.',
            '',
            'Child explores combinations:',
            '  - Try different blend + ending pairs',
            '  - Read each combination',
            '  - Determine: real word or nonsense?',
            '  - Keep real words; set aside nonsense',
            '',
            'Real word examples:',
            '  - BR + ag = brag',
            '  - CR + ab = crab',
            '  - DR + ag = drag',
            '  - DR + op = drop',
            '  - FR + og = frog',
            '  - GR + ab = grab',
            '  - GR + in = grin',
            '  - PR + op = prop',
            '  - TR + ap = trap',
            '  - TR + im = trim',
            '  - TR + ip = trip',
            '',
            'Nonsense examples (still good practice!):',
            '  - BR + og = "brog" (not real)',
            '  - CR + og = "crog" (not real)',
            '',
            'Encourage experimentation!',
            '  "Try every combination! See what you discover!"',
            'Celebrate both real words AND recognizing nonsense.'
          ],
          examples: [
            'Child: "TR" + "ip" = "Trip! That\'s real!" You: "Yes! Trip is a word!" Child: "CR" + "op" = "Crop! Real?" You: "Yes!"'
          ],
          watchFor: [
            'Successful blending of onset + rime.',
            'Recognition of real vs. nonsense words.',
            'Willingness to experiment.',
            'Growing confidence with word building.'
          ],
          parentTip: 'Nonsense words prove they\'re DECODING, not memorizing! Celebrate when they correctly identify "not a real word!"'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Word Family Collections',
        duration: '3-4 min',
        focus: 'Pattern Recognition',
        researchBasis: 'Ehri (2020) - Recognizing word family patterns accelerates reading through analogy.',
        content: {
          goal: 'Group words by their ending patterns, discovering word families.',
          whatToSay: [
            '"Let\'s organize our words into families!"',
            '"All the words that end the same way are in the same family!"',
            '"Let\'s find all the -ag family words!"'
          ],
          whatToDo: [
            'Create word family groups.',
            'Process:',
            '  1. Choose one ending: "-ag"',
            '  2. Find all blends that make real words with it',
            '  3. Line them up: brag, drag',
            '  4. Read the family together',
            '  5. Notice the pattern: "They all end in -ag!"',
            '',
            'Word families to explore:',
            '  **-ab family:** crab, grab',
            '  **-ag family:** brag, drag',
            '  **-ap family:** trap',
            '  **-ed family:** (bred, Fred - names count!)',
            '  **-im family:** trim, prim',
            '  **-ip family:** drip, grip, trip',
            '  **-og family:** frog, grog (nonsense but fun!)',
            '  **-op family:** drop, crop, prop',
            '  **-ug family:** drug (careful with meaning!)',
            '',
            'For each family:',
            '  - Line up the words',
            '  - Read them in sequence',
            '  - Notice what stays same/what changes',
            '  - Child adds more if they can think of any',
            '',
            'Write families on paper to save.',
            'Point out: "Look how many -ip words we found!"'
          ],
          examples: [
            'You line up: TRIP, DRIP, GRIP. You: "What do you notice?" Child: "They all say -IP!" You: "Yes! The -IP family!"'
          ],
          watchFor: [
            'Recognition of shared ending patterns.',
            'Understanding word family concept.',
            'Ability to read words in family sequence.',
            'Suggesting additional family members.'
          ],
          parentTip: 'Word families are POWERFUL! Once kids know "trip," they can easily read "drip, grip, ship, clip!"'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Speed Match Challenge',
        duration: '2-3 min',
        focus: 'Fluency & Automaticity',
        researchBasis: 'National Reading Panel (2000) - Timed practice builds automatic word recognition.',
        content: {
          goal: 'Quickly match blends with endings to build words, developing automaticity.',
          whatToSay: [
            '"Let\'s play Speed Match!"',
            '"I\'ll call out a word, you build it FAST with your cards!"',
            '"Ready? Build: CRAB!"'
          ],
          whatToDo: [
            'Call out R-blend words.',
            'Child finds correct blend + ending cards.',
            'Child builds word as quickly as possible.',
            'Words to call out:',
            '  1. "Build: TRAP!"',
            '  2. "Build: FROG!"',
            '  3. "Build: GRAB!"',
            '  4. "Build: DROP!"',
            '  5. "Build: BRAG!"',
            '  6. "Build: TRIM!"',
            '  7. "Build: CRAB!"',
            '  8. "Build: TRIP!"',
            '',
            'Process:',
            '  - You call word',
            '  - Child segments: "TR-IP"',
            '  - Child finds TR card + IP card',
            '  - Child puts together',
            '  - Child reads: "TRIP!"',
            '  - You celebrate: "Fast matching!"',
            '',
            'Optional variations:',
            '  - Time them (friendly challenge)',
            '  - Increase speed each round',
            '  - Switch roles: child calls words, you build',
            '',
            'End with: "You\'re a speed-matching expert!"'
          ],
          examples: [
            'You: "Build FROG!" Child finds FR + OG cards, places together. Child: "Frog!" You: "So fast! Great matching!"'
          ],
          watchFor: [
            'Quick location of correct cards.',
            'Accurate word building.',
            'Speed increasing with practice.',
            'Confidence and enjoyment.'
          ],
          parentTip: 'Speed comes with practice! First time may be slow — that\'s perfect! Celebrate effort and improvement!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎯 You\'re a Blend Match-Up master!',
            'You practiced:',
            '✓ Making blend and ending cards',
            '✓ Matching blends with endings to build words',
            '✓ Organizing words into families',
            '✓ Speed matching words'
          ],
          reviewQuestions: [
            'Could your child match blends and endings to make words?',
            'Did they recognize real vs. nonsense words?',
            'Did they understand word family patterns?',
            'Were they able to build words quickly?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Onset-rime instruction (breaking words into beginning blends + ending chunks) is one of the most effective phonics approaches. It leverages natural language patterns and helps children decode by analogy: if they know "trip," they can read "drip" and "grip" immediately. This lesson builds flexible word-building skills that transfer to both reading AND spelling. The hands-on card manipulation makes abstract phonics concrete and memorable. Keep these cards — they\'re a powerful, reusable learning tool!'
          },
          nextSteps: [
            'Keep the cards and play Match-Up daily!',
            'Add new ending cards as you encounter new word families.',
            'Challenge: How many words can you make with one blend?',
            'Tomorrow: We\'ll trace blend paths to practice letter formation!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 29 =========
  {
    id: 29,
    phase: '4',
    quarter: 1,
    week: 6,
    weekIndex: 5,
    dayInWeek: 4,
    title: 'Lesson 29: Trace the Blend Path',
    theme: 'Beginning Consonant Blends (R-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'writing', 'letter-formation'],
    relatedToPreviousSkills: ['consonant-blends', 'letter-formation', 'fine-motor'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Trace the Blend Path!',
        duration: '1 min',
        content: {
          goal: 'Practice writing R-blend words while tracing letter paths, strengthening letter formation and spelling.',
          parentGuide: [
            'This lesson bridges reading to writing — essential for literacy development.',
            'Tracing helps children learn proper letter formation.',
            'Writing words reinforces orthographic memory (how words look).',
            'The "path" metaphor makes writing practice engaging and purposeful.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Ruler (optional for straight lines)', 'Eraser'],
          tip: 'Model proper letter formation! Start at the top, show correct strokes.',
          researchNote: 'Ehri (2001) - Writing words while reading them strengthens orthographic mapping and spelling development.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Model the Blend Path',
        duration: '2-3 min',
        focus: 'Letter Formation Demonstration',
        researchBasis: 'National Reading Panel (2000) - Explicit instruction in letter formation supports writing and reading development.',
        content: {
          goal: 'Demonstrate how to write R-blend words with proper letter formation and spacing.',
          whatToSay: [
            '"Today we\'re writing R-blend words!"',
            '"Each word is like a path we trace with our pencil!"',
            '"Watch me write the word FROG. I\'ll show you the path!"',
            '"First: F... R... O... G... FROG!"'
          ],
          whatToDo: [
            'Write the word FROG slowly on paper.',
            'Model proper letter formation:',
            '  **F:** Top to bottom line, then two horizontal lines',
            '  **R:** Vertical line, curve at top, diagonal leg',
            '  **O:** Circle/oval shape',
            '  **G:** Circle with tail going down',
            '',
            'Emphasize the BLEND letters:',
            '  "F and R work together — FR!"',
            '  "Write them close, not far apart!"',
            '',
            'Point out letter features:',
            '  - Start at the top',
            '  - Proper spacing between letters',
            '  - Letters sitting on an imaginary line',
            '  - Blend letters extra close',
            '',
            'Say sounds while writing:',
            '  "F... R... O... G... FROG!"',
            '',
            'Child watches carefully.',
            'Ask: "Ready to try your own blend path?"',
            'Prepare paper for child to practice.'
          ],
          examples: [
            'You write F-R-O-G slowly, saying each letter. You: "The path for FROG! Now you try!"'
          ],
          watchFor: [
            'Child observing letter formation carefully.',
            'Understanding proper starting points.',
            'Recognition of blend letter proximity.',
            'Readiness to attempt writing.'
          ],
          parentTip: 'Don\'t expect perfection! Focus on effort and proper starting points — neat writing develops over time.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Trace Dotted Paths',
        duration: '4-5 min',
        focus: 'Guided Writing Practice',
        researchBasis: 'Ehri (2020) - Tracing letters provides kinesthetic support for developing writing automaticity.',
        content: {
          goal: 'Trace dotted R-blend words, building muscle memory and letter formation skills.',
          whatToSay: [
            '"I made dotted paths for you to trace!"',
            '"Follow the dots carefully with your pencil!"',
            '"Say each letter as you trace it!"',
            '"Let\'s start with CRAB!"'
          ],
          whatToDo: [
            'Create dotted letter words for tracing:',
            '  Use dots or very light pencil lines',
            '  Make letters LARGE (1-2 inches tall)',
            '',
            'Words to trace (write in dotted form):',
            '  1. CRAB',
            '  2. DRUM',
            '  3. FROG',
            '  4. GRAB',
            '  5. TRIP',
            '  6. BRAG',
            '',
            'For EACH word:',
            '  1. Child traces dotted letters',
            '  2. Child says each letter name while tracing',
            '  3. Child reads completed word',
            '  4. Celebrate!',
            '',
            'Guidance while tracing:',
            '  - "Start at the top of the letter!"',
            '  - "Make the blend letters close together!"',
            '  - "Nice and slow — no rushing!"',
            '  - Gently correct if needed',
            '',
            'After tracing each word:',
            '  "Now read what you traced!"',
            '',
            'Praise effort and progress:',
            '  "Your letters are getting neater!"',
            '  "I can see you starting at the top!"'
          ],
          examples: [
            'Child traces dotted C-R-A-B. Child: "C... R... A... B... CRAB!" You: "Beautiful tracing!"'
          ],
          watchFor: [
            'Proper letter formation attempts.',
            'Following dotted paths accurately.',
            'Saying letter names while writing.',
            'Reading completed words successfully.'
          ],
          parentTip: 'Tracing is a scaffold! Some kids need lots of tracing; others are ready to write independently sooner. Follow their lead!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Write Your Own Paths',
        duration: '4-5 min',
        focus: 'Independent Writing',
        researchBasis: 'Lonigan et al. (2000) - Independent writing practice strengthens orthographic knowledge and spelling skills.',
        content: {
          goal: 'Write R-blend words independently without tracing, developing writing confidence.',
          whatToSay: [
            '"Now you\'re ready to write your OWN blend paths!"',
            '"I\'ll say a word, and you write it all by yourself!"',
            '"Ready for the first word? DRAG!"',
            '"Sound it out, then write it!"'
          ],
          whatToDo: [
            'Child writes R-blend words independently.',
            'Words to write (call out one at a time):',
            '  1. DRAG',
            '  2. TRIP',
            '  3. GRIN',
            '  4. DROP',
            '  5. TRIM',
            '  6. FROG (practiced earlier)',
            '',
            'Process for EACH word:',
            '  1. You say the word: "DRAG"',
            '  2. Child repeats word',
            '  3. Child segments: "/d/ /r/ /a/ /g/"',
            '  4. Child identifies letters: "D-R-A-G"',
            '  5. Child writes word',
            '  6. Child reads what they wrote',
            '  7. You check and celebrate!',
            '',
            'Support as needed:',
            '  - If stuck: "What sounds do you hear?"',
            '  - If misspelled: "Let\'s check the blend — do you hear TWO sounds?"',
            '  - If letter formation unclear: Gently model',
            '',
            'Celebrate efforts:',
            '  "You wrote that all by yourself!"',
            '  "Look at your blend letters — so close together!"',
            '  "You\'re becoming a writer!"',
            '',
            'Optional: Child chooses 1-2 extra words to write.'
          ],
          examples: [
            'You: "Write TRIP." Child writes: T-R-I-P. You: "Perfect! You wrote TRIP all by yourself!"'
          ],
          watchFor: [
            'Successful sound-to-letter mapping.',
            'Accurate spelling (especially blends).',
            'Improved letter formation.',
            'Growing confidence in writing.'
          ],
          parentTip: 'Inventive spelling is okay! If they write "DREG" for "DRAG," celebrate the DR blend, then show the correct vowel!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Rainbow Blend Paths',
        duration: '2-3 min',
        focus: 'Creative Reinforcement',
        researchBasis: 'Whitehurst & Lonigan (1998) - Multi-sensory, playful writing activities increase engagement and retention.',
        content: {
          goal: 'Rewrite favorite blend words in rainbow colors, making writing joyful and memorable.',
          whatToSay: [
            '"Let\'s make our blend words BEAUTIFUL!"',
            '"Choose your favorite R-blend word!"',
            '"We\'ll write it in RAINBOW colors!"',
            '"Pick your first color!"'
          ],
          whatToDo: [
            'Child chooses 2-3 favorite R-blend words.',
            'Popular choices: FROG, CRAB, DRUM, TRIP',
            '',
            'Rainbow writing process:',
            '  1. Child writes word in pencil first',
            '  2. Child traces over it in one color',
            '  3. Child traces again in another color',
            '  4. Repeat with 3-5 different colors',
            '  5. Creates a rainbow effect!',
            '',
            'Alternative: Write each LETTER in a different color',
            '  - F (red) R (orange) O (yellow) G (green)',
            '',
            'As they write:',
            '  - Say letter names',
            '  - Say the word when complete',
            '  - Emphasize the blend colors',
            '',
            'Display finished rainbow words proudly!',
            'Take a photo if desired.',
            '',
            'Optional additions:',
            '  - Draw a picture next to the word',
            '  - Add decorative borders',
            '  - Make it a rainbow blend poster',
            '',
            'Celebrate: "Your blend words are works of art!"'
          ],
          examples: [
            'Child writes FROG, traces in red, then blue, then green. Result: colorful rainbow FROG! You: "Beautiful!"'
          ],
          watchFor: [
            'Enthusiasm for creative writing.',
            'Multiple practice repetitions (each trace = practice!).',
            'Pride in finished product.',
            'Letter recognition while coloring.'
          ],
          parentTip: 'Rainbow writing = sneaky practice! They\'re writing the word 5+ times but it feels like art!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '✏️ You traced beautiful blend paths!',
            'You practiced:',
            '✓ Watching proper letter formation',
            '✓ Tracing dotted R-blend words',
            '✓ Writing R-blend words independently',
            '✓ Creating rainbow blend art'
          ],
          reviewQuestions: [
            'Could your child trace letters following the dots?',
            'Were they able to write words independently?',
            'Did they spell R-blends correctly?',
            'Did they enjoy the rainbow writing activity?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Writing is the flip side of reading — encoding vs. decoding. When children write words, they strengthen their orthographic memory (mental image of how words look), which directly improves reading. The physical act of forming letters creates muscle memory that supports automatic writing. Tracing provides the perfect scaffold: children experience correct letter formation with support before attempting it independently. Every word written is a word more deeply learned!'
          },
          nextSteps: [
            'Practice writing R-blend words daily — even just 2-3 words!',
            'Create a "Word Wall" with written R-blend words.',
            'Let your child write labels for household items with R-blends.',
            'Tomorrow: Final R-blend lesson with a storyboard activity!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 30 =========
  {
    id: 30,
    phase: '4',
    quarter: 1,
    week: 6,
    weekIndex: 5,
    dayInWeek: 5,
    title: 'Lesson 30: R-Blend Storyboard',
    theme: 'Beginning Consonant Blends (R-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'story-creation', 'sequencing'],
    relatedToPreviousSkills: ['consonant-blends', 'story-sequencing', 'comprehension'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to R-Blend Storyboard!',
        duration: '1 min',
        content: {
          goal: 'Create a 3-panel comic strip story using R-blend words, integrating reading, writing, and storytelling.',
          parentGuide: [
            'This is the culminating R-blend lesson — celebrating the week\'s learning!',
            'Storyboards combine visual art, writing, and narrative sequencing.',
            'Children use R-blend words in meaningful, creative contexts.',
            'This shows reading and writing as tools for self-expression.'
          ],
          materials: ['Paper (fold into 3 panels or draw 3 boxes)', 'Pencil and eraser', 'Crayons, markers, or colored pencils'],
          tip: 'Simple stick figures are perfect! Focus on the STORY and the R-blend words, not artistic detail.',
          researchNote: 'Whitehurst & Lonigan (1998) - Narrative creation activities strengthen language, literacy, and comprehension skills.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Choose Your R-Blend Words',
        duration: '2-3 min',
        focus: 'Story Planning',
        researchBasis: 'Scarborough (2001) - Pre-writing planning supports narrative coherence and language development.',
        content: {
          goal: 'Select 3-4 R-blend words to feature in the story, giving structure to the narrative.',
          whatToSay: [
            '"Today you\'re creating a comic strip story!"',
            '"First, let\'s pick R-blend words to use in your story!"',
            '"Choose 3 or 4 favorite R-blend words!"',
            '"Think about words that could go together in a story!"'
          ],
          whatToDo: [
            'Review R-blend words learned this week.',
            'Brainstorm story-friendly words:',
            '  **Characters:** crab, frog, crow (bird)',
            '  **Actions:** grab, trip, drop, grin, drag',
            '  **Objects:** drum, truck, trap, prize, crown',
            '  **Places:** creek (water), grass',
            '',
            'Child selects 3-4 words.',
            'Suggested combinations:',
            '  - frog, grab, grin (animal story)',
            '  - truck, trip, drop (adventure story)',
            '  - crab, drum, prize (silly story)',
            '  - frog, trip, grass (nature story)',
            '',
            'Write chosen words on separate paper.',
            'Discuss: "How could these words work in a story?"',
            'Brainstorm simple plot:',
            '  - Beginning: Introduce character',
            '  - Middle: Something happens',
            '  - End: Problem solved or funny conclusion',
            '',
            'Keep it SIMPLE — 3 panels, 3 sentences.',
            'Build excitement: "Let\'s create your story!"'
          ],
          examples: [
            'Child: "Frog, trip, grin!" You: "Great! Maybe: A frog takes a trip and something makes him grin?"'
          ],
          watchFor: [
            'Thoughtful word selection.',
            'Words that could logically connect.',
            'Beginning story thinking.',
            'Enthusiasm about creating.'
          ],
          parentTip: 'Let THEM choose words! Even if the combination seems random, creativity matters most!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Draw Your Storyboard',
        duration: '5-6 min',
        focus: 'Visual Narrative',
        researchBasis: 'Ehri (2001) - Visual representation supports narrative structure and comprehension.',
        content: {
          goal: 'Create a 3-panel comic strip with simple drawings showing the story sequence.',
          whatToSay: [
            '"Now let\'s draw your story in 3 boxes — like a comic!"',
            '"Box 1: The beginning. Box 2: The middle. Box 3: The end!"',
            '"Keep drawings simple — stick figures are perfect!"',
            '"Let\'s draw!"'
          ],
          whatToDo: [
            'Create storyboard template:',
            '  - Fold paper in thirds, OR',
            '  - Draw 3 large boxes in a row',
            '  - Label: 1, 2, 3 (or Beginning, Middle, End)',
            '',
            'Child draws story in sequence.',
            'Example story: "Frog\'s Trip"',
            '  **Panel 1 (Beginning):**',
            '  - Draw a frog',
            '  - Frog is near grass or creek',
            '  - Caption idea: "A frog went on a trip."',
            '',
            '  **Panel 2 (Middle):**',
            '  - Frog encounters something (rock, log)',
            '  - Frog trips!',
            '  - Caption idea: "The frog took a trip... and tripped!"',
            '',
            '  **Panel 3 (End):**',
            '  - Frog is okay and smiling (grin)',
            '  - Caption idea: "The frog just grinned!"',
            '',
            'Drawing guidance:',
            '  - Stick figures are GREAT',
            '  - Simple backgrounds (grass = lines)',
            '  - Focus on key story elements',
            '  - Include chosen R-blend word objects',
            '',
            'Encourage creativity:',
            '  "This is YOUR story — make it how you want!"',
            '',
            'No pressure for artistic perfection!',
            'Celebrate each panel completion.'
          ],
          examples: [
            'Panel 1: Stick figure frog. Panel 2: Frog falling. Panel 3: Frog smiling. You: "I can see the whole story!"'
          ],
          watchFor: [
            'Clear sequence (beginning, middle, end).',
            'Story makes sense visually.',
            'Inclusion of R-blend word concepts.',
            'Confidence in artistic expression.'
          ],
          parentTip: 'Don\'t draw FOR them! Guide verbally: "What should happen next?" Let them create independently!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Write Your Captions',
        duration: '4-5 min',
        focus: 'Story Writing',
        researchBasis: 'National Reading Panel (2000) - Writing practice reinforces reading skills and phonics knowledge.',
        content: {
          goal: 'Write simple captions for each panel, using R-blend words in sentences.',
          whatToSay: [
            '"Every comic needs words!"',
            '"Let\'s write what\'s happening in each box!"',
            '"Use your R-blend words in your sentences!"',
            '"What should the first sentence say?"'
          ],
          whatToDo: [
            'Write captions below or beside each panel.',
            'Process for each panel:',
            '  1. Child describes what\'s happening',
            '  2. Together, create a simple sentence',
            '  3. Child writes sentence (help as needed)',
            '  4. Include at least one R-blend word',
            '',
            'Example captions (using "Frog\'s Trip"):',
            '  **Panel 1:** "A frog went on a trip."',
            '  **Panel 2:** "The frog tripped on grass."',
            '  **Panel 3:** "The frog just grinned!"',
            '',
            'Writing support:',
            '  - Child writes R-blend words independently',
            '  - Parent helps with other words if needed',
            '  - Focus on R-blend spelling accuracy',
            '  - Accept inventive spelling for other words',
            '',
            'Keep sentences SHORT:',
            '  - 4-7 words per sentence',
            '  - Simple subject-verb structure',
            '  - One idea per sentence',
            '',
            'After writing each caption:',
            '  - Child reads it aloud',
            '  - Celebrate the writing!',
            '',
            'Final check: Are R-blend words spelled correctly?'
          ],
          examples: [
            'Panel 1: Child writes "A frog went on a TRIP." You: "Perfect! You spelled TRIP correctly!"'
          ],
          watchFor: [
            'Captions matching illustrations.',
            'Correct spelling of R-blend words.',
            'Sentence structure (capital, period).',
            'Pride in written story.'
          ],
          parentTip: 'They\'re combining reading, writing, spelling, AND storytelling — that\'s HUGE! Celebrate the whole package!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Share Your Storyboard',
        duration: '2-3 min',
        focus: 'Performance & Celebration',
        researchBasis: 'Whitehurst & Lonigan (1998) - Sharing literacy products builds confidence and reinforces learning.',
        content: {
          goal: 'Child presents their storyboard to an audience, reading captions and explaining illustrations.',
          whatToSay: [
            '"Your storyboard is complete!"',
            '"Now it\'s time to share your story!"',
            '"Read each panel and tell me what\'s happening!"',
            '"I\'ll be your audience!"'
          ],
          whatToDo: [
            'Set up story sharing:',
            '  - Child holds or displays storyboard',
            '  - Parent (and any family members) = audience',
            '  - Optional: child stands like a presenter',
            '',
            'Presentation process:',
            '  1. Child introduces story: "My story is called..."',
            '  2. Child shows Panel 1',
            '  3. Child reads caption',
            '  4. Child explains illustration if desired',
            '  5. Repeat for Panels 2 and 3',
            '  6. Audience applauds!',
            '',
            'Encouragement:',
            '  - "Use your best reading voice!"',
            '  - "Point to the pictures as you read!"',
            '  - "Tell us what you like best about your story!"',
            '',
            'After presentation:',
            '  - Applaud enthusiastically',
            '  - Ask 1-2 questions:',
            '    "Why did the frog grin?"',
            '    "What was your favorite part to draw?"',
            '  - Praise specific elements:',
            '    "I loved how you used TRIP and GRIN!"',
            '    "Your story had a beginning, middle, and end!"',
            '',
            'Display options:',
            '  - Hang on refrigerator or wall',
            '  - Take photo to share with family',
            '  - Add to portfolio or special folder',
            '  - Create a "Story Collection" book',
            '',
            'Celebrate completing R-Blend Week!'
          ],
          examples: [
            'Child: "Panel 1: A frog went on a trip. Panel 2: The frog tripped. Panel 3: The frog grinned!" Everyone: (applause!)'
          ],
          watchFor: [
            'Confident presentation.',
            'Clear reading of captions.',
            'Explanation of story.',
            'Pride in creation.'
          ],
          parentTip: 'Authentic audience = powerful motivation! If possible, have siblings, grandparents, or family members as audience!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '📖 You created an R-Blend storyboard!',
            'You practiced:',
            '✓ Choosing R-blend words for your story',
            '✓ Drawing a 3-panel comic sequence',
            '✓ Writing captions with R-blend words',
            '✓ Presenting your story to an audience',
            '✓ Completing R-Blend Week!'
          ],
          reviewQuestions: [
            'Did your child create a coherent 3-panel story?',
            'Did they include and correctly spell R-blend words?',
            'Were they able to read their story aloud?',
            'Did they show pride in their creation?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Storyboard creation is the ultimate integration activity — it combines reading, writing, spelling, art, storytelling, and presentation skills. When children create their own stories using target phonics skills (R-blends), they move from passive learning to active creation. This ownership transforms phonics from abstract rules into practical tools for self-expression. The storyboard becomes concrete evidence of their literacy growth and a treasured keepsake. This is what literacy is FOR — to tell our stories, share our ideas, and connect with others!'
          },
          nextSteps: [
            'Create more storyboards with other phonics patterns!',
            'Make a "Storyboard Collection" book.',
            'Let your child teach you how to make a storyboard.',
            'Next week: S-Blends (sc, sk, sl, sm, sn, sp, st, sw)!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 31 =========
  {
    id: 31,
    phase: '4',
    quarter: 1,
    week: 7,
    weekIndex: 6,
    dayInWeek: 1,
    title: 'Lesson 31: S-Blends Speed Round',
    theme: 'Beginning Consonant Blends (S-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'phonemic-awareness', 'speed-decoding'],
    relatedToPreviousSkills: ['consonant-blends', 'sound-blending', 'letter-recognition'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to S-Blends Speed Round!',
        duration: '1 min',
        content: {
          goal: 'Quickly identify and decode S-blend beginnings (sc, sk, sl, sm, sn, sp, st, sw), building automaticity.',
          parentGuide: [
            'S-blends are the most common consonant blends in English!',
            'Speed practice builds automaticity — reading without thinking.',
            'This lesson focuses on the BEGINNING sounds, not full words yet.'
          ],
          materials: ['Paper and pencil', 'Your voice and enthusiasm!'],
          tip: 'Keep it playful and fast-paced! The goal is recognition speed, not perfection.',
          researchNote: 'National Reading Panel (2000) - Automaticity in phonics patterns directly supports reading fluency.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Meet the S-Blend Family',
        duration: '2-3 min',
        focus: 'S-Blend Introduction',
        researchBasis: 'Ehri (2020) - Explicit introduction of phonics patterns with clear articulation supports decoding development.',
        content: {
          goal: 'Introduce the eight S-blends with clear pronunciation and mouth awareness.',
          whatToSay: [
            '"Today we\'re meeting the S-Blend Family!"',
            '"Every blend starts with the /s/ sound!"',
            '"Let\'s say them together!"'
          ],
          whatToDo: [
            'Introduce S-blends one at a time:',
            '**SC** (scare): "/s/ /k/ ... SC!"',
            '**SK** (skip): "/s/ /k/ ... SK!"',
            '**SL** (slide): "/s/ /l/ ... SL!"',
            '**SM** (smile): "/s/ /m/ ... SM!"',
            '**SN** (snail): "/s/ /n/ ... SN!"',
            '**SP** (spin): "/s/ /p/ ... SP!"',
            '**ST** (stop): "/s/ /t/ ... ST!"',
            '**SW** (swim): "/s/ /w/ ... SW!"',
            '',
            'Quick review chant together:',
            '  "SC, SK, SL, SM, SN, SP, ST, SW!"',
            '  Repeat 2-3 times, building speed.'
          ],
          examples: [
            'You: "Listen: /s/ /l/ ... SL!" Child repeats: "SL!" You: "Yes! Like SLIDE!"'
          ],
          watchFor: [
            'Clear /s/ sound at the beginning.',
            'Smooth transition to second letter.',
            'Growing confidence with repetition.'
          ],
          parentTip: 'The /s/ sound is consistent across all blends — this makes them easier to master!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Blend Flash Challenge',
        duration: '3-4 min',
        focus: 'Rapid Visual Recognition',
        researchBasis: 'Lonigan et al. (2000) - Repeated exposure to phonics patterns increases automatic recognition speed.',
        content: {
          goal: 'Rapidly identify S-blends from written letter pairs, building visual recognition speed.',
          whatToSay: [
            '"Time for a speed challenge!"',
            '"I\'ll show you letters — you tell me the blend!"',
            '"Ready? Let\'s GO!"'
          ],
          whatToDo: [
            'Write S-blends on paper in LARGE letters.',
            'Flash or point to blends one at a time:',
            '  SL, SP, ST, SM, SN, SK, SW, SC',
            '',
            '**Round 1: Slow Pace** (3-4 seconds each)',
            '**Round 2: Medium Pace** (2 seconds each)',
            '**Round 3: SPEED ROUND** (1 second each)',
            '',
            'Mix up the order between rounds!',
            '',
            'Optional timer challenge:',
            '  "Can you name all 8 blends in 20 seconds?"',
            '',
            'Celebrate: "You\'re getting SO fast!"'
          ],
          examples: [
            'You show: "ST" Child: "ST!" You: "Lightning fast! Next!"'
          ],
          watchFor: [
            'Increasing speed with each round.',
            'Accurate blend identification.',
            'Confidence growing.'
          ],
          parentTip: 'Speed builds automaticity! The faster they recognize blends, the easier reading becomes!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Blend Beginning Hunt',
        duration: '3-4 min',
        focus: 'Auditory Discrimination',
        researchBasis: 'Anthony & Francis (2005) - Phonological awareness activities strengthen sound-letter connections.',
        content: {
          goal: 'Listen to words and identify which S-blend they begin with.',
          whatToSay: [
            '"Now let\'s HEAR the blends!"',
            '"I\'ll say a word — you tell me which blend you hear!"',
            '"Ready? First word: SNAP!"'
          ],
          whatToDo: [
            'Say words clearly, one at a time.',
            'Child identifies the beginning S-blend.',
            '',
            'Words to use (random order):',
            '  **SL:** slide, slow, sleep, sled',
            '  **SM:** smile, small, smell',
            '  **SN:** snap, snail, snow, snack',
            '  **SP:** spin, spot, spell',
            '  **ST:** stop, step, stick, star',
            '  **SK:** skip, ski, skunk',
            '  **SW:** swim, swing, sweet',
            '  **SC:** scare, score, scoop',
            '',
            'Process for EACH word:',
            '  1. You say word: "SLIDE"',
            '  2. Child repeats word',
            '  3. Child identifies blend: "SL!"',
            '  4. You confirm: "Yes! SL-SLIDE!"',
            '',
            'Support if needed:',
            '  - Emphasize beginning: "SSSS-LIDE"',
            '  - Ask: "What two sounds do you hear first?"'
          ],
          examples: [
            'You: "SNAP!" Child: "SN!" You: "Perfect! SN-SNAP!"'
          ],
          watchFor: [
            'Accurate auditory identification.',
            'Ability to isolate beginning sounds.',
            'Speed of response increasing.'
          ],
          parentTip: 'Listening BEFORE reading is crucial! Ear training strengthens decoding skills!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Blend Speed Chant',
        duration: '2-3 min',
        focus: 'Rhythmic Reinforcement',
        researchBasis: 'Trainor et al. (2002) - Rhythmic language activities enhance phonological memory.',
        content: {
          goal: 'Practice S-blends through rhythmic chanting, making memorization fun.',
          whatToSay: [
            '"Let\'s make a blend song!"',
            '"Clap along if you want!"',
            '"Ready? Here we go!"'
          ],
          whatToDo: [
            'Create a rhythmic chant:',
            '',
            '**Simple Chant (clap along):**',
            '  "SC, SK, SL, SM!" (clap clap)',
            '  "SN, SP, ST, SW!" (clap clap)',
            '  "S-blends everywhere!" (clap clap clap)',
            '',
            'Repeat 3-4 times:',
            '  - First: YOU lead',
            '  - Second: TOGETHER',
            '  - Third: CHILD leads',
            '  - Fourth: FAST version!',
            '',
            'Add motions (optional):',
            '  - SC: spooky hands',
            '  - SK: jump/skip',
            '  - SL: sliding motion',
            '  - SM: big smile',
            '  - SN: sniff air',
            '  - SP: spinning',
            '  - ST: stop signal',
            '  - SW: swimming arms',
            '',
            'Make it playful:',
            '  - Whisper version',
            '  - Loud version',
            '  - Super-fast version'
          ],
          examples: [
            'You both chant: "SC, SK, SL, SM! SN, SP, ST, SW!" (clapping together)'
          ],
          watchFor: [
            'Remembering all eight blends.',
            'Participating in rhythm.',
            'Having fun!'
          ],
          parentTip: 'Rhythm + movement = powerful memory! They\'ll remember this chant long-term!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '⚡ You mastered the S-Blend Speed Round!',
            'You practiced:',
            '✓ Saying all eight S-blends clearly',
            '✓ Recognizing S-blends visually',
            '✓ Identifying S-blends in spoken words',
            '✓ Chanting S-blends with rhythm'
          ],
          reviewQuestions: [
            'Can your child name all eight S-blends?',
            'Did they improve speed across rounds?',
            'Could they identify blends in spoken words?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'S-blends are the most frequent consonant blends in English — they appear in thousands of common words! Speed recognition (automaticity) is crucial because it frees up mental energy for comprehension. When children can instantly recognize "ST" or "SL," they can focus on meaning rather than decoding mechanics. This lesson builds automatic recognition through multi-sensory practice: visual, auditory, and kinesthetic.'
          },
          nextSteps: [
            'Practice the S-blend chant throughout the day!',
            'Point out S-blend words during reading.',
            'Play "I Spy S-Blends" around the house.',
            'Tomorrow: Say & Sweep with S-blend words!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 32 =========
  {
    id: 32,
    phase: '4',
    quarter: 1,
    week: 7,
    weekIndex: 6,
    dayInWeek: 2,
    title: 'Lesson 32: Say & Sweep',
    theme: 'Beginning Consonant Blends (S-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'kinesthetic-learning', 'decoding'],
    relatedToPreviousSkills: ['consonant-blends', 'sound-blending', 'fine-motor'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Say & Sweep!',
        duration: '1 min',
        content: {
          goal: 'Decode S-blend words while sweeping hand across the page, connecting physical motion to smooth blending.',
          parentGuide: [
            'Physical motion reinforces smooth blending — not choppy sounds!',
            'The "sweep" represents continuous sound flow in reading.',
            'Kinesthetic learning strengthens neural pathways for decoding.'
          ],
          materials: ['Paper and pencil', 'Enthusiasm and a sweeping hand!'],
          tip: 'Model the smooth sweeping motion — it should match smooth sound blending!',
          researchNote: 'Ehri (2001) - Multi-sensory decoding practice accelerates reading development.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Model the Sweep',
        duration: '2-3 min',
        focus: 'Demonstration of Smooth Blending',
        researchBasis: 'National Reading Panel (2000) - Explicit modeling of blending strategies supports decoding development.',
        content: {
          goal: 'Demonstrate how to sweep hand smoothly across words while blending sounds together.',
          whatToSay: [
            '"Today we\'re learning to SWEEP as we read!"',
            '"Watch my hand move smoothly across the word!"',
            '"Let me show you with STOP!"'
          ],
          whatToDo: [
            'Write STOP in large letters.',
            '',
            '**Demonstrate sweep technique:**',
            '  1. Point to each letter: "/s/ /t/ /o/ /p/"',
            '  2. NOW THE SWEEP:',
            '     - Place finger at start of word',
            '     - Slowly sweep left-to-right',
            '     - While sweeping, blend smoothly:',
            '       "/ssstooop/... STOP!"',
            '',
            'Emphasize difference:',
            '  **Choppy (wrong):** Tap each letter separately',
            '  **Smooth (correct):** Sweep continuously',
            '',
            'Key points:',
            '  - "The sweep is SMOOTH, not jumpy!"',
            '  - "Your hand moves like sounds connect!"',
            '',
            'Demonstrate with 2 more words:',
            '  - SWIM: "/ssswwwiiimmm/... SWIM!"',
            '  - SLIP: "/sssllliiip/... SLIP!"',
            '',
            'Ask: "Ready to try your own sweep?"'
          ],
          examples: [
            'You sweep across STOP: "/ssstooop/... STOP!" You: "See how smooth?"'
          ],
          watchFor: [
            'Child observing smooth motion.',
            'Understanding motion-sound connection.',
            'Readiness to attempt.'
          ],
          parentTip: 'The sweep is a scaffold — it physically guides smooth blending! Eventually they won\'t need it!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Guided Sweep Practice',
        duration: '4-5 min',
        focus: 'Child Attempts with Support',
        researchBasis: 'Lonigan et al. (2000) - Scaffolded practice with immediate feedback accelerates phonics mastery.',
        content: {
          goal: 'Child practices sweeping across S-blend words with parent guidance.',
          whatToSay: [
            '"Now YOU try the sweep!"',
            '"Remember: smooth motion, smooth sounds!"',
            '"First word: SNAP!"'
          ],
          whatToDo: [
            'Write S-blend words in large letters:',
            '  1. SNAP',
            '  2. SLIP',
            '  3. SPIN',
            '  4. STEP',
            '  5. SWIM',
            '  6. SKIP',
            '  7. STOP',
            '  8. SMELL',
            '',
            'For EACH word:',
            '  **Phase 1:** Point to each letter, say sounds',
            '  **Phase 2:** Sweep & blend while moving hand',
            '  **Phase 3:** Say complete word',
            '',
            'Provide guidance:',
            '  - Too fast: "Slower — match your sounds!"',
            '  - Choppy: "Smooth, like sliding!"',
            '  - Struggling: Sweep WITH them hand-over-hand',
            '',
            'Progressive release:',
            '  - Words 1-3: Guide closely',
            '  - Words 4-6: More independent',
            '  - Words 7-8: Fully independent',
            '',
            'Celebrate each successful sweep!'
          ],
          examples: [
            'Child sweeps SLIP: "/sssllliiip/... SLIP!" You: "Perfect smooth sweep!"'
          ],
          watchFor: [
            'Smooth, continuous sweeping motion.',
            'Sounds blending (not choppy).',
            'Accurate word reading.',
            'Growing independence.'
          ],
          parentTip: 'If struggling, DO the sweep with them hand-over-hand! Physical motion teaches the brain!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Sweep & Draw',
        duration: '3-4 min',
        focus: 'Creative Integration',
        researchBasis: 'Whitehurst & Lonigan (1998) - Integrating art with literacy practice increases engagement.',
        content: {
          goal: 'Child sweeps to decode words, then draws pictures, connecting reading to meaning.',
          whatToSay: [
            '"Let\'s make our words come to life!"',
            '"Sweep to read, then draw a picture!"',
            '"First word: STAR!"'
          ],
          whatToDo: [
            'Write 3-4 words with space for drawing:',
            '  1. STAR',
            '  2. SNAIL',
            '  3. SMILE',
            '  4. SWIM',
            '',
            'For EACH word:',
            '  **Step 1:** Sweep & decode',
            '  **Step 2:** Discuss meaning',
            '  **Step 3:** Draw simple picture',
            '  **Step 4:** Sweep & re-read',
            '',
            'Keep drawings SIMPLE:',
            '  - STAR: 5-point star',
            '  - SNAIL: spiral shell',
            '  - SMILE: smiley face',
            '  - SWIM: stick figure in water',
            '',
            'After all words:',
            '  - Review by sweeping each again',
            '  - Child explains drawings',
            '  - Celebrate!'
          ],
          examples: [
            'Child sweeps: "STAR!" Draws star. You: "Beautiful! Sweep and read again!"'
          ],
          watchFor: [
            'Accurate decoding with sweep.',
            'Connection between word and meaning.',
            'Willingness to re-read.'
          ],
          parentTip: 'Drawing = comprehension check! If they draw correctly, they understood!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Air Sweep Challenge',
        duration: '2-3 min',
        focus: 'Physical Reinforcement',
        researchBasis: 'Trainor et al. (2002) - Movement-based learning strengthens memory and automaticity.',
        content: {
          goal: 'Practice sweeping and blending WITHOUT written words — using air writing.',
          whatToSay: [
            '"Let\'s try sweeping in the AIR!"',
            '"I\'ll say a word — you sweep and spell it in the air!"',
            '"First word: STOP!"'
          ],
          whatToDo: [
            'NO materials needed — just voices and hands!',
            '',
            'Process:',
            '  1. You say S-blend word: "STOP"',
            '  2. Child repeats word',
            '  3. Child "air writes" while sweeping hand',
            '  4. Child says letters: "S-T-O-P!"',
            '  5. Child sweeps while blending: "STOP!"',
            '',
            'Words: STOP, SPIN, SLIP, SNAP, SWIM, STEP, SKIP, SMELL',
            '',
            'Make it dramatic:',
            '  - BIG sweeping motions',
            '  - Use whole arm',
            '',
            'Variations:',
            '  - Fast version',
            '  - Slow-motion version',
            '  - Tiny finger sweeps',
            '  - Giant body movements',
            '',
            'Final challenge:',
            '  "Can you do three words in a row?"'
          ],
          examples: [
            'You: "SWIM!" Child sweeps in air: "S-W-I-M... SWIM!" You: "Perfect!"'
          ],
          watchFor: [
            'Confident sweeping motions.',
            'Accurate letter sequencing.',
            'Smooth blending while moving.',
            'Enthusiasm!'
          ],
          parentTip: 'Air sweeping = no materials! Perfect for car rides, waiting rooms, anywhere!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '👋 You mastered Say & Sweep!',
            'You practiced:',
            '✓ Watching smooth sweeping demonstration',
            '✓ Sweeping across S-blend words',
            '✓ Drawing pictures for decoded words',
            '✓ Air sweeping with no written words'
          ],
          reviewQuestions: [
            'Did your child perform smooth sweeping motions?',
            'Were they able to blend sounds while sweeping?',
            'Could they air sweep confidently?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'The sweeping motion creates a physical representation of smooth sound blending. When children "feel" the continuous motion, their brains better understand that sounds flow together, not stay separated. This kinesthetic reinforcement creates muscle memory that supports automatic blending. The motion also slows down impulsive readers who guess. Eventually, the physical sweep becomes internalized as mental blending. But during learning, it\'s invaluable scaffolding!'
          },
          nextSteps: [
            'Practice sweeping during all reading time!',
            'Use air sweeps during car rides.',
            'Point out natural sweeping (celebrate it!).',
            'Tomorrow: Sorting S-blends by sound patterns!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 33 =========
  {
    id: 33,
    phase: '4',
    quarter: 1,
    week: 7,
    weekIndex: 6,
    dayInWeek: 3,
    title: 'Lesson 33: Sound Sorting S-Blends',
    theme: 'Beginning Consonant Blends (S-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'phonemic-awareness', 'categorization'],
    relatedToPreviousSkills: ['consonant-blends', 'sound-sorting', 'phonological-awareness'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Sound Sorting S-Blends!',
        duration: '1 min',
        content: {
          goal: 'Sort S-blend words by their beginning blend, strengthening phonemic discrimination and pattern recognition.',
          parentGuide: [
            'Sorting helps children see patterns and differences between similar sounds.',
            'Categorization is a critical thinking skill that supports reading.',
            'This activity reinforces auditory and visual discrimination.'
          ],
          materials: ['Paper', 'Pencil or crayons', 'Small objects to use as markers (optional)'],
          tip: 'Make it a game! Sorting is more fun than drilling.',
          researchNote: 'Anthony & Francis (2005) - Phonological awareness activities that involve categorization strengthen metalinguistic understanding.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Create Sorting Spaces',
        duration: '2 min',
        focus: 'Preparation and Setup',
        researchBasis: 'Lonigan et al. (2000) - Structured organization of phonological information supports learning.',
        content: {
          goal: 'Set up visual sorting spaces for different S-blends.',
          whatToSay: [
            '"Today we\'re sorting S-blend words!"',
            '"Let\'s make spaces for different blends!"',
            '"Each blend gets its own spot!"'
          ],
          whatToDo: [
            'Divide paper into 4-6 sections (boxes or columns).',
            '',
            'Label each section with an S-blend:',
            '  Start with 4 blends for younger/newer learners:',
            '    **SL  |  SP  |  ST  |  SM**',
            '',
            '  Or use 6 blends for more advanced:',
            '    **SL  |  SP  |  ST  |  SM  |  SN  |  SW**',
            '',
            'Write blend labels LARGE and clear at top of each space.',
            '',
            'Optional: Draw a picture hint under each:',
            '  - SL: sliding board',
            '  - SP: spinning top',
            '  - ST: stop sign',
            '  - SM: smiley face',
            '  - SN: snail',
            '  - SW: swimmer',
            '',
            'Show child the sorting board:',
            '  "This is our S-blend sorting board!"',
            '  Point to each section.',
            '  Say each blend together.',
            '',
            'Explain the task:',
            '  "I\'ll say words. You tell me which space they go in!"',
            '  "We\'ll sort them by their beginning blend!"'
          ],
          examples: [
            'You point to SL space: "This is for SL words like SLIDE!" Child: "SL!"'
          ],
          watchFor: [
            'Understanding of sorting concept.',
            'Recognition of blend labels.',
            'Readiness to begin sorting.'
          ],
          parentTip: 'Visual organization helps children see patterns! The board itself is a learning tool.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Auditory Sorting Game',
        duration: '4-5 min',
        focus: 'Listening and Categorizing',
        researchBasis: 'National Reading Panel (2000) - Phoneme categorization is a key phonological awareness skill.',
        content: {
          goal: 'Listen to words and sort them into correct S-blend categories.',
          whatToSay: [
            '"Listen carefully to each word!"',
            '"Tell me which blend you hear!"',
            '"Point to the right space!"',
            '"First word: SLIP!"'
          ],
          whatToDo: [
            'Say S-blend words one at a time (randomly mixed).',
            'Child identifies blend and points to correct space.',
            '',
            'Words for sorting (mix these up):',
            '',
            '**SL words:** slip, slow, sleep, sled, slam',
            '**SP words:** spot, spin, spell, spoon, spark',
            '**ST words:** stop, step, stick, star, stem',
            '**SM words:** smile, small, smell, smoke, smash',
            '**SN words:** snap, snail, snow, snack, snug',
            '**SW words:** swim, swing, sweet, swan, swept',
            '',
            'Process for EACH word:',
            '  1. You say word clearly: "SLIP"',
            '  2. Child repeats word',
            '  3. Child identifies blend: "SL!"',
            '  4. Child points to SL space',
            '  5. You write word in that space (or child does)',
            '  6. Celebrate: "Correct! SLIP goes in SL!"',
            '',
            'Start with 12-15 words total.',
            'Mix up the blends — don\'t do all SL, then all SP!',
            '',
            'Support strategies:',
            '  - If wrong: "Let\'s say it slowly: SSSS-LIP"',
            '  - Emphasize the blend: "SLIP starts with... SL!"',
            '  - Offer choices: "Is it SL or SM?"',
            '',
            'Keep score (optional):',
            '  - Count correct sorts',
            '  - "That\'s 8 out of 10! Amazing!"',
            '',
            'After sorting several words:',
            '  "Look at our board! All sorted by blend!"'
          ],
          examples: [
            'You: "SPIN!" Child: "SP!" Points to SP space. You write "spin" there. "Perfect!"'
          ],
          watchFor: [
            'Accurate blend identification.',
            'Correct categorization.',
            'Speed improving with practice.',
            'Self-correction when wrong.'
          ],
          parentTip: 'Mistakes are learning! When they sort incorrectly, it shows you which blends need more practice.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Visual Word Sort',
        duration: '3-4 min',
        focus: 'Reading and Sorting',
        researchBasis: 'Ehri (2020) - Visual word sorting reinforces orthographic pattern recognition.',
        content: {
          goal: 'Read written S-blend words and sort them into categories independently.',
          whatToSay: [
            '"Now YOU read the words and sort them!"',
            '"Look at the beginning carefully!"',
            '"Where does this word go?"'
          ],
          whatToDo: [
            'Write 10-12 S-blend words on separate small papers.',
            'Mix them up in a pile.',
            '',
            'Word cards to create:',
            '  SLIP, SPOT, STOP, SMILE',
            '  SNAP, SWIM, STEP, SLAM',
            '  SPIN, SNUG, SWEET, SMELL',
            '',
            'Child\'s task:',
            '  1. Pick up a word card',
            '  2. Read the word aloud',
            '  3. Identify the beginning blend',
            '  4. Place card in correct space on sorting board',
            '  5. Repeat!',
            '',
            'Process:',
            '  - Child works independently (you observe)',
            '  - Gently correct if needed',
            '  - Encourage self-checking: "Read it again — which blend?"',
            '',
            'After all cards sorted:',
            '  - Review each category together',
            '  - Read all words in SL space: "slip, slam, sleep..."',
            '  - Celebrate patterns: "Look! All these start with SL!"',
            '',
            'Extension activity:',
            '  "Can you think of another word that goes in the ST space?"',
            '  Child adds their own words to categories.',
            '',
            'Take a photo of finished sorting board!'
          ],
          examples: [
            'Child picks "SWIM" card, reads it, says "SW!", places in SW space. You: "Perfect sort!"'
          ],
          watchFor: [
            'Independent reading of words.',
            'Correct visual blend identification.',
            'Accurate placement in categories.',
            'Confidence in decision-making.'
          ],
          parentTip: 'Independent sorting shows true mastery! This is assessment disguised as play.'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Silly Sort Challenge',
        duration: '2-3 min',
        focus: 'Playful Reinforcement',
        researchBasis: 'Hirsh-Pasek et al. (2017) - Playful learning increases engagement and retention.',
        content: {
          goal: 'Make sorting fun with silly nonsense words, testing true blend knowledge.',
          whatToSay: [
            '"Let\'s sort SILLY words!"',
            '"I\'ll make up pretend words — you sort them!"',
            '"Ready? First silly word: SNOP!"'
          ],
          whatToDo: [
            'Say made-up nonsense words.',
            'Child identifies blend and sorts them.',
            '',
            'Nonsense words to use:',
            '  **SL:** slup, slob, slig, slep',
            '  **SP:** spim, spub, spog, splat',
            '  **ST:** stib, stum, stap, steg',
            '  **SM:** smib, smup, smog, smeb',
            '  **SN:** snib, snup, sneg, snop',
            '  **SW:** swib, swup, swig, swob',
            '',
            'Why nonsense words?',
            '  - Tests true phonics knowledge (not memorization)',
            '  - Shows if they really understand blends',
            '  - Makes it silly and fun!',
            '',
            'Process:',
            '  1. You say silly word: "SLUP!"',
            '  2. Child laughs and repeats: "SLUP!"',
            '  3. Child identifies blend: "SL!"',
            '  4. Child points to or says category',
            '  5. You celebrate: "Yes! SLUP would go in SL!"',
            '',
            'Make it extra silly:',
            '  - Use funny voices',
            '  - Act out the silly words',
            '  - "What would a SNOP look like?"',
            '',
            'Challenge round:',
            '  "Can YOU make up a silly SP word?"',
            '  Child creates their own nonsense words!',
            '',
            'This shows TRUE understanding of blends!'
          ],
          examples: [
            'You: "SMIB!" Child giggles: "SM! That\'s silly!" You: "But you sorted it right!"'
          ],
          watchFor: [
            'Ability to decode unfamiliar words.',
            'Accurate blend identification in nonsense.',
            'Understanding vs. memorization.',
            'Creativity and playfulness.'
          ],
          parentTip: 'Nonsense words are the BEST test! If they can sort made-up words, they truly understand blends!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎯 You\'re an S-Blend Sorting Expert!',
            'You practiced:',
            '✓ Creating visual sorting spaces',
            '✓ Listening and sorting words by blend',
            '✓ Reading and sorting word cards',
            '✓ Sorting silly nonsense words'
          ],
          reviewQuestions: [
            'Could your child identify blends in spoken words?',
            'Were they able to sort words independently?',
            'Did they succeed with nonsense words?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Sorting is a powerful cognitive skill that goes beyond reading. When children categorize words by sound patterns, they\'re building metalinguistic awareness — thinking ABOUT language, not just using it. This higher-level thinking strengthens their understanding of how English works. The ability to sort nonsense words proves they\'re not just memorizing — they truly understand the blend patterns. This deep understanding transfers to reading any new word they encounter!'
          },
          nextSteps: [
            'Keep the sorting board! Add new words throughout the week.',
            'Play "Sort the Groceries" using package labels with S-blends.',
            'Challenge: Find S-blend words in books and sort them.',
            'Tomorrow: Build-a-Word Lab with S-blends!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 34 =========
  {
    id: 34,
    phase: '4',
    quarter: 1,
    week: 7,
    weekIndex: 6,
    dayInWeek: 4,
    title: 'Lesson 34: Build-a-Word Lab',
    theme: 'Beginning Consonant Blends (S-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'word-building', 'spelling'],
    relatedToPreviousSkills: ['consonant-blends', 'phoneme-manipulation', 'encoding'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Build-a-Word Lab!',
        duration: '1 min',
        content: {
          goal: 'Create decodable S-blend words by combining blends with word endings, practicing encoding and spelling.',
          parentGuide: [
            'Word building develops both reading AND spelling skills.',
            'Children learn how words are constructed from sound units.',
            'This "lab" approach makes spelling feel like a science experiment!'
          ],
          materials: ['Paper', 'Pencil', 'Optional: write letters on small pieces of paper to rearrange'],
          tip: 'Let them experiment! Wrong combinations teach as much as right ones.',
          researchNote: 'Ehri (2001) - Encoding practice (spelling) strengthens decoding (reading) through orthographic mapping.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: The Word Lab Setup',
        duration: '2 min',
        focus: 'Introduction to Word Building',
        researchBasis: 'National Reading Panel (2000) - Systematic phonics instruction includes both reading and spelling.',
        content: {
          goal: 'Understand how blends combine with endings to create words.',
          whatToSay: [
            '"Welcome to the Word Lab!"',
            '"Today we\'re BUILDING words like scientists!"',
            '"We\'ll mix blends with endings to create words!"',
            '"Let\'s see what we can make!"'
          ],
          whatToDo: [
            'Draw or write two columns on paper:',
            '',
            '**BLENDS** (left side):',
            '  SL',
            '  SP',
            '  ST',
            '  SM',
            '  SN',
            '  SW',
            '',
            '**ENDINGS** (right side):',
            '  -ip',
            '  -op',
            '  -ot',
            '  -im',
            '  -ap',
            '  -ell',
            '',
            'Explain the concept:',
            '  "We take a BLEND from this side..."',
            '  Point to blend column.',
            '  "...and an ENDING from this side..."',
            '  Point to ending column.',
            '  "...and we BUILD a word!"',
            '',
            'Demonstrate with example:',
            '  "Let\'s try: SL + IP = ?"',
            '  Write: SL + ip = slip',
            '  "We made SLIP!"',
            '',
            'Do one more together:',
            '  "How about: ST + OP = ?"',
            '  Write: ST + op = stop',
            '  "We made STOP!"',
            '',
            'Show that some combinations don\'t work:',
            '  "What about SM + IP?"',
            '  "That would be SMIP... that\'s not a real word!"',
            '  "That\'s okay! We\'re experimenting!"',
            '',
            'Set expectations:',
            '  "Let\'s build 6-8 REAL words together!"'
          ],
          examples: [
            'You write: "SN + AP = ?" Child: "SNAP!" You: "Perfect! We built SNAP!"'
          ],
          watchFor: [
            'Understanding the building concept.',
            'Recognizing how parts combine.',
            'Willingness to experiment.'
          ],
          parentTip: 'Word building makes abstract spelling concrete! They SEE how words are constructed.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Guided Word Building',
        duration: '4-5 min',
        focus: 'Creating Real Words',
        researchBasis: 'Lonigan et al. (2000) - Phoneme blending and manipulation support both reading and spelling development.',
        content: {
          goal: 'Build 6-8 real S-blend words by combining blends with appropriate endings.',
          whatToSay: [
            '"Let\'s build some real words!"',
            '"I\'ll help you pick combinations that work!"',
            '"Ready to build?"'
          ],
          whatToDo: [
            'Guide child to build these words (one at a time):',
            '',
            '**Target words to build:**',
            '  1. SLIP (SL + ip)',
            '  2. STOP (ST + op)',
            '  3. SPOT (SP + ot)',
            '  4. SNAP (SN + ap)',
            '  5. SWIM (SW + im)',
            '  6. SMELL (SM + ell)',
            '  7. SPIN (SP + in)',
            '  8. STEP (ST + ep)',
            '',
            'For EACH word:',
            '',
            '  **Step 1: Choose blend**',
            '    "Which blend should we use for SLIP?"',
            '    Child identifies: "SL!"',
            '',
            '  **Step 2: Choose ending**',
            '    "What ending do we need?"',
            '    Child identifies: "-ip!"',
            '',
            '  **Step 3: Write combination**',
            '    Child writes: SL + ip = slip',
            '    Or you write and child copies',
            '',
            '  **Step 4: Read the built word**',
            '    Child blends: "SLIP!"',
            '',
            '  **Step 5: Use in sentence (optional)**',
            '    "I like to slip and slide!"',
            '',
            'Keep a running list of built words.',
            '',
            'Celebrate each success:',
            '  "You built that word perfectly!"',
            '  "You\'re a word scientist!"',
            '',
            'If child struggles:',
            '  - Say the word slowly',
            '  - Emphasize the parts: "SSSS-LIP"',
            '  - Point to the correct blend/ending',
            '',
            'Optional: Try building with physical letters if available.'
          ],
          examples: [
            'You: "How do we build SPIN?" Child: "SP + in!" Writes it. Reads: "SPIN!" You: "Excellent building!"'
          ],
          watchFor: [
            'Correct blend selection.',
            'Correct ending selection.',
            'Accurate writing of combinations.',
            'Successful reading of built words.'
          ],
          parentTip: 'Building = encoding! This is spelling practice that feels like a game!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Free Building Experiment',
        duration: '3-4 min',
        focus: 'Independent Exploration',
        researchBasis: 'Whitehurst & Lonigan (1998) - Self-directed exploration strengthens phonological understanding.',
        content: {
          goal: 'Child independently experiments with blend-ending combinations, discovering real and nonsense words.',
          whatToSay: [
            '"Now it\'s YOUR turn to experiment!"',
            '"Mix any blend with any ending!"',
            '"Let\'s see what words you discover!"',
            '"Some will be real, some will be silly!"'
          ],
          whatToDo: [
            'Child freely combines blends and endings.',
            'Try different combinations to see what happens!',
            '',
            'Encourage exploration:',
            '  "Try SL with different endings!"',
            '  "What happens if we use SM + ap?"',
            '  "Can you make 3 new words?"',
            '',
            'Possible discoveries (real words):',
            '  - SLIP, SLIT, SLAM, SLAP, SLED',
            '  - SPOT, SPIN, SPIT, SPUN',
            '  - STOP, STEP, STEM, STUB',
            '  - SNAP, SNAG, SNIP',
            '  - SWIM, SWAM',
            '  - SMELL, SMOG',
            '',
            'Nonsense words are OKAY:',
            '  - SMIP, SNOP, SWIP, STAP',
            '  "That\'s not a real word, but you built it correctly!"',
            '  "It COULD be a word in another language!"',
            '',
            'For each combination:',
            '  1. Child picks blend + ending',
            '  2. Child writes combination',
            '  3. Child reads result',
            '  4. Together decide: Real or silly?',
            '  5. If real: "Great discovery!"',
            '  6. If silly: "Silly but correctly built!"',
            '',
            'Keep track:',
            '  - Make two lists: "Real Words" and "Silly Words"',
            '  - Count how many of each',
            '',
            'Challenge:',
            '  "Can you build 5 real words on your own?"'
          ],
          examples: [
            'Child writes: "SN + OP = SNOP" You: "That\'s silly! But you built it perfectly!" Child laughs.'
          ],
          watchFor: [
            'Willingness to experiment.',
            'Correct phonics application.',
            'Understanding real vs. nonsense.',
            'Creative exploration.'
          ],
          parentTip: 'Nonsense words prove understanding! If they can build "SNOP" correctly, they know the rules!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Word Lab Report',
        duration: '2-3 min',
        focus: 'Review and Celebration',
        researchBasis: 'Scarborough (2001) - Reflection on learning strengthens metacognitive skills.',
        content: {
          goal: 'Review all built words and celebrate discoveries, reinforcing learning.',
          whatToSay: [
            '"Let\'s review all the words you built today!"',
            '"You\'re going to read your Word Lab Report!"',
            '"These are YOUR discoveries!"'
          ],
          whatToDo: [
            'Count total words built (real + silly).',
            '',
            'Create a "Word Lab Report":',
            '  **Title:** "[Child\'s Name]\'s Word Lab"',
            '  **Date:** [Today\'s date]',
            '  **Words Discovered:**',
            '    List all real words built',
            '  **Silly Experiments:**',
            '    List nonsense words tried',
            '  **Total Builds:** [number]',
            '',
            'Child reads entire list aloud:',
            '  - All real words',
            '  - All silly words',
            '  - Celebrate each one!',
            '',
            'Reflection questions:',
            '  "Which word was hardest to build?"',
            '  "Which blend was your favorite?"',
            '  "Did any combinations surprise you?"',
            '',
            'Certificate moment:',
            '  "You are now a Word Lab Scientist!"',
            '  Draw a star or badge on their report.',
            '',
            'Extension challenge:',
            '  "Tomorrow, can you find any of these words in books?"',
            '  "Can you use 3 of these words in sentences?"',
            '',
            'Save the Word Lab Report:',
            '  - Add to portfolio',
            '  - Display on refrigerator',
            '  - Share with family',
            '',
            'Final celebration:',
            '  "You built [X] words today! Amazing work!"'
          ],
          examples: [
            'Child reads list: "SLIP, STOP, SPOT, SNAP, SWIM, SMELL!" You: "Six words! Incredible!"'
          ],
          watchFor: [
            'Pride in accomplishments.',
            'Accurate reading of all built words.',
            'Reflection on process.',
            'Excitement about learning.'
          ],
          parentTip: 'The "report" makes learning official! Children love seeing their work documented like scientists!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🔬 You\'re a Word Lab Scientist!',
            'You practiced:',
            '✓ Understanding how words are built',
            '✓ Combining blends with endings',
            '✓ Building 6-8 real words',
            '✓ Experimenting with combinations',
            '✓ Creating your Word Lab Report'
          ],
          reviewQuestions: [
            'Could your child combine blends and endings correctly?',
            'Did they build at least 6 real words?',
            'Were they willing to experiment?',
            'Could they read all their built words?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Word building is encoding — the flip side of decoding (reading). When children build words, they deeply internalize how English spelling works. They learn that words aren\'t random letter strings but logical combinations of sound units. This understanding makes BOTH reading and spelling easier. The experimental approach teaches that language has patterns and rules, but also allows creativity. Most importantly, building words gives children a sense of control and mastery — they\'re not just receiving language, they\'re creating it!'
          },
          nextSteps: [
            'Build more words throughout the week!',
            'Challenge: Build words with other blend families (L-blends, R-blends).',
            'Create a "Word Lab Journal" for ongoing discoveries.',
            'Tomorrow: S-Blend Storymix — bringing it all together!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 35 =========
  {
    id: 35,
    phase: '4',
    quarter: 1,
    week: 7,
    weekIndex: 6,
    dayInWeek: 5,
    title: 'Lesson 35: S-Blend Storymix',
    theme: 'Beginning Consonant Blends (S-Blends)',
    skillRecurrenceTags: ['consonant-blends', 'story-comprehension', 'decoding-in-context'],
    relatedToPreviousSkills: ['consonant-blends', 'listening-comprehension', 'word-recognition'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to S-Blend Storymix!',
        duration: '1 min',
        content: {
          goal: 'Listen to a story filled with S-blend words, identify them, and practice reading in meaningful context.',
          parentGuide: [
            'This lesson integrates all S-blend learning into story context.',
            'Context helps children understand words have meaning, not just sounds.',
            'Story listening develops comprehension alongside decoding.'
          ],
          materials: ['Paper', 'Pencil or crayons', 'Your storytelling voice!'],
          tip: 'Use dramatic voices and expressions! Make the story come alive.',
          researchNote: 'Scarborough (2001) - Reading comprehension requires both word recognition AND language comprehension working together.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Story Preview',
        duration: '2 min',
        focus: 'Building Anticipation',
        researchBasis: 'Whitehurst & Lonigan (1998) - Pre-reading discussion activates prior knowledge and increases engagement.',
        content: {
          goal: 'Introduce the story and prepare child to listen for S-blend words.',
          whatToSay: [
            '"Today I have a special story for you!"',
            '"It\'s called \'Stan\'s Amazing Day!\'"',
            '"This story is FULL of S-blend words!"',
            '"Let\'s see how many you can find!"'
          ],
          whatToDo: [
            'Introduce the story mission:',
            '  "While I read, listen for S-blend words!"',
            '  "Every time you hear one, raise your hand!"',
            '  "We\'ll count how many we find together!"',
            '',
            'Set up tracking system:',
            '  - Draw a simple tally chart on paper',
            '  - Or prepare to count on fingers',
            '  - "We\'ll keep score!"',
            '',
            'Quick S-blend review:',
            '  "What S-blends do you remember?"',
            '  Child names some: "SL, SP, ST..."',
            '  "Perfect! Listen for ALL of those!"',
            '',
            'Build excitement:',
            '  "This story has surprises!"',
            '  "Stan does something really silly!"',
            '  "Ready to listen?"',
            '',
            'Set listening expectations:',
            '  "Sit comfortably"',
            '  "Listen carefully"',
            '  "Signal when you hear S-blends!"',
            '',
            'Optional: Show child the written story so they can follow along.'
          ],
          examples: [
            'You: "When you hear an S-blend word, give me a thumbs up!" Child practices thumbs up.'
          ],
          watchFor: [
            'Excitement about the story.',
            'Understanding of the listening task.',
            'Ready-to-listen posture.'
          ],
          parentTip: 'Active listening with a purpose increases engagement! They\'re not just hearing — they\'re hunting!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: First Story Reading',
        duration: '3-4 min',
        focus: 'Listening and Identifying',
        researchBasis: 'National Reading Panel (2000) - Repeated reading with different focuses builds both fluency and comprehension.',
        content: {
          goal: 'Listen to story and identify S-blend words, stopping to celebrate each discovery.',
          whatToSay: [
            '"Here we go! Listen carefully!"',
            '"Stan\'s Amazing Day... by [Your Name]!"'
          ],
          whatToDo: [
            'Read this story (or create your own with 8-10 S-blend words):',
            '',
            '**"Stan\'s Amazing Day"**',
            '',
            '"One sunny morning, Stan woke up with a big SMILE.',
            'He wanted to do something special today.',
            '',
            'First, he went to the park to SPIN on the merry-go-round.',
            'Round and round he went! Then he SLID down the big slide.',
            'SWOOSH! That was fun!',
            '',
            'Next, Stan saw a SNAIL on the path.',
            '"Hello, little friend," he said. The snail was so SLOW!',
            '',
            'Stan felt hungry, so he stopped for a SNACK.',
            'He ate some crackers and a SWEET apple.',
            '',
            'On the way home, Stan had to STEP over a big puddle.',
            'SPLASH! He didn\'t see a SPOT of mud and SLIPPED!',
            '',
            'Stan just laughed. "What an amazing day!" he said with a SMILE."',
            '',
            '**Reading technique:**',
            '  - Read expressively with character voices',
            '  - PAUSE after each S-blend word',
            '  - Wait for child to signal (hand raise, thumbs up)',
            '  - Confirm: "Yes! SMILE! That\'s SM!"',
            '  - Make a tally mark or count',
            '  - Continue reading',
            '',
            'S-blend words in story:',
            '  SMILE (x2), SPIN, SLID, SWOOSH, SNAIL, SLOW,',
            '  SNACK, SWEET, STEP, SPLASH, SPOT, SLIPPED',
            '',
            'Keep count together:',
            '  "That\'s 8 S-blend words so far!"',
            '',
            'After story:',
            '  "How many S-blend words did we find?"',
            '  Count together: 12 total!'
          ],
          examples: [
            'You read: "Stan had a big SMILE." Child raises hand. You: "Yes! SMILE! That\'s SM!" Make tally mark.'
          ],
          watchFor: [
            'Recognition of S-blend words in context.',
            'Appropriate signaling.',
            'Engagement with story.',
            'Accurate counting.'
          ],
          parentTip: 'Stopping to identify words breaks flow BUT builds awareness! Second reading will be smoother.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Story Retell & Highlight',
        duration: '3-4 min',
        focus: 'Comprehension and Word Focus',
        researchBasis: 'Bus & van IJzendoorn (1995) - Story retelling strengthens comprehension and narrative skills.',
        content: {
          goal: 'Retell story together, identifying S-blend words and checking comprehension.',
          whatToSay: [
            '"Let\'s tell Stan\'s story together!"',
            '"What happened first?"',
            '"Can you remember the S-blend words?"'
          ],
          whatToDo: [
            'Guide child through story retelling:',
            '',
            '**Beginning:**',
            '  You: "How did Stan feel in the morning?"',
            '  Child: "Happy!" or "He had a SMILE!"',
            '  You: "Yes! He woke up with a SMILE!"',
            '',
            '**Middle:**',
            '  You: "What did Stan do at the park?"',
            '  Child recalls: SPIN, SLID',
            '  You: "Right! He went for a SPIN and SLID down!"',
            '',
            '  You: "What did he see on the path?"',
            '  Child: "A SNAIL!"',
            '  You: "Yes! A SLOW SNAIL!"',
            '',
            '  You: "What did he eat?"',
            '  Child: "A SNACK... something SWEET?"',
            '  You: "Perfect memory! A SNACK and SWEET apple!"',
            '',
            '**End:**',
            '  You: "What happened with the puddle?"',
            '  Child: "He SLIPPED!"',
            '  You: "Yes! He had to STEP over it but SLIPPED!"',
            '',
            'Write or draw key moments:',
            '  - Draw 3-4 simple pictures representing story',
            '  - Label each with S-blend words',
            '  - Child helps write the words',
            '',
            'Comprehension check:',
            '  "Why was it an \'amazing day\'?"',
            '  "How did Stan feel at the end?"',
            '  "What was your favorite part?"',
            '',
            'Highlight S-blend words:',
            '  If story is written, circle all S-blend words together.',
            '  "Look how many S-blends we found!"'
          ],
          examples: [
            'You: "What did Stan do first?" Child: "He went to SPIN!" You: "Great! SPIN is an SP word!"'
          ],
          watchFor: [
            'Accurate story recall.',
            'Memory of S-blend words.',
            'Understanding of story sequence.',
            'Personal connection to story.'
          ],
          parentTip: 'Retelling = comprehension check! If they can retell it, they understood it!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Create Your Own S-Blend Story',
        duration: '3-4 min',
        focus: 'Creative Application',
        researchBasis: 'Whitehurst & Lonigan (1998) - Story creation demonstrates mastery and builds language skills.',
        content: {
          goal: 'Child creates a short original story using S-blend words.',
          whatToSay: [
            '"Now YOU make a story with S-blend words!"',
            '"It can be about anything you want!"',
            '"Let\'s use at least 3 S-blend words!"',
            '"What will your story be about?"'
          ],
          whatToDo: [
            'Help child create a 3-4 sentence story.',
            '',
            'Story starter options:',
            '  - "One day I went to..."',
            '  - "My friend [name] likes to..."',
            '  - "Once there was a..."',
            '',
            'Brainstorm S-blend words to include:',
            '  "Which S-blend words do you want to use?"',
            '  Child picks 3-5: SWIM, SMILE, STOP, SNAP, etc.',
            '',
            'Build story together:',
            '  **Sentence 1:** Introduction',
            '    Child: "One day I went to SWIM."',
            '    You write or child writes.',
            '',
            '  **Sentence 2:** Action',
            '    Child: "I saw my friend with a big SMILE."',
            '',
            '  **Sentence 3:** More action',
            '    Child: "We had to STOP for a SNACK."',
            '',
            '  **Sentence 4:** Ending',
            '    Child: "It was fun!"',
            '',
            'Keep it SIMPLE - focus on S-blend words!',
            '',
            'Child reads completed story aloud:',
            '  Practice smooth reading',
            '  Celebrate S-blend words used',
            '',
            'Optional additions:',
            '  - Draw a picture for the story',
            '  - Give story a title',
            '  - Add more sentences if excited',
            '',
            'Celebrate authorship:',
            '  "You wrote a story with S-blend words!"',
            '  "You\'re an author AND a reader!"',
            '',
            'Save the story:',
            '  Add to portfolio',
            '  Share with family',
            '  Read again later'
          ],
          examples: [
            'Child: "I like to SPIN and SLIDE!" You: "Perfect! Two S-blend words in one sentence!"'
          ],
          watchFor: [
            'Appropriate use of S-blend words.',
            'Story makes sense.',
            'Creativity and personal voice.',
            'Pride in creation.'
          ],
          parentTip: 'Their story doesn\'t need to be elaborate! Even "I like to SWIM and SMILE" is success!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '📖 You mastered S-Blend Storymix!',
            'You practiced:',
            '✓ Listening for S-blend words in a story',
            '✓ Identifying 12 S-blend words',
            '✓ Retelling story with S-blend words',
            '✓ Creating your OWN S-blend story',
            '✓ Completing S-Blend Week!'
          ],
          reviewQuestions: [
            'Could your child identify S-blends in story context?',
            'Did they retell the story accurately?',
            'Were they able to create their own story?',
            'Did they use S-blend words appropriately?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Stories are where all literacy skills come together! When children identify blends in stories (not just word lists), they\'re reading in authentic contexts — the ultimate goal. Story comprehension requires both decoding (sounding out words) and language comprehension (understanding meaning). This lesson integrates both strands of Scarborough\'s Reading Rope. Creating their own stories transforms children from passive consumers to active creators of language. This ownership is empowering and motivating!'
          },
          nextSteps: [
            'Read books together and hunt for S-blend words!',
            'Create more stories using different blend families.',
            'Act out "Stan\'s Amazing Day" as a play!',
            'Next week: Spiral Review — CVC + ALL Blends integration!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 36 =========
  {
    id: 36,
    phase: '4',
    quarter: 1,
    week: 8,
    weekIndex: 7,
    dayInWeek: 1,
    title: 'Lesson 36: CVC + Blend Race',
    theme: 'Spiral Review: CVC + Blends',
    skillRecurrenceTags: ['spiral-review', 'decoding-fluency', 'cvc-words', 'consonant-blends'],
    relatedToPreviousSkills: ['cvc-decoding', 'consonant-blends', 'reading-fluency'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to CVC + Blend Race!',
        duration: '1 min',
        content: {
          goal: 'Read a mix of CVC and blend words quickly, integrating all decoding skills learned so far.',
          parentGuide: [
            'This is Week 8 — Spiral Review Week! Time to integrate everything.',
            'Mixing word types prevents pattern-based guessing.',
            'Speed practice builds automaticity — essential for fluent reading.',
            'This is NOT a test — it\'s a celebration of skills!'
          ],
          materials: ['Paper', 'Pencil', 'Timer (optional)', 'Enthusiasm!'],
          tip: 'Focus on growth, not perfection! Compare progress to their own earlier performance.',
          researchNote: 'National Reading Panel (2000) - Fluency requires accuracy, speed, and proper expression. This lesson develops speed with accuracy.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Warm-Up Lap',
        duration: '2 min',
        focus: 'Review and Preparation',
        researchBasis: 'Ehri (2020) - Review of learned patterns before mixed practice strengthens retrieval.',
        content: {
          goal: 'Quick review of CVC and blend patterns before the race.',
          whatToSay: [
            '"Today we\'re having a reading race!"',
            '"But first, let\'s warm up like athletes!"',
            '"Let\'s remember what we know!"'
          ],
          whatToDo: [
            'Quick CVC review:',
            '  "Read these CVC words fast!"',
            '  Show: CAT, DOG, SUN, BED, TOP',
            '  Child reads quickly',
            '',
            'Quick blend review:',
            '  "Now read these blend words!"',
            '  Show: SLIP, CRAB, FROG, STOP, DRUM',
            '  Child reads quickly',
            '',
            'Point out the difference:',
            '  "CVC words have 3 sounds."',
            '  Write: C-A-T (point to each)',
            '  "Blend words have MORE sounds at the beginning!"',
            '  Write: S-L-I-P (point to SL blend)',
            '',
            'Practice identifying word types:',
            '  You say word, child identifies:',
            '  "CAT?" → "CVC!"',
            '  "STOP?" → "Blend!"',
            '  "BED?" → "CVC!"',
            '  "SLIP?" → "Blend!"',
            '',
            'Explain the race:',
            '  "Now we\'ll mix them together!"',
            '  "Read 10 mixed words as fast as you can!"',
            '  "Accuracy first, then speed!"',
            '',
            'Set encouraging tone:',
            '  "This is for FUN!"',
            '  "Let\'s see how many you can read!"'
          ],
          examples: [
            'You: "Is FROG a CVC or blend word?" Child: "Blend! It has FR!" You: "Perfect warm-up!"'
          ],
          watchFor: [
            'Clear understanding of difference.',
            'Confident reading in warm-up.',
            'Ready-to-race attitude.'
          ],
          parentTip: 'Warm-up prevents frustration! They need to activate their phonics knowledge first.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: First Race (10 Words)',
        duration: '3-4 min',
        focus: 'Mixed Reading Practice',
        researchBasis: 'Lonigan et al. (2000) - Mixed practice strengthens flexible decoding and prevents pattern dependence.',
        content: {
          goal: 'Read 10 mixed CVC and blend words, focusing on accuracy first.',
          whatToSay: [
            '"Here\'s your first race!"',
            '"10 words to read!"',
            '"Take your time — read them RIGHT first!"',
            '"Ready, set, GO!"'
          ],
          whatToDo: [
            'Write or show these 10 mixed words:',
            '',
            '**Race List 1:**',
            '  1. CAT (CVC)',
            '  2. SLIP (S-blend)',
            '  3. BED (CVC)',
            '  4. FROG (R-blend)',
            '  5. SUN (CVC)',
            '  6. STOP (S-blend)',
            '  7. PIG (CVC)',
            '  8. DRUM (R-blend)',
            '  9. SWIM (S-blend)',
            '  10. HAT (CVC)',
            '',
            'Child reads through list:',
            '  - One word at a time',
            '  - You point or they track',
            '  - Encourage smooth blending',
            '',
            'Provide support as needed:',
            '  - If stuck: "Sound it out"',
            '  - If wrong: "Try again — look at all the letters"',
            '  - If choppy: "Smooth it out"',
            '',
            'Track performance:',
            '  - Count how many correct',
            '  - Note which ones were difficult',
            '  - Optional: Time the reading',
            '',
            'After completion:',
            '  "You read [X] out of 10!"',
            '  "Let\'s look at any tricky ones."',
            '',
            'Re-read missed words:',
            '  - Break into sounds',
            '  - Blend together',
            '  - Read again correctly',
            '',
            'Celebrate effort:',
            '  "Great focus!"',
            '  "You worked through tough ones!"',
            '',
            'Prepare for next round:',
            '  "Ready to try even MORE words?"'
          ],
          examples: [
            'Child reads: "CAT, SLIP, BED, FROG..." You: "Smooth reading! Keep going!"'
          ],
          watchFor: [
            'Accurate decoding of both word types.',
            'Smooth blending (not choppy).',
            'Self-correction when wrong.',
            'Persistence with difficult words.'
          ],
          parentTip: '7-8 out of 10 is excellent! This is HARD — they\'re switching between patterns constantly!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Speed Challenge (Round 2)',
        duration: '3-4 min',
        focus: 'Building Fluency',
        researchBasis: 'National Reading Panel (2000) - Repeated reading of word lists increases automaticity.',
        content: {
          goal: 'Read a second list of 10 mixed words, aiming for improved speed.',
          whatToSay: [
            '"Ready for round 2?"',
            '"This time, try to read a little FASTER!"',
            '"But still get them RIGHT!"',
            '"On your mark... GO!"'
          ],
          whatToDo: [
            'Write or show these 10 NEW mixed words:',
            '',
            '**Race List 2:**',
            '  1. DOG (CVC)',
            '  2. PLAN (L-blend)',
            '  3. FAN (CVC)',
            '  4. SNAP (S-blend)',
            '  5. CUP (CVC)',
            '  6. CRAB (R-blend)',
            '  7. BUS (CVC)',
            '  8. STEP (S-blend)',
            '  9. CLIP (L-blend)',
            '  10. NET (CVC)',
            '',
            'Child reads through list:',
            '  - Encourage slightly faster pace',
            '  - Maintain accuracy',
            '  - Celebrate smooth reading',
            '',
            'Optional timing:',
            '  If you timed Round 1, time this one too.',
            '  "Let\'s see if you can beat your time!"',
            '  BUT: Accuracy matters more than speed!',
            '',
            'Track improvement:',
            '  Compare to Round 1:',
            '  - More correct?',
            '  - Faster?',
            '  - Smoother?',
            '',
            'Celebrate growth:',
            '  "You read more smoothly this time!"',
            '  "I noticed you didn\'t hesitate on SNAP!"',
            '  "Your blending is getting automatic!"',
            '',
            'Reflection:',
            '  "Which list was easier?"',
            '  "Which word type is easier for you — CVC or blends?"',
            '  "What helped you read faster?"',
            '',
            'Final review:',
            '  Read through both lists one more time',
            '  Aim for smooth, confident reading'
          ],
          examples: [
            'Child reads Round 2 faster. You: "Wow! 30 seconds faster AND all correct!"'
          ],
          watchFor: [
            'Improved speed from Round 1.',
            'Maintained or improved accuracy.',
            'Confidence building.',
            'Enjoyment of challenge.'
          ],
          parentTip: 'Speed will vary day to day! Focus on the trend over weeks, not perfection today.'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Champion Challenge',
        duration: '2-3 min',
        focus: 'Application and Celebration',
        researchBasis: 'Whitehurst & Lonigan (1998) - Playful application of skills increases motivation and retention.',
        content: {
          goal: 'Choose favorite words and use them in sentences, applying reading to meaning.',
          whatToSay: [
            '"You\'re a reading champion!"',
            '"Pick 3 favorite words from our lists!"',
            '"Let\'s use them in sentences!"',
            '"Show me what these words MEAN!"'
          ],
          whatToDo: [
            'Child chooses 3 words from any race list.',
            'Popular choices: FROG, SWIM, STOP, CAT, SLIP',
            '',
            'For EACH chosen word:',
            '',
            '  **Step 1: Read it again**',
            '    Child reads word aloud',
            '',
            '  **Step 2: Define it**',
            '    "What is a FROG?"',
            '    Child explains meaning',
            '',
            '  **Step 3: Use in sentence**',
            '    "Make a sentence with FROG."',
            '    Child creates: "I saw a frog at the pond!"',
            '    You write it down',
            '',
            '  **Step 4: Act it out (optional)**',
            '    Child acts out the word or sentence',
            '    FROG = hop around',
            '    SWIM = swimming motions',
            '    STOP = freeze pose',
            '',
            'Write all 3 sentences:',
            '  Child reads them back',
            '  Celebrate creative sentences',
            '',
            'Create "Champion Certificate":',
            '  Draw simple certificate on paper:',
            '  "[Child\'s Name] is a CVC + Blend Champion!"',
            '  "Words read today: 20+"',
            '  "Date: [today]"',
            '  Child decorates it',
            '',
            'Final celebration:',
            '  "You mixed CVC and blend words like a pro!"',
            '  "Your reading is getting so strong!"'
          ],
          examples: [
            'Child picks SLIP: "I like to slip and slide!" Acts out slipping. You: "Perfect sentence AND action!"'
          ],
          watchFor: [
            'Meaningful sentences (not just random words).',
            'Connection between reading and meaning.',
            'Pride in accomplishment.',
            'Creative expression.'
          ],
          parentTip: 'Sentences prove comprehension! If they use words meaningfully, they truly understand them!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🏆 You completed the CVC + Blend Race!',
            'You practiced:',
            '✓ Reviewing CVC and blend patterns',
            '✓ Reading 10 mixed words (Round 1)',
            '✓ Reading 10 more words faster (Round 2)',
            '✓ Using favorite words in sentences',
            '✓ Earning your Champion Certificate!'
          ],
          reviewQuestions: [
            'Could your child read both CVC and blend words accurately?',
            'Did their speed improve from Round 1 to Round 2?',
            'Were they able to create meaningful sentences?',
            'Did they show confidence and enjoyment?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Mixing word types is crucial for developing flexible, automatic decoding. When children can smoothly switch between CVC words and blend words, they\'re showing true phonics mastery — not just memorization of one pattern. Speed practice builds automaticity, which frees up mental energy for comprehension. When decoding becomes automatic, children can focus on what the words MEAN, not just how to sound them out. This is the bridge from "learning to read" to "reading to learn"!'
          },
          nextSteps: [
            'Practice mixed word reading daily (2-3 minutes).',
            'Time periodic "races" to track speed growth.',
            'Point out CVC vs. blend words in books.',
            'Tomorrow: Real vs. Silly word challenge!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 37 =========
  {
    id: 37,
    phase: '4',
    quarter: 1,
    week: 8,
    weekIndex: 7,
    dayInWeek: 2,
    title: 'Lesson 37: Real vs. Silly Blend Words',
    theme: 'Spiral Review: CVC + Blends',
    skillRecurrenceTags: ['spiral-review', 'decoding-assessment', 'phonics-application', 'critical-thinking'],
    relatedToPreviousSkills: ['consonant-blends', 'cvc-decoding', 'word-recognition'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Real vs. Silly Blend Words!',
        duration: '1 min',
        content: {
          goal: 'Decode both real and nonsense words, demonstrating true phonics understanding vs. memorization.',
          parentGuide: [
            'Nonsense words test TRUE decoding ability — not memorization!',
            'If children can read made-up words, they truly understand phonics.',
            'This is how reading assessments measure phonics skills.',
            'Make it playful, not test-like!'
          ],
          materials: ['Paper', 'Pencil', 'Two containers or spaces (Real / Silly)'],
          tip: 'Celebrate nonsense word reading! It proves they understand the rules.',
          researchNote: 'National Reading Panel (2000) - Nonsense word reading is a valid measure of phonics knowledge and decoding ability.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Understanding Real vs. Silly',
        duration: '2 min',
        focus: 'Concept Introduction',
        researchBasis: 'Ehri (2020) - Understanding word authenticity requires both decoding skills and vocabulary knowledge.',
        content: {
          goal: 'Learn to distinguish between real English words and correctly-decoded nonsense words.',
          whatToSay: [
            '"Today we\'re word detectives!"',
            '"Some words are REAL — we use them every day!"',
            '"Some words are SILLY — they follow the rules but aren\'t real!"',
            '"Let\'s practice!"'
          ],
          whatToDo: [
            'Explain the concept:',
            '  "Real words are in the dictionary."',
            '  "Silly words follow phonics rules but aren\'t real."',
            '  "Both kinds show you can READ!"',
            '',
            'Create two spaces on paper:',
            '  **REAL WORDS | SILLY WORDS**',
            '  Or use two containers/boxes',
            '',
            'Demonstrate with examples:',
            '',
            '  **Example 1: STOP**',
            '    You: "Is STOP real or silly?"',
            '    Child: "Real!"',
            '    You: "Yes! We say \'stop at the sign!\'"',
            '    Place in REAL column',
            '',
            '  **Example 2: SNOP**',
            '    You: "Now read this: SNOP"',
            '    Child sounds it out: "/s/ /n/ /o/ /p/ ... SNOP"',
            '    You: "Is SNOP real or silly?"',
            '    Child: "Silly!"',
            '    You: "Right! You read it correctly, but it\'s not a real word!"',
            '    Place in SILLY column',
            '',
            '  **Example 3: FROG**',
            '    Real word (animal)',
            '',
            '  **Example 4: BLIG**',
            '    Silly word (correct decoding, not real)',
            '',
            'Key point:',
            '  "Reading silly words correctly is AMAZING!"',
            '  "It shows you understand the phonics rules!"',
            '  "You\'re not just memorizing — you\'re READING!"',
            '',
            'Check understanding:',
            '  "So both kinds are read the same way?"',
            '  "We sound them out and blend!"',
            '  "Then we decide: real or silly?"'
          ],
          examples: [
            'You: "DRIP — real or silly?" Child: "Real! Water drips!" You: "Perfect detective work!"'
          ],
          watchFor: [
            'Understanding the concept.',
            'Ability to decode both types.',
            'Recognition that silly words are okay.',
            'Excitement about the game.'
          ],
          parentTip: 'Emphasize that reading silly words correctly is SUCCESS! Don\'t let them feel "wrong" for reading nonsense.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Detective Challenge (Mixed List)',
        duration: '4-5 min',
        focus: 'Decoding and Categorizing',
        researchBasis: 'Lonigan et al. (2000) - Nonsense word decoding strongly predicts reading achievement.',
        content: {
          goal: 'Read 12-15 mixed real and nonsense words, sorting them correctly.',
          whatToSay: [
            '"Time for detective work!"',
            '"I\'ll show you words — you read them!"',
            '"Then tell me: real or silly?"',
            '"Ready? First word!"'
          ],
          whatToDo: [
            'Present these mixed words one at a time:',
            '',
            '**Mixed Word List:**',
            '  1. SLIP (real)',
            '  2. BLOP (silly)',
            '  3. FROG (real)',
            '  4. SPIM (silly)',
            '  5. SNAP (real)',
            '  6. CRAB (real)',
            '  7. SNIB (silly)',
            '  8. STOP (real)',
            '  9. GLIB (silly)',
            '  10. DRUM (real)',
            '  11. FLIB (silly)',
            '  12. SWIM (real)',
            '  13. TROP (silly)',
            '  14. STEP (real)',
            '  15. CLUP (silly)',
            '',
            'Process for EACH word:',
            '',
            '  **Step 1: Show word**',
            '    Write it or display card',
            '',
            '  **Step 2: Child decodes**',
            '    Child sounds out and blends',
            '    "BLOP: /b/ /l/ /o/ /p/ ... BLOP!"',
            '',
            '  **Step 3: Categorize**',
            '    You: "Real or silly?"',
            '    Child decides',
            '',
            '  **Step 4: Confirm**',
            '    If correct: "Yes! BLOP is silly — great reading!"',
            '    If wrong: "Actually, SLIP is real — we say \'don\'t slip!\'"',
            '',
            '  **Step 5: Place in column**',
            '    Write word in appropriate column',
            '    Or place card in correct container',
            '',
            'Support strategies:',
            '  - If can\'t decode: Help sound it out',
            '  - If unsure real/silly: Give context clue',
            '    "Have you heard this word before?"',
            '    "Can you use it in a sentence?"',
            '',
            'Keep score:',
            '  "You read [X] words correctly!"',
            '  "You sorted [Y] correctly!"',
            '',
            'Celebrate both skills:',
            '  - Accurate decoding',
            '  - Correct categorization'
          ],
          examples: [
            'Child reads "SPIM." You: "Great reading! Is it real or silly?" Child: "Silly!" You: "Perfect detective work!"'
          ],
          watchFor: [
            'Accurate decoding of both real and nonsense.',
            'Correct categorization.',
            'Confidence with unfamiliar words.',
            'Understanding that both are readable.'
          ],
          parentTip: 'If they can read 10+ words accurately (real or silly), their phonics foundation is SOLID!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Make Your Own Silly Words',
        duration: '3-4 min',
        focus: 'Creative Word Building',
        researchBasis: 'Ehri (2001) - Creating words strengthens phonological awareness and encoding skills.',
        content: {
          goal: 'Child creates their own nonsense words using correct blend patterns.',
          whatToSay: [
            '"Now YOU make silly words!"',
            '"Use blend rules to create made-up words!"',
            '"Make them silly but readable!"',
            '"Let\'s invent some words!"'
          ],
          whatToDo: [
            'Child creates 4-6 nonsense words.',
            '',
            'Provide structure:',
            '  "Pick a blend: SL, SP, CR, FR, etc."',
            '  "Add a vowel: A, E, I, O, U"',
            '  "Add an ending sound: P, T, M, G, etc."',
            '',
            'Examples to inspire:',
            '  - SNUP (SN + u + p)',
            '  - FLIB (FL + i + b)',
            '  - SPOG (SP + o + g)',
            '  - CREM (CR + e + m)',
            '',
            'Child\'s creation process:',
            '  1. Child chooses blend',
            '  2. Child adds middle/ending',
            '  3. Child writes silly word',
            '  4. Child reads it aloud',
            '  5. You celebrate!',
            '',
            'Make it playful:',
            '  "What would a SNUP look like?"',
            '  "If FLIB was a food, what would it taste like?"',
            '  "Let\'s draw a picture of a SPOG!"',
            '',
            'Challenge extensions:',
            '  - "Use your silly word in a sentence!"',
            '    "The SNUP went to the park."',
            '  - "Make a silly word with TWO blends!"',
            '    "CLIPSNAP"',
            '',
            'Create a "Silly Word Dictionary":',
            '  List all invented words',
            '  Add definitions child creates',
            '  Draw pictures',
            '',
            'Celebrate creativity:',
            '  "You invented NEW words!"',
            '  "And they all follow phonics rules!"',
            '  "You understand how English works!"'
          ],
          examples: [
            'Child writes "BROP." You: "BROP! That\'s a great silly word! What does a BROP do?" Child: "It hops!"'
          ],
          watchFor: [
            'Correct phonics patterns.',
            'Readable nonsense words.',
            'Creativity and playfulness.',
            'Understanding of word structure.'
          ],
          parentTip: 'Creating nonsense words is ADVANCED! It shows true mastery of phonics rules!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Silly Word Story',
        duration: '2-3 min',
        focus: 'Application and Fun',
        researchBasis: 'Whitehurst & Lonigan (1998) - Playful language activities build metalinguistic awareness.',
        content: {
          goal: 'Create a short silly story using invented nonsense words.',
          whatToSay: [
            '"Let\'s use our silly words in a story!"',
            '"We\'ll make the silliest story ever!"',
            '"It won\'t make sense — that\'s the fun!"'
          ],
          whatToDo: [
            'Create a 3-4 sentence silly story together.',
            '',
            'Use child\'s invented words:',
            '  If child created: SNUP, FLIB, SPOG, BROP',
            '',
            'Story template:',
            '  "Once there was a [silly word]."',
            '  "The [silly word] liked to [action]."',
            '  "Then it met a [silly word]."',
            '  "They lived happily ever after!"',
            '',
            'Example story:',
            '  "Once there was a SNUP."',
            '  "The SNUP liked to FLIB around."',
            '  "Then it met a SPOG."',
            '  "They became best friends!"',
            '',
            'Build story together:',
            '  - You provide structure',
            '  - Child fills in silly words',
            '  - Both add creative details',
            '',
            'Read story aloud together:',
            '  - Use silly voices',
            '  - Act it out',
            '  - Laugh together!',
            '',
            'Optional additions:',
            '  - Draw pictures for the story',
            '  - Add more silly words from today\'s lesson',
            '  - Create a title: "The SNUP\'s Adventure"',
            '',
            'Reflection:',
            '  "Even silly words can tell stories!"',
            '  "You can READ any word if you know the rules!"',
            '',
            'Save the silly story:',
            '  Share with family for laughs',
            '  Add to portfolio',
            '  Create more silly stories later'
          ],
          examples: [
            'You: "Once there was a..." Child: "BROP!" You: "The BROP liked to..." Child: "SNUP around!" Everyone laughs.'
          ],
          watchFor: [
            'Creative use of nonsense words.',
            'Understanding of story structure.',
            'Enjoyment and laughter.',
            'Confident reading of silly words.'
          ],
          parentTip: 'Silly = memorable! They\'ll remember this lesson because it was FUN!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🕵️ You\'re a Word Detective!',
            'You practiced:',
            '✓ Understanding real vs. silly words',
            '✓ Reading and sorting 15 mixed words',
            '✓ Creating your own silly words',
            '✓ Writing a silly word story'
          ],
          reviewQuestions: [
            'Could your child decode nonsense words accurately?',
            'Did they correctly identify real vs. silly?',
            'Were they able to create readable nonsense words?',
            'Did they enjoy the silly story activity?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Nonsense word reading is the ultimate test of phonics mastery! When children can decode made-up words they\'ve never seen, they\'re proving they understand the SYSTEM, not just memorizing words. This skill directly predicts reading success because every new word a child encounters is essentially a "nonsense word" until they decode it the first time. The ability to apply phonics rules flexibly to any letter combination is the foundation of independent reading!'
          },
          nextSteps: [
            'Play "Real or Silly" with words in books!',
            'Create a family "Silly Word Dictionary."',
            'Invent silly words for household objects.',
            'Tomorrow: Decoding Path Game!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 38 =========
  {
    id: 38,
    phase: '4',
    quarter: 1,
    week: 8,
    weekIndex: 7,
    dayInWeek: 3,
    title: 'Lesson 38: Decoding Path Game',
    theme: 'Spiral Review: CVC + Blends',
    skillRecurrenceTags: ['spiral-review', 'game-based-learning', 'decoding-fluency', 'movement'],
    relatedToPreviousSkills: ['cvc-decoding', 'consonant-blends', 'reading-fluency'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Decoding Path Game!',
        duration: '1 min',
        content: {
          goal: 'Follow a visual path of words, reading each one to advance, combining reading with movement and play.',
          parentGuide: [
            'Games make practice feel like play, not work!',
            'Movement + reading = multi-sensory learning.',
            'Paths provide visual tracking practice.',
            'Success feels tangible — you literally move forward!'
          ],
          materials: ['Paper', 'Pencil or markers', 'Small token (coin, button, toy) to move along path', 'Optional: dice or number cards'],
          tip: 'Make the path visually appealing! Add decorations, colors, fun destinations.',
          researchNote: 'Hirsh-Pasek et al. (2017) - Game-based learning increases engagement and supports skill development through joyful practice.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Build the Decoding Path',
        duration: '3 min',
        focus: 'Game Setup',
        researchBasis: 'Whitehurst & Lonigan (1998) - Visual organization of reading activities supports engagement and success.',
        content: {
          goal: 'Create a game board path with 15-20 word "stepping stones."',
          whatToSay: [
            '"We\'re making a reading game!"',
            '"Each word is a stepping stone on a path!"',
            '"Read a word, move forward!"',
            '"Let\'s build it together!"'
          ],
          whatToDo: [
            'Draw a winding path on paper:',
            '  - Start at bottom (mark "START")',
            '  - Winding path up the page',
            '  - End at top (mark "FINISH" or "TREASURE!")',
            '',
            'Create 15-20 stepping stones (circles/boxes) along path.',
            '',
            'Fill stones with mixed CVC and blend words:',
            '',
            '**Word Path (15-20 words):**',
            '  START',
            '  1. CAT',
            '  2. SLIP',
            '  3. DOG',
            '  4. STOP',
            '  5. BED',
            '  6. FROG',
            '  7. SUN',
            '  8. SNAP',
            '  9. PIG',
            '  10. CRAB',
            '  11. CUP',
            '  12. SWIM',
            '  13. BUS',
            '  14. DRUM',
            '  15. HAT',
            '  16. STEP',
            '  17. FAN',
            '  18. SPIN',
            '  19. NET',
            '  20. CLIP',
            '  FINISH!',
            '',
            'Make it visually appealing:',
            '  - Color the path',
            '  - Draw scenery (trees, flowers, clouds)',
            '  - Add a "treasure" at finish (star, prize)',
            '  - Decorate stepping stones',
            '',
            'Optional additions:',
            '  - Special spaces: "Read twice!" or "Jump ahead!"',
            '  - Challenge spaces: "Use word in sentence!"',
            '  - Silly spaces: "Read in funny voice!"',
            '',
            'Get a game token:',
            '  Small object child can move along path',
            '  Button, coin, small toy, drawn character',
            '',
            'Explain the rules:',
            '  "Read the word on the stone."',
            '  "If correct, move your token forward!"',
            '  "If you need help, we\'ll work together!"',
            '  "Goal: reach the finish!"'
          ],
          examples: [
            'You draw a winding path with words. You: "Each word you read moves you closer to the treasure!"'
          ],
          watchFor: [
            'Excitement about the game.',
            'Understanding the rules.',
            'Visual tracking of the path.'
          ],
          parentTip: 'The more appealing the game board, the more engaged they\'ll be! This is worth the setup time.'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Play the Path Game',
        duration: '5-6 min',
        focus: 'Active Reading Play',
        researchBasis: 'National Reading Panel (2000) - Repeated reading practice in engaging formats builds fluency.',
        content: {
          goal: 'Move along path by successfully reading each word.',
          whatToSay: [
            '"Ready to play?"',
            '"Place your token at START!"',
            '"Read the first word to move forward!"',
            '"Let\'s begin!"'
          ],
          whatToDo: [
            'Child places token at START.',
            '',
            'Gameplay:',
            '',
            '  **Turn sequence:**',
            '    1. Child reads word on current stone',
            '    2. If correct: Move token to that stone',
            '    3. Celebrate: "Great reading!"',
            '    4. Continue to next word',
            '    5. If incorrect: Help decode, try again',
            '',
            'Support strategies:',
            '  - If stuck: "Let\'s sound it out together"',
            '  - If wrong: "Try again — look at all the letters"',
            '  - If frustrated: "We can skip this one and come back"',
            '',
            'Keep it moving:',
            '  - Encourage steady pace',
            '  - Celebrate every successful read',
            '  - Make sound effects for movement',
            '  - "Hop! You made it to FROG!"',
            '',
            'Optional variations:',
            '',
            '  **With dice:**',
            '    Roll dice to see how many stones to attempt',
            '    Read that many words to move forward',
            '',
            '  **Challenge mode:**',
            '    At certain stones, complete extra task:',
            '    - Use word in sentence',
            '    - Spell word aloud',
            '    - Act out the word',
            '',
            '  **Speed mode:**',
            '    Time how long it takes to complete path',
            '    Try to beat time on second playthrough',
            '',
            '  **Cooperative mode:**',
            '    Parent and child take turns reading',
            '    Work together to reach finish',
            '',
            'Track progress visually:',
            '  Child sees token moving up the path',
            '  Tangible sense of advancement',
            '',
            'Celebrate reaching milestones:',
            '  "Halfway there!"',
            '  "Only 5 more words!"',
            '  "Almost at the treasure!"'
          ],
          examples: [
            'Child reads "SLIP." Moves token. You: "Perfect! SLIP gets you to the next stone!" Child smiles.'
          ],
          watchFor: [
            'Accurate word reading.',
            'Engagement with game.',
            'Persistence through challenges.',
            'Celebration of progress.'
          ],
          parentTip: 'The game format makes "failures" feel like part of play, not mistakes! This reduces anxiety.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Speed Round Replay',
        duration: '3-4 min',
        focus: 'Fluency Building',
        researchBasis: 'Lonigan et al. (2000) - Repeated reading of the same material increases automaticity and fluency.',
        content: {
          goal: 'Play the path game again, aiming for faster, smoother reading.',
          whatToSay: [
            '"You made it to the finish!"',
            '"Want to play again FASTER?"',
            '"This time, zoom through the path!"',
            '"Ready for Speed Round?"'
          ],
          whatToDo: [
            'Reset token to START.',
            '',
            'Speed Round rules:',
            '  "This time, read as fast as you can!"',
            '  "But still get them RIGHT!"',
            '  "Let\'s see if you can beat your time!"',
            '',
            'Optional timing:',
            '  If you timed first round, time this one',
            '  "Can you finish in under 2 minutes?"',
            '',
            'Child plays through path again:',
            '  - Encourage faster pace',
            '  - Words should be easier (second exposure)',
            '  - Celebrate smoother reading',
            '',
            'Notice improvements:',
            '  "You didn\'t hesitate on CRAB this time!"',
            '  "Your blending is so smooth now!"',
            '  "Look how fast you\'re reading!"',
            '',
            'Compare rounds:',
            '  "First time: you needed help on 3 words."',
            '  "Second time: you read them all independently!"',
            '  "That\'s GROWTH!"',
            '',
            'Optional third round variations:',
            '',
            '  **Silly Voice Round:**',
            '    Read all words in funny voices',
            '    (Still practicing, but playfully!)',
            '',
            '  **Backwards Round:**',
            '    Start at FINISH, read backwards to START',
            '    Different challenge!',
            '',
            '  **Memory Round:**',
            '    Cover words after reading',
            '    Try to remember what comes next',
            '',
            'Celebrate completion:',
            '  "You\'ve completed the path [X] times!"',
            '  "You\'re a Decoding Path Champion!"'
          ],
          examples: [
            'Child zips through path in 90 seconds. You: "Wow! 30 seconds faster! You\'re a speed reader!"'
          ],
          watchFor: [
            'Improved speed from first round.',
            'Maintained accuracy.',
            'Confidence and automaticity.',
            'Willingness to replay.'
          ],
          parentTip: 'Second reading is ALWAYS easier! This shows them their own progress in real-time.'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Create Your Own Path',
        duration: '2-3 min',
        focus: 'Extension and Ownership',
        researchBasis: 'Ehri (2001) - Student-created materials increase investment and provide encoding practice.',
        content: {
          goal: 'Child creates their own mini decoding path with chosen words.',
          whatToSay: [
            '"Now YOU design a path!"',
            '"Pick 8-10 words for your path!"',
            '"Make it fun for someone else to play!"',
            '"What words will you choose?"'
          ],
          whatToDo: [
            'Child creates a shorter path (8-10 words).',
            '',
            'Setup:',
            '  - Child draws simple path',
            '  - Child chooses words to include',
            '  - Child writes words in stones',
            '',
            'Word selection:',
            '  "Pick your favorite words from today!"',
            '  "Or choose new blend words you know!"',
            '  Mix of CVC and blends',
            '',
            'Suggested words child might choose:',
            '  CAT, FROG, STOP, SWIM, BED,',
            '  SLIP, DRUM, SNAP, DOG, CLIP',
            '',
            'Creative additions child might add:',
            '  - Decorations',
            '  - Special challenge spaces',
            '  - Silly instructions',
            '  - A theme (space path, ocean path, forest path)',
            '',
            'After creating:',
            '  YOU play child\'s path!',
            '  Let child be the game master',
            '  Child confirms if you read correctly',
            '  Reverse roles = empowering!',
            '',
            'Process:',
            '  1. You read word on child\'s path',
            '  2. Child says if correct',
            '  3. Child moves your token',
            '  4. Celebrate when you finish!',
            '',
            'This role reversal:',
            '  - Builds confidence',
            '  - Reinforces word recognition',
            '  - Shows they\'re the expert',
            '  - Makes reading collaborative',
            '',
            'Save both paths:',
            '  Play again another day',
            '  Create path collection',
            '  Share with siblings/family'
          ],
          examples: [
            'Child creates path with FROG, SLIP, STOP. You play it. You: "Did I read SLIP right?" Child: "Yes! Move ahead!"'
          ],
          watchFor: [
            'Thoughtful word selection.',
            'Correct spelling of chosen words.',
            'Creativity in path design.',
            'Confidence as "game master."'
          ],
          parentTip: 'Letting them teach YOU is powerful! It shows they\'ve internalized the skills.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎮 You\'re a Decoding Path Champion!',
            'You practiced:',
            '✓ Building a decoding path game',
            '✓ Reading 15-20 words to complete path',
            '✓ Playing speed round for fluency',
            '✓ Creating your own path game'
          ],
          reviewQuestions: [
            'Could your child complete the full path?',
            'Did their speed improve in round 2?',
            'Were they able to create their own path?',
            'Did they enjoy the game format?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Game-based learning transforms practice from tedious drill to joyful play. The path format provides visual progress tracking, which is motivating and concrete — children can literally see themselves advancing. Movement engages motor memory alongside reading practice. The repeated exposure (playing multiple rounds) builds automaticity without feeling repetitive. Most importantly, creating their own path shifts them from consumer to creator, building ownership and metacognitive awareness of their reading skills!'
          },
          nextSteps: [
            'Create new paths with different word types!',
            'Make themed paths (animal words, action words).',
            'Challenge family members to play your paths.',
            'Tomorrow: Story Reconstruction challenge!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 39 =========
  {
    id: 39,
    phase: '4',
    quarter: 1,
    week: 8,
    weekIndex: 7,
    dayInWeek: 4,
    title: 'Lesson 39: Story Reconstruction',
    theme: 'Spiral Review: CVC + Blends',
    skillRecurrenceTags: ['spiral-review', 'reading-comprehension', 'sequencing', 'creative-thinking'],
    relatedToPreviousSkills: ['cvc-decoding', 'consonant-blends', 'story-structure', 'word-recognition'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Story Reconstruction!',
        duration: '1 min',
        content: {
          goal: 'Decode 6 mixed words and build a micro-story from them, combining decoding with creative storytelling.',
          parentGuide: [
            'Reading + storytelling = comprehension practice!',
            'Children learn words have meaning AND fit into narratives.',
            'Story building develops sequencing and logic skills.',
            'Keep it playful—silly stories are perfect!'
          ],
          materials: ['Paper', 'Pencil or markers'],
          tip: 'The story doesn\'t need to be "good"—it just needs to make sense to your child!',
          researchNote: 'Whitehurst & Lonigan (1998) - Narrative skills and decoding together build true reading comprehension.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Read the Mystery Words',
        duration: '2-3 min',
        focus: 'Decoding Practice',
        researchBasis: 'National Reading Panel (2000) - Contextual word practice strengthens both decoding and meaning-making.',
        content: {
          goal: 'Read 6 words that will become story ingredients.',
          whatToSay: [
            '"We have 6 mystery words today!"',
            '"Read each one carefully!"',
            '"Then we\'ll build a story together!"',
            '"First word!"'
          ],
          whatToDo: [
            'Write these 6 words on paper (or say them one at a time):',
            '',
            '**Story Word Set:**',
            '  1. FROG',
            '  2. JUMP',
            '  3. POND',
            '  4. SLIP',
            '  5. MUD',
            '  6. PLOP',
            '',
            'Child reads each word:',
            '  Point to word',
            '  Child decodes: "FROG: /f/ /r/ /o/ /g/ ... FROG!"',
            '  You confirm: "Yes! FROG!"',
            '  Quick meaning check: "What\'s a frog?"',
            '  Move to next word',
            '',
            'Support as needed:',
            '  If stuck: "Let\'s sound it out together"',
            '  If wrong: "Try again—look at all the letters"',
            '  Celebrate each success!',
            '',
            'After all 6 words are read:',
            '  "Great reading! Now let\'s use them in a story!"',
            '  Keep all words visible for reference'
          ],
          examples: [
            'Child reads "PLOP." You: "Perfect! What sound does PLOP make?" Child: "Splash!" You: "Yes!"'
          ],
          watchFor: [
            'Accurate decoding of all word types.',
            'Understanding word meanings.',
            'Engagement and confidence.'
          ],
          parentTip: 'These 6 words naturally suggest a frog story—it will flow easily!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Build the Story Together',
        duration: '4-5 min',
        focus: 'Collaborative Storytelling',
        researchBasis: 'Bruner (1983) - Scaffolded narrative construction builds both language and cognitive organization.',
        content: {
          goal: 'Create a simple story using the 6 decoded words.',
          whatToSay: [
            '"Let\'s tell a story with our words!"',
            '"I\'ll start sentences, you fill in the words!"',
            '"Ready? Once upon a time..."'
          ],
          whatToDo: [
            'Build story together—YOU provide structure, CHILD adds words.',
            '',
            'Story building process:',
            '',
            '  You: "Once there was a..."',
            '  Child: "FROG!"',
            '',
            '  You: "The frog lived in a..."',
            '  Child: "POND!"',
            '',
            '  You: "One day, the frog wanted to..."',
            '  Child: "JUMP!"',
            '',
            '  You: "But when it jumped, it..."',
            '  Child: "SLIP!"',
            '',
            '  You: "The frog slipped into the..."',
            '  Child: "MUD!"',
            '',
            '  You: "It fell with a big..."',
            '  Child: "PLOP!"',
            '',
            '  You: "The end!"',
            '',
            '**Complete story:**',
            '  "Once there was a FROG. The frog lived in a POND.',
            '  One day, the frog wanted to JUMP. But when it jumped,',
            '  it SLIP! The frog slipped into the MUD. It fell with',
            '  a big PLOP! The end!"',
            '',
            'As you build:',
            '  - Keep pace moving',
            '  - Accept their word choices',
            '  - Add connecting words naturally',
            '  - Use animated voice!',
            '',
            'After completing:',
            '  "Let\'s read the whole story together!"',
            '  Read it aloud one more time',
            '  Child says the decoded words',
            '  You provide the connecting words',
            '',
            'Ask comprehension questions:',
            '  "What happened first?"',
            '  "Why did the frog fall?"',
            '  "How did the story end?"'
          ],
          examples: [
            'Child says "JUMP!" You: "Yes! The frog wanted to JUMP! What happened next?"'
          ],
          watchFor: [
            'Logical sequencing.',
            'Using words in context.',
            'Understanding cause and effect.',
            'Engagement in storytelling.'
          ],
          parentTip: 'If child wants to change the order, let them! Their logic is valid.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Draw the Story',
        duration: '3-4 min',
        focus: 'Visual Connection',
        researchBasis: 'Scarborough (2001) - Multiple modes of story representation strengthen comprehension.',
        content: {
          goal: 'Draw one picture showing the story and label it with key words.',
          whatToSay: [
            '"Let\'s draw our frog story!"',
            '"Show what happened!"',
            '"Then we\'ll label it!"'
          ],
          whatToDo: [
            'Draw ONE scene from the story:',
            '',
            '  "Draw the frog jumping and falling—PLOP!"',
            '  Child draws:',
            '    - Frog in the air (jumping)',
            '    - Mud below',
            '    - Splash lines for PLOP',
            '    - Pond nearby',
            '',
            'Keep drawing simple:',
            '  Stick figure frog is perfect!',
            '  Quick sketch, not artwork',
            '  5 minutes max on drawing',
            '',
            'Add word labels:',
            '  After drawing, child writes 3-4 key words:',
            '  - FROG (with arrow to frog)',
            '  - MUD (with arrow to mud)',
            '  - POND (with arrow to water)',
            '  - PLOP! (near splash)',
            '',
            'Or YOU write, child tells you where:',
            '  "Where should I write FROG?"',
            '  Child points, you write',
            '',
            'While drawing, retell:',
            '  "Tell me what\'s happening in your picture!"',
            '  Child retells story using the image',
            '  Reinforces narrative memory',
            '',
            'Celebrate:',
            '  "You created a story from 6 words!"',
            '  "You\'re a storyteller AND artist!"',
            '  "Let\'s show this to [family member]!"'
          ],
          examples: [
            'Child draws frog. You: "Perfect! Where should we write FROG?" Child points. You write it together.'
          ],
          watchFor: [
            'Picture matches story events.',
            'Correct labeling with decoded words.',
            'Ability to retell using image.',
            'Pride in creation.'
          ],
          parentTip: 'Simple drawings are best! This isn\'t art class—it\'s reading comprehension!'
        }
      },
      {
        id: 4,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '📖 You\'re a Story Builder!',
            'You practiced:',
            '✓ Decoding 6 mixed CVC and blend words',
            '✓ Sequencing events logically',
            '✓ Creating a complete narrative',
            '✓ Connecting words to pictures'
          ],
          reviewQuestions: [
            'Could your child decode all 6 words?',
            'Did they understand story sequence?',
            'Were they able to retell the story?',
            'Did they enjoy the creative process?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Story reconstruction proves reading is about MEANING, not just sounds. When children take isolated words and weave them into coherent narratives, they\'re demonstrating true comprehension—they understand that words communicate ideas and that reading is about making sense. This cognitive work—sequencing, cause-effect, narrative structure—is the bridge from decoding to actual reading!'
          },
          nextSteps: [
            'Retell the frog story before bed tonight!',
            'Tomorrow we\'ll try new words for a different story.',
            'Tomorrow: Blend Bash celebration!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 40 =========
  {
    id: 40,
    phase: '4',
    quarter: 1,
    week: 8,
    weekIndex: 7,
    dayInWeek: 5,
    title: 'Lesson 40: Blend Bash Review',
    theme: 'Spiral Review: CVC + Blends',
    skillRecurrenceTags: ['spiral-review', 'celebration', 'fluency-building', 'mastery-check'],
    relatedToPreviousSkills: ['cvc-decoding', 'consonant-blends', 'l-blends', 'r-blends', 's-blends', 'reading-fluency'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Blend Bash Review!',
        duration: '1 min',
        content: {
          goal: 'Celebrate mastery of CVC words and consonant blends through quick-fire review.',
          parentGuide: [
            'This is CELEBRATION, not testing!',
            'Focus on what they CAN do!',
            'Keep energy high and positive!',
            'This shows them how much they\'ve learned!'
          ],
          materials: ['Paper', 'Pencil or markers'],
          tip: 'Be enthusiastic! Your energy makes this feel like a party, not a test!',
          researchNote: 'Diamond (1990) - Positive review experiences consolidate learning and build reading identity.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Blend Family Chant',
        duration: '1-2 min',
        focus: 'Quick Warm-Up',
        researchBasis: 'National Reading Panel (2000) - Rapid review builds automaticity.',
        content: {
          goal: 'Quickly chant all three blend families with energy.',
          whatToSay: [
            '"You\'re a Blend Champion!"',
            '"Let\'s shout out all the blends you know!"',
            '"Say them LOUD with me!"',
            '"Ready? Go!"'
          ],
          whatToDo: [
            'Chant blend families together with big energy:',
            '',
            '  You: "L-blends!"',
            '  Together: "BL! CL! FL! GL! PL! SL!"',
            '  You: "Awesome!"',
            '',
            '  You: "R-blends!"',
            '  Together: "BR! CR! DR! FR! GR! PR! TR!"',
            '  You: "Powerful!"',
            '',
            '  You: "S-blends!"',
            '  Together: "SN! SP! ST! SW!"',
            '  You: "You know them ALL!"',
            '',
            'Add movement:',
            '  Clap or stomp for each blend',
            '  Punch fists in air',
            '  March in place',
            '',
            'Quick word check:',
            '  "Give me a word with BL!"',
            '  Child: "BLOB!"',
            '  "Give me a word with TR!"',
            '  Child: "TRIP!"',
            '  "You\'re a blend expert!"'
          ],
          examples: [
            'You punch air: "FL!" Child copies and shouts: "FL!" Big smiles!'
          ],
          watchFor: [
            'Confident recall.',
            'Enthusiasm.',
            'Accurate blend sounds.'
          ],
          parentTip: 'Energy is contagious! Your excitement fuels theirs!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Speed Reading Challenge',
        duration: '4-5 min',
        focus: 'Fluency Practice',
        researchBasis: 'Ehri (2020) - Timed reading builds automaticity when framed playfully.',
        content: {
          goal: 'Read 12 mixed blend words quickly and accurately.',
          whatToSay: [
            '"Ready for a reading challenge?"',
            '"Read these words as fast as you can!"',
            '"But get them RIGHT too!"',
            '"Let\'s GO!"'
          ],
          whatToDo: [
            'Write these 12 words on paper in a list:',
            '',
            '**Speed Challenge Words:**',
            '  1. SLIP',
            '  2. FROG',
            '  3. STOP',
            '  4. SNAP',
            '  5. DRUM',
            '  6. STEP',
            '  7. CLIP',
            '  8. SWIM',
            '  9. FLAG',
            '  10. TRAP',
            '  11. SPIN',
            '  12. SLED',
            '',
            '**Round 1: Careful Reading**',
            '  "Read each word clearly."',
            '  Point to words one at a time',
            '  Child reads down the list',
            '  Help if needed',
            '  Celebrate each one!',
            '',
            'Track progress:',
            '  Mental note of which words were easy/hard',
            '  "You got 10 right away! Amazing!"',
            '',
            '**Round 2: Speed Round**',
            '  "Now let\'s read them FAST!"',
            '  "Start at the top—GO!"',
            '  Child reads through again',
            '  Faster pace this time',
            '',
            'Compare rounds:',
            '  "Round 1: You needed help on 2 words"',
            '  "Round 2: You read them all smoothly!"',
            '  "That\'s GROWTH!"',
            '',
            'Celebrate milestones:',
            '  After 6 words: "Halfway! Keep going!"',
            '  After 12 words: "YOU DID IT! All 12!"',
            '',
            'If child struggles:',
            '  Help sound out together',
            '  "What\'s the blend?"',
            '  "Now add the ending!"',
            '  No pressure—just practice!'
          ],
          examples: [
            'Child reads "FROG" instantly. You: "Lightning fast! Next!" Child grins.'
          ],
          watchFor: [
            'Improved speed in round 2.',
            'Accurate decoding.',
            'Confidence with all blend types.',
            'Smooth blending (not choppy).'
          ],
          parentTip: 'Second reading is always easier—this shows them their own progress!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Blend Sort Game',
        duration: '3-4 min',
        focus: 'Categorization',
        researchBasis: 'Anthony & Francis (2005) - Phonological categorization demonstrates deep pattern understanding.',
        content: {
          goal: 'Sort 9 words by blend family (L, R, or S).',
          whatToSay: [
            '"Now let\'s sort words by their blend families!"',
            '"Which words have L-blends?"',
            '"Which have R-blends?"',
            '"Which have S-blends?"'
          ],
          whatToDo: [
            'Draw three columns on paper:',
            '  **L-BLENDS | R-BLENDS | S-BLENDS**',
            '',
            'Present these 9 words to sort:',
            '  1. FLAG',
            '  2. CRAB',
            '  3. SNAP',
            '  4. CLIP',
            '  5. DRUM',
            '  6. STOP',
            '  7. GLAD',
            '  8. TRIP',
            '  9. SWIM',
            '',
            'Sorting process:',
            '',
            '  Show word: "FLAG"',
            '  Child reads: "FLAG!"',
            '  You ask: "What\'s the blend?"',
            '  Child: "FL!"',
            '  You ask: "Which family?"',
            '  Child: "L-blend!"',
            '  Child writes it in L-column (or you write it)',
            '',
            'Continue with all 9 words.',
            '',
            'Correct placement:',
            '  **L-BLENDS:** FLAG, CLIP, GLAD',
            '  **R-BLENDS:** CRAB, DRUM, TRIP',
            '  **S-BLENDS:** SNAP, STOP, SWIM',
            '',
            'After sorting:',
            '  "Let\'s count! How many L-blends? Three!"',
            '  "How many R-blends? Three!"',
            '  "How many S-blends? Three!"',
            '  "You sorted them perfectly!"',
            '',
            'Extension:',
            '  "Can you think of MORE words for each family?"',
            '  Child adds 1-2 more to each column:',
            '    L: SLED, BLUE',
            '    R: FROG, GRAB',
            '    S: SPIN, SKIP'
          ],
          examples: [
            'Child: "CRAB has CR!" You: "Yes! That\'s an R-blend! Where does it go?"'
          ],
          watchFor: [
            'Accurate blend identification.',
            'Correct categorization.',
            'Understanding of blend families.',
            'Ability to generate examples.'
          ],
          parentTip: 'Sorting shows they understand the SYSTEM, not just individual words!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Champion Celebration',
        duration: '2-3 min',
        focus: 'Recognition',
        researchBasis: 'Hirsh-Pasek et al. (2017) - Celebration of achievement builds intrinsic motivation.',
        content: {
          goal: 'Celebrate blend mastery with reflection and a simple certificate.',
          whatToSay: [
            '"You\'re officially a Blend Champion!"',
            '"Look at everything you can read now!"',
            '"Let\'s celebrate!"'
          ],
          whatToDo: [
            'Create simple celebration certificate together:',
            '',
            '  Draw a box/border on paper',
            '  Write (or child writes):',
            '',
            '  ═══════════════════',
            '  🌟 BLEND CHAMPION! 🌟',
            '  ═══════════════════',
            '',
            '  [Child\'s name]',
            '',
            '  Can read:',
            '  ✓ L-Blends',
            '  ✓ R-Blends',
            '  ✓ S-Blends',
            '  ✓ All CVC Words!',
            '',
            '  Date: [Today]',
            '  ═══════════════════',
            '',
            'Decorate together:',
            '  Add stars, colors, stickers',
            '  Make it special!',
            '',
            'Reflection questions:',
            '  "What was your favorite blend to learn?"',
            '  "Which words were trickiest?"',
            '  "What do you want to read now?"',
            '',
            'Celebrate growth:',
            '  "Remember Week 1? You learned SO much!"',
            '  "Now you can read blend words everywhere!"',
            '  "You\'re ready for even more reading!"',
            '',
            'Display certificate:',
            '  Hang on wall or fridge',
            '  Show family members',
            '  Take a picture!',
            '',
            'Look ahead:',
            '  "Next week we learn word families!"',
            '  "You\'re going to be amazed at how much you can read!"'
          ],
          examples: [
            'Child writes their name on certificate. You: "You EARNED this! You\'re a reading champion!"'
          ],
          watchFor: [
            'Pride in accomplishment.',
            'Reflection on learning journey.',
            'Excitement for next steps.',
            'Confidence as a reader.'
          ],
          parentTip: 'Display the certificate proudly! This validates their hard work!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎉 You\'re a Blend Champion!',
            'You practiced:',
            '✓ Reviewing all blend families',
            '✓ Reading 12 words for speed and accuracy',
            '✓ Sorting words by blend type',
            '✓ Celebrating your amazing progress!'
          ],
          reviewQuestions: [
            'Could your child read most words smoothly?',
            'Did they correctly sort by blend families?',
            'Were they proud of their progress?',
            'Are they excited for word families next week?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'This review week consolidates 8 weeks of learning! Your child has progressed from simple CVC words to complex consonant blends—a MASSIVE achievement. The ability to read, sort, and manipulate blend words shows true phonics mastery. They\'re not memorizing—they\'re applying rules systematically. This foundation prepares them for word families, sight words, and eventually independent reading. Celebrate this milestone—they\'ve earned it!'
          },
          nextSteps: [
            'Hang the Blend Champion certificate proudly!',
            'Point out blend words in books this weekend.',
            'Monday: Word families begin (-at, -an, -ap)!',
            'Keep celebrating every reading win!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 41 =========
  {
    id: 41,
    phase: '4',
    quarter: 1,
    week: 9,
    weekIndex: 8,
    dayInWeek: 1,
    title: 'Lesson 41: Meet the _AT Family',
    theme: 'Word Families: -at, -an, -ap',
    skillRecurrenceTags: ['word-families', 'rime-patterns', 'decoding-by-analogy'],
    relatedToPreviousSkills: ['cvc-decoding', 'consonant-blends', 'phoneme-manipulation'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to the _AT Family!',
        duration: '1 min',
        content: {
          goal: 'Introduce word families (onset-rime) as a powerful decoding strategy, starting with the -at family.',
          parentGuide: [
            'Word families teach children to decode by ANALOGY — a major breakthrough!',
            'If they know "cat," they can instantly read "bat," "hat," "mat," etc.',
            'This is MORE efficient than sound-by-sound decoding.',
            'Onset = beginning sound(s); Rime = ending chunk (-at, -an, -ap)',
            'This week builds on ALL previous CVC and blend work!'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Index cards or small papers (optional)'],
          tip: 'Emphasize that the -AT chunk STAYS THE SAME — only the beginning changes!',
          researchNote: 'Ehri (2001) - Reading by analogy (using word families/rimes) accelerates word recognition and reduces cognitive load compared to phoneme-by-phoneme decoding.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Discover the Pattern',
        duration: '3-4 min',
        focus: 'Pattern Recognition',
        researchBasis: 'National Reading Panel (2000) - Teaching onset-rime patterns is an effective phonics approach that supports decoding development.',
        content: {
          goal: 'Help child discover that changing the beginning sound creates new words while the ending stays the same.',
          whatToSay: [
            '"Today we are meeting word FAMILIES!"',
            '"Word families are like real families — they all share something!"',
            '"Look at these words. What do they have in common?"',
            '"Let me show you something amazing!"'
          ],
          whatToDo: [
            'Write these words vertically on paper:',
            '  CAT',
            '  HAT',
            '  MAT',
            '  RAT',
            '',
            'Point to each word:',
            '  "Read this word." Child: "CAT"',
            '  "Read this word." Child: "HAT"',
            '  "Read this word." Child: "MAT"',
            '  "Read this word." Child: "RAT"',
            '',
            'Draw attention to the pattern:',
            '  Use different color marker for -AT in each word',
            '  Circle or underline -AT in each',
            '  "Look! What is the same in ALL these words?"',
            '  Child notices: "AT!"',
            '',
            'Explain the discovery:',
            '  "Yes! They all end with -AT!"',
            '  "These words are in the same FAMILY!"',
            '  "They are the _AT FAMILY!"',
            '  "The _AT part stays the same."',
            '  "Only the BEGINNING changes!"',
            '',
            'Label the parts:',
            '  Write above words:',
            '  "Beginning Sound" | "Family Ending"',
            '  C | AT',
            '  H | AT',
            '  M | AT',
            '  R | AT',
            '',
            'Make the connection:',
            '  "If you know CAT..."',
            '  "You can read HAT, MAT, RAT, and MORE!"',
            '  "You do not have to sound out every letter!"',
            '  "Just change the beginning!"',
            '',
            'Quick practice:',
            '  You: "If this is CAT, what is THIS?" (point to HAT)',
            '  Child: "HAT!"',
            '  You: "You did not sound it out! You just changed C to H!"',
            '  "That is reading by FAMILY!"'
          ],
          examples: [
            'You: "What is the same in CAT and HAT?" Child: "AT!" You: "Yes! That is the family ending!"'
          ],
          watchFor: [
            'Recognition of shared -AT pattern.',
            'Understanding that beginning changes.',
            'Excitement about the pattern discovery.',
            'Beginning to read by analogy.'
          ],
          parentTip: 'This is a HUGE reading breakthrough! Word families make reading feel like magic!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Build the _AT Family',
        duration: '4-5 min',
        focus: 'Word Generation',
        researchBasis: 'Ehri (2020) - Active word building with onset-rime patterns strengthens orthographic mapping and spelling development.',
        content: {
          goal: 'Generate as many -AT family words as possible, including blend words.',
          whatToSay: [
            '"Let us find ALL the members of the _AT family!"',
            '"We will change the beginning to make new words!"',
            '"Some will be simple, some will have BLENDS!"',
            '"How many can we find?"'
          ],
          whatToDo: [
            'Create two columns on paper:',
            '  **Simple -AT Words | Blend -AT Words**',
            '',
            'Start with simple (single consonant):',
            '  "What letter could we put before -AT?"',
            '  Child suggests letters',
            '  Test each: "B-AT... BAT! Is that a word?"',
            '  If yes, write in Simple column',
            '  If no: "BIT is not a word, but good try!"',
            '',
            '**Simple -AT words to build:**',
            '  - BAT',
            '  - CAT',
            '  - FAT',
            '  - HAT',
            '  - MAT',
            '  - PAT',
            '  - RAT',
            '  - SAT',
            '  - VAT (might be new — a big container)',
            '',
            'Now add BLENDS:',
            '  "Remember our blends? Let us try them!"',
            '  "What if we put CH before -AT?"',
            '  "CHAT! That is a word!"',
            '',
            '**Blend -AT words to build:**',
            '  - BRAT (BR-blend)',
            '  - CHAT (CH-digraph)',
            '  - FLAT (FL-blend)',
            '  - SCAT (SC-blend)',
            '  - SLAT (SL-blend)',
            '  - SPAT (SP-blend)',
            '  - THAT (TH-digraph)',
            '',
            'Count total words:',
            '  "We found [X] simple words!"',
            '  "We found [Y] blend words!"',
            '  "That is [Z] total in the _AT family!"',
            '',
            'Celebrate the pattern:',
            '  "Look how MANY words you can read!"',
            '  "All because you know -AT!"',
            '  "Word families make reading FAST!"',
            '',
            'Test with nonsense:',
            '  "What if I put Z before -AT?"',
            '  "ZAT! Not a real word, but you can read it!"',
            '  "That proves you understand the pattern!"'
          ],
          examples: [
            'Child: "F-AT... FAT!" You write it. You: "Yes! FAT is in the family!" Keep building together.'
          ],
          watchFor: [
            'Child generating letter ideas.',
            'Recognizing real vs. nonsense words.',
            'Successfully building blend words.',
            'Excitement about word count.'
          ],
          parentTip: 'Let them suggest letters — even wrong guesses show thinking! "JAT is not a word, but you applied the pattern!"'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: _AT Family Speed Read',
        duration: '3-4 min',
        focus: 'Fluency Building',
        researchBasis: 'Lonigan et al. (2000) - Repeated reading of word families builds automatic word recognition through pattern exposure.',
        content: {
          goal: 'Read through all _AT family words quickly, demonstrating fluency through analogy.',
          whatToSay: [
            '"Now let us read the WHOLE family!"',
            '"Read them as fast as you can!"',
            '"Remember — you do not have to sound out AT every time!"',
            '"Just change the beginning!"'
          ],
          whatToDo: [
            'Arrange all _AT words in a list or grid:',
            '  BAT   CAT   FAT   HAT',
            '  MAT   PAT   RAT   SAT',
            '  BRAT  CHAT  FLAT  THAT',
            '',
            '**Round 1: Careful Reading**',
            '  "Read each word clearly."',
            '  Child reads through list',
            '  Help if needed',
            '  Emphasize pattern: "They all end the same!"',
            '',
            '**Round 2: Speed Reading**',
            '  "Now read them FAST!"',
            '  "Your brain already knows -AT!"',
            '  Child reads through again',
            '  Should be noticeably faster',
            '',
            'Point out efficiency:',
            '  "That was SO fast!"',
            '  "You read 12 words in [time]!"',
            '  "Sound-by-sound would take MUCH longer!"',
            '  "Word families make you a FAST reader!"',
            '',
            'Compare to earlier learning:',
            '  "Remember Week 1 when you read CAT?"',
            '  "You had to sound out C-A-T."',
            '  "Now you can read 12 words in the same time!"',
            '  "That is GROWTH!"',
            '',
            'Random order challenge:',
            '  Point to words randomly (not in order)',
            '  "Read THIS one! Now THIS one!"',
            '  Tests true recognition vs. memorized sequence',
            '',
            'Celebrate automaticity:',
            '  "You are not THINKING about it anymore!"',
            '  "Your brain just KNOWS -AT words!"',
            '  "That is called AUTOMATIC reading!"'
          ],
          examples: [
            'Child zips through: "BAT, CAT, FAT, HAT..." You: "Lightning speed! Word families make you FAST!"'
          ],
          watchFor: [
            'Increased speed from Round 1 to 2.',
            'No sounding out (reading by pattern).',
            'Accurate reading in random order.',
            'Confidence and automaticity.'
          ],
          parentTip: 'If they are still sounding out, that is okay! Pattern recognition takes time. Keep practicing!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Draw the _AT Family',
        duration: '2-3 min',
        focus: 'Meaning Connection',
        researchBasis: 'Scarborough (2001) - Connecting word recognition to meaning strengthens comprehension alongside decoding.',
        content: {
          goal: 'Draw pictures for 3-4 _AT family words, connecting reading to meaning.',
          whatToSay: [
            '"Let us draw some _AT family members!"',
            '"Pick your 3 favorites!"',
            '"Draw them and label them!"'
          ],
          whatToDo: [
            'Child chooses 3-4 _AT words to illustrate.',
            'Popular choices: CAT, HAT, BAT, RAT',
            '',
            'For EACH chosen word:',
            '  **Step 1: Draw picture**',
            '    Child draws simple illustration',
            '    CAT = simple cat drawing',
            '    HAT = hat on head',
            '    BAT = flying bat or baseball bat',
            '',
            '  **Step 2: Label with word**',
            '    Child writes the word below picture',
            '    Emphasize -AT ending: write AT in different color',
            '',
            '  **Step 3: Use in sentence (optional)**',
            '    "The CAT sat on a MAT."',
            '    "I wear a HAT."',
            '',
            'Create _AT Family Gallery:',
            '  Arrange drawings together',
            '  Title: "The _AT Family"',
            '  Display proudly!',
            '',
            'Extension activity:',
            '  "Can you draw a picture that shows TWO _AT words?"',
            '  Example: Cat wearing a hat',
            '  Example: Rat on a mat',
            '  This shows understanding of multiple words',
            '',
            'Celebrate the collection:',
            '  "Look at your _AT family portrait!"',
            '  "You can read AND draw all these words!"',
            '  "Tomorrow we will switch the beginning sounds!"'
          ],
          examples: [
            'Child draws cat, labels "CAT" with AT in red. You: "Perfect! I can see it is a cat AND read the word!"'
          ],
          watchFor: [
            'Pictures match word meanings.',
            'Correct spelling with -AT ending.',
            'Pride in visual word collection.',
            'Connection between reading and meaning.'
          ],
          parentTip: 'Simple drawings are best! This is reading practice with art, not art class!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '👨‍👩‍👧‍👦 You met the _AT Family!',
            'You practiced:',
            '✓ Discovering the -AT pattern',
            '✓ Building 12+ _AT family words',
            '✓ Speed reading with word families',
            '✓ Drawing and labeling _AT words'
          ],
          reviewQuestions: [
            'Could your child recognize the -AT pattern?',
            'Did they generate multiple family words?',
            'Was their reading faster with word families?',
            'Did they understand the onset-rime concept?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Word families are a MAJOR reading breakthrough! This is the moment children shift from laborious phoneme-by-phoneme decoding to efficient pattern-based reading. When they know one word in a family (CAT), they can instantly read dozens more (HAT, MAT, FLAT, CHAT, etc.). This is called "reading by analogy" — using known words to decode new words. It is cognitively efficient, builds fluency rapidly, and makes reading feel successful. Word families also make spelling easier because children see consistent patterns. This week builds on all the CVC and blend work from Weeks 1-8, showing how foundational skills combine into powerful reading strategies!'
          },
          nextSteps: [
            'Hunt for -AT family words in books!',
            'Create an _AT Family poster to hang.',
            'Practice speed reading the family daily.',
            'Tomorrow: Switch the Sound! (transforming words within families)'
          ]
        }
      }
    ]
  },

  // ========= LESSON 42 =========
  {
    id: 42,
    phase: '4',
    quarter: 1,
    week: 9,
    weekIndex: 8,
    dayInWeek: 2,
    title: 'Lesson 42: Switch the Sound!',
    theme: 'Word Families: -at, -an, -ap',
    skillRecurrenceTags: ['word-families', 'phoneme-manipulation', 'onset-substitution'],
    relatedToPreviousSkills: ['word-families', 'phoneme-manipulation', 'cvc-decoding'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Switch the Sound!',
        duration: '1 min',
        content: {
          goal: 'Practice transforming words within the -AT family by systematically changing the beginning sound.',
          parentGuide: [
            'This lesson builds on yesterday\'s word family introduction.',
            'Onset substitution is a critical phoneme manipulation skill.',
            'Children learn flexible thinking: change ONE sound = NEW word.',
            'This prepares for spelling and advanced decoding.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Letter cards (optional - can just write letters)'],
          tip: 'Make it playful! "Word magic" or "word transformations" keep it engaging.',
          researchNote: 'National Reading Panel (2000) - Phoneme manipulation (substitution, deletion, addition) is a strong predictor of reading success and supports both decoding and encoding.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Transformation Demo',
        duration: '2-3 min',
        focus: 'Onset Substitution Introduction',
        researchBasis: 'Ehri (2020) - Explicit instruction in phoneme substitution develops phonological flexibility essential for reading and spelling.',
        content: {
          goal: 'Demonstrate how changing the beginning sound transforms one word into another.',
          whatToSay: [
            '"Yesterday we met the _AT family!"',
            '"Today we are doing WORD MAGIC!"',
            '"Watch this — I can change words with one letter!"',
            '"It is like a magic trick!"'
          ],
          whatToDo: [
            'Write CAT in large letters on paper.',
            '',
            'Demonstrate the transformation:',
            '  "This word is CAT."',
            '  "What if I erase the C..."',
            '  Erase or cross out C',
            '  "...and put in an H?"',
            '  Write H',
            '  "Now what word is it?"',
            '  Child: "HAT!"',
            '  "Yes! CAT became HAT!"',
            '  "I switched ONE sound and made a NEW word!"',
            '',
            'Do another example:',
            '  "Now watch: HAT"',
            '  "Switch H to M..."',
            '  "MAT!"',
            '  "The -AT part stayed the same!"',
            '  "Only the BEGINNING changed!"',
            '',
            'Explain the pattern:',
            '  "This is called SWITCHING THE SOUND."',
            '  "The word FAMILY stays the same."',
            '  "But we SWITCH the beginning."',
            '  "And boom — new word!"',
            '',
            'Connect to yesterday:',
            '  "Remember we found 12 _AT words yesterday?"',
            '  "We made them ALL by switching the beginning!"',
            '  "Today YOU will be the word switcher!"',
            '',
            'Set up challenge:',
            '  "I will give you a word."',
            '  "You switch the beginning."',
            '  "Let us see how many you can make!"'
          ],
          examples: [
            'You transform CAT → HAT → MAT → RAT in sequence. Child watches the pattern.'
          ],
          watchFor: [
            'Understanding that one change = new word.',
            'Recognition that -AT stays constant.',
            'Excitement about word transformations.',
            'Readiness to try independently.'
          ],
          parentTip: 'The visual transformation (erasing/rewriting) makes the concept concrete!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Guided Switching',
        duration: '4-5 min',
        focus: 'Phoneme Substitution Practice',
        researchBasis: 'Lonigan et al. (2000) - Scaffolded phoneme manipulation practice develops phonological awareness essential for literacy.',
        content: {
          goal: 'Child practices switching beginning sounds with guidance, creating word chains.',
          whatToSay: [
            '"Now YOU do the switching!"',
            '"I will tell you what to switch!"',
            '"You make the new word!"',
            '"Ready? Let us start with BAT!"'
          ],
          whatToDo: [
            'Create word transformation chains.',
            '',
            '**Chain 1: Simple Switches**',
            'Write: BAT',
            '',
            '  You: "We have BAT. Switch B to C. What word?"',
            '  Child: "CAT!"',
            '  Write CAT below BAT',
            '',
            '  You: "Now switch C to H. What word?"',
            '  Child: "HAT!"',
            '  Write HAT',
            '',
            '  You: "Switch H to M!"',
            '  Child: "MAT!"',
            '',
            '  You: "Switch M to R!"',
            '  Child: "RAT!"',
            '',
            '  You: "Switch R to S!"',
            '  Child: "SAT!"',
            '',
            'Result: BAT → CAT → HAT → MAT → RAT → SAT',
            'Draw arrows between words to show transformations',
            '',
            '**Chain 2: Add Blends**',
            'Start with: CAT',
            '',
            '  You: "Switch C to CH. What word?"',
            '  Child: "CHAT!"',
            '',
            '  You: "Switch CH to FL!"',
            '  Child: "FLAT!"',
            '',
            '  You: "Switch FL to SP!"',
            '  Child: "SPAT!"',
            '',
            '  You: "Switch SP to TH!"',
            '  Child: "THAT!"',
            '',
            'Result: CAT → CHAT → FLAT → SPAT → THAT',
            '',
            '**Chain 3: Longer Chain**',
            'Can you make a chain of 8 words?',
            'Example: BAT → CAT → FAT → HAT → MAT → PAT → RAT → SAT → VAT',
            '',
            'Celebrate each transformation:',
            '  "You switched it!"',
            '  "New word!"',
            '  "You are a word transformer!"',
            '',
            'Point out the pattern:',
            '  "Look at your chain!"',
            '  "Every word is different..."',
            '  "...but they are all in the _AT family!"',
            '  "They all have -AT at the end!"'
          ],
          examples: [
            'You: "Switch HAT\'s H to M!" Child thinks: "/m/... /at/... MAT!" You: "Perfect switch!"'
          ],
          watchFor: [
            'Accurate phoneme substitution.',
            'Quick transformation (not full sound-out).',
            'Success with both single sounds and blends.',
            'Understanding the chain concept.'
          ],
          parentTip: 'If they sound out the whole word each time, that is okay! Speed comes with practice.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Independent Switching',
        duration: '3-4 min',
        focus: 'Self-Directed Transformation',
        researchBasis: 'Ehri (2001) - Independent phoneme manipulation demonstrates mastery and builds decoding flexibility.',
        content: {
          goal: 'Child independently creates word transformations without prompts.',
          whatToSay: [
            '"Now YOU are in charge!"',
            '"Pick a starting word!"',
            '"Then switch it however you want!"',
            '"Make as many as you can!"'
          ],
          whatToDo: [
            'Child chooses starting _AT word.',
            'Popular choices: CAT, HAT, BAT',
            '',
            'Child independently transforms:',
            '  Child: "CAT!"',
            '  Child: "I will switch to... RAT!"',
            '  Child writes RAT',
            '  Child: "Now... FAT!"',
            '  Child writes FAT',
            '  Continue...',
            '',
            'Your role:',
            '  - Observe and celebrate',
            '  - Help with spelling if needed',
            '  - Confirm: "Is that a real word?"',
            '  - Challenge: "Can you make a blend word?"',
            '',
            'Track transformations:',
            '  Write or draw arrows showing connections',
            '  Create a web or chain pattern visually',
            '',
            'Challenge extensions:',
            '',
            '  **Challenge 1: Speed switching**',
            '    "How many can you make in 1 minute?"',
            '    Time them (optional)',
            '',
            '  **Challenge 2: Backwards chain**',
            '    "Start with THAT, go backwards to CAT!"',
            '    THAT → CHAT → HAT → CAT',
            '',
            '  **Challenge 3: Nonsense allowed**',
            '    "Can you make a silly word?"',
            '    "ZAT! Not real, but you switched correctly!"',
            '',
            '  **Challenge 4: Longest chain**',
            '    "Can you make a chain of 10 words?"',
            '    "Use ALL the _AT words you know!"',
            '',
            'Count total words:',
            '  "You made [X] different _AT words!"',
            '  "All by switching the beginning!"',
            '  "You are a switching expert!"',
            '',
            'Compare to beginning readers:',
            '  "Some kids can only read 3-4 words."',
            '  "You just made [X] words in minutes!"',
            '  "That is because you understand FAMILIES!"'
          ],
          examples: [
            'Child: "BAT... now RAT... now... FLAT!" You: "You added a blend! Advanced switching!"'
          ],
          watchFor: [
            'Independent word generation.',
            'Creative switching choices.',
            'Inclusion of blend words.',
            'Pride in word count.',
            'Accurate spelling.'
          ],
          parentTip: 'Let them lead completely! This shows true mastery of the concept.'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Switch & Illustrate',
        duration: '2-3 min',
        focus: 'Meaning Connection',
        researchBasis: 'Scarborough (2001) - Connecting word transformations to meaning ensures reading comprehension alongside decoding skill.',
        content: {
          goal: 'Choose 2 word pairs that are one switch apart and illustrate both, showing the relationship.',
          whatToSay: [
            '"Pick two words that are one switch apart!"',
            '"Like CAT and HAT!"',
            '"Draw BOTH and show how they are different!"',
            '"But both end with -AT!"'
          ],
          whatToDo: [
            'Child chooses 2 related _AT words.',
            'Examples:',
            '  - CAT / HAT',
            '  - BAT / RAT',
            '  - FAT / SAT',
            '',
            'Create side-by-side drawing:',
            '',
            '  **Left side: Word 1**',
            '    Child draws first word',
            '    Labels: CAT',
            '    Shows what makes it CAT (ears, tail, whiskers)',
            '',
            '  **Right side: Word 2**',
            '    Child draws second word',
            '    Labels: HAT',
            '    Shows what makes it HAT (head covering)',
            '',
            '  **Between them:**',
            '    Draw arrow: CAT → HAT',
            '    Write: "Switch C to H"',
            '',
            'Alternative: Combination drawing',
            '  Draw one picture showing BOTH words',
            '  Example: A cat wearing a hat',
            '  Label both: CAT and HAT',
            '  Show how one letter change = two different things',
            '',
            'Add sentence (optional):',
            '  "The CAT wore a HAT."',
            '  "The BAT and RAT are friends."',
            '  Uses both switched words in one sentence',
            '',
            'Reflection:',
            '  "What is the same about CAT and HAT?"',
            '  Child: "Both end in -AT!"',
            '  "What is different?"',
            '  Child: "The beginning sound!"',
            '  "And that changes the MEANING!"',
            '',
            'Display proudly:',
            '  "This shows you understand word switching!"',
            '  "One letter can change EVERYTHING!"'
          ],
          examples: [
            'Child draws cat on left, hat on right. Arrow between: "C→H" You: "Perfect! Same family, different meanings!"'
          ],
          watchFor: [
            'Both drawings clearly different.',
            'Correct labeling with -AT words.',
            'Understanding of meaning difference.',
            'Visual representation of switching concept.'
          ],
          parentTip: 'The drawing shows they understand that spelling changes = meaning changes!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🔄 You are a Word Switcher!',
            'You practiced:',
            '✓ Watching word transformations',
            '✓ Following guided switching',
            '✓ Creating independent word chains',
            '✓ Illustrating switched word pairs'
          ],
          reviewQuestions: [
            'Could your child switch beginning sounds successfully?',
            'Did they create word chains independently?',
            'Can they explain how switching works?',
            'Did they include blend words in their switching?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Phoneme substitution (switching sounds) is a sophisticated phonological skill that powers both reading and spelling. When children can mentally manipulate sounds (switch BAT to HAT), they develop flexible thinking about language. This skill allows them to decode new words by analogy, spell by pattern, and self-correct reading errors. The word family approach makes this complex skill accessible and fun. By practicing onset substitution within word families, children internalize the pattern: change the beginning, keep the ending. This understanding accelerates their reading development dramatically and builds the foundation for morphological awareness (word parts like prefixes and suffixes) in later grades!'
          },
          nextSteps: [
            'Play "Switch It!" throughout the day: "CAT... switch to HAT!"',
            'Create switching challenges: "Start with BAT, end with THAT!"',
            'Tomorrow: Meet the _AN and _AP families!',
            'Practice switching with all three families together!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 43 =========
  {
    id: 43,
    phase: '4',
    quarter: 1,
    week: 9,
    weekIndex: 8,
    dayInWeek: 3,
    title: 'Lesson 43: _AN & _AP Families',
    theme: 'Word Families: -at, -an, -ap',
    skillRecurrenceTags: ['word-families', 'rime-patterns', 'multi-family-comparison'],
    relatedToPreviousSkills: ['word-families', 'cvc-decoding', 'pattern-recognition'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Two New Families!',
        duration: '1 min',
        content: {
          goal: 'Introduce -AN and -AP word families, building on -AT family knowledge.',
          parentGuide: [
            'Today expands from one family to THREE families!',
            'This teaches children that word families are a SYSTEM, not isolated patterns.',
            'Comparing families strengthens discrimination and pattern recognition.',
            'This is excellent prep for reading stamina and vocabulary growth.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers (3 colors recommended)', 'Index cards (optional)'],
          tip: 'Use different colors for each family to make visual discrimination easier!',
          researchNote: 'Ehri (2020) - Learning multiple word families simultaneously builds orthographic mapping and pattern recognition more effectively than isolated family practice.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Meet the _AN Family',
        duration: '3-4 min',
        focus: 'New Family Introduction',
        researchBasis: 'National Reading Panel (2000) - Systematic phonics instruction including multiple rime patterns accelerates word recognition.',
        content: {
          goal: 'Build and decode the -AN word family using the same strategy as -AT.',
          whatToSay: [
            '"You are experts on the _AT family!"',
            '"Today we meet the _AT family\'s cousins!"',
            '"Meet the _AN family!"',
            '"They work exactly the same way!"'
          ],
          whatToDo: [
            'Write -AN in large letters (use different color than -AT).',
            '',
            'Build the -AN family:',
            '  "Let\'s find all the _AN family members!"',
            '  "What letter could start an -AN word?"',
            '',
            '**Simple -AN words:**',
            '  - BAN',
            '  - CAN',
            '  - FAN',
            '  - MAN',
            '  - PAN',
            '  - RAN',
            '  - TAN',
            '  - VAN',
            '',
            'Write words in column:',
            '  Highlight -AN ending in consistent color',
            '  Point out: "See? -AN stays the same!"',
            '  "Only the beginning changes!"',
            '',
            'Add blend -AN words:',
            '  "Now let\'s add blends!"',
            '',
            '**Blend -AN words:**',
            '  - BRAN (BR-blend)',
            '  - CLAN (CL-blend)',
            '  - PLAN (PL-blend)',
            '  - SCAN (SC-blend)',
            '  - SPAN (SP-blend)',
            '  - THAN (TH-digraph)',
            '',
            'Compare to -AT:',
            '  "How is -AN like -AT?"',
            '  Child: "Both have short A!"',
            '  "Yes! Both are short-A families!"',
            '  "What\'s different?"',
            '  Child: "The ending sound!"',
            '  "Right! -AT ends with /t/, -AN ends with /n/!"',
            '',
            'Quick read through:',
            '  Point to words randomly',
            '  Child reads -AN family',
            '  "You already know this pattern!"',
            '  "It\'s just like -AT!"',
            '',
            'Count family members:',
            '  "We found [X] simple words!"',
            '  "Plus [Y] blend words!"',
            '  "The -AN family is BIG!"'
          ],
          examples: [
            'You: "C plus AN?" Child: "CAN!" You: "Just like CAT used the -AT pattern!"'
          ],
          watchFor: [
            'Quick recognition of pattern similarity.',
            'Successful generation of -AN words.',
            'Discrimination between -AT and -AN.',
            'Inclusion of blend words.'
          ],
          parentTip: 'They should find -AN easier because they understand the word family concept already!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Meet the _AP Family',
        duration: '3-4 min',
        focus: 'Third Family Introduction',
        researchBasis: 'Lonigan et al. (2000) - Multiple rime exposures build phonological flexibility and decoding efficiency.',
        content: {
          goal: 'Build and decode the -AP word family, now working with three families.',
          whatToSay: [
            '"Now meet the third cousin!"',
            '"The _AP family!"',
            '"Three families — same short A!"',
            '"You\'re becoming a family expert!"'
          ],
          whatToDo: [
            'Write -AP in large letters (third color).',
            '',
            'Build the -AP family:',
            '  "Same game — what letters work with -AP?"',
            '',
            '**Simple -AP words:**',
            '  - CAP',
            '  - GAP',
            '  - LAP',
            '  - MAP',
            '  - NAP',
            '  - PAP (might be new — soft baby food)',
            '  - RAP',
            '  - SAP',
            '  - TAP',
            '  - ZAP',
            '',
            'Write in column with -AP highlighted.',
            '',
            'Add blend -AP words:',
            '',
            '**Blend -AP words:**',
            '  - CHAP (CH-digraph)',
            '  - CLAP (CL-blend)',
            '  - FLAP (FL-blend)',
            '  - SCRAP (SCR-blend - advanced!)',
            '  - SLAP (SL-blend)',
            '  - SNAP (SN-blend)',
            '  - STRAP (STR-blend - advanced!)',
            '  - TRAP (TR-blend)',
            '  - WRAP (WR-digraph)',
            '',
            'Celebrate the pattern:',
            '  "Look! You can read SO many -AP words!"',
            '  "That\'s because you know the -AP pattern!"',
            '',
            'Compare all three:',
            '  Write side by side:',
            '  **-AT  |  -AN  |  -AP**',
            '  CAT   |  CAN  |  CAP',
            '  HAT   |  PAN  |  MAP',
            '  RAT   |  RAN  |  RAP',
            '',
            '  "What do ALL three have?"',
            '  Child: "Short A!"',
            '  "Yes! They\'re all short-A families!"',
            '  "What\'s different?"',
            '  Child: "The ending sound!"',
            '  "Perfect! -T, -N, or -P!"',
            '',
            'Three-family reading:',
            '  Point to words across all three families',
            '  Child practices reading mixed',
            '  "You can read THREE families now!"'
          ],
          examples: [
            'You point: "This one?" Child: "CAP!" You: "Now this?" Child: "CAN!" You: "Now this?" Child: "CAT!" You: "Three families mastered!"'
          ],
          watchFor: [
            'Successful -AP word generation.',
            'Clear discrimination among three families.',
            'Confidence reading across families.',
            'Recognition of shared short-A sound.'
          ],
          parentTip: '-AP has the most blend words — celebrate when they read the harder ones!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Family Sorting Game',
        duration: '3-4 min',
        focus: 'Multi-Family Discrimination',
        researchBasis: 'Ehri (2001) - Sorting activities strengthen orthographic pattern recognition and phonological discrimination.',
        content: {
          goal: 'Sort words into correct families, strengthening discrimination skills.',
          whatToSay: [
            '"Time to test your family knowledge!"',
            '"I\'ll say a word!"',
            '"You tell me which family!"',
            '"Is it -AT, -AN, or -AP?"'
          ],
          whatToDo: [
            'Create three columns on paper:',
            '  **-AT  |  -AN  |  -AP**',
            '',
            'Call out words randomly:',
            '  You say word → child identifies family → writes in correct column',
            '',
            '**Words to sort (mix them up!):**',
            '  - CAT → -AT family',
            '  - MAN → -AN family',
            '  - TAP → -AP family',
            '  - HAT → -AT family',
            '  - CAN → -AN family',
            '  - MAP → -AP family',
            '  - FLAT → -AT family',
            '  - PLAN → -AN family',
            '  - SNAP → -AP family',
            '  - CHAT → -AT family',
            '  - SPAN → -AN family',
            '  - TRAP → -AP family',
            '',
            'For each word:',
            '  Child hears word',
            '  Child identifies ending sound',
            '  "Is it -T, -N, or -P at the end?"',
            '  Child places in correct family',
            '  Confirm: "Yes! RAT is in the -AT family!"',
            '',
            'Increase difficulty:',
            '  **Round 2: Faster pace**',
            '    Call words more quickly',
            '    Child sorts without writing (verbal)',
            '',
            '  **Round 3: Minimal pairs**',
            '    CAT / CAN / CAP',
            '    "All start the same! Different endings!"',
            '    RAT / RAN / RAP',
            '    "Same beginning, different family!"',
            '',
            'Count sorted words:',
            '  "How many in each family?"',
            '  Compare totals',
            '  "You sorted [X] words correctly!"',
            '',
            'Reflection questions:',
            '  "Which family was easiest to hear?"',
            '  "Which was trickiest?"',
            '  "How did you know which family?"',
            '  Child explains strategy',
            '',
            'Challenge: Create new words',
            '  "Pick a family."',
            '  "Make a word I didn\'t say!"',
            '  Tests creative application'
          ],
          examples: [
            'You: "SNAP!" Child: "Hmmm... /p/ at the end! -AP family!" You: "Perfect listening!"'
          ],
          watchFor: [
            'Accurate family identification.',
            'Attention to ending sound.',
            'Speed improving through rounds.',
            'Success with minimal pairs.',
            'Ability to explain reasoning.'
          ],
          parentTip: 'If they confuse families, have them say the word slowly and emphasize the ending!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Three-Family Gallery',
        duration: '2-3 min',
        focus: 'Visual Organization',
        researchBasis: 'Scarborough (2001) - Visual organization of word patterns supports orthographic memory and retrieval.',
        content: {
          goal: 'Create organized visual display of all three families with examples.',
          whatToSay: [
            '"Let\'s make a family portrait!"',
            '"We\'ll draw one word from EACH family!"',
            '"This shows you know all three!"'
          ],
          whatToDo: [
            'Divide paper into three sections.',
            '',
            '**Section 1: -AT Family**',
            '  Title: "The -AT Family"',
            '  Child chooses favorite -AT word',
            '  Popular: CAT, HAT, BAT',
            '  Child draws picture',
            '  Labels with word (highlight -AT)',
            '',
            '**Section 2: -AN Family**',
            '  Title: "The -AN Family"',
            '  Child chooses favorite -AN word',
            '  Popular: CAN, MAN, VAN, FAN',
            '  Child draws picture',
            '  Labels with word (highlight -AN)',
            '',
            '**Section 3: -AP Family**',
            '  Title: "The -AP Family"',
            '  Child chooses favorite -AP word',
            '  Popular: MAP, CAP, SNAP, CLAP',
            '  Child draws picture',
            '  Labels with word (highlight -AP)',
            '',
            'Add family banner:',
            '  Top of page: "Three Short-A Families!"',
            '  Note: "All have short A sound!"',
            '  Note: "Different endings: -T, -N, -P"',
            '',
            'Extension: Family sentences',
            '  "Can you use all three words in one sentence?"',
            '  Example: "A MAN in a CAP used a MAP."',
            '  Example: "The CAT in a VAN wore a HAT."',
            '  Demonstrates understanding of all families',
            '',
            'Decorate and display:',
            '  Add color, borders',
            '  Hang prominently',
            '  "This shows your three families!"',
            '  "You can read DOZENS of words now!"',
            '',
            'Family list challenge:',
            '  On back, list as many words as possible:',
            '  How many -AT words can you write?',
            '  How many -AN?',
            '  How many -AP?',
            '  Total word count!',
            '',
            'Celebrate growth:',
            '  "Two days ago you knew one family."',
            '  "Now you know THREE families!"',
            '  "That\'s over 30 words you can read!"',
            '  "Tomorrow we\'ll practice all three together!"'
          ],
          examples: [
            'Child draws cat, can, map. Labels each. You: "Perfect family portrait! Three families, three pictures!"'
          ],
          watchFor: [
            'Clear representation of each family.',
            'Correct spelling and labeling.',
            'Distinct pictures for each word.',
            'Pride in three-family mastery.',
            'Optional: successful multi-word sentences.'
          ],
          parentTip: 'This gallery becomes a reference tool for tomorrow\'s lesson!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎉 You Met Three Families!',
            'You practiced:',
            '✓ Building the -AN family',
            '✓ Building the -AP family',
            '✓ Sorting words by family',
            '✓ Creating a three-family gallery'
          ],
          reviewQuestions: [
            'Can your child name all three families?',
            'Can they generate words for each family?',
            'Can they hear the different endings?',
            'Do they understand all three share short A?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Learning multiple word families simultaneously is more effective than learning one at a time! When children compare patterns (-AT vs. -AN vs. -AP), they develop phonological discrimination — the ability to hear subtle sound differences. This skill is crucial for both reading and spelling. They also learn that word families are a SYSTEM, not isolated tricks. This "pattern awareness" transfers to other families (-IG, -OT, -UG, etc.) and eventually to more complex patterns (prefixes, suffixes, morphemes). By mastering three families this week, your child can now decode over 30 words — and the strategy works for hundreds more! This exponential growth is what makes word families such a powerful reading tool.'
          },
          nextSteps: [
            'Play "Family or Not?" — say words, child identifies family.',
            'Hunt for -AT, -AN, -AP words in books.',
            'Tomorrow: Write words from all three families!',
            'Practice reading mixed family words.'
          ]
        }
      }
    ]
  },

  // ========= LESSON 44 =========
  {
    id: 44,
    phase: '4',
    quarter: 1,
    week: 9,
    weekIndex: 8,
    dayInWeek: 4,
    title: 'Lesson 44: Rhyme & Write',
    theme: 'Word Families: -at, -an, -ap',
    skillRecurrenceTags: ['word-families', 'writing-practice', 'spelling-patterns'],
    relatedToPreviousSkills: ['word-families', 'phoneme-manipulation', 'fine-motor-writing'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Rhyme & Write!',
        duration: '1 min',
        content: {
          goal: 'Write words from two different families, reinforcing spelling patterns and rhyme awareness.',
          parentGuide: [
            'Writing cements reading! Encoding strengthens decoding.',
            'This lesson bridges reading TO writing.',
            'Spelling by pattern is more efficient than letter-by-letter.',
            'Choose any TWO of the three families for writing practice.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Yesterday\'s family gallery (optional reference)'],
          tip: 'Let child choose which two families to write — ownership increases engagement!',
          researchNote: 'Ehri (2020) - Encoding (spelling) and decoding (reading) are reciprocal processes; writing practice strengthens orthographic mapping for reading.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Choose Your Families',
        duration: '2 min',
        focus: 'Family Selection & Planning',
        researchBasis: 'National Reading Panel (2000) - Student choice in literacy activities increases motivation and engagement.',
        content: {
          goal: 'Child selects two families to focus on for writing practice.',
          whatToSay: [
            '"Today YOU are the writer!"',
            '"We\'ll write words from TWO families!"',
            '"Which two families do you want to write?"',
            '"You choose!"'
          ],
          whatToDo: [
            'Present the three family options:',
            '  **Option 1: -AT and -AN**',
            '  **Option 2: -AT and -AP**',
            '  **Option 3: -AN and -AP**',
            '',
            'Child makes selection:',
            '  "Which two sound fun to write?"',
            '  Child chooses',
            '  "Great choice! Let\'s write [family] and [family]!"',
            '',
            'Set up paper:',
            '  Divide paper into two columns',
            '  **Left: [First family]  |  Right: [Second family]**',
            '  Example: **-AT Words  |  -AN Words**',
            '',
            'Write family headers:',
            '  Child writes family endings at top',
            '  -AT (left side)',
            '  -AN (right side)',
            '  Use different colors for each',
            '',
            'Set writing goal:',
            '  "Let\'s write 3-5 words in EACH family!"',
            '  "That\'s 6-10 words total!"',
            '  "You can write simple words OR blend words!"',
            '',
            'Reference available tools:',
            '  "You can look at yesterday\'s gallery if you need ideas."',
            '  "Or we can sound out new words together!"',
            '  "Remember — the family ending stays the same!"',
            '',
            'Explain the process:',
            '  "For each word:"',
            '  "1. Say the word out loud"',
            '  "2. Write the beginning sound"',
            '  "3. Add the family ending"',
            '  "4. Read it back to check!"'
          ],
          examples: [
            'Child: "I choose -AT and -AP!" You: "Perfect! CAT and CAP, HAT and TAP — lots of rhymes!"'
          ],
          watchFor: [
            'Clear family choice made.',
            'Understanding of two-column setup.',
            'Readiness to write.',
            'Reference to previous learning.'
          ],
          parentTip: 'All three combinations work equally well — let them choose what feels fun!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Write Family 1',
        duration: '3-4 min',
        focus: 'Spelling Practice',
        researchBasis: 'Ehri (2001) - Spelling practice using known patterns (rimes) builds orthographic memory more effectively than random word lists.',
        content: {
          goal: 'Child writes 3-5 words from first chosen family with correct spelling.',
          whatToSay: [
            '"Let\'s start with [first family]!"',
            '"Say a word, write it down!"',
            '"Remember — the ending stays the same!"',
            '"Only change the beginning!"'
          ],
          whatToDo: [
            'Child generates first family words.',
            '',
            '**For EACH word:**',
            '',
            '  Step 1: Child says word aloud',
            '    "I want to write CAT!"',
            '',
            '  Step 2: Segment beginning from ending',
            '    You: "What sound comes first?"',
            '    Child: "/c/"',
            '    You: "What comes next?"',
            '    Child: "AT"',
            '',
            '  Step 3: Child writes word',
            '    Writes C',
            '    Writes AT (family ending)',
            '    Result: CAT',
            '',
            '  Step 4: Child reads back',
            '    "C-AT... CAT!"',
            '    Confirms correct spelling',
            '',
            'Repeat for 3-5 words minimum.',
            '',
            'Example -AT family list:',
            '  1. CAT',
            '  2. HAT',
            '  3. BAT',
            '  4. FLAT (blend word!)',
            '  5. THAT (digraph word!)',
            '',
            'Example -AN family list:',
            '  1. CAN',
            '  2. MAN',
            '  3. PAN',
            '  4. PLAN (blend!)',
            '  5. THAN',
            '',
            'Example -AP family list:',
            '  1. CAP',
            '  2. MAP',
            '  3. TAP',
            '  4. CLAP (blend!)',
            '  5. SNAP (blend!)',
            '',
            'Scaffolding support:',
            '',
            '  **If struggling with beginning sound:**',
            '    "Say it slowly: /c/... /at/... CAT"',
            '    "What letter makes /c/?"',
            '    Help identify letter',
            '',
            '  **If spelling family ending wrong:**',
            '    "Remember — the family ending is always -AT!"',
            '    "A-T. That never changes in this family!"',
            '',
            '  **If writing blend words:**',
            '    "FLAT has two letters at the start!"',
            '    "F and L together make /fl/"',
            '    "Then add -AT!"',
            '',
            'Celebrate each word:',
            '  "You spelled it!"',
            '  "That\'s another word in the family!"',
            '  "Look at your list growing!"',
            '',
            'Count words written:',
            '  "How many [family] words did you write?"',
            '  Child counts',
            '  "Excellent! Now let\'s do [second family]!"'
          ],
          examples: [
            'Child writes: M-A-T. You: "Read it back." Child: "MAT!" You: "Perfect spelling!"'
          ],
          watchFor: [
            'Correct beginning sound identification.',
            'Consistent family ending spelling.',
            'Independent word generation.',
            'Successful blend word spelling.',
            'Self-correction through reading back.'
          ],
          parentTip: 'If handwriting is challenging, you can write while they spell aloud — focus is on pattern, not penmanship!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Write Family 2',
        duration: '3-4 min',
        focus: 'Pattern Transfer',
        researchBasis: 'Lonigan et al. (2000) - Applying spelling patterns across multiple word families demonstrates phonological flexibility and pattern generalization.',
        content: {
          goal: 'Child writes 3-5 words from second chosen family, demonstrating pattern transfer.',
          whatToSay: [
            '"You\'re a [first family] spelling expert!"',
            '"Now let\'s write [second family] words!"',
            '"Same strategy — change the beginning, keep the ending!"',
            '"You\'ve got this!"'
          ],
          whatToDo: [
            'Child generates second family words.',
            '',
            'Use same process as Family 1:',
            '  1. Say word',
            '  2. Segment sounds',
            '  3. Write word',
            '  4. Read back',
            '',
            'Child should be MORE independent now:',
            '  Less scaffolding needed',
            '  Faster writing',
            '  More confidence',
            '',
            'Encourage variety:',
            '  "Can you write a blend word?"',
            '  "Try a word with two letters at the start!"',
            '  "What about a word we haven\'t said yet?"',
            '',
            '**Target 3-5 words minimum.**',
            '',
            'Compare the two lists:',
            '  After completing second family:',
            '  "Let\'s look at BOTH lists!"',
            '  "What\'s the same?"',
            '  Child: "They\'re all short A words!"',
            '  "What\'s different?"',
            '  Child: "Different endings!"',
            '',
            'Count total words:',
            '  "You wrote [X] words in [family 1]!"',
            '  "You wrote [Y] words in [family 2]!"',
            '  "That\'s [Z] total words!"',
            '  "All spelled correctly!"',
            '',
            'Highlight spelling success:',
            '  "You didn\'t spell these letter by letter."',
            '  "You used PATTERNS!"',
            '  "That\'s smart spelling!"',
            '',
            'Challenge: Speed write',
            '  "Can you write one more word in each family?"',
            '  "As fast as you can!"',
            '  "GO!"',
            '  Tests automaticity'
          ],
          examples: [
            'Child writes CAN, then quickly writes CAP. You: "Look! You switched families easily! Same beginning, different ending!"'
          ],
          watchFor: [
            'Increased independence.',
            'Faster writing speed.',
            'Pattern consistency.',
            'Ability to switch between families.',
            'Inclusion of blend/digraph words.'
          ],
          parentTip: 'If they fly through this, they\'ve mastered the pattern — celebrate that automaticity!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Rhyme Connection Game',
        duration: '2-3 min',
        focus: 'Phonological Awareness',
        researchBasis: 'Scarborough (2001) - Connecting written patterns to phonological awareness (rhyme) strengthens the word recognition-language comprehension connection.',
        content: {
          goal: 'Draw lines connecting rhyming words across both family lists.',
          whatToSay: [
            '"Look at your two lists!"',
            '"Can you find words that rhyme?"',
            '"They won\'t be in the SAME family..."',
            '"But they might have the same BEGINNING!"'
          ],
          whatToDo: [
            'Explain the game:',
            '  "We\'re looking for words that START the same!"',
            '  "Like CAT and CAN both start with C!"',
            '  "They rhyme at the BEGINNING, not the ending!"',
            '  "These are called ALLITERATION pairs!"',
            '',
            'Find alliteration pairs:',
            '  Look across both columns',
            '  Find words with same beginning',
            '',
            'Example pairs if writing -AT and -AN:',
            '  CAT ←→ CAN (both start with C)',
            '  HAT ←→ (no HAN in common words)',
            '  BAT ←→ BAN (both start with B)',
            '  MAN ←→ MAT (both start with M)',
            '',
            'Example pairs if writing -AT and -AP:',
            '  CAT ←→ CAP (both start with C)',
            '  HAT ←→ (no HAP)',
            '  RAT ←→ RAP (both start with R)',
            '  FLAT ←→ FLAP (both start with FL!)',
            '',
            'Example pairs if writing -AN and -AP:',
            '  CAN ←→ CAP (both start with C)',
            '  MAN ←→ MAP (both start with M)',
            '  PLAN ←→ (no PLAP)',
            '  SNAP ←→ (no SNAP)',
            '',
            'Draw connection lines:',
            '  Use crayon or colored pencil',
            '  Draw line from one word to its pair',
            '  CAT ←→ CAN',
            '',
            'Say each pair aloud:',
            '  "CAT and CAN!"',
            '  "They START with the same sound!"',
            '  "They END with different sounds!"',
            '  "That makes them interesting rhyme cousins!"',
            '',
            'Count pairs:',
            '  "How many pairs did you find?"',
            '  "That\'s [X] connection lines!"',
            '',
            'Extend to sentences:',
            '  "Can you use a pair in a sentence?"',
            '  "The CAT sat on the CAN."',
            '  "A MAN used a MAP."',
            '  Shows understanding of both words',
            '',
            'Alternative: Minimal pair focus',
            '  If child wrote CAT, CAN, CAP:',
            '  "These are MINIMAL PAIRS!"',
            '  "Only ONE sound different!"',
            '  "CAT, CAN, CAP — all start with C!"',
            '  "Only the ending changes!"',
            '  Circle these special trios',
            '',
            'Final reflection:',
            '  "You wrote words from two families!"',
            '  "You found words that connect!"',
            '  "You\'re seeing patterns EVERYWHERE!"'
          ],
          examples: [
            'Child draws line: CAT ←→ CAN. You: "Yes! Both start with /c/! Same beginning, different family!"'
          ],
          watchFor: [
            'Identification of shared beginnings.',
            'Accurate line connections.',
            'Understanding of alliteration vs. rhyme.',
            'Ability to use pairs in sentences.',
            'Recognition of minimal pair sets.'
          ],
          parentTip: 'This activity shows they understand words have PARTS — beginnings and endings can be mixed and matched!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '✍️ You\'re a Pattern Speller!',
            'You practiced:',
            '✓ Choosing two families to write',
            '✓ Spelling 3-5 words in each family',
            '✓ Writing 6-10+ total words',
            '✓ Finding connection patterns'
          ],
          reviewQuestions: [
            'Did your child spell family endings consistently?',
            'Could they generate words independently?',
            'Did they include blend words in writing?',
            'Can they find alliteration pairs across families?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Writing is the flip side of reading — and just as important! When children SPELL words using word family patterns, they cement their orthographic knowledge (understanding of letter patterns). This lesson demonstrates a crucial literacy concept: patterns work for BOTH reading and writing. Children who spell "CAT" and immediately know how to spell "HAT" and "FLAT" are thinking like efficient spellers. They\'re not memorizing every word individually; they\'re applying patterns. This is exactly how proficient spellers work — they use analogy, pattern knowledge, and phonological awareness together. The connection-finding game (alliteration pairs) shows sophisticated metalinguistic awareness: understanding that words can be analyzed, compared, and manipulated. This skill transfers to morphology later (prefixes, suffixes, root words) and supports vocabulary growth!'
          },
          nextSteps: [
            'Keep the word lists — add to them over the week!',
            'Practice writing family words daily.',
            'Tomorrow: Create a rhyming story using all three families!',
            'Challenge: Can you write 10 words in ONE family?'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 45 =========
  {
    id: 45,
    phase: '4',
    quarter: 1,
    week: 9,
    weekIndex: 8,
    dayInWeek: 5,
    title: 'Lesson 45: Word-Family Story Seeds',
    theme: 'Word Families: -at, -an, -ap',
    skillRecurrenceTags: ['word-families', 'storytelling', 'creative-application'],
    relatedToPreviousSkills: ['word-families', 'rhyming', 'decoding-fluency'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Story Seeds!',
        duration: '1 min',
        content: {
          goal: 'Create a short rhyming story using words from the -at, -an, and -ap families.',
          parentGuide: [
            'This lesson integrates ALL the week\'s learning into creative storytelling.',
            'Rhyming stories are memorable and fun to read repeatedly.',
            'This builds comprehension alongside decoding — both strands of the Reading Rope!',
            'Stories can be silly — humor increases engagement!'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'This week\'s word lists (reference)'],
          tip: 'Keep it SHORT — 4-6 sentences is perfect! Quality over quantity.',
          researchNote: 'Scarborough (2001) - The Reading Rope model emphasizes that word recognition AND language comprehension must develop together for reading success.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Pick Your Story Words',
        duration: '2 min',
        focus: 'Word Selection',
        researchBasis: 'Whitehurst & Lonigan (1998) - Emergent literacy includes using known words in meaningful contexts.',
        content: {
          goal: 'Choose 6-8 words from the three families to use in the story.',
          whatToSay: [
            '"We\'re going to write a story!"',
            '"But we can only use _AT, _AN, and _AP words!"',
            '"Pick your favorite words from this week!"'
          ],
          whatToDo: [
            'Review the three families: -AT, -AN, -AP',
            'Child selects 2-3 words from EACH family',
            'Aim for 6-8 total words',
            'Write selected words in a list',
            '',
            'Example word selections:',
            '  From -AT: CAT, HAT, FLAT',
            '  From -AN: MAN, CAN, PLAN',
            '  From -AP: MAP, CAP, CLAP',
            '',
            'Encourage variety:',
            '  "Pick at least one blend word!"',
            '  "Choose words that might go together!"',
            '',
            'Test rhyme potential:',
            '  "CAT, HAT — those rhyme at the end!"',
            '  "MAN, CAN — those rhyme too!"',
            '  "We can make rhyming lines!"',
            '',
            'Consider story possibilities:',
            '  "A MAN with a CAT?"',
            '  "A MAP in a HAT?"',
            '  "Get creative!"'
          ],
          examples: [
            'Child picks: CAT, HAT, MAN, CAN, MAP, CAP. You: "Great choices! I already see story ideas!"'
          ],
          watchFor: [
            'Selection from all three families.',
            'Mix of simple and blend words.',
            'Excitement about possibilities.'
          ],
          parentTip: 'Let them pick ANY words — even unusual ones make great silly stories!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Build Story Sentences',
        duration: '4 min',
        focus: 'Story Construction',
        researchBasis: 'National Reading Panel (2000) - Connecting decoding to meaningful text supports reading comprehension development.',
        content: {
          goal: 'Create 4-6 simple sentences using the chosen words, with rhyming when possible.',
          whatToSay: [
            '"Now let\'s make sentences!"',
            '"Use your word list!"',
            '"Try to make some lines rhyme!"',
            '"It can be silly!"'
          ],
          whatToDo: [
            'Start with opening line using first word.',
            '',
            'Example Story Build:',
            '  Words: CAT, HAT, MAN, CAN, MAP, CLAP',
            '',
            '  Line 1: "A CAT wore a HAT." (rhyme!)',
            '  Line 2: "A MAN had a CAN."',
            '  Line 3: "He looked at a MAP." (introduces third family)',
            '  Line 4: "Then gave a big CLAP!" (rhyme with MAP!)',
            '',
            'Coaching prompts:',
            '  "What could happen with CAT and HAT?"',
            '  "Can you rhyme HAT with FLAT or THAT?"',
            '  "What would the MAN do with the CAN?"',
            '',
            'Rhyme scaffolding:',
            '  "You used CAT. What rhymes with CAT?"',
            '  Child: "HAT, BAT, FLAT..."',
            '  "Pick one for the next line!"',
            '',
            'Keep sentences simple:',
            '  Subject + verb + object pattern works well',
            '  "The CAT sat."',
            '  "A MAN ran."',
            '  "She had a MAP."',
            '',
            'Connect sentences:',
            '  Use transition words: then, next, so',
            '  "The CAT sat. Then a MAN ran."',
            '',
            'Write or dictate:',
            '  Child can write themselves (slower but valuable)',
            '  OR child dictates, parent writes (faster)',
            '  OR collaborative: alternate sentences',
            '',
            'Read aloud as you build:',
            '  After each sentence, read full story so far',
            '  "Does it make sense?"',
            '  "What should happen next?"'
          ],
          examples: [
            'Child: "The CAT sat on a MAT!" You: "Perfect! MAT rhymes with CAT! What happens next?"'
          ],
          watchFor: [
            'Use of selected word-family words.',
            'Attempt at rhyming lines.',
            'Logical story flow.',
            'Creative thinking.'
          ],
          parentTip: 'Prioritize fun over perfect grammar — silly stories are more memorable!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Read & Illustrate',
        duration: '4 min',
        focus: 'Story Performance',
        researchBasis: 'Bus & van IJzendoorn (1995) - Repeated reading and engagement with self-created texts strengthens literacy motivation.',
        content: {
          goal: 'Read the completed story aloud multiple times and add illustrations.',
          whatToSay: [
            '"Let\'s read our story!"',
            '"Read it like you\'re performing!"',
            '"Then we\'ll draw pictures!"'
          ],
          whatToDo: [
            'Read the story together 2-3 times:',
            '',
            '  **Round 1: Shared reading**',
            '    Parent and child read together',
            '    Point to each word',
            '    Emphasize rhyming words',
            '',
            '  **Round 2: Child\'s turn**',
            '    Child reads independently',
            '    Parent helps with any stuck words',
            '    Celebrate fluency!',
            '',
            '  **Round 3: Performance reading**',
            '    Add expression and voices',
            '    Emphasize rhythm',
            '    Make it theatrical!',
            '',
            'Illustration options:',
            '',
            '  **Option A: Scene-by-scene**',
            '    Divide paper into boxes (comic strip)',
            '    One picture per sentence',
            '    Quick sketches',
            '',
            '  **Option B: Full scene**',
            '    One large picture showing whole story',
            '    All characters and elements included',
            '',
            '  **Option C: Key moment**',
            '    Draw the funniest or most important part',
            '    Label with story words',
            '',
            'While illustrating:',
            '  Parent re-reads story aloud',
            '  Child draws while listening',
            '  Reinforces story memory',
            '',
            'Label pictures with story words:',
            '  Point to CAT in picture → write "CAT"',
            '  Point to HAT → write "HAT"',
            '  Connect images to text',
            '',
            'Create title and author credit:',
            '  Child creates story title',
            '  "The Cat in a Hat" or "The Man and His Can"',
            '  Add: "By [Child\'s Name]"',
            '  Celebrate authorship!'
          ],
          examples: [
            'Child reads with expression: "A CAT wore a HAT! A MAN had a CAN!" You: "Awesome reading!"'
          ],
          watchFor: [
            'Improved fluency with each reading.',
            'Recognition of word-family words.',
            'Illustrations matching text.',
            'Pride in story creation.'
          ],
          parentTip: 'Save this story — it\'s evidence of their word family mastery!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Story Share & Celebrate',
        duration: '2 min',
        focus: 'Presentation',
        researchBasis: 'Vygotsky (1978) - Social sharing of literacy products reinforces learning and builds confidence.',
        content: {
          goal: 'Share the story with someone else and celebrate the week\'s word family learning.',
          whatToSay: [
            '"You created a real story!"',
            '"Let\'s share it with someone!"',
            '"You\'re an author and illustrator!"'
          ],
          whatToDo: [
            'Choose an audience:',
            '  Family member, sibling, or stuffed animal',
            '',
            'Child performs story:',
            '  Holds up paper',
            '  Reads story aloud',
            '  Shows illustrations',
            '  Points to word-family words used',
            '',
            'Highlight word families:',
            '  "This story uses three word families!"',
            '  "Can you find the -AT words?"',
            '  Audience or child circles them',
            '  "Can you find -AN words?"',
            '  "Can you find -AP words?"',
            '',
            'Reflect on the week:',
            '  "On Monday, you met the _AT family."',
            '  "On Tuesday, you learned to switch sounds."',
            '  "On Wednesday, you met _AN and _AP families."',
            '  "On Thursday, you wrote word-family words."',
            '  "Today, you made a STORY!"',
            '  "Look how much you learned in one week!"',
            '',
            'Extension challenge:',
            '  "Could you write another story tomorrow?"',
            '  "What about using DIFFERENT family words?"',
            '  "Could you make it longer?"',
            '',
            'Display options:',
            '  Hang story on wall/refrigerator',
            '  Add to reading portfolio',
            '  Create "story book" with folded paper',
            '  Read to family members throughout week'
          ],
          examples: [
            'Child reads to sibling, who claps. You: "They loved it! You\'re a real author!"'
          ],
          watchFor: [
            'Confidence in reading aloud.',
            'Ability to identify word families in context.',
            'Pride in creation.',
            'Willingness to share.'
          ],
          parentTip: 'This story becomes a treasured reading memory — keep it!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '📖 You\'re a Story Writer!',
            'You practiced:',
            '✓ Selecting word-family words',
            '✓ Building rhyming sentences',
            '✓ Reading your own story',
            '✓ Sharing your creation'
          ],
          reviewQuestions: [
            'Did the story use words from all three families?',
            'Did your child read with increasing fluency?',
            'Was there evidence of rhyming?',
            'Did they show pride in their creation?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Creating stories with learned words is the ultimate application of decoding skills! This lesson shows your child that reading isn\'t just about sounding out words — it\'s about MEANING and CREATIVITY. When children write and read their own stories, they experience the purpose of literacy. The word family approach makes this possible early in reading development. Instead of being limited to "See Spot run," they can create original, rhyming, engaging stories using dozens of decodable words. This success builds reading identity: "I am a reader AND a writer!" This week, they mastered 30+ words across three families — that\'s genuine reading power!'
          },
          nextSteps: [
            'Re-read the story daily to build fluency!',
            'Write a sequel using the same families.',
            'Next week: Meet three NEW word families (-et, -en, -eg)!',
            'Keep using word families in everyday reading.'
          ]
        }
      }
    ]
  },

  // ========= LESSON 46 =========
  {
    id: 46,
    phase: '4',
    quarter: 1,
    week: 10,
    weekIndex: 9,
    dayInWeek: 1,
    title: 'Lesson 46: Meet the _ET Family',
    theme: 'Word Families: -et, -en, -eg',
    skillRecurrenceTags: ['word-families', 'short-e', 'pattern-recognition'],
    relatedToPreviousSkills: ['word-families', 'short-vowels', 'rime-patterns'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to the _ET Family!',
        duration: '1 min',
        content: {
          goal: 'Introduce the -ET word family, building on word family knowledge with new short-E vowel.',
          parentGuide: [
            'This week shifts from short-A families to short-E families.',
            'Children apply the SAME strategy with a different vowel sound.',
            'This shows word families work across ALL vowels!',
            'Short E (/ĕ/ as in "bed") is the focus vowel this week.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Index cards (optional)'],
          tip: 'Emphasize the /ĕ/ sound clearly — it\'s different from short A!',
          researchNote: 'Ehri (2020) - Transferring word family strategies across different vowel sounds builds phonological flexibility and decoding efficiency.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: New Vowel, Same Strategy',
        duration: '3 min',
        focus: 'Pattern Transfer',
        researchBasis: 'National Reading Panel (2000) - Systematic phonics across multiple vowel patterns strengthens decoding automaticity.',
        content: {
          goal: 'Connect last week\'s -AT/-AN/-AP strategy to this week\'s -ET family.',
          whatToSay: [
            '"Last week you were short-A family experts!"',
            '"This week: new vowel, same strategy!"',
            '"Meet the short-E families!"',
            '"Starting with the _ET family!"'
          ],
          whatToDo: [
            'Review last week briefly:',
            '  "What were our three families last week?"',
            '  Child: "-AT, -AN, -AP!"',
            '  "What vowel did they all have?"',
            '  Child: "Short A!"',
            '',
            'Introduce short E:',
            '  "This week: short E!"',
            '  "Say the short E sound: /ĕ/ /ĕ/ /ĕ/"',
            '  Child repeats: "/ĕ/"',
            '  "Like in BED, PET, WET"',
            '',
            'Show the parallel:',
            '  Write side by side:',
            '  **Short A | Short E**',
            '  CAT     | (coming soon)',
            '  MAN     | (coming soon)',
            '  "Same strategy, new vowel!"',
            '',
            'Introduce -ET:',
            '  Write -ET in large letters',
            '  "This is the _ET family!"',
            '  "Short E + T"',
            '  "/ĕ/ + /t/ = /ĕt/"',
            '  "Just like -AT was /ă/ + /t/!"',
            '',
            'Make first -ET word:',
            '  "What if we put P before -ET?"',
            '  "P-ET... PET!"',
            '  Write: PET',
            '  "You just read your first -ET word!"'
          ],
          examples: [
            'You: "Short A was CAT. Short E is...?" Child: "PET!" You: "Same strategy, new vowel!"'
          ],
          watchFor: [
            'Clear /ĕ/ sound pronunciation.',
            'Recognition of strategy transfer.',
            'First successful -ET word reading.'
          ],
          parentTip: 'Emphasize: "You already know HOW to do this — just a different vowel sound!"'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Build the _ET Family',
        duration: '4 min',
        focus: 'Word Generation',
        researchBasis: 'Ehri (2001) - Active word building with new rime patterns strengthens orthographic mapping.',
        content: {
          goal: 'Generate and read all -ET family words, including blends.',
          whatToSay: [
            '"Let\'s find ALL the _ET family members!"',
            '"Change the beginning, keep the -ET!"',
            '"Just like we did with -AT!"'
          ],
          whatToDo: [
            'Create two columns: Simple | Blends',
            '',
            'Build simple -ET words:',
            '  "What letters work before -ET?"',
            '',
            '  **Simple -ET words:**',
            '  - BET',
            '  - GET',
            '  - JET',
            '  - LET',
            '  - MET',
            '  - NET',
            '  - PET',
            '  - SET',
            '  - VET',
            '  - WET',
            '  - YET',
            '',
            'Write each word as child suggests.',
            'Test: "Is that a real word?"',
            'If yes, add to list.',
            '',
            'Now add blends:',
            '  "Remember our blends? Let\'s try them with -ET!"',
            '',
            '  **Blend -ET words:**',
            '  - FRET (FR-blend)',
            '',
            '  Note: -ET has fewer common blends than -AT',
            '  This is normal! Different families have different sizes.',
            '',
            'Count family members:',
            '  "We found [X] simple words!"',
            '  "Plus [Y] blend word(s)!"',
            '  "The -ET family is big!"',
            '',
            'Quick reading practice:',
            '  Point to words randomly',
            '  Child reads each',
            '  "PET, WET, JET, LET..."',
            '  Celebrate fluency!',
            '',
            'Compare to -AT:',
            '  "How is -ET like -AT?"',
            '  Child: "Same strategy! Change beginning!"',
            '  "What\'s different?"',
            '  Child: "Different vowel sound!"',
            '  "Yes! /ă/ vs /ĕ/!"'
          ],
          examples: [
            'Child: "J-ET... JET!" You write it. You: "Yes! Like a jet plane! Part of the -ET family!"'
          ],
          watchFor: [
            'Successful word generation.',
            'Clear short-E pronunciation.',
            'Recognition of strategy similarity.',
            'Reading fluency with -ET words.'
          ],
          parentTip: 'Some families are bigger than others — that\'s okay! Quality over quantity.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Switch & Read Game',
        duration: '3 min',
        focus: 'Fluency Practice',
        researchBasis: 'Lonigan et al. (2000) - Rapid word recognition through pattern-based reading builds decoding fluency.',
        content: {
          goal: 'Practice switching beginning sounds within -ET family for fluency.',
          whatToSay: [
            '"Remember Switch the Sound?"',
            '"We played it with -AT!"',
            '"Let\'s play with -ET!"'
          ],
          whatToDo: [
            'Start with one word: PET',
            '',
            'Create transformation chains:',
            '',
            '  **Chain 1:**',
            '  You: "We have PET. Switch P to W. What word?"',
            '  Child: "WET!"',
            '  You: "Switch W to N!"',
            '  Child: "NET!"',
            '  You: "Switch N to J!"',
            '  Child: "JET!"',
            '  Result: PET → WET → NET → JET',
            '',
            '  **Chain 2:**',
            '  Start: GET',
            '  Switch to: LET → MET → SET → VET',
            '',
            '  **Chain 3: Challenge**',
            '  "Start with BET, end with YET!"',
            '  Child creates own chain',
            '',
            'Write chains visually:',
            '  PET → WET → NET → JET',
            '  Draw arrows between words',
            '',
            'Speed challenge:',
            '  "How fast can you read this chain?"',
            '  Time them (optional)',
            '  "Read it backwards!"',
            '  JET → NET → WET → PET',
            '',
            'Random switching:',
            '  Parent calls out switches faster',
            '  "PET to LET!"',
            '  "LET to MET!"',
            '  "MET to GET!"',
            '  Tests quick pattern application',
            '',
            'Celebrate mastery:',
            '  "You learned a NEW family in one lesson!"',
            '  "That\'s because you understand the STRATEGY!"'
          ],
          examples: [
            'You: "Switch WET to GET!" Child: "/g/...ET... GET!" You: "Lightning fast!"'
          ],
          watchFor: [
            'Quick word transformations.',
            'No hesitation on -ET ending.',
            'Success with random switches.',
            'Confidence with new family.'
          ],
          parentTip: 'Speed shows automaticity — they\'re not thinking, they\'re pattern-reading!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: _ET Family Illustrations',
        duration: '2 min',
        focus: 'Meaning Connection',
        researchBasis: 'Scarborough (2001) - Connecting word recognition to meaning supports comprehension development.',
        content: {
          goal: 'Draw and label 3-4 -ET family words, connecting reading to meaning.',
          whatToSay: [
            '"Let\'s draw some _ET family members!"',
            '"Pick your 3 favorites!"',
            '"Draw and label them!"'
          ],
          whatToDo: [
            'Child selects 3-4 -ET words to illustrate.',
            '',
            'Popular choices:',
            '  - PET (dog, cat, fish)',
            '  - JET (airplane)',
            '  - WET (water, rain)',
            '  - NET (fishing net, butterfly net)',
            '',
            'For each word:',
            '  Child draws simple picture',
            '  Child labels with word',
            '  Highlight -ET in different color',
            '',
            'Sentence extension (optional):',
            '  "My PET is wet."',
            '  "The JET is in a NET." (silly!)',
            '  Uses multiple -ET words together',
            '',
            'Title the page:',
            '  "The _ET Family"',
            '  "Short E Words"',
            '',
            'Display alongside last week\'s -AT/-AN/-AP gallery:',
            '  "Now you have TWO vowel families!"',
            '  "Short A and Short E!"',
            '  "Next: -EN and -EG families!"'
          ],
          examples: [
            'Child draws airplane, labels "JET" with ET in red. You: "Perfect! I can see it\'s a jet AND read the word!"'
          ],
          watchFor: [
            'Pictures match word meanings.',
            'Correct spelling.',
            'Clear -ET highlighting.',
            'Pride in new family mastery.'
          ],
          parentTip: 'Start a "Family Gallery Wall" with both short-A and short-E families!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '✨ You Met the _ET Family!',
            'You practiced:',
            '✓ Learning short-E sound',
            '✓ Building 10+ -ET words',
            '✓ Switching sounds with -ET',
            '✓ Drawing -ET family members'
          ],
          reviewQuestions: [
            'Can your child clearly say /ĕ/?',
            'Did they generate -ET words successfully?',
            'Can they switch sounds within -ET family?',
            'Do they see the strategy transfer from -AT to -ET?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Moving from short-A to short-E families is a crucial step! It shows children that word family strategies are TRANSFERABLE — they work across all vowels. This realization accelerates learning dramatically. Instead of learning each word individually, children recognize they have a powerful SYSTEM that applies everywhere. This week they\'ll master short-E families (-ET, -EN, -EG) using the exact same approach they used last week. This pattern recognition and strategy transfer is what separates struggling readers from proficient ones. Your child is learning to be a strategic, efficient reader!'
          },
          nextSteps: [
            'Hunt for -ET words in books today!',
            'Practice: "I spy something that rhymes with PET!"',
            'Tomorrow: Meet the -EN and -EG families!',
            'Review: Say short-A words, then short-E words.'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 47 =========
  {
    id: 47,
    phase: '4',
    quarter: 1,
    week: 10,
    weekIndex: 9,
    dayInWeek: 2,
    title: 'Lesson 47: Word-Family Sort',
    theme: 'Word Families: -et, -en, -eg',
    skillRecurrenceTags: ['word-families', 'short-e', 'phonological-discrimination'],
    relatedToPreviousSkills: ['word-families', 'sorting', 'pattern-recognition'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Word-Family Sort!',
        duration: '1 min',
        content: {
          goal: 'Introduce -EN and -EG families, then practice sorting all three short-E families.',
          parentGuide: [
            'Today introduces TWO new families in one lesson!',
            'Children apply yesterday\'s -ET learning to -EN and -EG.',
            'Sorting activities strengthen phonological discrimination.',
            'All three families share short-E vowel but have different endings.'
          ],
          materials: ['Paper and pencil', 'Crayons (3 colors)', 'Index cards or small papers'],
          tip: 'Use colors to code families: -ET in blue, -EN in green, -EG in red!',
          researchNote: 'Ehri (2001) - Sorting words by rime patterns strengthens orthographic pattern recognition and phoneme awareness simultaneously.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Meet the _EN Family',
        duration: '3 min',
        focus: 'New Family Introduction',
        researchBasis: 'National Reading Panel (2000) - Multiple rime pattern exposure builds systematic phonics knowledge.',
        content: {
          goal: 'Build and read the -EN word family quickly, using established strategy.',
          whatToSay: [
            '"Yesterday: _ET family!"',
            '"Today: TWO more short-E families!"',
            '"First: the _EN family!"',
            '"Same vowel, different ending!"'
          ],
          whatToDo: [
            'Write -EN in large letters (different color than -ET).',
            '',
            'Build simple -EN words:',
            '  "What letters work with -EN?"',
            '',
            '  **Simple -EN words:**',
            '  - BEN (a name)',
            '  - DEN',
            '  - HEN',
            '  - KEN (a name)',
            '  - MEN',
            '  - PEN',
            '  - TEN',
            '',
            'Add blend -EN words:',
            '  **Blend -EN words:**',
            '  - GLEN (GL-blend, a valley)',
            '  - THEN (TH-digraph)',
            '  - WHEN (WH-digraph)',
            '',
            'Quick comparison:',
            '  Write: -ET | -EN',
            '  "Both have short E!"',
            '  "Different endings: /t/ vs /n/"',
            '',
            'Read through -EN family:',
            '  Point to words randomly',
            '  Child reads each',
            '  "PEN, HEN, TEN, MEN..."',
            '',
            'Count members:',
            '  "We found [X] -EN words!"',
            '  "The -EN family is ready!"'
          ],
          examples: [
            'Child: "H-EN... HEN!" You: "Yes! Like a chicken! Part of the -EN family!"'
          ],
          watchFor: [
            'Quick family building.',
            'Clear /ĕn/ pronunciation.',
            'Recognition of short-E connection.',
            'Success with new family.'
          ],
          parentTip: 'This should go faster than yesterday — they know the strategy now!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Meet the _EG Family',
        duration: '3 min',
        focus: 'Third Family Introduction',
        researchBasis: 'Lonigan et al. (2000) - Multiple phonological pattern exposure strengthens decoding flexibility.',
        content: {
          goal: 'Build and read the -EG word family, completing the three short-E families.',
          whatToSay: [
            '"Now the third short-E cousin!"',
            '"The _EG family!"',
            '"Three families, one vowel!"'
          ],
          whatToDo: [
            'Write -EG in large letters (third color).',
            '',
            'Build simple -EG words:',
            '',
            '  **Simple -EG words:**',
            '  - BEG',
            '  - KEG (a small barrel)',
            '  - LEG',
            '  - MEG (a name)',
            '  - PEG',
            '',
            '  Note: -EG is a smaller family — that\'s normal!',
            '',
            'No common blend words for -EG.',
            '  "Not every family has blends — that\'s okay!"',
            '',
            'Compare all three:',
            '  Write side by side:',
            '  **-ET  |  -EN  |  -EG**',
            '  PET  |  PEN  |  PEG',
            '  WET  |  (no WEN)  |  (no WEG)',
            '  LET  |  (no LEN)  |  LEG',
            '',
            '  "All three have short E!"',
            '  "Different endings: /t/, /n/, /g/"',
            '',
            'Read across families:',
            '  You point randomly across all three',
            '  "Read this! Now this! Now this!"',
            '  PET → PEN → PEG → BET → BEG → HEN',
            '  Tests discrimination',
            '',
            'Celebrate three-family mastery:',
            '  "You know THREE short-E families!"',
            '  "Just like last week\'s short-A families!"',
            '  "Same strategy, new vowel!"'
          ],
          examples: [
            'You point: PET, PEN, PEG. Child reads all three. You: "Perfect! You hear the different endings!"'
          ],
          watchFor: [
            'Clear -EG pronunciation.',
            'Discrimination across all three families.',
            'Understanding family size varies.',
            'Confidence with multiple families.'
          ],
          parentTip: 'Small families like -EG are just as valuable — quality over quantity!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Three-Family Sorting',
        duration: '4 min',
        focus: 'Phonological Sorting',
        researchBasis: 'Ehri (2020) - Active sorting by phonological patterns develops metalinguistic awareness and orthographic knowledge.',
        content: {
          goal: 'Sort mixed words into correct -ET, -EN, or -EG families.',
          whatToSay: [
            '"Time to sort!"',
            '"I\'ll say words — you tell me the family!"',
            '"Listen to the ending!"'
          ],
          whatToDo: [
            'Create three columns on paper:',
            '  **-ET  |  -EN  |  -EG**',
            '',
            'Call out words in random order:',
            '  Child listens → identifies family → places in column',
            '',
            '**Words to sort (mix them up!):**',
            '  - PET → -ET',
            '  - HEN → -EN',
            '  - LEG → -EG',
            '  - WET → -ET',
            '  - TEN → -EN',
            '  - BEG → -EG',
            '  - NET → -ET',
            '  - PEN → -EN',
            '  - PEG → -EG',
            '  - JET → -ET',
            '  - MEN → -EN',
            '  - LET → -ET',
            '  - DEN → -EN',
            '  - KEG → -EG',
            '  - GET → -ET',
            '',
            'For each word:',
            '  You say: "HEN"',
            '  Child thinks: "Ends with /n/"',
            '  Child: "-EN family!"',
            '  Write in -EN column',
            '  Confirm: "Correct! HEN is -EN!"',
            '',
            'Increase difficulty:',
            '',
            '  **Round 2: Minimal pairs**',
            '    PET / PEN / PEG — all start same!',
            '    BET / (no BEN) / BEG',
            '    (no WET counterpart) / (no WEN) / (no WEG)',
            '    Focus on ENDING discrimination',
            '',
            '  **Round 3: Faster pace**',
            '    Call words more quickly',
            '    "JET! LEG! PEN! WET!"',
            '    Tests automatic recognition',
            '',
            '  **Round 4: Write instead of verbal**',
            '    Child writes word in correct column',
            '    Adds spelling practice',
            '',
            'Count sorted words:',
            '  "How many in -ET?"',
            '  "How many in -EN?"',
            '  "How many in -EG?"',
            '  Compare family sizes',
            '',
            'Reflection questions:',
            '  "Which ending was easiest to hear?"',
            '  "Which was trickiest?"',
            '  "How did you know which family?"',
            '  Child explains: "I listened to the last sound!"'
          ],
          examples: [
            'You: "BEG!" Child: "Ends with /g/! -EG family!" You: "Perfect listening and sorting!"'
          ],
          watchFor: [
            'Accurate ending sound identification.',
            'Correct family placement.',
            'Speed improving through rounds.',
            'Success with minimal pairs.'
          ],
          parentTip: 'If they struggle, have them say the word slowly and emphasize the ending!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Family Gallery Update',
        duration: '2 min',
        focus: 'Visual Organization',
        researchBasis: 'Scarborough (2001) - Visual word displays support orthographic memory and reading development.',
        content: {
          goal: 'Add -EN and -EG families to the word family gallery.',
          whatToSay: [
            '"Let\'s add to our gallery!"',
            '"Now we have -ET, -EN, and -EG!"',
            '"Three short-E families!"'
          ],
          whatToDo: [
            'Create three-section display (or add to yesterday\'s).',
            '',
            '**Section 1: -ET Family (from yesterday)**',
            '  Already complete',
            '  One picture/label',
            '',
            '**Section 2: -EN Family (new)**',
            '  Title: "The -EN Family"',
            '  Child draws favorite -EN word',
            '  Popular: HEN, PEN, TEN',
            '  Label with word (highlight -EN)',
            '',
            '**Section 3: -EG Family (new)**',
            '  Title: "The -EG Family"',
            '  Child draws favorite -EG word',
            '  Popular: LEG, PEG, BEG',
            '  Label with word (highlight -EG)',
            '',
            'Add gallery title:',
            '  "Three Short-E Families!"',
            '  "All have /ĕ/ sound!"',
            '  "Different endings!"',
            '',
            'Compare to last week:',
            '  Place SHORT-A gallery next to SHORT-E gallery',
            '  "Last week: -AT, -AN, -AP (short A)"',
            '  "This week: -ET, -EN, -EG (short E)"',
            '  "Same strategy, different vowels!"',
            '',
            'Count total words known:',
            '  "You can read ALL these families!"',
            '  Short-A: 30+ words',
            '  Short-E: 25+ words',
            '  "That\'s over 50 words using word families!"',
            '',
            'Display proudly:',
            '  Hang both galleries together',
            '  Create "Word Family Wall"',
            '  Reference for ongoing reading'
          ],
          examples: [
            'Child draws hen, labels "HEN" with -EN in green. You: "Perfect! Now we have all three short-E families!"'
          ],
          watchFor: [
            'Clear representation of new families.',
            'Correct spelling and labeling.',
            'Connection between weeks.',
            'Pride in growing word knowledge.'
          ],
          parentTip: 'This visual display shows concrete evidence of their reading growth!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎯 You Sorted Three Families!',
            'You practiced:',
            '✓ Building the -EN family',
            '✓ Building the -EG family',
            '✓ Sorting words by ending sound',
            '✓ Creating short-E family gallery'
          ],
          reviewQuestions: [
            'Can your child name all three short-E families?',
            'Can they sort words accurately by ending?',
            'Do they recognize the pattern similarity to short-A families?',
            'Can they generate words for each family?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Sorting activities are powerful learning tools! When children actively categorize words by sound patterns, they develop phonological awareness AND orthographic knowledge simultaneously. Today\'s lesson showed pattern transfer at work: your child applied the SAME strategy they learned with -AT/-AN/-AP to -ET/-EN/-EG. This is exactly how proficient readers think — they recognize patterns across different contexts. The ability to discriminate between similar-sounding words (PET/PEN/PEG) is crucial for both reading accuracy and spelling. These sorting skills predict long-term reading success!'
          },
          nextSteps: [
            'Play "I Spy" with word families: "I spy a word in the -EN family!"',
            'Practice sorting throughout the day.',
            'Tomorrow: Write and draw three words from two families!',
            'Keep adding to the Word Family Wall!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 48 =========
  {
    id: 48,
    phase: '4',
    quarter: 1,
    week: 10,
    weekIndex: 9,
    dayInWeek: 3,
    title: 'Lesson 48: Write 3, Draw 3',
    theme: 'Word Families: -et, -en, -eg',
    skillRecurrenceTags: ['word-families', 'writing-practice', 'meaning-connection'],
    relatedToPreviousSkills: ['word-families', 'spelling-patterns', 'encoding'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Write 3, Draw 3!',
        duration: '1 min',
        content: {
          goal: 'Write three words from two different short-E families, then illustrate all three.',
          parentGuide: [
            'This lesson combines spelling practice with meaning connection.',
            'Writing cements the orthographic patterns learned through reading.',
            'Drawing ensures children understand word meanings, not just sounds.',
            'Choose any TWO of the three families: -ET, -EN, or -EG.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Word family galleries (reference)'],
          tip: 'Let them choose which families — ownership increases motivation!',
          researchNote: 'Ehri (2020) - Combined encoding (spelling) and semantic connection (meaning) activities strengthen orthographic mapping more than either alone.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Choose Two Families',
        duration: '2 min',
        focus: 'Selection & Planning',
        researchBasis: 'National Reading Panel (2000) - Student choice in literacy activities increases engagement and effort.',
        content: {
          goal: 'Child selects two short-E families to practice with.',
          whatToSay: [
            '"Today you\'re the writer AND artist!"',
            '"Pick TWO short-E families!"',
            '"You\'ll write words, then draw them!"'
          ],
          whatToDo: [
            'Present three options:',
            '  **Option 1: -ET and -EN**',
            '  **Option 2: -ET and -EG**',
            '  **Option 3: -EN and -EG**',
            '',
            'Child chooses two families.',
            '',
            'Set up paper:',
            '  "We\'ll write 3 words from EACH family."',
            '  "That\'s 6 words total!"',
            '  "Then draw pictures for 3 of them!"',
            '',
            'Divide paper:',
            '  Top half: Two columns for writing',
            '  **Family 1  |  Family 2**',
            '  Bottom half: Three boxes for drawing',
            '  **Drawing 1 | Drawing 2 | Drawing 3**',
            '',
            'Write family headers:',
            '  Child writes family endings at top',
            '  Example: -ET | -EN',
            '  Use different colors for each',
            '',
            'Explain the process:',
            '  "First: Write 3 words in each family"',
            '  "Then: Pick your favorite 3 total"',
            '  "Finally: Draw those 3!"'
          ],
          examples: [
            'Child: "I pick -ET and -EG!" You: "Great! PET and LEG, WET and BEG — lots of options!"'
          ],
          watchFor: [
            'Clear family selection.',
            'Understanding of paper setup.',
            'Readiness to begin writing.'
          ],
          parentTip: 'All combinations work well — let their interest guide them!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Write Three in Each Family',
        duration: '4 min',
        focus: 'Spelling Practice',
        researchBasis: 'Ehri (2001) - Writing words by analogy (using family patterns) is more effective than memorizing spellings individually.',
        content: {
          goal: 'Write three words from each chosen family with correct spelling.',
          whatToSay: [
            '"Write 3 words in [family 1]!"',
            '"Then 3 words in [family 2]!"',
            '"Remember the family ending stays the same!"'
          ],
          whatToDo: [
            '**Family 1: Write three words**',
            '',
            'Process for each word:',
            '  1. Child says word aloud',
            '  2. Segments: beginning + family ending',
            '  3. Writes word',
            '  4. Reads back to check',
            '',
            'Example with -ET family:',
            '  Word 1: PET → P + ET → writes "PET"',
            '  Word 2: WET → W + ET → writes "WET"',
            '  Word 3: JET → J + ET → writes "JET"',
            '',
            'Example with -EN family:',
            '  Word 1: PEN → P + EN → writes "PEN"',
            '  Word 2: HEN → H + EN → writes "HEN"',
            '  Word 3: TEN → T + EN → writes "TEN"',
            '',
            'Example with -EG family:',
            '  Word 1: LEG → L + EG → writes "LEG"',
            '  Word 2: PEG → P + EG → writes "PEG"',
            '  Word 3: BEG → B + EG → writes "BEG"',
            '',
            'Scaffolding support:',
            '  If struggling: "What\'s the first sound you hear?"',
            '  If spelling ending wrong: "Remember — -ET never changes!"',
            '  If unsure: "Look at your gallery for examples!"',
            '',
            '**Family 2: Write three more words**',
            '',
            'Repeat same process with second family.',
            'Should be faster and more independent.',
            '',
            'Total result: 6 words written',
            '  3 from first family',
            '  3 from second family',
            '  All spelled correctly',
            '',
            'Review all six:',
            '  "Read all your words!"',
            '  Child reads through both lists',
            '  "You wrote 6 words!"',
            '  "All from short-E families!"',
            '',
            'Celebrate spelling success:',
            '  "You used PATTERNS to spell!"',
            '  "Not letter-by-letter!"',
            '  "That\'s smart spelling!"'
          ],
          examples: [
            'Child writes: H-E-N. You: "Read it!" Child: "HEN!" You: "Perfect spelling! -EN family!"'
          ],
          watchFor: [
            'Correct family ending spelling.',
            'Accurate beginning sound.',
            'Independent word generation.',
            'Ability to read back words.',
            'Growing speed/confidence.'
          ],
          parentTip: 'If handwriting is slow, focus on spelling accuracy — penmanship comes later!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Choose Your Three Favorites',
        duration: '1 min',
        focus: 'Selection',
        researchBasis: 'Vygotsky (1978) - Personal choice and investment increase learning motivation and retention.',
        content: {
          goal: 'Child selects three words from the six written to illustrate.',
          whatToSay: [
            '"Look at all 6 words you wrote!"',
            '"Which 3 do you want to draw?"',
            '"Pick your favorites!"'
          ],
          whatToDo: [
            'Child reviews six written words.',
            '',
            'Selection considerations:',
            '  "Which ones are fun to draw?"',
            '  "Which ones can you picture clearly?"',
            '  "Do you want one from each family?"',
            '',
            'Examples of good drawing choices:',
            '  - PET (easy to draw animals)',
            '  - JET (fun airplane)',
            '  - HEN (recognizable chicken)',
            '  - LEG (simple body part)',
            '  - NET (interesting pattern)',
            '',
            'Mark chosen words:',
            '  Circle or star the three selected',
            '  Or write numbers 1, 2, 3 next to them',
            '',
            'If unsure:',
            '  Parent suggests: "PET is fun to draw!"',
            '  "What kind of pet would you draw?"',
            '  Helps child visualize',
            '',
            'Prepare drawing section:',
            '  Look at three boxes at bottom',
            '  "Box 1 for [first word]"',
            '  "Box 2 for [second word]"',
            '  "Box 3 for [third word]"'
          ],
          examples: [
            'Child: "I pick JET, HEN, and LEG!" You: "Great choices! Easy to draw and from different families!"'
          ],
          watchFor: [
            'Clear selection made.',
            'Variety encouraged (optional).',
            'Readiness to illustrate.'
          ],
          parentTip: 'Let them choose based on what they\'re excited to draw!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Draw Your Three Words',
        duration: '4 min',
        focus: 'Meaning Connection',
        researchBasis: 'Scarborough (2001) - Connecting word form to meaning strengthens the language comprehension strand of reading.',
        content: {
          goal: 'Illustrate three chosen words, connecting reading/spelling to meaning.',
          whatToSay: [
            '"Now bring your words to life!"',
            '"Draw what each word means!"',
            '"Make it clear and colorful!"'
          ],
          whatToDo: [
            'For EACH of the three chosen words:',
            '',
            '  **Step 1: Draw picture**',
            '    Child draws in designated box',
            '    Simple drawings work best',
            '    Add details and color',
            '',
            '  **Step 2: Label picture**',
            '    Write word below or above picture',
            '    Copy from written list above',
            '    Or write from memory',
            '    Highlight family ending in color',
            '',
            '  **Step 3: Check connection**',
            '    "Does your picture show the word?"',
            '    "Can someone tell it\'s a [word]?"',
            '    Ensures clear representation',
            '',
            'Example drawings:',
            '',
            '  **PET:**',
            '    Draw dog, cat, fish, or bird',
            '    Add details (collar, bowl, leash)',
            '    Label: PET (highlight -ET)',
            '',
            '  **JET:**',
            '    Draw airplane with wings',
            '    Add windows, clouds',
            '    Label: JET (highlight -ET)',
            '',
            '  **HEN:**',
            '    Draw chicken with feathers',
            '    Add beak, feet, eggs',
            '    Label: HEN (highlight -EN)',
            '',
            '  **LEG:**',
            '    Draw leg with foot',
            '    Or whole person with arrow to leg',
            '    Label: LEG (highlight -EG)',
            '',
            'As they draw, discuss:',
            '  "What makes a good PET?"',
            '  "Have you seen a JET in the sky?"',
            '  "What sound does a HEN make?"',
            '  Connects words to real-world knowledge',
            '',
            'Extension: Add sentences (optional):',
            '  "My PET is a cat."',
            '  "A HEN lays eggs."',
            '  "I have two legs."',
            '  Shows understanding beyond single words',
            '',
            'Final review:',
            '  Look at complete page',
            '  "You wrote 6 words!"',
            '  "You drew 3 pictures!"',
            '  "You connected spelling AND meaning!"'
          ],
          examples: [
            'Child draws airplane, labels "JET" with -ET in blue. You: "Perfect! I can see it\'s a jet AND read the word!"'
          ],
          watchFor: [
            'Pictures clearly represent words.',
            'Correct labeling with proper spelling.',
            'Family endings highlighted.',
            'Pride in completed work.',
            'Connection between writing and meaning.'
          ],
          parentTip: 'Simple drawings are perfect — this is about word meaning, not art skill!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎨 You Wrote AND Drew!',
            'You practiced:',
            '✓ Choosing two short-E families',
            '✓ Writing 6 family words',
            '✓ Selecting favorite words',
            '✓ Drawing meaningful illustrations'
          ],
          reviewQuestions: [
            'Did your child spell family endings correctly?',
            'Were they able to generate words independently?',
            'Do their pictures clearly show word meanings?',
            'Can they explain why words belong to specific families?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'This lesson bridges the gap between decoding and comprehension! When children WRITE words, they strengthen their orthographic knowledge (how words are spelled). When they DRAW words, they strengthen their semantic knowledge (what words mean). Both are essential for reading success. The Reading Rope model shows that skilled reading requires BOTH word recognition (which includes decoding and spelling) AND language comprehension (which includes vocabulary and meaning). Today\'s lesson exercised both strands simultaneously. Children who understand what they read — not just decode sounds — become strong, lifelong readers!'
          },
          nextSteps: [
            'Display the Write 3, Draw 3 page proudly!',
            'Tomorrow: Riddle Me This — word family riddles!',
            'Practice: "I\'m thinking of a -EN word you write with..."',
            'Keep adding to Word Family Wall!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 49 =========
  {
    id: 49,
    phase: '4',
    quarter: 1,
    week: 10,
    weekIndex: 9,
    dayInWeek: 4,
    title: 'Lesson 49: Riddle Me This!',
    theme: 'Word Families: -et, -en, -eg',
    skillRecurrenceTags: ['word-families', 'vocabulary', 'inferencing'],
    relatedToPreviousSkills: ['word-families', 'decoding', 'meaning-connection'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Riddle Me This!',
        duration: '1 min',
        content: {
          goal: 'Solve and create riddles using short-E word family clues to build vocabulary and inferencing.',
          parentGuide: [
            'Riddles combine vocabulary, inferencing, and word family knowledge.',
            'This activity builds comprehension skills alongside decoding.',
            'Children learn to think about word meanings, not just sounds.',
            'Creating riddles is higher-order thinking!'
          ],
          materials: ['Paper and pencil', 'Word family lists (reference)', 'Optional: small objects/toys'],
          tip: 'Start with easier riddles, then let child create their own!',
          researchNote: 'Scarborough (2001) - Vocabulary and verbal reasoning are critical components of the language comprehension strand of reading development.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Introduction to Word Riddles',
        duration: '2 min',
        focus: 'Riddle Concept',
        researchBasis: 'Whitehurst & Lonigan (1998) - Interactive verbal games build vocabulary and inferencing skills essential for reading comprehension.',
        content: {
          goal: 'Introduce riddle format using word family clues.',
          whatToSay: [
            '"Today we\'re playing a guessing game!"',
            '"I\'ll give you clues about a word!"',
            '"You figure out which -ET, -EN, or -EG word it is!"',
            '"Ready? Here comes the first riddle!"'
          ],
          whatToDo: [
            'Explain riddle format:',
            '  "A riddle gives you CLUES."',
            '  "You use the clues to guess the word."',
            '  "The answer is always from our word families!"',
            '',
            'Demo riddle #1:',
            '  "I am an -ET word."',
            '  "I fly in the sky."',
            '  "I go very fast."',
            '  "What am I?"',
            '  Child: "JET!"',
            '  "Yes! JET! You solved it!"',
            '',
            'Demo riddle #2:',
            '  "I am an -EN word."',
            '  "You use me to write."',
            '  "I have ink inside."',
            '  "What am I?"',
            '  Child: "PEN!"',
            '  "Perfect! PEN!"',
            '',
            'Point out clue types:',
            '  "Some clues tell the FAMILY: -ET, -EN, -EG"',
            '  "Some clues tell WHAT IT DOES: fly, write"',
            '  "Some clues tell WHERE IT IS: sky, farm"',
            '  "Some clues tell WHAT IT LOOKS LIKE"',
            '',
            'Set expectations:',
            '  "First I\'ll give riddles."',
            '  "Then YOU\'ll make riddles!"',
            '  "Ready to solve more?"'
          ],
          examples: [
            'You: "I am an -EG word. You use me to walk. What am I?" Child: "LEG!" You: "Solved it!"'
          ],
          watchFor: [
            'Understanding of riddle format.',
            'Using multiple clues to solve.',
            'Excitement about guessing.',
            'Readiness for more.'
          ],
          parentTip: 'Pause between clues to let them think before revealing next clue!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Parent Riddles, Child Solves',
        duration: '4 min',
        focus: 'Solving Practice',
        researchBasis: 'National Reading Panel (2000) - Vocabulary development through context clues supports reading comprehension.',
        content: {
          goal: 'Child solves 6-8 riddles using word family and meaning clues.',
          whatToSay: [
            '"Here come more riddles!"',
            '"Listen to ALL the clues!"',
            '"Think about the word family AND the meaning!"'
          ],
          whatToDo: [
            'Present riddles one at a time.',
            'Give 2-3 clues per riddle.',
            'Child guesses after hearing clues.',
            '',
            '**Riddle Set: -ET Family**',
            '',
            'Riddle 1: WET',
            '  "I am an -ET word."',
            '  "I am what you are after swimming."',
            '  "Water makes things ___."',
            '  Answer: WET',
            '',
            'Riddle 2: NET',
            '  "I am an -ET word."',
            '  "You use me to catch fish or butterflies."',
            '  "I have lots of little holes."',
            '  Answer: NET',
            '',
            'Riddle 3: PET',
            '  "I am an -ET word."',
            '  "I am an animal you keep at home."',
            '  "Dogs and cats are ___s."',
            '  Answer: PET',
            '',
            '**Riddle Set: -EN Family**',
            '',
            'Riddle 4: HEN',
            '  "I am an -EN word."',
            '  "I am a bird that lays eggs."',
            '  "I live on a farm and say \'cluck cluck.\'"',
            '  Answer: HEN',
            '',
            'Riddle 5: TEN',
            '  "I am an -EN word."',
            '  "I am a number."',
            '  "I come after nine."',
            '  Answer: TEN',
            '',
            'Riddle 6: DEN',
            '  "I am an -EN word."',
            '  "I am where a bear lives."',
            '  "It is like a cave."',
            '  Answer: DEN',
            '',
            '**Riddle Set: -EG Family**',
            '',
            'Riddle 7: LEG',
            '  "I am an -EG word."',
            '  "You have two of me."',
            '  "You use me to walk and run."',
            '  Answer: LEG',
            '',
            'Riddle 8: BEG',
            '  "I am an -EG word."',
            '  "I mean to ask nicely for something."',
            '  "Please, please, please! I ___ you!"',
            '  Answer: BEG',
            '',
            'For each riddle:',
            '  Give first clue → pause',
            '  Give second clue → pause',
            '  Give third clue if needed',
            '  Child guesses',
            '  Celebrate: "You solved it!"',
            '',
            'If stuck:',
            '  "What family is it?"',
            '  "What does that clue tell us?"',
            '  "Can you think of -ET words about water?"',
            '',
            'Track solved riddles:',
            '  Keep count: "That\'s 3 solved!"',
            '  Celebrate each success'
          ],
          examples: [
            'You: "An -EN word. Lives on a farm. Lays eggs." Child thinks... "HEN!" You: "Yes! You used all the clues!"'
          ],
          watchFor: [
            'Using word family clue.',
            'Integrating meaning clues.',
            'Logical thinking process.',
            'Success rate improving.'
          ],
          parentTip: 'If they guess wrong, give an extra clue rather than saying "no" — keep it positive!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Child Creates Riddles',
        duration: '4 min',
        focus: 'Riddle Creation',
        researchBasis: 'Vygotsky (1978) - Creating language tasks (rather than just responding) develops higher-order thinking and metalinguistic awareness.',
        content: {
          goal: 'Child creates 3-4 riddles for parent to solve.',
          whatToSay: [
            '"Now YOU make the riddles!"',
            '"Pick a word from our families!"',
            '"Give me clues — I\'ll try to guess!"'
          ],
          whatToDo: [
            'Role reversal: Child gives riddles, parent solves.',
            '',
            'Process for creating each riddle:',
            '',
            '  **Step 1: Child picks word**',
            '    "I\'m thinking of a word!"',
            '    Child picks from -ET, -EN, or -EG families',
            '    Keeps it secret from parent',
            '',
            '  **Step 2: Child gives family clue**',
            '    "It is an -EN word."',
            '    Parent: "Hmm... -EN word..."',
            '',
            '  **Step 3: Child gives meaning clue**',
            '    "You use it to write."',
            '    Parent: "Is it PEN?"',
            '    Child: "Yes!"',
            '',
            'Scaffolding for clue-giving:',
            '  If child struggles to make clues:',
            '  "What does a PEN do?"',
            '  "Where do you see a HEN?"',
            '  "What does a JET look like?"',
            '  Help them formulate clues',
            '',
            'Parent solving tips:',
            '  Pretend to struggle a bit!',
            '  "Hmm... let me think..."',
            '  "Is it...?"',
            '  Makes child feel successful',
            '',
            '**Target: 3-4 child-created riddles**',
            '',
            'Example child riddles:',
            '',
            '  Child riddle 1:',
            '    "It\'s an -EG word."',
            '    "You walk with it."',
            '    Answer: LEG',
            '',
            '  Child riddle 2:',
            '    "It\'s an -ET word."',
            '    "It flies."',
            '    Answer: JET',
            '',
            '  Child riddle 3:',
            '    "It\'s an -EN word."',
            '    "It\'s a number."',
            '    Answer: TEN',
            '',
            'Celebrate creativity:',
            '  "That was a great clue!"',
            '  "Your riddles are tricky!"',
            '  "You\'re a riddle-maker!"',
            '',
            'Write down child\'s riddles:',
            '  Create a "Riddle Book"',
            '  Child dictates, parent writes',
            '  Or child writes if able',
            '  Save for later use'
          ],
          examples: [
            'Child: "It\'s -ET and it\'s wet!" Parent: "Hmm... is it WET?" Child: "Yes! Too easy!" You: "Great riddle!"'
          ],
          watchFor: [
            'Ability to formulate clues.',
            'Understanding of riddle structure.',
            'Creative clue-giving.',
            'Pride in stumping parent.'
          ],
          parentTip: 'Let them stump you! Act surprised when you "finally" figure it out!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Riddle Challenge Game',
        duration: '2 min',
        focus: 'Speed & Review',
        researchBasis: 'Ehri (2001) - Rapid word retrieval through meaning-based activities strengthens semantic-phonological connections.',
        content: {
          goal: 'Quick-fire riddles for speed and review of all word families.',
          whatToSay: [
            '"Final challenge!"',
            '"Fast riddles — quick answers!"',
            '"Ready... GO!"'
          ],
          whatToDo: [
            'Speed riddle format:',
            '  Parent gives ONE clue',
            '  Child answers quickly',
            '  No pause — rapid fire!',
            '',
            '**Quick Riddles:**',
            '',
            '  "An animal you keep!" → PET',
            '  "You write with it!" → PEN',
            '  "You walk with it!" → LEG',
            '  "Flies fast!" → JET',
            '  "A farm bird!" → HEN',
            '  "After swimming!" → WET',
            '  "What bears live in!" → DEN',
            '  "To catch fish!" → NET',
            '  "The number after 9!" → TEN',
            '  "To ask nicely!" → BEG',
            '',
            'Keep score (optional):',
            '  "You got [X] out of 10!"',
            '  Most should get 8-10 correct',
            '',
            'Reverse: Child asks rapid riddles',
            '  Child gives one-clue riddles',
            '  Parent answers quickly',
            '  Fun role reversal',
            '',
            'Create riddle cards (optional):',
            '  Write riddles on index cards',
            '  Draw picture on back',
            '  Can be reused as game',
            '',
            'Final celebration:',
            '  "You solved SO many riddles!"',
            '  "You created riddles too!"',
            '  "You know these word families perfectly!"',
            '',
            'Extension ideas:',
            '  "Can you make a riddle about a -AT word?"',
            '  "What about a -AN word?"',
            '  Connect to last week\'s families',
            '  "You can make riddles for ANY word family!"'
          ],
          examples: [
            'You: "Flies in sky!" Child: "JET!" You: "Number after nine!" Child: "TEN!" You: "So fast!"'
          ],
          watchFor: [
            'Quick word retrieval.',
            'Accurate answers.',
            'Confidence with all families.',
            'Enjoyment of game format.'
          ],
          parentTip: 'This rapid-fire format shows they\'ve truly mastered the word families!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🧩 You\'re a Riddle Master!',
            'You practiced:',
            '✓ Solving word family riddles',
            '✓ Using multiple clues',
            '✓ Creating your own riddles',
            '✓ Quick word retrieval'
          ],
          reviewQuestions: [
            'Could your child solve riddles using family + meaning clues?',
            'Did they create clear, logical riddles?',
            'Can they retrieve words quickly from meaning clues?',
            'Did they show understanding of word meanings?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Riddles are powerful learning tools! They require children to think about words in multiple ways simultaneously: sound patterns (word families), meanings (vocabulary), and logical connections (inference). When children solve riddles, they practice the exact skill proficient readers use: integrating phonological and semantic information to identify words. When they CREATE riddles, they demonstrate even deeper understanding — they must analyze word meanings and formulate clear descriptions. This metacognitive skill (thinking about words) is what separates word-callers from true readers. Your child is learning to think WITH words, not just decode them!'
          },
          nextSteps: [
            'Make a family riddle book together!',
            'Play riddles throughout the day: "I spy a -EN word..."',
            'Tomorrow: The _ET Pet story!',
            'Create riddles for short-A families too!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 50 =========
  {
    id: 50,
    phase: '4',
    quarter: 1,
    week: 10,
    weekIndex: 9,
    dayInWeek: 5,
    title: 'Lesson 50: Storytime: The _ET Pet',
    theme: 'Word Families: -et, -en, -eg',
    skillRecurrenceTags: ['word-families', 'storytelling', 'reading-fluency'],
    relatedToPreviousSkills: ['word-families', 'decoding-fluency', 'comprehension'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to The _ET Pet Story!',
        duration: '1 min',
        content: {
          goal: 'Create or read a mini-story using predominantly -ET, -EN, and -EG words to celebrate the week.',
          parentGuide: [
            'This lesson integrates the entire week\'s learning into a story.',
            'Stories make word families memorable and meaningful.',
            'Reading connected text builds fluency and comprehension.',
            'This is the week\'s culminating celebration activity!'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'This week\'s word lists'],
          tip: 'Keep the story simple and fun — 6-8 sentences is perfect!',
          researchNote: 'Scarborough (2001) - Connected text reading (vs. word lists) develops fluency, prosody, and comprehension simultaneously.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Story Planning',
        duration: '2 min',
        focus: 'Story Setup',
        researchBasis: 'Whitehurst & Lonigan (1998) - Co-constructed narratives build both decoding and language comprehension skills.',
        content: {
          goal: 'Plan a simple story using short-E word family words.',
          whatToSay: [
            '"Let\'s make a story called \'The _ET Pet\'!"',
            '"We\'ll use our short-E family words!"',
            '"What kind of pet should it be?"'
          ],
          whatToDo: [
            'Choose story approach:',
            '  **Option A: Create together**',
            '    Parent and child build story collaboratively',
            '  **Option B: Use provided story below**',
            '    Read pre-made story featuring word families',
            '',
            'If creating together, brainstorm:',
            '  "The pet is a ___ (dog, cat, bird?)"',
            '  "What does the pet do?"',
            '  "Where does the story happen?"',
            '  "How does it end?"',
            '',
            'Word bank for planning:',
            '  From -ET: pet, wet, net, jet, get, let, vet',
            '  From -EN: pen, hen, den, ten, men',
            '  From -EG: leg, beg, peg',
            '',
            'Story structure:',
            '  Beginning: Introduce the pet',
            '  Middle: Something happens',
            '  End: Problem solved or happy ending',
            '',
            'Target length: 6-8 sentences',
            '  Short enough to complete in lesson',
            '  Long enough to be satisfying',
            '  Uses 8-12 word family words',
            '',
            '**Pre-Made Story Option:**',
            '  "The Wet Pet"',
            '',
            '  "Ben has a pet.', // PET, -EN name
            '  His pet is a hen.', // HEN, PET
            '  The hen got wet.', // HEN, WET
            '  Ben let the hen in his den.', // BEN, LET, HEN, DEN
            '  The hen met ten men.', // HEN, MET, TEN, MEN
            '  The men pet the hen.', // MEN, PET, HEN
            '  Now the hen is not wet!', // HEN, WET
            '  The end!"', // -EN
            '',
            '  Word count: 12 word-family words used!',
            '',
            'Choose your path:',
            '  Parent: "Should we make our own or use this one?"',
            '  Child decides',
            '  Either way works perfectly!'
          ],
          examples: [
            'Child: "A dog pet!" You: "Great! What could happen to the dog? Could it GET WET?"'
          ],
          watchFor: [
            'Story ideas forming.',
            'Word family words suggested.',
            'Excitement about story.',
            'Understanding of story structure.'
          ],
          parentTip: 'If creating feels overwhelming, use the pre-made story — learning happens in the reading!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Write or Read the Story',
        duration: '4 min',
        focus: 'Story Creation/Reading',
        researchBasis: 'National Reading Panel (2000) - Decodable text practice builds reading fluency and confidence.',
        content: {
          goal: 'Write the story (if creating) or prepare to read it (if using pre-made).',
          whatToSay: [
            '"Let\'s write our story!"',
            '"I\'ll write, you tell me what to say!"',
            '"Use lots of our word family words!"'
          ],
          whatToDo: [
            '**If Creating Story Together:**',
            '',
            'Sentence-by-sentence construction:',
            '',
            '  Sentence 1 - Introduction:',
            '    Child: "Tim has a pet."',
            '    Parent writes: "Tim has a pet."',
            '    Identify word families: PET',
            '',
            '  Sentence 2 - Description:',
            '    Child: "His pet is a dog."',
            '    Or suggest: "Can you use a word family word?"',
            '    Revised: "His pet can get wet."',
            '    Word families: PET, GET, WET',
            '',
            '  Continue for 6-8 sentences total.',
            '',
            'Encourage word family words:',
            '  "Can you use NET in the story?"',
            '  "What about HEN or DEN?"',
            '  "Try to use TEN somehow!"',
            '',
            'Parent writes as child dictates:',
            '  Large, clear letters',
            '  Leave space for illustrations',
            '  Highlight word family words in color',
            '',
            '**If Using Pre-Made Story:**',
            '',
            'Write out "The Wet Pet" story:',
            '  Copy story from planning section',
            '  Write in large letters',
            '  Highlight all word family words',
            '',
            'As you write, identify families:',
            '  "Look — PET is -ET family!"',
            '  "HEN is -EN family!"',
            '  "See how many family words we used?"',
            '',
            '**For Both Approaches:**',
            '',
            'Title and author:',
            '  Write title at top',
            '  "The ___ Pet" (fill in adjective)',
            '  Add: "By [Child\'s Name]"',
            '',
            'Read through once together:',
            '  Parent and child read aloud',
            '  Point to each word',
            '  Celebrate word family words',
            '  "So many -ET words!"',
            '',
            'Check story makes sense:',
            '  "Does our story have a beginning?"',
            '  "What happens in the middle?"',
            '  "How does it end?"'
          ],
          examples: [
            'Child: "The pet can jump!" You: "Great! Can we say \'the pet can get up\'? That uses GET from -ET family!"'
          ],
          watchFor: [
            'Story coherence.',
            'Use of word family words.',
            'Engagement in creation process.',
            'Understanding of story flow.'
          ],
          parentTip: 'If child gets stuck, refer to word family lists for inspiration!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Illustrate the Story',
        duration: '3 min',
        focus: 'Visual Storytelling',
        researchBasis: 'Bus & van IJzendoorn (1995) - Illustrations support text comprehension and engagement with stories.',
        content: {
          goal: 'Add 2-4 illustrations to the story showing key moments.',
          whatToSay: [
            '"Now let\'s bring the story to life!"',
            '"Draw pictures for the important parts!"',
            '"Show what happens in the story!"'
          ],
          whatToDo: [
            'Choose illustration approach:',
            '',
            '  **Option A: Panel format**',
            '    Divide page into 4 boxes',
            '    One picture per key moment',
            '    Comic strip style',
            '',
            '  **Option B: Full page scene**',
            '    One large illustration',
            '    Shows whole story or main moment',
            '',
            '  **Option C: Margins**',
            '    Small pictures next to text',
            '    Illustrates specific lines',
            '',
            'Key moments to illustrate:',
            '  For "The Wet Pet":',
            '    Picture 1: Ben with his hen',
            '    Picture 2: Hen getting wet',
            '    Picture 3: Hen in the den',
            '    Picture 4: Hen with ten men',
            '',
            'As child draws:',
            '  Parent re-reads story aloud',
            '  "What does this part look like?"',
            '  "Show the HEN getting WET!"',
            '',
            'Label pictures with story words:',
            '  Point to hen in picture → write "HEN"',
            '  Point to den → write "DEN"',
            '  Connects images to text',
            '',
            'Add details:',
            '  Encourage color',
            '  "What color is the hen?"',
            '  "Is there a NET in the picture?"',
            '  "Show all TEN men!"',
            '',
            'Keep it simple:',
            '  "Quick sketches are perfect!"',
            '  "We want to finish so we can read it!"',
            '  Prioritize completion over perfection'
          ],
          examples: [
            'Child draws hen with water drops. You: "Perfect! The hen looks WET! Label it \'WET HEN\'!"'
          ],
          watchFor: [
            'Pictures match story events.',
            'Key story moments captured.',
            'Labels with word family words.',
            'Completion of illustrations.'
          ],
          parentTip: 'Simple stick figures work great — focus is on story, not art!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Story Performance & Celebration',
        duration: '3 min',
        focus: 'Reading Fluency',
        researchBasis: 'Ehri (2020) - Repeated reading of connected text builds automaticity, fluency, and reading confidence.',
        content: {
          goal: 'Read the story multiple times with increasing fluency and expression.',
          whatToSay: [
            '"Time to read your story!"',
            '"Let\'s read it THREE times!"',
            '"Each time will be better!"'
          ],
          whatToDo: [
            '**Round 1: Shared Reading**',
            '  Parent and child read together',
            '  Point to each word',
            '  Read at comfortable pace',
            '  Help with any tricky words',
            '',
            '**Round 2: Child\'s Turn**',
            '  Child reads independently',
            '  Parent follows along',
            '  Offer help only if needed',
            '  Should be smoother than Round 1',
            '',
            '**Round 3: Performance Reading**',
            '  Add expression and character voices',
            '  "Read it like you\'re performing!"',
            '  Emphasize emotions',
            '  Make it theatrical!',
            '',
            'After each round:',
            '  "That was even better!"',
            '  "Your reading is so smooth!"',
            '  "You barely hesitated!"',
            '',
            'Highlight word family success:',
            '  "Count how many -ET words you read!"',
            '  "How many -EN words?"',
            '  "How many -EG words?"',
            '  Tally totals',
            '',
            'Share with audience:',
            '  Choose someone to read to',
            '  Family member or stuffed animal',
            '  Child performs final reading',
            '  Shows completed story with illustrations',
            '',
            'Celebrate the week:',
            '  "On Monday, you met the _ET family."',
            '  "On Tuesday, you learned _EN and _EG."',
            '  "On Wednesday, you wrote and drew words."',
            '  "On Thursday, you solved riddles."',
            '  "Today, you created and read a STORY!"',
            '  "Look how much you learned!"',
            '',
            'Compare to last week:',
            '  "Last week: short-A story"',
            '  "This week: short-E story"',
            '  "You can read TWO vowels now!"',
            '  "Next week: short-I families!"',
            '',
            'Save the story:',
            '  "This is your second story book!"',
            '  "Keep it to read again and again!"',
            '  "You\'re a real author!"'
          ],
          examples: [
            'Child reads with expression: "The hen got WET! Oh no!" You: "Amazing reading! So much expression!"'
          ],
          watchFor: [
            'Improved fluency each round.',
            'Accurate word recognition.',
            'Expression and prosody.',
            'Pride in reading accomplishment.',
            'Confidence with word families.'
          ],
          parentTip: 'The third reading should sound almost fluent — that\'s the power of word families!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '📚 You Created A Story Book!',
            'You practiced:',
            '✓ Planning a story with word families',
            '✓ Writing/reading the story',
            '✓ Illustrating key moments',
            '✓ Reading with fluency and expression'
          ],
          reviewQuestions: [
            'Did the story use multiple word family words?',
            'Did reading fluency improve with each round?',
            'Did your child read with expression?',
            'Do they show pride in their story?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Creating and reading original stories is the ultimate literacy accomplishment for early readers! This week, your child learned three new word families (-ET, -EN, -EG) and applied them in meaningful context. The story shows they understand that reading isn\'t just about decoding isolated words — it\'s about MEANING, STORIES, and COMMUNICATION. When children read connected text (stories vs. word lists), they develop fluency, prosody, and comprehension together. The repeated readings built automaticity — by the third reading, they likely weren\'t "sounding out" anymore; they were READING. This is the transformation from beginning reader to fluent reader. Two weeks, six word families, two original stories — that\'s incredible progress!'
          },
          nextSteps: [
            'Re-read "The _ET Pet" story daily this weekend!',
            'Create more short-E stories using the same families.',
            'Next week: Short-I families (-ig, -in, -ip)!',
            'Keep both story books for proud re-reading!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 51 =========
  {
    id: 51,
    phase: '4',
    quarter: 1,
    week: 11,
    weekIndex: 10,
    dayInWeek: 1,
    title: 'Lesson 51: Meet the I-Families',
    theme: 'Word Families: -ig, -in, -ip',
    skillRecurrenceTags: ['word-families', 'short-i', 'vowel-patterns'],
    relatedToPreviousSkills: ['word-families', 'short-vowels', 'pattern-transfer'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to the I-Families!',
        duration: '1 min',
        content: {
          goal: 'Introduce all three short-I word families (-ig, -in, -ip) in one lesson, building on established strategy.',
          parentGuide: [
            'This is the THIRD vowel for word families!',
            'Children apply the same strategy to short-I sound.',
            'All three I-families introduced today — fast-paced!',
            'Short I (/ĭ/ as in "pig") is the focus vowel this week.'
          ],
          materials: ['Paper and pencil', 'Crayons (3 colors)', 'Word family wall (reference)'],
          tip: 'By now, this should feel familiar — they know the strategy!',
          researchNote: 'Ehri (2020) - Repeated application of decoding strategies across vowel patterns builds phonological flexibility and accelerates reading development.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: New Vowel, Same Strategy',
        duration: '2 min',
        focus: 'Vowel Introduction',
        researchBasis: 'National Reading Panel (2000) - Systematic phonics across multiple vowels strengthens decoding automaticity.',
        content: {
          goal: 'Introduce short-I sound and connect to previous vowel families.',
          whatToSay: [
            '"You know short-A families!"',
            '"You know short-E families!"',
            '"This week: short-I families!"',
            '"Same strategy, new vowel!"'
          ],
          whatToDo: [
            'Review vowel progression:',
            '  Week 9: Short A — /ă/ like CAT',
            '  Week 10: Short E — /ĕ/ like PET',
            '  Week 11: Short I — /ĭ/ like PIG',
            'Introduce short-I: "Say /ĭ/ /ĭ/ /ĭ/ — like in PIG, BIG, SIT, PIN"',
            'Compare vowels side by side: CAT | PET | PIG',
            'Preview three I-families: -IG, -IN, -IP',
            '"This will go FAST — you already know the strategy!"'
          ],
          examples: [
            'You: "Short A was CAT. Short E was PET. Short I is...?" Child: "PIG!" You: "Yes!"'
          ],
          watchFor: [
            'Clear /ĭ/ pronunciation.',
            'Recognition of strategy transfer.',
            'Confidence and readiness.'
          ],
          parentTip: 'They\'re pros now — celebrate their expertise!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Build All Three I-Families',
        duration: '5 min',
        focus: 'Rapid Family Building',
        researchBasis: 'Ehri (2001) - Efficient word family generation demonstrates mastery of onset-rime strategy.',
        content: {
          goal: 'Build -IG, -IN, and -IP families quickly using established strategy.',
          whatToSay: [
            '"Let\'s build all three families!"',
            '"First -IG, then -IN, then -IP!"',
            '"Change the beginning, keep the ending!"'
          ],
          whatToDo: [
            'Set up three columns: -IG | -IN | -IP',
            'BUILD -IG: BIG, DIG, FIG, GIG, JIG, PIG, RIG, WIG, TWIG',
            'BUILD -IN: BIN, DIN, FIN, KIN, PIN, SIN, TIN, WIN, CHIN, GRIN, SHIN, SKIN, SPIN, THIN, TWIN',
            'BUILD -IP: DIP, HIP, LIP, NIP, RIP, SIP, TIP, ZIP, CHIP, CLIP, DRIP, FLIP, GRIP, SHIP, SKIP, SLIP, SNIP, TRIP, WHIP',
            'Count all words: "Total: 40+ short-I words!"',
            'Quick read across families randomly',
            'Compare family sizes: "-IP has the MOST words!"'
          ],
          examples: [
            'You point to PIN, PIG, DIP. Child reads all three. You: "Three families mastered!"'
          ],
          watchFor: [
            'Quick word generation.',
            'Clear short-I pronunciation.',
            'No hesitation on endings.',
            'Pride in word count.'
          ],
          parentTip: 'This should go MUCH faster than Week 9 — they\'re experts!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Three-Family Speed Read',
        duration: '3 min',
        focus: 'Fluency Building',
        researchBasis: 'Lonigan et al. (2000) - Rapid word recognition through pattern-based reading builds automaticity.',
        content: {
          goal: 'Read through all I-family words for fluency and pattern mastery.',
          whatToSay: [
            '"Time to read ALL the I-family words!"',
            '"Fast and smooth!"',
            '"Show me your fluency!"'
          ],
          whatToDo: [
            'Round 1: Column by column',
            'Round 2: Random pointing across families',
            'Round 3: Speed challenge — "How many in 30 seconds?"',
            'Round 4: Minimal pairs — PIG/PIN, BIG/BIN, DIG/DIN/DIP',
            'Celebrate fluency: "You read [X] words in 30 seconds!"',
            'Cross-vowel challenge: Mix in short-A and E words',
            '"Remember Week 1? You sounded out C-A-T slowly. Now you read 50+ words quickly!"'
          ],
          examples: [
            'Child reads rapidly: "BIG, DIG, PIG, WIG!" You: "Lightning speed! Perfect I-family reading!"'
          ],
          watchFor: [
            'Smooth, fast reading.',
            'No sounding out.',
            'Accurate across families.',
            'Cross-vowel accuracy.'
          ],
          parentTip: 'Their speed shows true mastery — they\'re pattern readers now!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: I-Family Gallery',
        duration: '2 min',
        focus: 'Visual Organization',
        researchBasis: 'Scarborough (2001) - Visual displays support orthographic memory and word recognition.',
        content: {
          goal: 'Create quick visual gallery of all three I-families.',
          whatToSay: [
            '"Let\'s add I-families to our Word Family Wall!"',
            '"Draw one word from each family!"',
            '"Three quick drawings!"'
          ],
          whatToDo: [
            'Divide paper into three sections',
            'Section 1: -IG (popular: PIG, BIG, DIG)',
            'Section 2: -IN (popular: PIN, FIN, SPIN)',
            'Section 3: -IP (popular: SHIP, CHIP, FLIP)',
            'Add to Word Family Wall next to A and E galleries',
            'Count total: Short-A: 30+, Short-E: 25+, Short-I: 40+ = "Almost 100 words!"',
            'Point to all galleries: "Same strategy, three vowels! You\'re a word family expert!"'
          ],
          examples: [
            'Child draws pig, labels "PIG" with -IG in red. You: "Perfect! Now all three on our wall!"'
          ],
          watchFor: [
            'Clear representation.',
            'Correct spelling.',
            'Connection to previous galleries.',
            'Pride in collection.'
          ],
          parentTip: 'The Wall shows concrete evidence of their incredible progress!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎯 You Met THREE I-Families in One Day!',
            'You practiced:',
            '✓ Learning short-I sound',
            '✓ Building -IG, -IN, and -IP families',
            '✓ Speed reading 40+ words',
            '✓ Creating I-family gallery'
          ],
          reviewQuestions: [
            'Can your child clearly pronounce /ĭ/?',
            'Did they generate words for all three families?',
            'Can they read I-family words fluently?',
            'Do they see the pattern transfer across vowels?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Today\'s lesson was FAST because your child has mastered the word family STRATEGY! They didn\'t need to learn "how" word families work — they already know. They just applied that knowledge to a new vowel. This is exactly how proficient readers operate: they transfer strategies efficiently. The fact that your child built THREE families in one lesson shows genuine mastery. They\'re not memorizing individual words; they\'re using a flexible, transferable decoding system. This week adds 40+ words to their reading vocabulary — but more importantly, it proves they can learn ANY word family independently!'
          },
          nextSteps: [
            'Hunt for I-family words in books today!',
            'Play "I spy a word in the -IP family!"',
            'Tomorrow: Pick & Flip game with beginning sounds!',
            'Practice mixing all three vowel families together!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 52 =========
  {
    id: 52,
    phase: '4',
    quarter: 1,
    week: 11,
    weekIndex: 10,
    dayInWeek: 2,
    title: 'Lesson 52: Pick & Flip',
    theme: 'Word Families: -ig, -in, -ip',
    skillRecurrenceTags: ['word-families', 'phoneme-manipulation', 'onset-substitution'],
    relatedToPreviousSkills: ['word-families', 'switching-sounds', 'phoneme-flexibility'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Pick & Flip!',
        duration: '1 min',
        content: {
          goal: 'Practice flexible phoneme manipulation by "flipping" beginning sounds to create new I-family words.',
          parentGuide: [
            'This lesson builds phonological flexibility.',
            'Flipping beginning sounds = onset substitution practice.',
            'Children learn to mentally manipulate word parts.',
            'This skill supports both reading and spelling!'
          ],
          materials: ['Paper and pencil', 'Index cards (optional)', 'Letter tiles or written letters'],
          tip: 'Keep it playful — this is a game, not a drill!',
          researchNote: 'Ehri (2020) - Phoneme manipulation activities strengthen phonological awareness and support orthographic mapping for both reading and spelling.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Flip Demo',
        duration: '2 min',
        focus: 'Concept Introduction',
        researchBasis: 'National Reading Panel (2000) - Explicit phoneme manipulation instruction develops phonological awareness.',
        content: {
          goal: 'Demonstrate how to "flip" beginning sounds to transform words.',
          whatToSay: [
            '"Remember Switch the Sound?"',
            '"Today we\'re playing Pick & Flip!"',
            '"We\'ll flip beginning sounds like flipping cards!"',
            '"Watch this magic trick!"'
          ],
          whatToDo: [
            'Write PIG. "What if I FLIP the P to B?" → BIG',
            'Do another: PIN → flip P to W → WIN',
            'Visual demo: Draw two cards (P | -IG), flip left card to B',
            'Explain: "1. Pick beginning sound, 2. Flip to new sound, 3. Read new word!"',
            'Practice together: "DIP → flip D to Z → ZIP!"',
            '"HIP → flip H to L → LIP!"'
          ],
          examples: [
            'You flip PIG → BIG → DIG → WIG. Child watches. "Only the beginning changes!"'
          ],
          watchFor: [
            'Understanding of "flip" concept.',
            'Recognition ending stays constant.',
            'Excitement about transformations.'
          ],
          parentTip: 'The physical "flip" motion makes the concept concrete!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Guided Flipping Chains',
        duration: '4 min',
        focus: 'Scaffolded Practice',
        researchBasis: 'Lonigan et al. (2000) - Guided phoneme manipulation builds phonological flexibility.',
        content: {
          goal: 'Create word chains by flipping beginning sounds with guidance.',
          whatToSay: [
            '"Now YOU flip the sounds!"',
            '"I\'ll tell you what to flip!"',
            '"Make long flip chains!"'
          ],
          whatToDo: [
            'Chain 1 (-IG): PIG → BIG → DIG → WIG → JIG',
            'Chain 2 (-IN): PIN → BIN → FIN → WIN → CHIN → SHIN → THIN',
            'Chain 3 (-IP): DIP → HIP → LIP → NIP → RIP → SIP → TIP → ZIP',
            'Chain 4 (blends): CLIP → FLIP → SLIP → SKIP → SHIP → CHIP → TRIP',
            'For each: Child says word → parent writes → child reads back',
            'Increase challenge: "Flip PIN to a blend word!" → SPIN',
            'Draw chains with arrows showing transformations',
            'Count total flips: "You flipped [X] times and made [X] new words!"'
          ],
          examples: [
            'You: "Flip DIG to WIG!" Child: "/w/...IG... WIG!" You: "Perfect! Just one sound changed!"'
          ],
          watchFor: [
            'Accurate sound substitution.',
            'Quick transformation.',
            'Success with blends/digraphs.',
            'Understanding of chains.'
          ],
          parentTip: 'If struggling, isolate the flip: "D changes to W. DIG becomes WIG."'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Independent Pick & Flip',
        duration: '4 min',
        focus: 'Self-Directed Manipulation',
        researchBasis: 'Ehri (2001) - Independent phoneme manipulation demonstrates mastery and metacognitive awareness.',
        content: {
          goal: 'Child independently creates flip chains without prompts.',
          whatToSay: [
            '"Now YOU are the flip master!"',
            '"Pick a starting word!"',
            '"Flip it however you want!"',
            '"Make the longest chain you can!"'
          ],
          whatToDo: [
            'Child chooses starting word and creates own chains',
            'Challenge 1: Longest chain (target 6-10 words)',
            'Challenge 2: Family switch — start -IG, flip to -IN, flip to -IP',
            'Challenge 3: Backwards chain — ZIP → TIP → SIP → ... → DIP',
            'Challenge 4: Blend chains only — CLIP → FLIP → SLIP → SKIP',
            'Challenge 5: Speed flipping — "How many in 1 minute?"',
            'Create flip map with arrows showing all connections',
            'Reflection: "How did you decide which sound to flip to?"'
          ],
          examples: [
            'Child: "BIG → DIG → PIG → WIG → FIG!" You: "Five flips! Amazing chain!"'
          ],
          watchFor: [
            'Independent generation.',
            'Logical flip choices.',
            'Long chains.',
            'Creative approaches.',
            'Pride in accomplishments.'
          ],
          parentTip: 'Let them lead completely — this shows mastery!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Flip Card Game',
        duration: '2 min',
        focus: 'Playful Application',
        researchBasis: 'Vygotsky (1978) - Play-based learning consolidates skills and increases motivation.',
        content: {
          goal: 'Turn flipping into a quick game for reinforcement.',
          whatToSay: [
            '"Let\'s play a flip card game!"',
            '"I\'ll give you a challenge!"',
            '"You flip to solve it!"'
          ],
          whatToDo: [
            'Game 1 - Flip Race: "Flip PIG to WIG!" Child: "WIG!" Fast-paced',
            'Game 2 - Flip Path: "Start BIG, end WIN!" Child creates: BIG → DIG → DIN → WIN',
            'Game 3 - Mystery Flip: "I flipped HIP and got LIP. What did I flip?" Child: "H to L!"',
            'Game 4 - Silly Flip: "Flip to make nonsense!" Tests real vs. nonsense understanding',
            'Game 5 - Categories: "Flip to an animal!" (PIG), "Flip to something you do!" (DIG, WIN)',
            'Partner mode: Child gives parent flip challenges',
            'Celebrate: "You\'re a flip expert! You can flip ANY sound!"'
          ],
          examples: [
            'You: "Flip CLIP to SHIP!" Child: "SHIP!" You: "Fast! You changed the whole blend!"'
          ],
          watchFor: [
            'Quick responses.',
            'Accurate flipping.',
            'Enjoyment of games.',
            'Creative challenge-giving.'
          ],
          parentTip: 'Games consolidate learning — they\'re practicing without realizing it!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🔄 You\'re a Flip Master!',
            'You practiced:',
            '✓ Flipping beginning sounds',
            '✓ Creating long word chains',
            '✓ Independent flip challenges',
            '✓ Playing flip card games'
          ],
          reviewQuestions: [
            'Can your child flip beginning sounds independently?',
            'Did they create long chains (6+ words)?',
            'Can they flip blends and digraphs?',
            'Do they understand the strategy applies to all families?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Phoneme manipulation is one of the strongest predictors of reading success! When children can mentally "flip" sounds (substitute phonemes), they develop flexible thinking about language structure. This skill powers BOTH reading and spelling. For reading, it allows them to decode new words by analogy: "If I know PIG, I can read BIG, DIG, WIG..." For spelling, it lets them generate words: "If I can spell PIN, I can spell WIN, SPIN, THIN..." This metacognitive awareness — thinking ABOUT the structure of words — is what proficient readers and spellers do automatically. Today\'s lesson built that awareness explicitly through play!'
          },
          nextSteps: [
            'Play Pick & Flip throughout the day!',
            'Challenge: "Flip CAT to..." (cross-vowel practice)',
            'Tomorrow: Sound Pattern Detective!',
            'Create flip challenge cards for ongoing practice!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 53 =========
  {
    id: 53,
    phase: '4',
    quarter: 1,
    week: 11,
    weekIndex: 10,
    dayInWeek: 3,
    title: 'Lesson 53: Sound Pattern Detective',
    theme: 'Word Families: -ig, -in, -ip',
    skillRecurrenceTags: ['word-families', 'rime-identification', 'pattern-recognition'],
    relatedToPreviousSkills: ['phoneme-segmentation', 'onset-rime', 'sound-isolation'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome, Detective!',
        duration: '1 min',
        content: {
          goal: 'Introduce detective work: finding and identifying the consistent ending pattern (rime) in word families.',
          parentGuide: [
            'This lesson shifts focus to the ENDING of words.',
            'Children learn to "hear" and "see" the rime pattern.',
            'Detective work makes pattern recognition playful!',
            'This deepens understanding of word structure.'
          ],
          materials: ['Paper and pencil', 'Magnifying glass (real or drawn)', 'Detective hat (optional/playful)'],
          tip: 'Make it mysterious and fun — they\'re solving word puzzles!',
          researchNote: 'Anthony & Francis (2005) - Explicit rime awareness instruction strengthens phonological segmentation and supports both reading and spelling development.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: What\'s a Pattern?',
        duration: '2 min',
        focus: 'Concept Introduction',
        researchBasis: 'Ehri (2020) - Metalinguistic awareness of word structure (onset-rime) accelerates orthographic mapping.',
        content: {
          goal: 'Define "pattern" and show how word families share ending patterns.',
          whatToSay: [
            '"Detectives find patterns — things that repeat!"',
            '"Word families have SECRET patterns!"',
            '"The ending stays THE SAME!"',
            '"Let\'s find the pattern together!"'
          ],
          whatToDo: [
            'Write three words: PIG, BIG, DIG',
            'Circle the -IG in each word with a bright color',
            '"See? -IG, -IG, -IG! That\'s the pattern!"',
            'Do again with PIN, BIN, WIN → circle -IN',
            'One more: DIP, HIP, LIP → circle -IP',
            'Explain: "The BEGINNING changes, the ENDING stays!"',
            'Show visually: Draw boxes around beginnings (different colors), circles around endings (same color)',
            '"When you find the pattern, you can read LOTS of words!"'
          ],
          examples: [
            'You: "What\'s the same in PIG, BIG, DIG?" Child: "-IG!" You: "YES! That\'s the pattern!"'
          ],
          watchFor: [
            'Recognition of shared ending.',
            'Understanding "pattern" concept.',
            'Visual tracking of circled rimes.',
            'Excitement about discovery.'
          ],
          parentTip: 'Physical circling makes the invisible pattern visible!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Detective Listening Game',
        duration: '3 min',
        focus: 'Auditory Pattern Detection',
        researchBasis: 'Lonigan et al. (2000) - Auditory rime detection precedes and supports visual pattern recognition in early reading.',
        content: {
          goal: 'Train ears to HEAR the rime pattern before seeing it.',
          whatToSay: [
            '"Close your eyes — use detective ears!"',
            '"Listen for the ending sound!"',
            '"Tell me what pattern you hear!"'
          ],
          whatToDo: [
            'Round 1 - Same Family: Say "PIG... BIG... DIG." Ask: "What\'s the ending?" → -IG',
            'Round 2 - New Family: Say "PIN... WIN... CHIN." Ask: "What\'s the ending?" → -IN',
            'Round 3 - Mixed: Say "DIP... SHIP... CLIP... TRIP." → All -IP!',
            'Round 4 - Challenge: "BIG... PIN... HIP." Ask: "Are these the SAME family?" → No! Different endings.',
            'Round 5 - Tricky: "GRIN... PIN... THIN." → Same! All -IN, even with blends.',
            'Round 6 - Real vs. Nonsense: "BIG... ZIG... PLIG." → All -IG, but is PLIG real?',
            'Celebrate: "Your detective ears found [X] patterns!"'
          ],
          examples: [
            'You say: "FIG... WIG... TWIG." Child: "They all end in -IG!" You: "Perfect detective work!"'
          ],
          watchFor: [
            'Accurate rime identification.',
            'Success even with blends.',
            'Recognition of different families.',
            'Confidence in auditory detection.'
          ],
          parentTip: 'Eyes closed = pure listening! This strengthens phonemic awareness.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Visual Pattern Hunt',
        duration: '4 min',
        focus: 'Written Pattern Recognition',
        researchBasis: 'Scarborough (2001) - Orthographic pattern recognition builds sight vocabulary and decoding efficiency.',
        content: {
          goal: 'Hunt for and circle rime patterns in written words.',
          whatToSay: [
            '"Now your detective eyes work!"',
            '"Find the pattern and circle it!"',
            '"Use your magnifying glass!"'
          ],
          whatToDo: [
            'Write 15-20 mixed I-family words randomly on paper:',
            'BIG, PIN, DIP, WIG, FIN, HIP, DIG, SPIN, CHIP, PIG, WIN, LIP, GRIN, SKIP, THIN, CLIP, SIN, TIP, JIG, BIN',
            'Task 1: "Circle ALL the -IG words" (use one color)',
            'Task 2: "Circle ALL the -IN words" (use different color)',
            'Task 3: "Circle ALL the -IP words" (use third color)',
            'Count each family: "-IG: [X], -IN: [X], -IP: [X]"',
            'Challenge: "Which family has the MOST words on our page?"',
            'Extension: "Can you find any words with blends?" → SPIN, CHIP, GRIN, SKIP, THIN, CLIP',
            'Create color key: "-IG = RED, -IN = BLUE, -IP = GREEN"'
          ],
          examples: [
            'Child circles BIG, DIG, PIG, WIG, JIG in red. You: "Five -IG patterns found! Excellent detective work!"'
          ],
          watchFor: [
            'Accurate pattern identification.',
            'Consistent circling of correct rime.',
            'Success with blend words.',
            'Visual tracking and focus.',
            'Pride in "solving" the hunt.'
          ],
          parentTip: 'The color-coding creates a visual map of patterns!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Pattern Detective Report',
        duration: '3 min',
        focus: 'Metacognitive Reflection',
        researchBasis: 'Ehri (2001) - Explicit reflection on word patterns strengthens metalinguistic awareness and strategic reading.',
        content: {
          goal: 'Summarize findings like a real detective and articulate the pattern strategy.',
          whatToSay: [
            '"Every detective writes a report!"',
            '"Tell me what patterns you found!"',
            '"What\'s your detective secret?"'
          ],
          whatToDo: [
            'Create simple "Detective Report" template:',
            '  Case: I-Family Word Patterns',
            '  Patterns Found: -IG, -IN, -IP',
            '  Evidence: [Child names 2-3 words from each]',
            '  Detective Strategy: "Look for the ending that\'s the same!"',
            'Child dictates, parent writes (or child writes if able)',
            'Questions to ask:',
            '  "What\'s the SAME in all -IG words?" → The -IG!',
            '  "What CHANGES in word families?" → The beginning!',
            '  "How does knowing the pattern help you read?" → "I only decode the beginning, then add the ending!"',
            'Draw detective badge: "Pattern Detective — I-Family Expert!"',
            'Sign and date the report',
            'Celebrate: "This report proves you\'re a REAL pattern detective!"'
          ],
          examples: [
            'Child: "I found -IG in PIG, BIG, and DIG!" You write it. "Perfect evidence! Case solved!"'
          ],
          watchFor: [
            'Clear articulation of patterns.',
            'Understanding of strategy.',
            'Pride in accomplishment.',
            'Metacognitive awareness: "I know HOW I know."'
          ],
          parentTip: 'The report makes learning visible and builds metacognition!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🔍 You\'re a Pattern Detective!',
            'You practiced:',
            '✓ Finding patterns in words',
            '✓ Listening for rimes',
            '✓ Circling visual patterns',
            '✓ Writing a detective report'
          ],
          reviewQuestions: [
            'Can your child identify the rime (ending pattern)?',
            'Did they hear patterns even with eyes closed?',
            'Can they explain what makes a word family?',
            'Do they understand the onset-rime structure?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Pattern recognition is the KEY to efficient reading! When children recognize that word families share consistent rimes, they stop decoding letter-by-letter and start reading by analogy. Instead of sounding out P-I-G, B-I-G, D-I-G separately, they decode once and apply the pattern: "If I know PIG, I automatically know BIG, DIG, WIG..." This metacognitive awareness — understanding WHAT the pattern is and HOW to use it — transforms children from slow decoders into strategic readers. Today\'s detective work made this invisible process visible and conscious. Your child now knows they\'re a pattern-finder, not just a word-reader!'
          },
          nextSteps: [
            'Play pattern detective with other families!',
            'Look for patterns in road signs and books!',
            'Tomorrow: Write the Pattern (creating charts)!',
            'Challenge: "Find patterns in your name or family names!"'
          ]
        }
      }
    ]
  },

  // ========= LESSON 54 =========
  {
    id: 54,
    phase: '4',
    quarter: 1,
    week: 11,
    weekIndex: 10,
    dayInWeek: 4,
    title: 'Lesson 54: Write the Pattern',
    theme: 'Word Families: -ig, -in, -ip',
    skillRecurrenceTags: ['word-families', 'spelling-patterns', 'orthographic-mapping'],
    relatedToPreviousSkills: ['phoneme-segmentation', 'letter-sound-correspondence', 'encoding'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'From Reading to Writing!',
        duration: '1 min',
        content: {
          goal: 'Transition from reading word families to WRITING them, applying pattern knowledge to spelling.',
          parentGuide: [
            'This lesson applies reading patterns to spelling.',
            'Writing reinforces orthographic memory.',
            'Children see that patterns work BOTH ways!',
            'This builds encoding (spelling) skills alongside decoding.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Ruler (optional, for neat charts)'],
          tip: 'Celebrate invented spelling — focus is on PATTERN, not perfection!',
          researchNote: 'Ehri (2020) - Writing word family patterns strengthens orthographic mapping and creates reciprocal connections between reading and spelling.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Reading to Writing Connection',
        duration: '2 min',
        focus: 'Concept Bridge',
        researchBasis: 'National Reading Panel (2000) - Explicit connections between decoding and encoding accelerate literacy development.',
        content: {
          goal: 'Show how reading patterns directly support spelling patterns.',
          whatToSay: [
            '"You can READ word families!"',
            '"Now you can WRITE them too!"',
            '"Same pattern, different direction!"',
            '"Your brain already knows this!"'
          ],
          whatToDo: [
            'Demonstration:',
            '  Step 1: Read PIG together',
            '  Step 2: "Now let\'s WRITE PIG." Sound it out: /p/ /ĭ/ /g/',
            '  Step 3: Write P-I-G',
            '  Step 4: "The pattern is -IG. Watch what happens..."',
            '  Step 5: Change P to B → BIG. "We only changed ONE letter!"',
            'Repeat demo with -IN: Write PIN → change P to W → WIN',
            'One more with -IP: Write DIP → change D to Z → ZIP',
            'Key insight: "When you WRITE word families, you only change the BEGINNING!"',
            'Connect to yesterday: "Yesterday you were a pattern DETECTIVE. Today you\'re a pattern WRITER!"'
          ],
          examples: [
            'You write PIG, then BIG. Child: "You only changed the P to B!" You: "YES! That\'s the power of patterns!"'
          ],
          watchFor: [
            'Recognition of reading-writing connection.',
            'Understanding that patterns apply to spelling.',
            'Confidence that they "already know this."',
            'Excitement about writing words.'
          ],
          parentTip: 'This "aha moment" — patterns work both ways — is powerful!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Create a Word Family Chart',
        duration: '5 min',
        focus: 'Organized Pattern Writing',
        researchBasis: 'Scarborough (2001) - Visual organization of orthographic patterns supports word recognition and spelling retrieval.',
        content: {
          goal: 'Build organized charts showing all three I-families with written words.',
          whatToSay: [
            '"Let\'s make a beautiful word family chart!"',
            '"Three columns, one for each family!"',
            '"You write, I\'ll help!"'
          ],
          whatToDo: [
            'Set up chart with three columns: -IG | -IN | -IP',
            'Write family headers together (child traces or writes)',
            'BUILD -IG column:',
            '  You say word, child writes: BIG, DIG, FIG, PIG, WIG',
            '  Optional add: JIG, RIG, TWIG (if time)',
            'BUILD -IN column:',
            '  Child writes: BIN, FIN, PIN, WIN',
            '  Challenge words: CHIN, GRIN, SPIN, THIN',
            'BUILD -IP column:',
            '  Child writes: DIP, HIP, LIP, TIP, ZIP',
            '  Challenge words: CHIP, CLIP, SHIP, SKIP',
            'Decorate: Add colors, stars, or small pictures',
            'Count words: "You wrote [X] words! You\'re a pattern writer!"',
            'Display chart: "This is YOUR reference chart. Use it whenever you read or write!"'
          ],
          examples: [
            'Child writes BIG, DIG, PIG. You: "Three -IG words! Only the beginning changed! Perfect pattern writing!"'
          ],
          watchFor: [
            'Correct rime spelling (-IG, -IN, -IP).',
            'Appropriate onset selection.',
            'Success with single consonants.',
            'Attempts at blends/digraphs.',
            'Pride in completed chart.',
            'Neat organization (with help).'
          ],
          parentTip: 'Guide letter formation, but let them lead! This builds ownership.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Dictation Practice',
        duration: '3 min',
        focus: 'Applied Encoding',
        researchBasis: 'Ehri (2001) - Phoneme-grapheme mapping through dictation strengthens orthographic memory and spelling accuracy.',
        content: {
          goal: 'Practice encoding I-family words through dictation.',
          whatToSay: [
            '"I\'ll say a word, you write it!"',
            '"Remember the pattern!"',
            '"Sound it out, then write!"'
          ],
          whatToDo: [
            'Round 1 - Simple words (5 words):',
            '  Say "PIG" → child writes',
            '  Say "BIN" → child writes',
            '  Say "TIP" → child writes',
            '  Say "DIG" → child writes',
            '  Say "WIN" → child writes',
            'Round 2 - Blend words (5 words):',
            '  Say "CHIP" → child writes',
            '  Say "SPIN" → child writes',
            '  Say "CLIP" → child writes',
            '  Say "GRIN" → child writes',
            '  Say "SKIP" → child writes',
            'For each word:',
            '  Child segments sounds aloud first',
            '  Child writes letters',
            '  Parent checks, praises pattern recognition',
            'If errors: "Let\'s sound it out together. What\'s the pattern?" Guide, don\'t correct harshly.',
            'Celebrate: "You wrote 10 words from memory! You\'re using patterns to spell!"'
          ],
          examples: [
            'You: "Write CHIP." Child: "/ch/ /ĭ/ /p/... C-H-I-P!" Writes it. You: "Perfect! You heard the pattern!"'
          ],
          watchFor: [
            'Accurate phoneme segmentation.',
            'Correct rime spelling.',
            'Appropriate onset spelling.',
            'Success with consonant blends.',
            'Self-correction attempts.',
            'Confidence and independence.'
          ],
          parentTip: 'Invented spelling is OK! Focus is on pattern recognition, not perfection.'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Pattern Writing Challenge',
        duration: '2 min',
        focus: 'Creative Application',
        researchBasis: 'National Reading Panel (2000) - Generative activities demonstrate mastery and transfer of orthographic knowledge.',
        content: {
          goal: 'Child generates and writes own word family examples independently.',
          whatToSay: [
            '"Now YOU choose words to write!"',
            '"Pick any I-family words!"',
            '"Show me your pattern power!"'
          ],
          whatToDo: [
            'Challenge 1: "Write 3 more -IG words we haven\'t used yet"',
            'Challenge 2: "Write 3 more -IN words"',
            'Challenge 3: "Write 3 more -IP words"',
            'Challenge 4: "Write your FAVORITE word from each family"',
            'Challenge 5: "Can you write a word with a blend?"',
            'Optional: "Can you write a NONSENSE word that follows the pattern?" (e.g., PLIG, SNIN, DRUP)',
            'Reflection: "How did you know how to spell these?" → "I used the pattern!"',
            'Create "My Pattern Writing" badge or certificate',
            'Display: Add new words to existing chart'
          ],
          examples: [
            'Child writes TWIG, SKIN, DRIP independently. You: "THREE challenge words! You\'re a pattern expert!"'
          ],
          watchFor: [
            'Independent generation.',
            'Correct pattern application.',
            'Creative word choices.',
            'Confidence in spelling.',
            'Pride in accomplishment.',
            'Understanding of real vs. nonsense.'
          ],
          parentTip: 'Independence is the goal! Step back and let them lead.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '✍️ You\'re a Pattern Writer!',
            'You practiced:',
            '✓ Writing word family patterns',
            '✓ Creating organized charts',
            '✓ Spelling through dictation',
            '✓ Independent word generation'
          ],
          reviewQuestions: [
            'Can your child write word family patterns accurately?',
            'Did they successfully encode words through dictation?',
            'Can they generate new family words independently?',
            'Do they see reading-writing connection?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Writing patterns is WHERE reading and spelling unite! When children can encode (spell) word families, they demonstrate true orthographic mapping — their brains have connected sounds to letters in both directions. This reciprocal skill is crucial: strong readers are strong spellers and vice versa. Today\'s lesson proved your child doesn\'t just recognize patterns; they can PRODUCE them. This generative ability — creating new words using known patterns — is the hallmark of literacy competence. The chart they created isn\'t just practice; it\'s a reference tool they\'ll use for months. They\'ve moved from passive readers to active writers!'
          },
          nextSteps: [
            'Use the chart as a spelling reference!',
            'Write I-family words in daily activities!',
            'Tomorrow: Create an I-Family Silly Poem!',
            'Challenge: "Can you write a sentence using 3 I-family words?"'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 55 =========
  {
    id: 55,
    phase: '4',
    quarter: 1,
    week: 11,
    weekIndex: 10,
    dayInWeek: 5,
    title: 'Lesson 55: I-Family Silly Poem',
    theme: 'Word Families: -ig, -in, -ip',
    skillRecurrenceTags: ['word-families', 'rhyming', 'creative-application'],
    relatedToPreviousSkills: ['rhyme-awareness', 'phonological-memory', 'language-play'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Poetry Playtime!',
        duration: '1 min',
        content: {
          goal: 'Celebrate the week by creating a fun, rhyming poem using I-family words.',
          parentGuide: [
            'This lesson integrates ALL week\'s learning into creative play!',
            'Poetry shows children that reading and writing are JOY.',
            'Rhyming poems reinforce word family patterns naturally.',
            'Silly = memorable and fun!'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Imagination!'],
          tip: 'Let it be SILLY! Nonsense is perfectly fine — the goal is rhyme and fun!',
          researchNote: 'Anthony & Francis (2005) - Playful rhyming activities strengthen phonological awareness and make literacy joyful, increasing motivation and engagement.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: What Makes a Poem Silly?',
        duration: '2 min',
        focus: 'Poetry Concept',
        researchBasis: 'Hirsh-Pasek et al. (2017) - Play-based language activities build literacy skills while maintaining intrinsic motivation.',
        content: {
          goal: 'Introduce simple poetry structure and celebrate silliness.',
          whatToSay: [
            '"Poems are fun word pictures!"',
            '"Poems RHYME — words sound the same at the end!"',
            '"Silly poems make us laugh!"',
            '"Let\'s make the SILLIEST I-family poem ever!"'
          ],
          whatToDo: [
            'Read example silly poem (parent):',
            '  "There once was a pig,',
            '  Who loved to dance a jig,',
            '  He wore a big wig,',
            '  And ate a sweet fig,',
            '  That silly, dancing, wiggly pig!"',
            'Ask: "What words rhymed?" → PIG, JIG, WIG, FIG, BIG',
            'Point out: "They ALL end in -IG! That\'s why they rhyme!"',
            'Show another:',
            '  "I won, you won, we all won!',
            '  Spinning and grinning in the sun!"',
            'Ask: "What family?" → -IN (WIN, SPIN, GRIN)',
            'Explain structure: "2-4 lines, rhyming words, silly pictures!"',
            '"Your turn! We\'ll make THREE silly poems — one for each family!"'
          ],
          examples: [
            'Child giggles at "wiggly pig." You: "See? Silly words make poetry FUN!"'
          ],
          watchFor: [
            'Understanding of rhyme.',
            'Laughter and engagement.',
            'Recognition of word families in poems.',
            'Excitement about creating own.'
          ],
          parentTip: 'Model enthusiasm! Your joy makes poetry appealing!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Build Three Family Poems',
        duration: '6 min',
        focus: 'Collaborative Creation',
        researchBasis: 'Vygotsky (1978) - Joint creative activities scaffold language development and build confidence in self-expression.',
        content: {
          goal: 'Co-create three short silly poems, one for each I-family.',
          whatToSay: [
            '"Let\'s make a -IG poem first!"',
            '"You pick the words, I\'ll help connect them!"',
            '"Make it as silly as possible!"'
          ],
          whatToDo: [
            'POEM 1: -IG Family',
            '  Brainstorm -IG words together: PIG, BIG, DIG, WIG, JIG, FIG',
            '  Pick 3-4 favorites',
            '  Build 2-4 line poem together:',
            '    Example: "A big pig in a wig,',
            '              Did a silly jig,',
            '              Then dug and dug and dig,',
            '              And ate a purple fig!"',
            '  Child chooses words, parent helps with sentence structure',
            '  Write it out together',
            '',
            'POEM 2: -IN Family',
            '  Brainstorm: BIN, FIN, PIN, WIN, CHIN, GRIN, SPIN, THIN',
            '  Create poem:',
            '    Example: "I win, you win, we all grin!',
            '              Spin and spin with a pin,',
            '              A shark with a big fin,',
            '              Jumped into the bin!"',
            '',
            'POEM 3: -IP Family',
            '  Brainstorm: DIP, HIP, LIP, TIP, ZIP, CHIP, CLIP, SHIP, SKIP, TRIP',
            '  Create poem:',
            '    Example: "Skip and trip on a ship,',
            '              Eat a chip, do a flip!',
            '              Zip your lip, that\'s the tip,',
            '              Don\'t let the paper clip slip!"',
            '',
            'For each poem:',
            '  Read aloud together 2-3 times',
            '  Emphasize rhyming words',
            '  Celebrate silliness!',
            '  Child can add or change words'
          ],
          examples: [
            'Child: "A pig in a wig!" You: "Perfect! That rhymes! Let\'s add more -IG words!"'
          ],
          watchFor: [
            'Active word selection.',
            'Recognition of rhymes.',
            'Creative contributions.',
            'Laughter and enjoyment.',
            'Understanding of structure.',
            'Pride in creation.'
          ],
          parentTip: 'Follow their lead! If they want a "purple dinosaur ship," go with it!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Illustrate the Poems',
        duration: '3 min',
        focus: 'Visual-Linguistic Integration',
        researchBasis: 'Scarborough (2001) - Multimodal activities (combining text and images) strengthen comprehension and memory.',
        content: {
          goal: 'Draw pictures to match each silly poem, creating a visual poetry book.',
          whatToSay: [
            '"Every poem needs pictures!"',
            '"Draw your favorite silly image from each poem!"',
            '"Make it colorful and fun!"'
          ],
          whatToDo: [
            'Divide paper into three sections (or use three pages)',
            'Section 1: Illustrate -IG poem',
            '  Popular images: pig in a wig, dancing jig, digging',
            '  Child draws while parent re-reads poem',
            '  Add labels to key words (PIG, WIG, etc.)',
            '',
            'Section 2: Illustrate -IN poem',
            '  Popular images: grinning face, spinning top, shark fin',
            '  Encourage action drawings (spinning, winning)',
            '',
            'Section 3: Illustrate -IP poem',
            '  Popular images: ship, chips, someone skipping',
            '  Can draw whole scene or focus on one favorite moment',
            '',
            'Add title at top: "My I-Family Silly Poems by [Child\'s Name]"',
            'Decorate borders with stars, hearts, or patterns',
            'Optional: Add small word family references (-IG, -IN, -IP) in corners',
            'Display: "This is YOUR poetry book! Let\'s hang it up!"'
          ],
          examples: [
            'Child draws pig wearing huge wig. You: "That pig looks so silly! Perfect illustration!"'
          ],
          watchFor: [
            'Images matching poem content.',
            'Enthusiasm in drawing.',
            'Labeling key words.',
            'Connection between text and image.',
            'Pride in finished product.'
          ],
          parentTip: 'Art doesn\'t need to be perfect — it\'s about the connection to words!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Poetry Performance!',
        duration: '2 min',
        focus: 'Oral Expression',
        researchBasis: 'National Reading Panel (2000) - Repeated oral reading (including poetry) builds fluency and prosody.',
        content: {
          goal: 'Perform the poems aloud with expression and celebrate the week.',
          whatToSay: [
            '"Time for the poetry show!"',
            '"Read your poems with BIG expression!"',
            '"Use silly voices!"'
          ],
          whatToDo: [
            'Set up "stage" (even if just standing up)',
            'Performance 1: Read -IG poem together',
            '  Add motions: dance for "jig," dig motion for "dig"',
            '  Encourage silly voices',
            '',
            'Performance 2: Read -IN poem together',
            '  Add motions: spin around, point to chin, grin big',
            '',
            'Performance 3: Read -IP poem together',
            '  Add motions: skip, trip (carefully!), zip lips',
            '',
            'Solo performance: Child reads all three alone (if comfortable)',
            'Audience reaction: Parent gives standing ovation!',
            'Share (optional): Perform for family members to enjoy',
            'Reflection:',
            '  "What was your favorite poem?"',
            '  "Which family was the silliest?"',
            '  "Can you write poems with OTHER word families?"',
            'Celebrate: "You\'re a POET! You used I-families to make art!"'
          ],
          examples: [
            'Child reads with big grin and silly voice. You applaud wildly: "BRAVO! Amazing performance!"'
          ],
          watchFor: [
            'Confident reading.',
            'Appropriate expression.',
            'Enjoyment of performance.',
            'Willingness to perform solo.',
            'Pride in accomplishment.',
            'Understanding that literacy = fun!'
          ],
          parentTip: 'Your enthusiasm matters! Celebrate like it\'s Broadway!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎭 You\'re a Poet!',
            'You practiced:',
            '✓ Creating rhyming poems',
            '✓ Using all three I-families',
            '✓ Illustrating your words',
            '✓ Performing with expression'
          ],
          reviewQuestions: [
            'Did your child create rhyming lines?',
            'Did they use word family words appropriately?',
            'Can they read their poems with expression?',
            'Do they see literacy as creative and fun?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Poetry transforms literacy from skill-building into ART! When children create poems, they\'re not just practicing word families — they\'re becoming authors and performers. This lesson demonstrated that reading and writing are tools for self-expression, creativity, and joy. The silly poems made learning memorable through emotion (laughter!) and multimodal encoding (words + images + performance). Most importantly, your child experienced the POWER of literacy: they used patterns they learned this week to CREATE something entirely new and uniquely theirs. This ownership — "I made this! This is MY poem!" — builds identity as a reader and writer. Week 11 complete, and they\'re not just reading I-families; they\'re playing with language like a pro!'
          },
          nextSteps: [
            'Share poems with family and friends!',
            'Create more silly poems with other families!',
            'Next week: Mixed word families and review!',
            'Start a poetry collection journal!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 56 =========
  {
    id: 56,
    phase: '4',
    quarter: 1,
    week: 12,
    weekIndex: 11,
    dayInWeek: 1,
    title: 'Lesson 56: Family Spin Game',
    theme: 'Mixed Word Families Review',
    skillRecurrenceTags: ['word-families', 'review', 'game-based-learning'],
    relatedToPreviousSkills: ['word-families', 'decoding-fluency', 'pattern-recognition'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Mixed Family Week!',
        duration: '1 min',
        content: {
          goal: 'Review ALL word families learned (short-A, short-E, short-I) through engaging game format.',
          parentGuide: [
            'Week 12 reviews and integrates 9 word families!',
            'Short-A: -at, -an, -ap',
            'Short-E: -et, -en, -eg',
            'Short-I: -ig, -in, -ip',
            'Games make review fun and pressure-free.',
            'This week assesses mastery before moving to new content.'
          ],
          materials: ['Paper and pencil', 'Paper plate or spinner (homemade)', 'Pencil/paperclip spinner (or just point randomly)'],
          tip: 'Keep energy HIGH! Review can be the most fun week!',
          researchNote: 'National Reading Panel (2000) - Spaced review through varied activities strengthens long-term retention and automaticity.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Build the Family Spinner',
        duration: '3 min',
        focus: 'Game Setup',
        researchBasis: 'Vygotsky (1978) - Child involvement in creating learning materials increases engagement and ownership.',
        content: {
          goal: 'Create a simple spinner featuring all 9 word families.',
          whatToSay: [
            '"We\'re making a game spinner!"',
            '"It has ALL the families we\'ve learned!"',
            '"Let\'s decorate it together!"'
          ],
          whatToDo: [
            'Option 1 - Paper Plate Spinner:',
            '  Divide paper plate into 9 sections',
            '  Write one family in each: -at, -an, -ap, -et, -en, -eg, -ig, -in, -ip',
            '  Use 3 colors: A-families (red), E-families (blue), I-families (green)',
            '  Add small picture clues: cat for -at, pig for -ig, etc.',
            '  Attach pencil and paperclip as spinner arrow',
            '',
            'Option 2 - Simple Pointing Game:',
            '  Write all 9 families in a circle on paper',
            '  Child closes eyes and points',
            '',
            'Option 3 - Family Cards:',
            '  Write each family on separate cards',
            '  Shuffle and draw randomly',
            '',
            'Test spinner: "Let\'s spin 3 times to test it!"',
            'Explain rules: "Spin → land on family → build a word from that family!"',
            'Set goal: "Let\'s build 15-20 words total today!"'
          ],
          examples: [
            'Child spins, lands on -ig. You: "IG family! Can you make a word?" Child: "PIG!" You: "Perfect spin!"'
          ],
          watchFor: [
            'Engagement in creating game.',
            'Excitement about playing.',
            'Understanding of all 9 families.',
            'Recognition of color-coding.'
          ],
          parentTip: 'Making the game together = instant buy-in!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Spin & Build',
        duration: '5 min',
        focus: 'Word Generation Game',
        researchBasis: 'Ehri (2020) - Flexible word family generation demonstrates mastery of onset-rime decoding strategy.',
        content: {
          goal: 'Spin, land on family, generate and write a word from that family.',
          whatToSay: [
            '"Spin the spinner!"',
            '"Say a word from that family!"',
            '"Write it down!"',
            '"Spin again!"'
          ],
          whatToDo: [
            'Round 1 - Quick Fire (10 spins):',
            '  Child spins',
            '  Calls out ONE word from that family',
            '  Parent writes it OR child writes it',
            '  Celebrate and spin again immediately',
            '  Track which families appear most',
            '',
            'Round 2 - Build & Write (5 spins):',
            '  Child spins',
            '  Says word aloud',
            '  Child writes the word themselves',
            '  Parent checks and celebrates',
            '  Can build word chain: if land on -at twice, make CAT then BAT',
            '',
            'Round 3 - Challenge Spins (5 spins):',
            '  Add challenge: "Use a BLEND!" (CLIP not DIP)',
            '  Or: "Use a word you haven\'t used yet!"',
            '  Or: "Make the longest word possible!" (TWIST vs. AT)',
            '',
            'Bonus Spin - Wild Card:',
            '  "Spin twice, combine families!" (Land on -at and -ig → use BOTH in sentence)',
            '',
            'Keep score: Count total words generated',
            'Create word bank: List all words made today',
            'Celebrate: "You generated [X] words across all 9 families!"'
          ],
          examples: [
            'Child spins -en, says "PEN," writes it. Spins -ip, says "SHIP," writes it. You: "Two families, two words! Rolling!"'
          ],
          watchFor: [
            'Quick family recognition.',
            'Accurate word generation.',
            'Variety across families.',
            'Success with blend words.',
            'Writing accuracy.',
            'Sustained engagement.'
          ],
          parentTip: 'Speed keeps energy high — don\'t pause too long between spins!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Spin & Switch Challenge',
        duration: '3 min',
        focus: 'Cross-Family Flexibility',
        researchBasis: 'Lonigan et al. (2000) - Phonological flexibility across multiple word families demonstrates advanced phonemic awareness.',
        content: {
          goal: 'Build words, then transform them by changing vowels (switching families).',
          whatToSay: [
            '"Now let\'s do magic word switches!"',
            '"Change the vowel, change the family!"',
            '"Watch the word transform!"'
          ],
          whatToDo: [
            'Demo transformation:',
            '  Write CAT (-at family)',
            '  "What if I change A to I?" → CIT (nonsense!) → Try CIG → No... → Try PIG!',
            '  Show: CAT (A) → PET (E) → PIT (I)',
            '  Explain: "Same beginning, different vowel, different family!"',
            '',
            'Challenge 1 - Vowel Swap:',
            '  Write BAT',
            '  "Change to E-family!" → BET',
            '  "Change to I-family!" → BIT',
            '',
            'Challenge 2 - Random Swap:',
            '  Spin to get family (e.g., -ig)',
            '  Build word (PIG)',
            '  Spin again (e.g., -an)',
            '  "Keep the P, change to -AN!" → PAN',
            '',
            'Challenge 3 - Triple Switch:',
            '  Start with CAT',
            '  Switch to PET',
            '  Switch to PIG',
            '  "You went through all three vowels!"',
            '',
            'Play 5-8 rounds',
            'Track successful switches',
            'Celebrate cross-vowel flexibility: "You can switch ANY family!"'
          ],
          examples: [
            'Child writes MAT → MET → MIT (nonsense) → changes to PIT. You: "Great thinking! You found the I-family version!"'
          ],
          watchFor: [
            'Understanding vowel changes.',
            'Successful transformations.',
            'Recognition when word is nonsense.',
            'Creative problem-solving.',
            'Speed improving over rounds.'
          ],
          parentTip: 'Nonsense words are GREAT! They show true decoding, not memorization!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Spin & Story',
        duration: '2 min',
        focus: 'Creative Integration',
        researchBasis: 'Scarborough (2001) - Integrating word recognition with meaning-making strengthens reading comprehension.',
        content: {
          goal: 'Use spinner to create a silly story incorporating multiple word families.',
          whatToSay: [
            '"Let\'s spin to make a story!"',
            '"Each spin = one word for our tale!"',
            '"Make it silly and fun!"'
          ],
          whatToDo: [
            'Story Frame (parent sets up):',
            '  "Once there was a ___ (spin 1) who loved to ___ (spin 2)..."',
            '',
            'Collaborative Story Building:',
            '  Spin 1: Land on -at → child picks CAT',
            '    Story: "Once there was a CAT..."',
            '  Spin 2: Land on -in → child picks SPIN',
            '    Story: "...who loved to SPIN..."',
            '  Spin 3: Land on -ip → child picks SHIP',
            '    Story: "...on a SHIP..."',
            '  Spin 4: Land on -et → child picks WET',
            '    Story: "...and got very WET..."',
            '  Spin 5: Land on -ig → child picks BIG',
            '    Story: "...with a BIG splash!"',
            '',
            'Read complete story aloud together',
            'Illustrate favorite moment',
            'Alternative: Spin 6 times, write down all words, then build story using all of them',
            'Challenge: "Can you remember all the words we used?"',
            'Celebrate: "You made a story using SIX different word families!"'
          ],
          examples: [
            'Story: "A pig in a wig won a pet hen on a ship and got wet!" You: "Hilarious! Five families in one sentence!"'
          ],
          watchFor: [
            'Successful word incorporation.',
            'Story coherence (even if silly).',
            'Memory of spin results.',
            'Creative connections.',
            'Joy in storytelling.',
            'Recognition of multiple families.'
          ],
          parentTip: 'The sillier, the better! Nonsense stories are memorable!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎡 You\'re a Family Spinner Champion!',
            'You practiced:',
            '✓ All 9 word families',
            '✓ Quick word generation',
            '✓ Vowel switching',
            '✓ Creative storytelling'
          ],
          reviewQuestions: [
            'Can your child recognize all 9 families quickly?',
            'Did they generate words from each family?',
            'Can they switch vowels between families?',
            'Did they enjoy the game format?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Today\'s spinning game revealed true mastery! When children can flexibly move between 9 different word families, generating words on demand, they\'ve internalized the onset-rime strategy. This isn\'t memorization — it\'s systematic application of phonics knowledge. The vowel-switching challenge was particularly important: it proved they understand that changing the middle sound (vowel) creates entirely new word families. This metalinguistic awareness — understanding the STRUCTURE of how words work — is what separates emerging readers from proficient ones. The game format made assessment invisible; you gathered rich data about their skills while they just had FUN. That\'s the magic of play-based review!'
          },
          nextSteps: [
            'Play Family Spin throughout the week!',
            'Create variations: speed rounds, memory challenges!',
            'Tomorrow: Read the Rhyme Line!',
            'Keep the spinner for ongoing practice!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 57 =========
  {
    id: 57,
    phase: '4',
    quarter: 1,
    week: 12,
    weekIndex: 11,
    dayInWeek: 2,
    title: 'Lesson 57: Read the Rhyme Line',
    theme: 'Mixed Word Families Review',
    skillRecurrenceTags: ['word-families', 'fluency', 'decoding-automaticity'],
    relatedToPreviousSkills: ['word-families', 'rapid-recognition', 'blending-fluency'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to the Rhyme Line!',
        duration: '1 min',
        content: {
          goal: 'Build reading fluency by rapidly decoding mixed word family words in sequence.',
          parentGuide: [
            'Today focuses on SPEED and ACCURACY together.',
            'Fluency = automatic, smooth, rapid reading.',
            'Mixed families test true mastery vs. memorization.',
            'The "line" format mimics real sentence reading.'
          ],
          materials: ['Paper and pencil', 'Timer (optional)', 'Pointer or finger for tracking'],
          tip: 'Celebrate speed, but accuracy comes first!',
          researchNote: 'National Reading Panel (2000) - Repeated reading of word lists builds automaticity, a critical foundation for reading comprehension.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Build the Rhyme Lines',
        duration: '3 min',
        focus: 'List Creation',
        researchBasis: 'Ehri (2020) - Mixed word lists prevent pattern prediction and ensure genuine decoding rather than guessing.',
        content: {
          goal: 'Create three "rhyme lines" of mixed word family words.',
          whatToSay: [
            '"We\'re making reading lines!"',
            '"Each line has words from DIFFERENT families!"',
            '"No guessing — you have to really read!"'
          ],
          whatToDo: [
            'Create LINE 1 - Short-A Mix (10 words):',
            '  CAT, MAN, TAP, BAT, PAN, MAP, RAT, VAN, CAP, SAT',
            '  Write horizontally across page',
            '  Space words evenly',
            '',
            'Create LINE 2 - Short-E Mix (10 words):',
            '  PET, PEN, LEG, BET, TEN, BEG, WET, HEN, PEG, MET',
            '',
            'Create LINE 3 - Short-I Mix (10 words):',
            '  PIG, PIN, DIP, BIG, FIN, HIP, DIG, WIN, TIP, WIG',
            '',
            'Optional LINE 4 - MEGA MIX (12 words, all vowels):',
            '  CAT, PET, PIG, MAN, PEN, PIN, TAP, LEG, DIP, BAT, WET, BIG',
            '',
            'Add visual cues:',
            '  Draw arrow showing left-to-right direction',
            '  Number each word (1, 2, 3...) for tracking',
            '  Use light colors to code vowels (red=A, blue=E, green=I)',
            '',
            'Explain: "Read across the line, like a train going down tracks!"',
            'Set expectation: "We\'ll read each line 3 times to get faster!"'
          ],
          examples: [
            'Child sees LINE 1. You: "Ten A-family words all mixed up! Let\'s read them!"'
          ],
          watchFor: [
            'Understanding of task.',
            'Recognition of mixed families.',
            'Anticipation of challenge.',
            'Confidence despite mixing.'
          ],
          parentTip: 'Mixing prevents guessing — this tests REAL decoding!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: First Pass - Accuracy',
        duration: '4 min',
        focus: 'Careful Decoding',
        researchBasis: 'Scarborough (2001) - Initial accurate decoding is prerequisite for developing reading fluency.',
        content: {
          goal: 'Read each line slowly and accurately, focusing on correct decoding.',
          whatToSay: [
            '"First time = SLOW and CAREFUL!"',
            '"Get every word RIGHT!"',
            '"Point as you read!"'
          ],
          whatToDo: [
            'LINE 1 - First Read:',
            '  Child points to each word',
            '  Decodes at own pace',
            '  Parent listens and supports',
            '  If error: "Let\'s sound that out together"',
            '  Count words read correctly: "___ out of 10!"',
            '',
            'LINE 2 - First Read:',
            '  Same process',
            '  Note: Was it easier than Line 1?',
            '  Celebrate any improvement',
            '',
            'LINE 3 - First Read:',
            '  Child should be gaining confidence',
            '  Note which families cause pauses',
            '',
            'For each error:',
            '  Don\'t just correct — help decode:',
            '    "What family? What sound does ___ make?"',
            '  Have child re-read word correctly',
            '  Praise self-correction',
            '',
            'Reflection after Line 3:',
            '  "Which line was easiest?"',
            '  "Which families were trickiest?"',
            '  "Let\'s practice those again quickly!"',
            '',
            'Record results: Line 1: _/10, Line 2: _/10, Line 3: _/10'
          ],
          examples: [
            'Child reads "CAT... MAN... TAP..." carefully. You: "Perfect accuracy! Every word correct!"'
          ],
          watchFor: [
            'Accurate decoding.',
            'Self-correction attempts.',
            'Use of sounding-out strategy.',
            'Growing confidence across lines.',
            'Identification of difficult families.'
          ],
          parentTip: 'Slow is OK! Accuracy builds the foundation for speed.'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Second Pass - Fluency',
        duration: '3 min',
        focus: 'Increasing Speed',
        researchBasis: 'National Reading Panel (2000) - Repeated reading of the same text improves rate and accuracy simultaneously.',
        content: {
          goal: 'Re-read lines with increased speed while maintaining accuracy.',
          whatToSay: [
            '"Now let\'s read FASTER!"',
            '"You know these words now!"',
            '"Smooth and quick!"'
          ],
          whatToDo: [
            'LINE 1 - Second Read:',
            '  Set goal: "Can you read faster than last time?"',
            '  Optional: Use timer (not pressuring, just measuring)',
            '  Child reads without pausing between words',
            '  Parent tracks speed increase',
            '  Celebrate: "That was __ seconds faster!"',
            '',
            'LINE 2 - Second Read:',
            '  Build momentum: "Let\'s keep the speed going!"',
            '  Should notice smoother reading',
            '',
            'LINE 3 - Second Read:',
            '  By now, child should be reading with confidence',
            '  Minimal hesitation expected',
            '',
            'MEGA MIX LINE (if time):',
            '  First AND second read of mixed-vowel line',
            '  This is the ULTIMATE test',
            '  All three vowels randomly mixed',
            '',
            'Compare to first pass:',
            '  "You read Line 1 in [X] seconds first time, [Y] seconds this time!"',
            '  "Your brain is getting faster at reading!"',
            '',
            'Note improvements:',
            '  Fewer pauses',
            '  Smoother transitions',
            '  More confident voice'
          ],
          examples: [
            'Child reads "PIG PIN DIP BIG FIN" rapidly. You: "Wow! That was lightning speed! And all correct!"'
          ],
          watchFor: [
            'Increased reading rate.',
            'Maintained accuracy.',
            'Reduced hesitations.',
            'Confident expression.',
            'Smooth word-to-word transitions.'
          ],
          parentTip: 'Speed WITHOUT accuracy means nothing — but they can do both!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Champion Challenge',
        duration: '2 min',
        focus: 'Fluency Celebration',
        researchBasis: 'Lonigan et al. (2000) - Mastery demonstrations build self-efficacy and reading confidence.',
        content: {
          goal: 'Final fluency showcase and celebration of progress.',
          whatToSay: [
            '"Time for the CHAMPION round!"',
            '"Pick your favorite line!"',
            '"Show me your best, fastest, smoothest reading!"'
          ],
          whatToDo: [
            'Challenge 1 - Personal Best:',
            '  Child picks one line to read',
            '  Gives their absolute best performance',
            '  Parent times (optional) and celebrates',
            '  "That\'s your champion speed!"',
            '',
            'Challenge 2 - Backwards Line:',
            '  Read one line from RIGHT to LEFT',
            '  Tests true decoding (no memorization possible)',
            '  Example: WIG, TIP, WIN, DIG, HIP... → read backwards',
            '',
            'Challenge 3 - Random Point:',
            '  Parent randomly points to words across all lines',
            '  Child reads instantly',
            '  "Quick fire champion mode!"',
            '',
            'Challenge 4 - Eyes Closed:',
            '  Parent reads word, child spells it aloud',
            '  Tests orthographic memory',
            '',
            'Create "Rhyme Line Champion" certificate:',
            '  "Read ___ words in ___ minutes!"',
            '  "Achieved ___% accuracy!"',
            '  "Fluency Level: CHAMPION!"',
            '',
            'Reflection:',
            '  "How did you get faster?"',
            '  "What helped you read smoothly?"',
            '  "Which families are easiest now?"'
          ],
          examples: [
            'Child blazes through Line 3 in 15 seconds, all correct. You: "CHAMPION! That was incredible!"'
          ],
          watchFor: [
            'Pride in performance.',
            'Automatic word recognition.',
            'Success with backwards/random challenges.',
            'Metacognitive awareness of strategies.',
            'Confidence as a reader.'
          ],
          parentTip: 'This celebration cements their identity as a FLUENT reader!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🏆 You\'re a Rhyme Line Champion!',
            'You practiced:',
            '✓ Reading 30+ mixed words',
            '✓ Building speed and fluency',
            '✓ Maintaining accuracy',
            '✓ Mastering all word families'
          ],
          reviewQuestions: [
            'Can your child read word lists fluently?',
            'Did their speed increase with repeated reads?',
            'Was accuracy maintained during faster reads?',
            'Do they show automatic word recognition?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Fluency is the bridge between decoding and comprehension! When children read word family words automatically — without conscious effort — their brains are free to focus on MEANING. Today\'s repeated reading built exactly this: automaticity. The first read required conscious decoding; by the third read, recognition was instant. This progression from effortful to automatic is HOW fluency develops. The mixed-family format was critical: it prevented pattern-guessing and ensured genuine decoding. Your child proved they can read ANY word family word, in ANY order, with speed AND accuracy. That\'s the definition of mastery! The certificate isn\'t just celebration — it\'s evidence of a major literacy milestone.'
          },
          nextSteps: [
            'Create new rhyme lines for daily practice!',
            'Time yourself reading — track improvement!',
            'Tomorrow: Make the Mistake game!',
            'Challenge: Read rhyme lines to family members!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 58 =========
  {
    id: 58,
    phase: '4',
    quarter: 1,
    week: 12,
    weekIndex: 11,
    dayInWeek: 3,
    title: 'Lesson 58: Make the Mistake!',
    theme: 'Mixed Word Families Review',
    skillRecurrenceTags: ['word-families', 'error-detection', 'metacognitive-awareness'],
    relatedToPreviousSkills: ['critical-listening', 'phonemic-accuracy', 'self-monitoring'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Mistake Mania!',
        duration: '1 min',
        content: {
          goal: 'Develop metacognitive reading skills by identifying and correcting intentional reading errors.',
          parentGuide: [
            'This playful activity builds CRITICAL LISTENING.',
            'When children catch errors, they prove comprehension.',
            'Error detection requires comparing expected to actual sounds.',
            'This builds self-monitoring — key for independent reading!'
          ],
          materials: ['Paper and pencil', 'Word lists from previous lessons', 'Silly enthusiasm!'],
          tip: 'Make mistakes OBVIOUS and FUNNY — kids love catching adults "messing up"!',
          researchNote: 'Scarborough (2001) - Self-monitoring and error detection are metacognitive skills that distinguish proficient from struggling readers.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: What\'s Wrong with This?',
        duration: '2 min',
        focus: 'Concept Introduction',
        researchBasis: 'Ehri (2020) - Explicit attention to reading errors strengthens phonemic precision and orthographic knowledge.',
        content: {
          goal: 'Introduce the game: parent reads incorrectly, child identifies and corrects errors.',
          whatToSay: [
            '"Today I\'m going to make MISTAKES!"',
            '"Your job is to CATCH me!"',
            '"Fix my silly errors!"',
            '"Ready to be the teacher?"'
          ],
          whatToDo: [
            'Demo Round 1 - Obvious Error:',
            '  Write CAT',
            '  Parent reads: "DOG!"',
            '  Child: "No! That says CAT!"',
            '  Parent: "Oops! You caught me! What does it really say?"',
            '  Child: "CAT!"',
            '  Parent: "You\'re right! Thanks for fixing my mistake!"',
            '',
            'Demo Round 2 - Subtle Error:',
            '  Write PIG',
            '  Parent reads: "BIG"',
            '  Child: "No, that\'s PIG!"',
            '  Parent: "Oh! I said BIG but it\'s PIG. What letter did I get wrong?"',
            '  Child: "The first letter — it\'s P not B!"',
            '',
            'Demo Round 3 - Vowel Error:',
            '  Write PIN',
            '  Parent reads: "PEN"',
            '  Child: "That\'s PIN not PEN!"',
            '  Parent: "What did I mess up?"',
            '  Child: "The vowel! It\'s I not E!"',
            '',
            'Explain the Game:',
            '  "I\'ll read 10 words. Some are RIGHT, some are WRONG!"',
            '  "Raise your hand when you catch a mistake!"',
            '  "Tell me what I SHOULD have said!"',
            '  "You\'re the mistake detective!"'
          ],
          examples: [
            'You read "CAT" as "RAT." Child jumps: "That\'s CAT not RAT!" You: "You caught me!"'
          ],
          watchFor: [
            'Immediate error detection.',
            'Accurate corrections.',
            'Explanation of what\'s wrong.',
            'Excitement about "teaching" you.',
            'Understanding of phoneme differences.'
          ],
          parentTip: 'Ham it up! The sillier your mistakes, the more engaged they\'ll be!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Mistake Hunt - Round 1',
        duration: '4 min',
        focus: 'Error Detection Practice',
        researchBasis: 'National Reading Panel (2000) - Active error monitoring during reading supports comprehension and accuracy.',
        content: {
          goal: 'Parent reads word list with intentional errors; child catches and corrects.',
          whatToSay: [
            '"Here comes Round 1!"',
            '"Listen VERY carefully!"',
            '"Some are right, some are wrong!"'
          ],
          whatToDo: [
            'Prepare list of 10 words (write on paper where child can see):',
            '  CAT, PIG, MAN, WET, PIN, BAT, HEN, DIP, VAN, BIG',
            '',
            'Reading script with intentional errors:',
            '  Word 1: CAT → Read "CAT" ✓ CORRECT',
            '  Word 2: PIG → Read "BIG" ✗ WRONG (onset error)',
            '  Word 3: MAN → Read "MAN" ✓ CORRECT',
            '  Word 4: WET → Read "PET" ✗ WRONG (onset error)',
            '  Word 5: PIN → Read "PEN" ✗ WRONG (vowel error)',
            '  Word 6: BAT → Read "BAT" ✓ CORRECT',
            '  Word 7: HEN → Read "PEN" ✗ WRONG (onset error)',
            '  Word 8: DIP → Read "DIP" ✓ CORRECT',
            '  Word 9: VAN → Read "MAN" ✗ WRONG (onset error)',
            '  Word 10: BIG → Read "PIG" ✗ WRONG (onset error)',
            '',
            'For each word:',
            '  Point to written word',
            '  Read aloud (correctly OR incorrectly per script)',
            '  Wait for child response',
            '  If child catches error: "YES! What should it be?"',
            '  If child misses error: Point and say "Look again carefully"',
            '',
            'Keep score:',
            '  "You caught ___ out of 6 mistakes!"',
            '  "Mistakes found: ___/6"',
            '  Goal: Catch all 6!'
          ],
          examples: [
            'You point to PIN, say "PEN." Child: "No! That\'s PIN!" You: "Correct! The vowel is I!"'
          ],
          watchFor: [
            'Immediate recognition of errors.',
            'Accurate identification of error type.',
            'Clear correction pronunciation.',
            'Sustained attention across 10 words.',
            'Pride when catching mistakes.'
          ],
          parentTip: 'Mix 4 correct with 6 errors — keeps them alert for both!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Mistake Hunt - Round 2',
        duration: '3 min',
        focus: 'Advanced Error Detection',
        researchBasis: 'Lonigan et al. (2000) - Fine-grained phonemic discrimination develops through focused attention to minimal pairs.',
        content: {
          goal: 'Increase difficulty with blend words and more subtle errors.',
          whatToSay: [
            '"Round 2 is TRICKY!"',
            '"I\'ll make sneakier mistakes!"',
            '"Can you still catch me?"'
          ],
          whatToDo: [
            'Prepare harder list (10 words with blends/digraphs):',
            '  CHIP, SPIN, CLIP, GRIN, SHIP, THIN, SKIP, DRIP, TWIN, SLIP',
            '',
            'Reading script with subtle errors:',
            '  Word 1: CHIP → Read "SHIP" ✗ (digraph confusion)',
            '  Word 2: SPIN → Read "SPIN" ✓',
            '  Word 3: CLIP → Read "FLIP" ✗ (blend confusion)',
            '  Word 4: GRIN → Read "GRIN" ✓',
            '  Word 5: SHIP → Read "CHIP" ✗ (digraph confusion)',
            '  Word 6: THIN → Read "CHIN" ✗ (digraph confusion)',
            '  Word 7: SKIP → Read "SKIP" ✓',
            '  Word 8: DRIP → Read "TRIP" ✗ (blend confusion)',
            '  Word 9: TWIN → Read "TWIN" ✓',
            '  Word 10: SLIP → Read "CLIP" ✗ (blend confusion)',
            '',
            'For each:',
            '  Read with confidence (even when wrong)',
            '  Test if child is truly listening vs. assuming',
            '',
            'Challenge questions after corrections:',
            '  "What sound did I mix up?"',
            '  "What letters make the /ch/ sound?"',
            '  "How is CLIP different from FLIP?"',
            '',
            'Scoring:',
            '  "Round 2: ___/6 mistakes caught!"',
            '  "You\'re getting really good at this!"',
            '',
            'Optional Round 3 - Speed Mistakes:',
            '  Read 5 words very quickly',
            '  Include 2 errors',
            '  Tests sustained attention at faster pace'
          ],
          examples: [
            'You say "SHIP" while pointing to CHIP. Child: "That\'s CHIP! Ch not Sh!" You: "Perfect! You know your digraphs!"'
          ],
          watchFor: [
            'Detection of subtle differences.',
            'Success with blend/digraph errors.',
            'Explanation of differences.',
            'Sustained focus on harder words.',
            'Confidence even with challenging pairs.'
          ],
          parentTip: 'These minimal pairs test TRUE phonemic awareness — they\'re doing great work!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Role Reversal!',
        duration: '3 min',
        focus: 'Active Production',
        researchBasis: 'Vygotsky (1978) - Role reversal activities promote metacognitive awareness and deepen understanding.',
        content: {
          goal: 'Child becomes the "mistake maker" while parent catches errors.',
          whatToSay: [
            '"Now YOU make the mistakes!"',
            '"I\'ll try to catch you!"',
            '"Make them tricky!"'
          ],
          whatToDo: [
            'Setup:',
            '  Child picks 6-8 words from any family',
            '  Writes them (or parent writes them)',
            '  Child plans which ones to "read wrong"',
            '',
            'Child\'s Turn:',
            '  Child points to word',
            '  Reads correctly OR incorrectly (their choice)',
            '  Parent responds: "I think that\'s right!" or "Wait! That says ___!"',
            '  Child confirms: "You caught me!" or "No, I was right!"',
            '',
            'Coaching if needed:',
            '  "Can you read PIG but say BIG?"',
            '  "Try changing just one sound!"',
            '',
            'Examples of child-made mistakes:',
            '  Points to CAT, says "BAT" → Parent catches it',
            '  Points to WIN, says "WIN" → Parent: "Correct!"',
            '  Points to SHIP, says "CHIP" → Parent catches it',
            '',
            'Reflection after:',
            '  "Was it hard to make mistakes on purpose?"',
            '  "What kinds of mistakes are easiest to catch?"',
            '  "Does this help you read more carefully?"',
            '',
            'Metacognitive insight:',
            '  "When you catch MY mistakes, you\'re showing you REALLY know the words!"',
            '  "Good readers notice when something sounds wrong!"',
            '  "That\'s called self-monitoring — you\'ll use it when you read books!"'
          ],
          examples: [
            'Child points to DIG, says "PIG." You: "Hmm, I see DIG but you said PIG!" Child giggles: "I tricked you!"'
          ],
          watchFor: [
            'Ability to create intentional errors.',
            'Understanding of subtle differences.',
            'Appropriate error types (not random).',
            'Enjoyment of role reversal.',
            'Metacognitive awareness developing.'
          ],
          parentTip: 'Let them "win" — even if obvious! This builds confidence and awareness.'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🕵️ You\'re a Mistake Detective Master!',
            'You practiced:',
            '✓ Catching reading errors',
            '✓ Identifying subtle differences',
            '✓ Explaining corrections',
            '✓ Making intentional mistakes'
          ],
          reviewQuestions: [
            'Can your child detect reading errors immediately?',
            'Did they identify what type of error occurred?',
            'Can they explain the correct pronunciation?',
            'Do they understand self-monitoring concept?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Error detection is a CRITICAL metacognitive skill! When children notice reading mistakes — whether theirs or someone else\'s — they demonstrate active comprehension monitoring. This self-awareness separates proficient readers from struggling ones. Struggling readers often don\'t notice when they misread words; proficient readers immediately sense something is "wrong" and self-correct. Today\'s playful game built exactly this skill. Your child proved they\'re not just decoding automatically; they\'re actively MONITORING their reading for accuracy. This metacognitive awareness will serve them forever: when reading independently, they\'ll catch their own errors and fix them without prompting. That\'s the mark of a truly independent, proficient reader!'
          },
          nextSteps: [
            'Play Make the Mistake during story time!',
            'Have child catch YOUR mistakes when reading aloud!',
            'Tomorrow: Create a Word Family Map!',
            'Challenge: Can they catch mistakes in real books?'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 59 =========
  {
    id: 59,
    phase: '4',
    quarter: 1,
    week: 12,
    weekIndex: 11,
    dayInWeek: 4,
    title: 'Lesson 59: Word Family Map',
    theme: 'Mixed Word Families Review',
    skillRecurrenceTags: ['word-families', 'visual-organization', 'pattern-relationships'],
    relatedToPreviousSkills: ['categorization', 'pattern-recognition', 'orthographic-mapping'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Mapping Time!',
        duration: '1 min',
        content: {
          goal: 'Create a comprehensive visual map organizing all learned word families by vowel and pattern.',
          parentGuide: [
            'Visual mapping consolidates learning across weeks.',
            'Organization by vowel reinforces phonics structure.',
            'This creates a reference tool for ongoing use.',
            'Mapping shows relationships between patterns.'
          ],
          materials: ['Large paper or poster board', 'Markers or crayons (3 colors)', 'Ruler (optional)', 'Stickers or decorations (optional)'],
          tip: 'This becomes a bedroom wall reference — make it beautiful!',
          researchNote: 'Scarborough (2001) - Visual organization of orthographic patterns supports word recognition, spelling retrieval, and metacognitive awareness of language structure.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Map Blueprint',
        duration: '2 min',
        focus: 'Planning Structure',
        researchBasis: 'Bruner (1960) - Organizing information hierarchically supports schema development and retrieval.',
        content: {
          goal: 'Design the structure of the word family map before filling it in.',
          whatToSay: [
            '"We\'re making a MAP of ALL your word families!"',
            '"Like a treasure map showing where words live!"',
            '"Let\'s plan it out first!"'
          ],
          whatToDo: [
            'Layout design:',
            '  Divide large paper into THREE main sections',
            '  Top section: SHORT A (use red)',
            '  Middle section: SHORT E (use blue)',
            '  Bottom section: SHORT I (use green)',
            '',
            'Draw borders with ruler (optional for neatness)',
            '',
            'Title at very top: "MY WORD FAMILY MAP"',
            'Add subtitle: "[Child\'s Name]\'s Reading Kingdom"',
            '',
            'Within each vowel section, create 3 columns:',
            '  SHORT A: | -at | -an | -ap |',
            '  SHORT E: | -et | -en | -eg |',
            '  SHORT I: | -ig | -in | -ip |',
            '',
            'Label each column with the family name in LARGE letters',
            '',
            'Color-code:',
            '  All A-families in red/pink',
            '  All E-families in blue/purple',
            '  All I-families in green/teal',
            '',
            'Add decorative elements:',
            '  Draw small crown above title',
            '  Stars in corners',
            '  Simple border design',
            '',
            'Explain: "This map shows ALL 9 families organized by vowel!"'
          ],
          examples: [
            'Child draws three big sections. You: "Perfect! Red for A, blue for E, green for I! Now we have our blueprint!"'
          ],
          watchFor: [
            'Understanding of organization.',
            'Recognition of 3-3-3 structure.',
            'Excitement about creating reference.',
            'Care in setting up layout.'
          ],
          parentTip: 'The planning phase ensures a neat, usable final product!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Fill the Map',
        duration: '6 min',
        focus: 'Word Generation and Organization',
        researchBasis: 'Ehri (2020) - Active generation and organization of word patterns strengthens orthographic memory and retrieval pathways.',
        content: {
          goal: 'Populate each family section with 4-6 example words.',
          whatToSay: [
            '"Time to fill our map with words!"',
            '"Let\'s put words in their family homes!"',
            '"You say them, I\'ll help write!"'
          ],
          whatToDo: [
            'SHORT A FAMILIES:',
            '  -at family: CAT, BAT, RAT, SAT, MAT, FAT',
            '  -an family: MAN, PAN, VAN, RAN, CAN, TAN',
            '  -ap family: MAP, TAP, CAP, RAP, NAP, ZAP',
            '  Child generates words, parent writes (or child writes if able)',
            '  Aim for 5-6 words per family',
            '',
            'SHORT E FAMILIES:',
            '  -et family: PET, BET, WET, MET, SET, JET',
            '  -en family: PEN, HEN, TEN, MEN, DEN, WHEN',
            '  -eg family: LEG, BEG, PEG, MEG (note: smaller family!)',
            '  Can add blend words: SLED, STEM if space',
            '',
            'SHORT I FAMILIES:',
            '  -ig family: PIG, BIG, DIG, WIG, FIG, JIG',
            '  -in family: PIN, BIN, FIN, WIN, TIN, CHIN, SPIN',
            '  -ip family: DIP, HIP, LIP, TIP, ZIP, CHIP, SHIP, SKIP',
            '  Note: I-families are LARGEST!',
            '',
            'For each family:',
            '  Write family name at top of column',
            '  List words vertically',
            '  Keep writing neat and consistent',
            '  Add small pictures next to favorite words (optional)',
            '',
            'Count total words:',
            '  Short A: ~15-18 words',
            '  Short E: ~13-15 words',
            '  Short I: ~18-20 words',
            '  TOTAL: ~50 words on one map!',
            '',
            'Celebrate each section: "Look at all those A-family words!"'
          ],
          examples: [
            'Child says "CAT, BAT, RAT, SAT!" You write them in -at column. "Five words! This family is growing!"'
          ],
          watchFor: [
            'Accurate word generation.',
            'Appropriate family placement.',
            'Variety within families.',
            'Pride in growing word count.',
            'Recognition of family sizes.',
            'Neat presentation (with help).'
          ],
          parentTip: 'Let them lead generation — you support with writing. This builds ownership!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Make Connections',
        duration: '3 min',
        focus: 'Pattern Analysis',
        researchBasis: 'National Reading Panel (2000) - Explicit attention to pattern relationships deepens phonological and orthographic understanding.',
        content: {
          goal: 'Draw visual connections and analyze relationships between families.',
          whatToSay: [
            '"Let\'s see how families connect!"',
            '"What patterns do you notice?"',
            '"Let\'s mark special discoveries!"'
          ],
          whatToDo: [
            'Connection Activity 1 - Same Beginning:',
            '  Find words with same onset across families',
            '  Example: CAT (A), PET (E), PIG (I) — all start with stop consonants',
            '  Draw light lines or use highlighter to connect',
            '',
            'Connection Activity 2 - Vowel Contrast:',
            '  Circle the vowel in 2-3 words from each section',
            '  "See? The vowel changes, but the pattern stays!"',
            '  Example: CAT → PET → PIT (same structure, different vowel)',
            '',
            'Connection Activity 3 - Family Size:',
            '  Draw star next to largest families',
            '  "The -IP family has the MOST words!"',
            '  "The -EG family is smallest!"',
            '  Discuss: "Why might some families be bigger?"',
            '',
            'Connection Activity 4 - Blend Power:',
            '  Put small "B" next to any blend/digraph words',
            '  Example: CHIN, SHIP, SPIN, CLIP',
            '  "These are special — they use TWO letters for one sound!"',
            '',
            'Create a key/legend:',
            '  Red = Short A',
            '  Blue = Short E',
            '  Green = Short I',
            '  ⭐ = Largest family',
            '  B = Has blend or digraph',
            '',
            'Reflection questions:',
            '  "What do ALL these words have in common?" → CVC pattern!',
            '  "What\'s DIFFERENT?" → The vowel sound and family ending!',
            '  "Which vowel has the most words?" → Short I!',
            '  "Can you make NEW words for any family?"'
          ],
          examples: [
            'Child notices PIN and PAN both start with P. You: "Great connection! Same beginning, different vowel!"'
          ],
          watchFor: [
            'Recognition of cross-family patterns.',
            'Understanding of vowel role.',
            'Ability to articulate relationships.',
            'Analytic thinking.',
            'Metacognitive awareness of structure.'
          ],
          parentTip: 'These connections show DEEP understanding — they see the system!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Map Tour & Celebration',
        duration: '2 min',
        focus: 'Review and Ownership',
        researchBasis: 'Vygotsky (1978) - Verbalization of learning consolidates knowledge and builds metacognitive awareness.',
        content: {
          goal: 'Child "tours" the map, explaining it aloud, demonstrating mastery.',
          whatToSay: [
            '"Give me a tour of your map!"',
            '"Teach ME about word families!"',
            '"Show me what you know!"'
          ],
          whatToDo: [
            'Tour format:',
            '  Child acts as guide/teacher',
            '  Parent asks questions',
            '  Child explains using map',
            '',
            'Tour stops:',
            '  Stop 1 - Overview: "Tell me about the three sections."',
            '    Child: "Red is short A, blue is short E, green is short I!"',
            '',
            '  Stop 2 - Pick a family: "Tell me about the -AT family."',
            '    Child: "These words all end in -AT. We have CAT, BAT, RAT..."',
            '',
            '  Stop 3 - Compare: "How are -AT and -IG different?"',
            '    Child: "Different vowel! A versus I!"',
            '',
            '  Stop 4 - Patterns: "What makes these WORD FAMILIES?"',
            '    Child: "The ending stays the same, the beginning changes!"',
            '',
            '  Stop 5 - Application: "How does this map help you read?"',
            '    Child: "If I know one word, I can read the whole family!"',
            '',
            'Celebrate and display:',
            '  "This is YOUR reference map!"',
            '  "Hang it on your wall!"',
            '  "Use it when you read or write!"',
            '  "Add more words as you discover them!"',
            '',
            'Take photo for documentation',
            '',
            'Final reflection:',
            '  "How many total words?" → Count together!',
            '  "How many families?" → 9!',
            '  "How many vowels?" → 3!',
            '  "You\'re a word family EXPERT!"'
          ],
          examples: [
            'Child points to each section explaining. You: "You taught me so well! You really understand word families!"'
          ],
          watchFor: [
            'Clear explanations.',
            'Accurate information.',
            'Confidence as teacher.',
            'Pride in creation.',
            'Understanding of practical use.',
            'Ownership of learning.'
          ],
          parentTip: 'When they teach YOU, they prove true mastery!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🗺️ You Created a Word Family Map!',
            'You practiced:',
            '✓ Organizing 9 word families',
            '✓ Writing 50+ words',
            '✓ Making pattern connections',
            '✓ Teaching and explaining'
          ],
          reviewQuestions: [
            'Can your child explain the map organization?',
            'Did they generate words for all 9 families?',
            'Can they identify pattern relationships?',
            'Do they see the map as a useful tool?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Visual organization is a POWERFUL learning tool! Today\'s map transformed abstract phonics knowledge into concrete, visible structure. By organizing word families hierarchically — by vowel, then by rime — your child constructed a mental schema for how English spelling works. This isn\'t just a pretty poster; it\'s an external representation of internal knowledge. When children can TEACH using their map, they demonstrate genuine understanding. The map also serves a practical purpose: it\'s a spelling and reading reference they\'ll use for months. Every time they consult it, they reinforce the patterns. Most importantly, THEY made it — not you, not a workbook, not a teacher. This ownership transforms the map from a study aid into a proud achievement!'
          },
          nextSteps: [
            'Display map prominently in child\'s space!',
            'Add new words as you discover them!',
            'Tomorrow: Mixed-Family Storytime!',
            'Use map during reading and writing activities!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 60 =========
  {
    id: 60,
    phase: '4',
    quarter: 1,
    week: 12,
    weekIndex: 11,
    dayInWeek: 5,
    title: 'Lesson 60: Mixed-Family Storytime',
    theme: 'Mixed Word Families Review',
    skillRecurrenceTags: ['word-families', 'contextual-reading', 'comprehension'],
    relatedToPreviousSkills: ['decoding-in-context', 'fluency', 'meaning-making'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Storytime!',
        duration: '1 min',
        content: {
          goal: 'Apply word family knowledge to reading connected text (story format) with comprehension.',
          parentGuide: [
            'This lesson bridges isolated words to connected reading.',
            'Stories provide meaningful context for decoding.',
            'Comprehension is as important as decoding!',
            'This previews "real reading" of books.'
          ],
          materials: ['Paper and pencil', 'Crayons or markers', 'Child\'s word family map (from yesterday)'],
          tip: 'Story should be SHORT and SIMPLE — focus is on application, not length!',
          researchNote: 'Scarborough (2001) - Integration of word recognition and language comprehension defines skilled reading; contextualized practice bridges decoding to reading comprehension.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Build Our Story',
        duration: '3 min',
        focus: 'Collaborative Story Creation',
        researchBasis: 'National Reading Panel (2000) - Decodable text with high proportion of known patterns supports early reading success.',
        content: {
          goal: 'Co-create a short decodable story using word family words.',
          whatToSay: [
            '"Let\'s write a story together!"',
            '"We\'ll use YOUR word family words!"',
            '"You pick the words, I\'ll help connect them!"'
          ],
          whatToDo: [
            'Story structure (keep simple):',
            '  Beginning: Introduce character',
            '  Middle: One problem or action',
            '  End: Simple resolution',
            '',
            'Collaborative process:',
            '  Parent: "Who is our story about?"',
            '  Child picks character: "A cat!" (or pig, man, hen, etc.)',
            '  Parent writes: "The cat..."',
            '',
            '  Parent: "What does the cat do?"',
            '  Child contributes: "sat on a mat"',
            '  Parent writes: "The cat sat on a mat."',
            '',
            '  Parent: "Then what happened?"',
            '  Child: "A rat ran!"',
            '  Parent: "The cat sat on a mat. A rat ran."',
            '',
            '  Continue for 4-6 sentences total',
            '',
            'Example complete story:',
            '  "The cat sat on a mat.',
            '   A rat ran to the cat.',
            '   The cat met the rat.',
            '   The rat had a hat.',
            '   The cat and rat sat on the mat.',
            '   They had a snack!"',
            '',
            'Guidelines for parent:',
            '  Use mostly CVC words',
            '  Include words from multiple families',
            '  Keep sentences SHORT (3-6 words each)',
            '  Allow sight words: the, a, and, to, on, had',
            '  Let child guide content — silly is fine!',
            '',
            'Write story on paper clearly',
            'Space sentences for easy reading',
            'Optional: Draw simple picture boxes next to each sentence'
          ],
          examples: [
            'Child: "A pig in a wig!" You write: "A pig had a big wig. The pig did a jig." Child: "Perfect!"'
          ],
          watchFor: [
            'Active story contribution.',
            'Use of word family vocabulary.',
            'Understanding of story structure.',
            'Excitement about creation.',
            'Logical (or creatively silly!) plot.'
          ],
          parentTip: 'You\'re the scribe, they\'re the author — let their imagination lead!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: First Read - Decode Together',
        duration: '3 min',
        focus: 'Guided Decoding',
        researchBasis: 'Ehri (2020) - Initial reading of new text benefits from scaffolding; echo reading supports fluency development.',
        content: {
          goal: 'Read the story together, with parent support for first pass.',
          whatToSay: [
            '"Let\'s read OUR story!"',
            '"I\'ll help if you need it!"',
            '"Point to each word as you read!"'
          ],
          whatToDo: [
            'Echo reading approach:',
            '  Parent reads sentence first',
            '  Child repeats/echoes',
            '  Move through all sentences',
            '',
            'Process for each sentence:',
            '  Parent: "The cat sat on a mat." (with expression)',
            '  Child: "The cat sat on a mat." (echoing)',
            '  Parent: "Perfect! Let\'s point to each word and read again."',
            '  Child points and reads independently',
            '',
            'Word family identification:',
            '  After reading sentence: "Which word families did you hear?"',
            '  Example: "CAT and MAT — both -AT family!"',
            '  Circle or underline family words in story',
            '',
            'Support strategies:',
            '  If child struggles: "Sound it out. What family is that?"',
            '  If child misreads: "Look carefully. That says ___."',
            '  Praise all attempts: "Great sounding out!"',
            '',
            'Comprehension check (simple):',
            '  "What is happening in our story?"',
            '  "Who are the characters?"',
            '  "What did they do?"',
            '',
            'Count word families used:',
            '  "We used words from ___ different families!"',
            '  Check against map if needed'
          ],
          examples: [
            'You: "The pig did a jig." Child echoes: "The pig did a jig." You: "Yes! PIG and JIG — both -IG!"'
          ],
          watchFor: [
            'Accurate echoing.',
            'Pointing to track words.',
            'Recognition of word families.',
            'Decoding of new arrangements.',
            'Basic comprehension.',
            'Growing confidence.'
          ],
          parentTip: 'Echo reading builds confidence before independent reading!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Second Read - Independent',
        duration: '3 min',
        focus: 'Fluent Independent Reading',
        researchBasis: 'National Reading Panel (2000) - Repeated reading of the same text significantly improves fluency and prosody.',
        content: {
          goal: 'Child reads story independently with increasing fluency.',
          whatToSay: [
            '"Now read it all by yourself!"',
            '"Make it smooth and expressive!"',
            '"You know all these words!"'
          ],
          whatToDo: [
            'Independent read 1:',
            '  Child reads entire story alone',
            '  Parent listens without interrupting',
            '  Note: speed, accuracy, expression',
            '  Only help if child asks or completely stuck',
            '',
            'Celebration after first independent read:',
            '  "You just read a WHOLE STORY!"',
            '  "Count the sentences — you read ___ sentences!"',
            '  "Let\'s read it even better this time!"',
            '',
            'Independent read 2 (with expression):',
            '  "This time, use voices!"',
            '  "Make the cat sound like a cat!"',
            '  "Show excitement when they had a snack!"',
            '  Model briefly if needed',
            '',
            'Compare reads:',
            '  "Was the second time smoother?"',
            '  "Did you notice any words getting easier?"',
            '  "Which sentence is your favorite to read?"',
            '',
            'Optional read 3 (speed challenge):',
            '  "Can you read it even faster?"',
            '  Time it (optional)',
            '  Must maintain accuracy!',
            '',
            'Final comprehension:',
            '  "Tell me the story in your own words."',
            '  "What was your favorite part?"',
            '  "What would happen next?"',
            '  "Can we make a sequel?"'
          ],
          examples: [
            'Child reads full story smoothly with expression. You: "WOW! You just read like a real author reading their book!"'
          ],
          watchFor: [
            'Independent decoding.',
            'Improved fluency on rereads.',
            'Appropriate expression.',
            'Comprehension retention.',
            'Pride in accomplishment.',
            'Desire to read again.'
          ],
          parentTip: 'Each reread should sound better — that\'s fluency developing in real time!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Illustrate & Perform',
        duration: '3 min',
        focus: 'Multimodal Integration',
        researchBasis: 'Hirsh-Pasek et al. (2017) - Combining reading with artistic expression and performance deepens engagement and comprehension.',
        content: {
          goal: 'Illustrate the story and perform it expressively.',
          whatToSay: [
            '"Every good story needs pictures!"',
            '"Draw your favorite part!"',
            '"Then we\'ll perform it like a play!"'
          ],
          whatToDo: [
            'Illustration time:',
            '  Divide paper into 3-4 boxes (mini comic strip)',
            '  Each box = one key moment',
            '  Child draws while parent rereads story',
            '  Can draw: beginning, middle, end',
            '  Add simple labels or speech bubbles',
            '',
            'Example boxes:',
            '  Box 1: Cat sitting on mat',
            '  Box 2: Rat running to cat',
            '  Box 3: Cat and rat together on mat',
            '',
            'Performance preparation:',
            '  Practice with expression',
            '  Add motions: sitting, running, eating',
            '  Assign voices: cat voice, rat voice, narrator',
            '',
            'Story performance:',
            '  Child stands and reads/performs',
            '  Uses illustrations as props',
            '  Parent is enthusiastic audience',
            '  Applause and standing ovation!',
            '',
            'Optional extensions:',
            '  Celebrate the performance together',
            '  Create title page: "The Cat and the Rat by [Child\'s Name]"',
            '  Make it into mini book (staple or fold)',
            '  Read to family members later',
            '',
            'Reflection:',
            '  "You wrote it, read it, drew it, and performed it!"',
            '  "You\'re an author AND an illustrator AND an actor!"',
            '  "This is YOUR story — you created everything!"'
          ],
          examples: [
            'Child draws cat and rat on mat, then performs with funny voices. You cheer: "Bravo! Amazing storyteller!"'
          ],
          watchFor: [
            'Illustrations matching story.',
            'Expressive performance.',
            'Confidence in reading aloud.',
            'Ownership of creation.',
            'Joy in sharing.',
            'Understanding of story arc.'
          ],
          parentTip: 'This multi-step process (write, read, draw, perform) shows literacy as creative power!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '📖 You\'re a Story Reader AND Creator!',
            'You practiced:',
            '✓ Writing a decodable story',
            '✓ Reading independently',
            '✓ Illustrating your story',
            '✓ Performing expressively'
          ],
          reviewQuestions: [
            'Can your child read connected text fluently?',
            'Did they decode word family words in context?',
            'Can they comprehend and retell the story?',
            'Do they see themselves as readers and authors?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Today marked a MAJOR milestone — reading connected text! Until now, lessons focused on isolated word practice. Today, your child applied those skills to REAL READING: sentences with meaning, narrative structure, and context clues. This is the ultimate test of mastery. They proved they can decode word family words even when mixed with other words, embedded in sentences, and integrated into stories. The fact that they CO-CREATED the story is powerful: it proved their phonics knowledge is flexible enough to work with ANY words, not just memorized lists. The illustration and performance transformed literacy from a skill into a creative tool. They didn\'t just read — they authored, illustrated, and performed. That\'s the identity of a literate person. Week 12 review complete, and they\'re READY for new challenges!'
          },
          nextSteps: [
            'Write more stories together this week!',
            'Next week begins Quarter 2 content!',
            'Keep the story to reread often!',
            'Share the story with family and friends!'
          ]
        }
      }
    ]
  }
],

[
  // ========= LESSON 61 =========
  {
    id: 61,
    phase: '4',
    quarter: 1,
    week: 13,
    weekIndex: 12,
    dayInWeek: 1,
    title: 'Lesson 61: Decode & Dance',
    theme: 'Quarter 1 Review: "Word Builder Party!"',
    skillRecurrenceTags: ['quarter-review', 'multi-sensory-learning', 'movement-integration'],
    relatedToPreviousSkills: ['CVC-decoding', 'blends', 'word-families', 'kinesthetic-learning'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Review Week!',
        duration: '1 min',
        content: {
          goal: 'Celebrate Quarter 1 achievements while reviewing all skills through engaging, movement-based activities.',
          parentGuide: [
            'Week 13 = Quarter Review and Celebration!',
            'This week reviews: CVC words, blends, word families.',
            'Games and movement keep review FUN, not tedious.',
            'This week assesses readiness for Quarter 2.',
            'Focus is celebration + consolidation, not testing!'
          ],
          materials: ['Paper and pencil', 'Open space for movement', 'Music (optional)', 'Enthusiasm!'],
          tip: 'Make this PLAYFUL — they\'ve earned a party week!',
          researchNote: 'Hirsh-Pasek et al. (2017) - Movement-integrated learning enhances memory retention and makes review engaging, particularly effective for kinesthetic learners.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Quarter 1 Snapshot',
        duration: '2 min',
        focus: 'Achievement Recognition',
        researchBasis: 'Vygotsky (1978) - Reflection on learning progress builds metacognitive awareness and self-efficacy.',
        content: {
          goal: 'Review what was learned across all 12 weeks of Quarter 1.',
          whatToSay: [
            '"Look how much you\'ve learned!"',
            '"Let\'s remember everything from Quarter 1!"',
            '"You started small and grew SO much!"'
          ],
          whatToDo: [
            'Create visual timeline on paper (quick sketch):',
            '  Week 1-2: CVC Short A (CAT, MAP, JAM)',
            '  Week 3: Blending practice',
            '  Week 5-7: L-blends, R-blends, S-blends',
            '  Week 9-11: Word families across A, E, I',
            '  Week 12: Mixed review',
            '',
            'Point and review:',
            '  "Week 1, you learned CAT. Now you can read 50+ words!"',
            '  "Week 5, you learned BL blends. Now you read CLIP, FLIP, SLIP!"',
            '  "Week 9, you learned word families. Now you know 9 families!"',
            '',
            'Key achievements checklist:',
            '  ✓ Can decode CVC words with short A, E, I',
            '  ✓ Can read consonant blends (L, R, S)',
            '  ✓ Can read 9 word families',
            '  ✓ Can read 50+ words total',
            '  ✓ Can write and spell word family patterns',
            '',
            'Emotional reflection:',
            '  "Remember Week 1 when CAT felt hard?"',
            '  "Now you read TWIST and SKIP easily!"',
            '  "That\'s INCREDIBLE growth!"',
            '',
            'Set tone: "This week we CELEBRATE with games and fun!"'
          ],
          examples: [
            'You point to timeline. Child: "I remember that! I can read SHIP now!" You: "Yes! From CAT to SHIP in 12 weeks!"'
          ],
          watchFor: [
            'Recognition of growth.',
            'Pride in achievements.',
            'Memory of earlier lessons.',
            'Excitement for review activities.'
          ],
          parentTip: 'This reflection builds confidence before the review challenges!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Dance Move Dictionary',
        duration: '3 min',
        focus: 'Movement-Phonics Pairing',
        researchBasis: 'Diamond (1990) - Motor-memory coupling enhances retention; embodied cognition strengthens learning pathways.',
        content: {
          goal: 'Assign specific dance moves to different word types for decode-and-dance game.',
          whatToSay: [
            '"Every word type gets a dance move!"',
            '"When you read it, DO the move!"',
            '"Let\'s create our dance dictionary!"'
          ],
          whatToDo: [
            'Create movement-word pairings together:',
            '',
            'SHORT A WORDS → Clap hands above head',
            '  Practice: Say "CAT" → clap!',
            '  Say "MAP" → clap!',
            '',
            'SHORT E WORDS → Touch toes',
            '  Practice: Say "PET" → touch toes!',
            '  Say "WET" → touch toes!',
            '',
            'SHORT I WORDS → Spin around',
            '  Practice: Say "PIG" → spin!',
            '  Say "DIP" → spin!',
            '',
            'L-BLENDS → Jump twice',
            '  Practice: Say "CLIP" → jump jump!',
            '  Say "FLIP" → jump jump!',
            '',
            'R-BLENDS → Stomp feet',
            '  Practice: Say "TRIP" → stomp stomp!',
            '  Say "GRIN" → stomp stomp!',
            '',
            'S-BLENDS → Wave arms side to side',
            '  Practice: Say "SKIP" → wave!',
            '  Say "SPIN" → wave!',
            '',
            'Optional: Child creates additional moves',
            '',
            'Practice all moves:',
            '  Run through each category twice',
            '  Speed practice: "CAT!" Child claps immediately',
            '  Celebrate: "You\'re ready to decode and dance!"'
          ],
          examples: [
            'You say "SLIP!" Child immediately waves arms (S-blend). You: "Perfect! You remembered!"'
          ],
          watchFor: [
            'Quick move recall.',
            'Correct word-to-move pairing.',
            'Enthusiastic participation.',
            'Physical coordination.',
            'Memory of word types.'
          ],
          parentTip: 'The sillier the moves, the better the memory! Let them be creative!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Decode & Dance Game',
        duration: '6 min',
        focus: 'Active Decoding Practice',
        researchBasis: 'National Reading Panel (2000) - Multi-sensory engagement during reading practice increases automaticity and motivation.',
        content: {
          goal: 'Read words and immediately perform corresponding dance moves.',
          whatToSay: [
            '"Time to decode and dance!"',
            '"Read the word, do the move!"',
            '"Let\'s see how fast you can go!"'
          ],
          whatToDo: [
            'Prepare word list (write clearly on paper, 20 words):',
            '  CAT (A-clap), PET (E-toes), PIG (I-spin), BAT (A-clap)',
            '  CLIP (L-jump), GRIN (R-stomp), SKIP (S-wave), WET (E-toes)',
            '  SHIP (S-wave), FLIP (L-jump), TRAP (R-stomp), WIN (I-spin)',
            '  MAN (A-clap), SLIP (S-wave), HEN (E-toes), SPIN (S-wave)',
            '  DIG (I-spin), CLAN (L-jump), DRIP (R-stomp), MAP (A-clap)',
            '',
            'Round 1 - Practice Pace:',
            '  Parent points to word',
            '  Child reads aloud',
            '  Child does corresponding move',
            '  Celebrate each success',
            '  Work through all 20 words',
            '',
            'Round 2 - Speed Challenge:',
            '  Parent points randomly (not in order)',
            '  Child reads and moves quickly',
            '  Time it: "20 words in ___ seconds!"',
            '  Can they beat their time?',
            '',
            'Round 3 - Memory Challenge:',
            '  Parent says word (no visual)',
            '  Child does move from memory',
            '  Tests: Can they remember word type?',
            '',
            'Round 4 - Dance Chain:',
            '  Read 3-5 words in a row',
            '  Child performs all moves in sequence',
            '  Creates a "dance routine"',
            '  Example: CAT-PET-PIG = clap, toes, spin!',
            '',
            'Optional: Add music',
            '  Play upbeat music in background',
            '  "Dance party decoding!"',
            '',
            'Celebration:',
            '  "You decoded 20 words while dancing!"',
            '  "Your body AND brain worked together!"'
          ],
          examples: [
            'Child reads "FLIP" and jumps twice. You: "Yes! L-blend jump! Now try GRIN!" Child reads and stomps.'
          ],
          watchFor: [
            'Accurate decoding.',
            'Correct move pairing.',
            'Quick transitions.',
            'Sustained energy.',
            'Joy and laughter.',
            'Automaticity developing.'
          ],
          parentTip: 'Movement prevents fatigue — they can practice longer when it\'s active!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Choreograph Your Own',
        duration: '2 min',
        focus: 'Creative Application',
        researchBasis: 'Vygotsky (1978) - Child-led creation demonstrates mastery and promotes executive function development.',
        content: {
          goal: 'Child creates own decode-and-dance routine to perform.',
          whatToSay: [
            '"Now YOU create a dance routine!"',
            '"Pick 6 words and plan the moves!"',
            '"Then perform it for me!"'
          ],
          whatToDo: [
            'Creation process:',
            '  Child picks 6 favorite words',
            '  Writes them down (or parent scribes)',
            '  Plans move sequence',
            '  Optional: Draw simple stick figures showing moves',
            '',
            'Example routine:',
            '  1. PIG (spin)',
            '  2. CAT (clap)',
            '  3. FLIP (jump jump)',
            '  4. PET (touch toes)',
            '  5. SKIP (wave)',
            '  6. GRIN (stomp)',
            '',
            'Practice run:',
            '  Child practices routine 2-3 times',
            '  Refines movements',
            '  Adds personal flair',
            '',
            'Performance:',
            '  Parent sits as audience',
            '  Child performs full routine',
            '  Reads each word clearly',
            '  Executes each move',
            '  Parent provides standing ovation!',
            '',
            'Variations:',
            '  "Can you do it backwards?"',
            '  "Can you do it faster?"',
            '  "Can you teach ME your routine?"',
            '',
            'Optional: Perform again for family members',
            '',
            'Reflection:',
            '  "How did movement help you remember?"',
            '  "Was it easier to read while moving?"',
            '  "Which was your favorite move?"'
          ],
          examples: [
            'Child performs 6-word routine smoothly. You cheer: "BRAVO! You\'re a decode-and-dance champion!"'
          ],
          watchFor: [
            'Thoughtful word selection.',
            'Planned sequencing.',
            'Confident performance.',
            'Smooth execution.',
            'Pride in creation.',
            'Metacognitive reflection.'
          ],
          parentTip: 'When they choreograph, they demonstrate complete mastery of word types!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '💃 You Decoded AND Danced!',
            'You practiced:',
            '✓ Reviewing Quarter 1 achievements',
            '✓ Pairing moves with word types',
            '✓ Reading 20+ words actively',
            '✓ Creating your own routine'
          ],
          reviewQuestions: [
            'Can your child decode while moving?',
            'Did they remember word-type categories?',
            'Can they perform moves automatically?',
            'Did movement enhance engagement and memory?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Movement-integrated learning is POWERFUL for memory and engagement! When children pair physical actions with cognitive tasks, they create dual-coding in memory: both verbal and motor. This makes recall stronger and faster. Today\'s lesson proved your child can decode automatically — they read AND moved simultaneously without confusion or hesitation. That demonstrates genuine automaticity. The choreography challenge showed executive function: planning, sequencing, and performing. Most importantly, this lesson proved that review doesn\'t have to be boring! When learning is joyful and embodied, children practice longer, remember better, and build positive associations with literacy. This is exactly how to make skills stick!'
          },
          nextSteps: [
            'Play Decode & Dance anytime this week!',
            'Tomorrow: Ultimate Blend Challenge!',
            'Create new dance moves for Quarter 2 content!',
            'Share your routine with family!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 62 =========
  {
    id: 62,
    phase: '4',
    quarter: 1,
    week: 13,
    weekIndex: 12,
    dayInWeek: 2,
    title: 'Lesson 62: Ultimate Blend Challenge',
    theme: 'Quarter 1 Review: "Word Builder Party!"',
    skillRecurrenceTags: ['quarter-review', 'consonant-blends', 'speed-accuracy'],
    relatedToPreviousSkills: ['L-blends', 'R-blends', 'S-blends', 'fluency-building'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Blend Mastery!',
        duration: '1 min',
        content: {
          goal: 'Review and celebrate mastery of all consonant blends learned in Quarter 1.',
          parentGuide: [
            'Today focuses specifically on BLENDS.',
            'Blends were introduced Week 5-7.',
            'This lesson tests fluency and automaticity.',
            'Challenge format makes review exciting!'
          ],
          materials: ['Paper and pencil', 'Timer', 'Stickers or stamps (optional rewards)'],
          tip: 'Frame this as a "challenge" not a "test" — they\'re showing off skills!',
          researchNote: 'Ehri (2020) - Automaticity with consonant blends is a critical milestone; fluent blend reading indicates strong phonological processing and supports complex word decoding.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Blend Family Reunion',
        duration: '2 min',
        focus: 'Blend Review',
        researchBasis: 'National Reading Panel (2000) - Systematic review of phonics elements strengthens long-term retention.',
        content: {
          goal: 'Review all three blend families learned this quarter.',
          whatToSay: [
            '"Remember the three blend families?"',
            '"Let\'s bring them ALL back!"',
            '"L-blends, R-blends, and S-blends!"'
          ],
          whatToDo: [
            'Create three columns on paper:',
            '',
            'L-BLENDS | R-BLENDS | S-BLENDS',
            '',
            'Populate each column together:',
            '',
            'L-BLENDS (bl, cl, fl, gl, pl, sl):',
            '  BLUE, CLIP, FLAG, GLOW, PLUG, SLIP',
            '  Quick practice: "Say each blend sound: /bl/ /cl/ /fl/..."',
            '',
            'R-BLENDS (br, cr, dr, fr, gr, pr, tr):',
            '  BRIM, CRAB, DRIP, FROG, GRIN, PROP, TRIP',
            '  Quick practice: "Say each: /br/ /cr/ /dr/..."',
            '',
            'S-BLENDS (sc, sk, sl, sm, sn, sp, st, sw):',
            '  SCAN, SKIP, SLIP, SMOG, SNIP, SPIN, STEP, SWIM',
            '  Quick practice: "Say each: /sk/ /sm/ /sn/..."',
            '',
            'Key reminders:',
            '  "Blends = TWO letters, TWO sounds, said FAST!"',
            '  "Not one new sound — two sounds blended!"',
            '  "Your mouth moves twice, but QUICKLY!"',
            '',
            'Demo comparison:',
            '  Single consonant: "P in PIG — one sound"',
            '  Blend: "CL in CLIP — two sounds /c/ + /l/ = /cl/"',
            '',
            'Count total blends: "You know 20+ different blends!"',
            '',
            'Preview challenge: "Now let\'s see how many blend WORDS you can read!"'
          ],
          examples: [
            'You point to columns. Child: "L-blends, R-blends, S-blends! I know them all!" You: "Yes! Ready to prove it?"'
          ],
          watchFor: [
            'Accurate blend pronunciation.',
            'Recognition of all three families.',
            'Understanding of "two sounds blended."',
            'Confidence in knowledge.',
            'Readiness for challenge.'
          ],
          parentTip: 'Quick review before challenge prevents errors from forgetting!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Timed Blend Challenge',
        duration: '5 min',
        focus: 'Speed and Accuracy',
        researchBasis: 'Lonigan et al. (2000) - Timed reading activities measure and build automaticity, essential for reading fluency.',
        content: {
          goal: 'Read blend words rapidly and accurately under time pressure.',
          whatToSay: [
            '"Challenge time!"',
            '"How many blend words can you read in 3 minutes?"',
            '"Ready, set, READ!"'
          ],
          whatToDo: [
            'Prepare master list (30 blend words, write clearly):',
            '  CLIP, TRIP, SKIP, FLIP, GRIN, SPIN, SLIP, DRIP',
            '  CRAB, BRIM, FROG, PROP, CLAP, FLAG, PLUG, GLAD',
            '  STEP, SNIP, SWIM, SMOG, SCAN, SKIN, SPOT, STOP',
            '  PLAN, SLED, GRIP, TRAP, SLOP, TRIM',
            '',
            'Challenge Round 1 - First Pass (Goal: Accuracy):',
            '  Set timer for 3 minutes',
            '  Child reads down list',
            '  Parent tracks: correct vs. errors',
            '  If error: Pause timer, help decode, continue',
            '  Count total read in 3 minutes',
            '  "You read ___ words in 3 minutes!"',
            '',
            'Analyze performance:',
            '  "Which blends were easiest?" → Often L-blends',
            '  "Which were trickiest?" → Often S-blends (more options)',
            '  "Were you faster at the start or end?"',
            '',
            'Challenge Round 2 - Speed Round (Goal: Beat your time):',
            '  "Can you read MORE words this time?"',
            '  Set timer again',
            '  Child rereads list',
            '  Should be noticeably faster',
            '  Count again',
            '  "You read ___ words! That\'s ___ more than Round 1!"',
            '',
            'Challenge Round 3 - Random Point (No timer):',
            '  Parent randomly points to words',
            '  Child reads instantly',
            '  Tests sight recognition vs. decoding',
            '  "Quick! This one! Now this one!"',
            '  Fast-paced, energetic',
            '',
            'Scoring system (optional):',
            '  20-24 words = Blend Builder',
            '  25-28 words = Blend Master',
            '  29-30 words = Blend CHAMPION',
            '',
            'Celebrate achievement with sticker or drawn medal'
          ],
          examples: [
            'Child reads 22 words in Round 1, 27 in Round 2. You: "Five more words! You\'re getting faster AND staying accurate!"'
          ],
          watchFor: [
            'Baseline speed (Round 1).',
            'Improvement in Round 2.',
            'Maintained accuracy under pressure.',
            'Quick recognition in Round 3.',
            'Confidence with all blend types.',
            'Persistence through challenge.'
          ],
          parentTip: 'The jump from Round 1 to 2 shows learning in real-time — celebrate it!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Blend Building Speed Game',
        duration: '4 min',
        focus: 'Generative Fluency',
        researchBasis: 'Ehri (2001) - Productive phonics activities (generating words) demonstrate deeper mastery than receptive tasks (reading given words).',
        content: {
          goal: 'Generate new blend words rapidly from given word families.',
          whatToSay: [
            '"Now YOU make blend words!"',
            '"I\'ll give you a family, you add a blend!"',
            '"Let\'s see how many you can create!"'
          ],
          whatToDo: [
            'Game format: Parent provides rime, child adds blend onset',
            '',
            'Round 1 - -IP Family:',
            '  Parent: "Make a blend word ending in -IP!"',
            '  Child: "CLIP!" "FLIP!" "TRIP!" "SKIP!" "SLIP!" "DRIP!" "GRIP!"',
            '  Count: "Seven blend -IP words! Amazing!"',
            '',
            'Round 2 - -AN Family:',
            '  Parent: "Blend words ending in -AN!"',
            '  Child: "PLAN!" "SCAN!" "CLAN!" "GRAN!"',
            '  Count responses',
            '',
            'Round 3 - -OT Family:',
            '  Parent: "Blend words ending in -OT!"',
            '  Child: "PLOT!" "SLOT!" "TROT!" "SPOT!"',
            '  Note: Introduces short O casually',
            '',
            'Round 4 - -AG Family:',
            '  Parent: "Blend words ending in -AG!"',
            '  Child: "FLAG!" "DRAG!" "BRAG!" "SLAG!"',
            '',
            'Round 5 - -IN Family:',
            '  Parent: "Blend words ending in -IN!"',
            '  Child: "SPIN!" "GRIN!" "TWIN!" "SKIN!" "CHIN!"',
            '',
            'Challenge variation - Specific blend:',
            '  "Give me THREE words starting with CL-"',
            '  Child: "CLIP, CLAP, CLAN!"',
            '  "Give me THREE words starting with GR-"',
            '  Child: "GRIN, GRIP, GRAN!"',
            '',
            'Speed rounds (30 seconds each):',
            '  "How many S-blend words in 30 seconds?"',
            '  Time and count',
            '',
            'Total count celebration:',
            '  "You generated ___ blend words from memory!"',
            '  "That proves you REALLY understand blends!"'
          ],
          examples: [
            'You: "Blend word ending in -IP!" Child: "CLIP! FLIP! TRIP! SKIP!" You: "Four in 5 seconds! Incredible!"'
          ],
          watchFor: [
            'Quick generation.',
            'Accurate blend usage.',
            'Variety across blend types.',
            'Real vs. nonsense awareness.',
            'Sustained rapid production.',
            'Excitement in creation.'
          ],
          parentTip: 'Generation is HARDER than reading — this shows true mastery!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Blend Champion Certificate',
        duration: '2 min',
        focus: 'Achievement Recognition',
        researchBasis: 'Vygotsky (1978) - Recognition and celebration of mastery strengthens self-concept as competent learner.',
        content: {
          goal: 'Create and award a "Blend Champion" certificate celebrating Quarter 1 mastery.',
          whatToSay: [
            '"You\'ve proven you\'re a Blend Champion!"',
            '"Let\'s make it official!"',
            '"Certificate time!"'
          ],
          whatToDo: [
            'Create certificate on paper (decorated nicely):',
            '',
            '  ═══════════════════════════════',
            '  🏆 BLEND CHAMPION CERTIFICATE 🏆',
            '  ═══════════════════════════════',
            '',
            '  This certifies that',
            '  [CHILD\'S NAME]',
            '',
            '  has mastered:',
            '  ✓ L-Blends (bl, cl, fl, gl, pl, sl)',
            '  ✓ R-Blends (br, cr, dr, fr, gr, pr, tr)',
            '  ✓ S-Blends (sc, sk, sl, sm, sn, sp, st, sw)',
            '',
            '  Achievement highlights:',
            '  • Read ___ words in 3 minutes',
            '  • Generated ___ blend words',
            '  • Mastered 20+ consonant blends',
            '',
            '  Awarded on: [DATE]',
            '  Signed: _______________ (Parent)',
            '',
            '  ═══════════════════════════════',
            '',
            'Decorations:',
            '  Child colors border',
            '  Add stickers or stars',
            '  Draw favorite blend word (with picture)',
            '',
            'Presentation ceremony:',
            '  Parent reads certificate aloud formally',
            '  Handshake or high-five',
            '  "You\'ve earned this through hard work!"',
            '',
            'Display:',
            '  Hang on wall or refrigerator',
            '  Photo for documentation',
            '  Share with family',
            '',
            'Reflection:',
            '  "How does it feel to be a Blend Champion?"',
            '  "What was hardest about learning blends?"',
            '  "What helped you master them?"',
            '  "What are you most proud of?"'
          ],
          examples: [
            'You present certificate. Child beams with pride. You: "You EARNED this! From not knowing blends to reading 27 blend words in 3 minutes!"'
          ],
          watchFor: [
            'Pride in achievement.',
            'Understanding of growth.',
            'Articulation of learning strategies.',
            'Confidence boost.',
            'Motivation for continued learning.'
          ],
          parentTip: 'Tangible recognition matters — this certificate validates their hard work!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🏆 You\'re a Blend Champion!',
            'You practiced:',
            '✓ Reviewing all blend families',
            '✓ Reading 30 blend words',
            '✓ Generating blend words rapidly',
            '✓ Earning your certificate'
          ],
          reviewQuestions: [
            'Can your child read blend words automatically?',
            'Did they improve speed from Round 1 to 2?',
            'Can they generate blend words independently?',
            'Do they show confidence with all blend types?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Consonant blend mastery is a MAJOR literacy milestone! Blends are more complex than single consonants because they require processing TWO phonemes rapidly in sequence. When children can decode blends automatically — as your child just demonstrated — they\'re ready for increasingly complex words. The timed challenge revealed true automaticity: your child didn\'t laboriously sound out each blend; they recognized them instantly. The generation task proved even deeper mastery: they didn\'t just read given words; they created NEW blend words from memory. This generative ability shows internalized phonological knowledge. The certificate isn\'t just a reward — it\'s documentation of a critical achievement that predicts continued reading success. Celebrate big!'
          },
          nextSteps: [
            'Display certificate proudly!',
            'Practice blend words in real books!',
            'Tomorrow: Storytime Remix!',
            'Challenge: Find blend words everywhere you go!'
          ]
        }
      }
    ]
  }
],

// ========= LESSON 63 =========
  {
    id: 63,
    phase: '4',
    quarter: 1,
    week: 13,
    weekIndex: 12,
    dayInWeek: 3,
    title: 'Lesson 63: Storytime Remix',
    theme: 'Quarter 1 Review: "Word Builder Party!"',
    skillRecurrenceTags: ['quarter-review', 'reading-comprehension', 'creative-retelling'],
    relatedToPreviousSkills: ['CVC-decoding', 'blends', 'word-families', 'storytelling'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Story Magic!',
        duration: '1 min',
        content: {
          goal: 'Retell familiar stories using Quarter 1 vocabulary, demonstrating reading comprehension and creative application.',
          parentGuide: [
            'This lesson combines ALL Quarter 1 skills in storytelling.',
            'Children apply learned words in meaningful contexts.',
            'Creative retelling proves true comprehension.',
            'This is joy-filled, not performance-pressured!'
          ],
          materials: ['Paper and drawing tools', 'Favorite picture book (optional)', 'Child\'s imagination!'],
          tip: 'Let THEM lead the story — you\'re the enthusiastic audience!',
          researchNote: 'Whitehurst & Lonigan (1998) - Story retelling is a powerful indicator of emergent literacy; it demonstrates vocabulary knowledge, narrative comprehension, and sequencing skills simultaneously.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: Pick Your Story World',
        duration: '2 min',
        focus: 'Story Selection',
        researchBasis: 'Bruner (1983) - Child-directed narrative construction promotes agency and deeper engagement with literacy.',
        content: {
          goal: 'Choose a familiar story framework to remix with Quarter 1 words.',
          whatToSay: [
            '"Let\'s retell a story YOUR way!"',
            '"Pick a favorite, or we\'ll make one up!"',
            '"We\'ll use all the words you learned!"'
          ],
          whatToDo: [
            'Offer story options (child chooses one):',
            '',
            'Option 1 - Classic Story Remix:',
            '  Use familiar tale (Three Little Pigs, Goldilocks, etc.)',
            '  Child will retell using their words',
            '  Example: "The three pigs built a hut. A big bad wolf said \'Let me in!\' The pig ran fast and hid in a den."',
            '',
            'Option 2 - Everyday Adventure:',
            '  Story about their day or imagined day',
            '  "Tell me about a trip to the park using your words!"',
            '  Example: "I ran to the swing. I met a cat. We had a snack on the mat."',
            '',
            'Option 3 - Make-Believe Quest:',
            '  Child invents adventure',
            '  "Make up a story about a hero who needs to find something!"',
            '  Example: "Sam the frog went on a trip. He met a big fish in a pond. They swam fast!"',
            '',
            'Decision support:',
            '  "Which sounds most fun?"',
            '  "Do you want animals, people, or both?"',
            '  "Adventure or silly story?"',
            '',
            'Set parameters:',
            '  Story should be 5-8 sentences',
            '  Use at least 10 Quarter 1 words',
            '  Include: CVC words, blends, word-family words',
            '',
            'Preview: "Once you pick, we\'ll plan it together, then YOU tell it!"'
          ],
          examples: [
            'Child: "I want to tell about three cats!" You: "Perfect! Three cats having an adventure! Let\'s plan it!"'
          ],
          watchFor: [
            'Confident choice-making.',
            'Understanding of story concept.',
            'Excitement about retelling.',
            'Engagement with process.'
          ],
          parentTip: 'Choice creates ownership — they\'ll work harder on THEIR story!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Build Your Word Bank',
        duration: '3 min',
        focus: 'Vocabulary Planning',
        researchBasis: 'Ehri (2020) - Pre-planning vocabulary before composition supports successful independent reading and writing.',
        content: {
          goal: 'Brainstorm and list Quarter 1 words that fit the chosen story.',
          whatToSay: [
            '"What words from this quarter fit your story?"',
            '"Let\'s make a word bank!"',
            '"These are your story\'s building blocks!"'
          ],
          whatToDo: [
            'Create word bank on paper (three columns):',
            '',
            'CHARACTERS | ACTIONS | PLACES',
            '',
            'Brainstorm together (child suggests, parent writes):',
            '',
            'Characters (nouns):',
            '  CVC: cat, pig, man, hen, fox',
            '  Blend words: frog, crab, snail',
            '  "Who\'s in your story?"',
            '',
            'Actions (verbs):',
            '  CVC: ran, sat, hid, got, met',
            '  Blend words: jump, swim, slip, trip, spin, grip',
            '  "What do they DO?"',
            '',
            'Places (settings):',
            '  CVC: den, pen, map, hill',
            '  Blend words: cliff, pond, shed',
            '  Word families: -at (mat, flat), -in (cabin)',
            '  "WHERE does it happen?"',
            '',
            'Additional describing words:',
            '  big, red, wet, hot, bad, glad, fast',
            '',
            'Target: 15-20 words listed',
            '',
            'Review process:',
            '  Parent reads list aloud',
            '  Child points to favorites',
            '  "Which words MUST be in your story?"',
            '  Circle or star top 10',
            '',
            'Story planning:',
            '  "Now, what happens FIRST in your story?"',
            '  "Then what?"',
            '  "How does it end?"',
            '  Quick 3-part outline (beginning/middle/end)',
            '',
            'Encourage: "You have EVERYTHING you need to tell a great story!"'
          ],
          examples: [
            'Child: "The frog will jump and swim!" You write: jump, swim. "Perfect! Those are blend words you mastered!"'
          ],
          watchFor: [
            'Relevant word suggestions.',
            'Use of diverse word types.',
            'Story logic emerging.',
            'Confidence with vocabulary.',
            'Excitement building.'
          ],
          parentTip: 'The planning makes the telling easier — don\'t skip this step!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Tell Your Story!',
        duration: '5 min',
        focus: 'Story Performance',
        researchBasis: 'Vygotsky (1978) - Oral narrative construction is a bridge between spoken language and written literacy, demonstrating sophisticated cognitive integration.',
        content: {
          goal: 'Child retells their planned story aloud using Quarter 1 vocabulary.',
          whatToSay: [
            '"Story time! YOU\'RE the storyteller!"',
            '"I\'m your audience — I can\'t wait to hear it!"',
            '"Use your word bank whenever you need help!"'
          ],
          whatToDo: [
            'Performance setup:',
            '  Child sits or stands comfortably',
            '  Word bank visible for reference',
            '  Parent gives full attention (no distractions)',
            '',
            'Storytelling process:',
            '  Child begins: "Once upon a time..." or "One day..."',
            '  Parent listens actively',
            '  Nod, smile, react authentically',
            '  NO interrupting or correcting during telling',
            '',
            'Support strategy:',
            '  If child pauses: Point to word bank, "What happens next?"',
            '  If stuck: "Do they go somewhere? Meet someone?"',
            '  If word-finding struggle: "Look at your action words! Pick one!"',
            '  Minimal prompting — let them work it out',
            '',
            'Note vocabulary usage (mentally or jot down):',
            '  Count Quarter 1 words used',
            '  Note blend words specifically',
            '  Track word-family words',
            '',
            'Example story flow:',
            '  "The cat and the pig met at the den."',
            '  "They had a plan to swim in the pond."',
            '  "A big crab said \'Stop!\' But they ran fast."',
            '  "The cat slid on the wet rock and fell in!"',
            '  "The pig helped his friend. They were glad."',
            '',
            'Celebrate throughout:',
            '  Laugh at funny parts',
            '  Gasp at exciting moments',
            '  Show genuine engagement',
            '',
            'Ending:',
            '  Applause when finished!',
            '  "What a fantastic story!"',
            '  "You used SO many of your new words!"'
          ],
          examples: [
            'Child: "The frog can jump and swim in the pond!" You: "Wow! Jump, swim, pond — all your blend words!"'
          ],
          watchFor: [
            'Clear story structure.',
            'Use of target vocabulary.',
            'Narrative coherence.',
            'Confidence in delivery.',
            'Joy in storytelling.',
            'Problem-solving when stuck.'
          ],
          parentTip: 'Be their BEST audience — your engagement fuels their confidence!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Draw Your Favorite Part',
        duration: '3 min',
        focus: 'Story Visualization',
        researchBasis: 'National Early Literacy Panel (2008) - Integration of drawing and storytelling strengthens comprehension and memory.',
        content: {
          goal: 'Illustrate the story to reinforce narrative memory and vocabulary.',
          whatToSay: [
            '"Every great story needs a picture!"',
            '"Draw your favorite part!"',
            '"Then we\'ll label it together!"'
          ],
          whatToDo: [
            'Drawing process:',
            '  Child picks favorite story moment',
            '  "Which part do you want to draw?"',
            '  "The beginning, the exciting middle, or the happy ending?"',
            '',
            'Drawing time (2 minutes):',
            '  Provide paper and drawing tools',
            '  Child draws freely',
            '  No pressure for "good" art',
            '  Focus on representing the story moment',
            '',
            'While drawing, talk about it:',
            '  "Tell me what\'s happening in your picture."',
            '  "Who is this?" (point to character)',
            '  "What are they doing?"',
            '',
            'Labeling together (1 minute):',
            '  "Let\'s add words to your picture!"',
            '  Parent writes or child writes (child\'s choice)',
            '  Label key elements',
            '',
            'Labeling examples:',
            '  Arrow to character → "pig"',
            '  Arrow to location → "pond"',
            '  Arrow to action → "swim"',
            '  Title at top → "The Pig Can Swim"',
            '',
            'Optional: Speech bubble',
            '  "What is the pig saying?"',
            '  Child dictates, parent writes: "Help!"',
            '',
            'Celebrate the creation:',
            '  "This is YOUR story AND your art!"',
            '  "You made something from your imagination and your reading skills!"',
            '',
            'Display option:',
            '  "Should we hang this on the fridge?"',
            '  "Want to show family tonight?"',
            '',
            'Connection to literacy:',
            '  "Look — you drew AND wrote your story!"',
            '  "Writers and artists work together!"'
          ],
          examples: [
            'Child draws pig in water. You: "Let\'s label! This is the pig. This is the pond. What\'s he doing? Swimming! Let\'s write \'swim\'!"'
          ],
          watchFor: [
            'Story accuracy in drawing.',
            'Enthusiasm for illustration.',
            'Willing to label/write.',
            'Pride in creation.',
            'Narrative memory evident.'
          ],
          parentTip: 'Drawing cements the story in memory — this isn\'t just art, it\'s literacy!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '📖 You\'re a Storyteller AND Reader!',
            'You practiced:',
            '✓ Planning a complete story',
            '✓ Using 10+ Quarter 1 words',
            '✓ Retelling with confidence',
            '✓ Illustrating your creation'
          ],
          reviewQuestions: [
            'Did your child tell a coherent story?',
            'How many Quarter 1 words did they use?',
            'Were they confident as storyteller?',
            'Did drawing help cement the narrative?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Story retelling is one of the MOST powerful literacy indicators at this age! When children can take learned vocabulary and construct original narratives, they demonstrate reading comprehension, sequencing skills, vocabulary mastery, and creative thinking simultaneously. Today your child proved they don\'t just recognize words — they USE them meaningfully. They don\'t just decode — they comprehend and create. The planning process showed executive function. The performance demonstrated confidence. The illustration reinforced memory. This lesson integrated everything learned in Quarter 1 into authentic, joyful literacy practice. Story remix proves your child is not just learning TO read — they\'re reading to LEARN and create!'
          },
          nextSteps: [
            'Retell more stories this week!',
            'Share your illustrated story with family!',
            'Tomorrow: Word Master Test-Play!',
            'Keep creating — every story builds skills!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 64 =========
  {
    id: 64,
    phase: '4',
    quarter: 1,
    week: 13,
    weekIndex: 12,
    dayInWeek: 4,
    title: 'Lesson 64: Word Master Test-Play',
    theme: 'Quarter 1 Review: "Word Builder Party!"',
    skillRecurrenceTags: ['quarter-review', 'assessment-as-play', 'mastery-demonstration'],
    relatedToPreviousSkills: ['all-quarter-1-skills'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Word Master Challenge!',
        duration: '1 min',
        content: {
          goal: 'Playfully assess Quarter 1 mastery through game-based reading activities.',
          parentGuide: [
            'This is assessment DISGUISED as play!',
            'You\'ll see exactly what they\'ve mastered.',
            'Frame as "showing off" not "testing."',
            'Celebrate every success, learn from struggles.',
            'This guides Quarter 2 focus areas.'
          ],
          materials: ['Paper and pencil', 'Prepared word list (provided below)', 'Stickers or stamps for "stamps of mastery"'],
          tip: 'NO pressure language! Say "show me" not "test." This is a celebration!',
          researchNote: 'National Reading Panel (2000) - Ongoing informal assessment through authentic reading tasks provides the most accurate picture of early literacy development.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: The Word Master Game Rules',
        duration: '1 min',
        focus: 'Game Introduction',
        researchBasis: 'Hirsh-Pasek et al. (2017) - Framing assessment as play reduces anxiety and yields more accurate performance data.',
        content: {
          goal: 'Introduce the challenge as an exciting game to showcase skills.',
          whatToSay: [
            '"Today you get to show me EVERYTHING you learned!"',
            '"We\'re playing Word Master Challenge!"',
            '"For every word you read, you get a point!"'
          ],
          whatToDo: [
            'Game setup explanation:',
            '  "I have 12 special words for you."',
            '  "They use ALL the skills from Quarter 1!"',
            '  "Some are CVC, some have blends, some are families!"',
            '  "Your job: Read them like a champion!"',
            '',
            'Scoring system (playful, not stressful):',
            '  Each word read correctly = 1 star/sticker',
            '  Goal: Earn 10+ stars = Word Master!',
            '  "But we celebrate EVERY star you earn!"',
            '',
            'Safety net (important!):',
            '  "If a word is tricky, I\'ll help."',
            '  "Then you can try again later."',
            '  "This is about showing what you CAN do!"',
            '',
            'Excitement building:',
            '  "Are you ready to show off your skills?"',
            '  "Let\'s see how many stars you earn!"',
            '  "I bet you\'ll surprise yourself!"',
            '',
            'Parent mindset reminder (to yourself):',
            '  This reveals mastery and gaps',
            '  Note which skills are solid vs. need more practice',
            '  Stay encouraging regardless of performance',
            '  Celebrate progress, not just perfection'
          ],
          examples: [
            'Child: "I\'m ready!" You: "Excellent! Let\'s discover how much you\'ve learned!"'
          ],
          watchFor: [
            'Confidence vs. anxiety.',
            'Understanding of game format.',
            'Excitement not stress.',
            'Willingness to try.'
          ],
          parentTip: 'Your enthusiasm sets the tone — make this FUN, not scary!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: The 12 Word Challenge',
        duration: '6 min',
        focus: 'Mastery Assessment',
        researchBasis: 'Ehri (2001) - Sampling across phonics patterns reveals automaticity level and guides differentiated instruction.',
        content: {
          goal: 'Child reads 12 carefully selected words representing all Quarter 1 skills.',
          whatToSay: [
            '"Here come your 12 words!"',
            '"Take your time — read carefully!"',
            '"Earn those stars!"'
          ],
          whatToDo: [
            'Prepare word list (write clearly, one per line, large print):',
            '',
            'THE 12 WORD MASTER LIST:',
            '  1. CAT (CVC short-A)',
            '  2. PIG (CVC short-I)',
            '  3. WET (CVC short-E)',
            '  4. CLIP (L-blend)',
            '  5. FROG (R-blend)',
            '  6. SKIP (S-blend)',
            '  7. PLAN (L-blend + short-A)',
            '  8. GRIN (R-blend + short-I)',
            '  9. STEM (S-blend + short-E)',
            '  10. HAT (word family -at)',
            '  11. DIP (word family -ip)',
            '  12. PEN (word family -en)',
            '',
            'Testing protocol:',
            '  Present one word at a time',
            '  Parent points to word',
            '  "Read this word for me."',
            '  Wait 5-10 seconds',
            '  Child attempts',
            '',
            'Response handling:',
            '',
            'CORRECT:',
            '  "Yes! That\'s right!" (enthusiastic)',
            '  Give star/sticker immediately',
            '  "That\'s star #___!"',
            '  Move to next word',
            '',
            'INCORRECT:',
            '  "Not quite — let\'s sound it out together."',
            '  Help decode: "What\'s the first sound? /c/"',
            '  "Middle sound? /a/"',
            '  "Last sound? /t/"',
            '  "Blend them: CAT!"',
            '  Mark for review (mental note)',
            '  "Let\'s try another one!"',
            '  No star for this one (but NO negativity)',
            '',
            'PARTIALLY CORRECT:',
            '  (e.g., says "cut" instead of "cat")',
            '  "Almost! Listen: /c/ /a/ /t/. What vowel is in the middle?"',
            '  Guide to correction',
            '  Give star if they self-correct',
            '',
            'Track performance (parent notes):',
            '  ✓ Correct on first try',
            '  ~ Correct with help',
            '  ✗ Could not decode even with help',
            '',
            'Throughout:',
            '  Maintain positive energy',
            '  Celebrate every success',
            '  "You\'re doing great!"',
            '  "Look how many stars you have!"',
            '',
            'After all 12 words:',
            '  Count stars together',
            '  "You earned ___ out of 12 stars!"',
            '  Celebrate regardless of number',
            '',
            'Scoring interpretation (for parent):',
            '  10-12 = Quarter 1 Mastery!',
            '  7-9 = Strong foundation, minor gaps',
            '  4-6 = Emerging skills, needs Quarter 2 review',
            '  0-3 = Needs more Quarter 1 practice before moving on',
            '',
            'Pattern analysis (note to self):',
            '  Which words were easiest? (likely early CVCs)',
            '  Which were hardest? (likely blends)',
            '  Any vowel confusion? (A vs. E vs. I)',
            '  Blend struggles? (which type: L, R, or S?)'
          ],
          examples: [
            'Child reads "CLIP" correctly. You: "YES! That L-blend! Star earned! That\'s number 4!"'
          ],
          watchFor: [
            'Decoding strategies used.',
            'Speed of recognition.',
            'Self-correction attempts.',
            'Confidence throughout.',
            'Where struggles occur.',
            'Emotional response to challenge.'
          ],
          parentTip: 'This data is GOLD for planning — note patterns, not just scores!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: Bonus Round - Word Building',
        duration: '3 min',
        focus: 'Generative Application',
        researchBasis: 'Ehri (2020) - Productive phonics (creating words) is more complex than receptive (reading given words) and indicates deeper mastery.',
        content: {
          goal: 'Child generates words using Quarter 1 skills, demonstrating internalized knowledge.',
          whatToSay: [
            '"Bonus challenge time!"',
            '"Now YOU make words for ME to read!"',
            '"Show me you can build words from scratch!"'
          ],
          whatToDo: [
            'Bonus Round format:',
            '  "I\'ll give you challenges — you create words!"',
            '',
            'Challenge 1: "Give me a CVC word with short-A."',
            '  Child responds verbally: "MAP" "BAT" "CAT"',
            '  Accept any valid CVC short-A',
            '  Bonus star earned!',
            '',
            'Challenge 2: "Give me a word with a blend at the start."',
            '  Child: "SLIP" "FROG" "CLIP"',
            '  Any L, R, or S blend word = success',
            '  Bonus star earned!',
            '',
            'Challenge 3: "Give me two words from the same word family."',
            '  Child: "CAT and HAT" or "DIP and SKIP"',
            '  Must rhyme',
            '  Bonus star earned!',
            '',
            'Challenge 4: "Make the longest word you can think of."',
            '  Child uses any Quarter 1 skills',
            '  Blend words often chosen: "TWIST" "STRAP"',
            '  Accept ANY multi-sound word',
            '  Bonus star earned!',
            '',
            'Challenge 5 (hardest): "Tell me how to spell PIG."',
            '  Child segments: "/p/ /i/ /g/"',
            '  If correct: Double bonus star!',
            '',
            'Scoring:',
            '  3+ correct = Master Word Builder!',
            '  All 5 correct = ULTIMATE CHAMPION!',
            '',
            'Add bonus stars to total from Step 2',
            '',
            'Celebrate creation:',
            '  "You didn\'t just READ words — you MADE words!"',
            '  "That\'s even harder!"',
            '  "Your brain is working like a real reader!"'
          ],
          examples: [
            'You: "Give me a blend word!" Child: "SKIP!" You: "Perfect! S-blend! Bonus star earned!"'
          ],
          watchFor: [
            'Quick generation vs. struggle.',
            'Accurate phonics application.',
            'Confidence in creating.',
            'Variety of responses.',
            'Evidence of internalized rules.'
          ],
          parentTip: 'If they struggle here, it\'s OKAY — this is advanced! Use this to guide focus.'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Word Master Celebration & Reflection',
        duration: '3 min',
        focus: 'Metacognitive Reflection',
        researchBasis: 'Vygotsky (1978) - Reflection on learning process builds metacognitive awareness essential for continued growth.',
        content: {
          goal: 'Celebrate achievements and reflect on Quarter 1 journey.',
          whatToSay: [
            '"Look at all your stars!"',
            '"Let\'s talk about what you learned!"',
            '"You should be SO proud!"'
          ],
          whatToDo: [
            'Star count ceremony:',
            '  Count all stars (main challenge + bonus)',
            '  "You earned ___ stars total!"',
            '  Write number on paper in big letters',
            '',
            'Achievement declaration:',
            '  10+ stars: "WORD MASTER ACHIEVED!"',
            '  7-9 stars: "WORD CHAMPION!"',
            '  4-6 stars: "WORD BUILDER!"',
            '  1-3 stars: "WORD LEARNER — Growing Strong!"',
            '  Frame ALL levels positively',
            '',
            'Reflection questions:',
            '  "What was EASIEST for you today?"',
            '  Listen: Often early CVCs or familiar patterns',
            '',
            '  "What was HARDEST?"',
            '  Listen: Identifies struggle areas',
            '',
            '  "What surprised you?"',
            '  Listen: Self-awareness emerging',
            '',
            '  "What are you most proud of learning this quarter?"',
            '  Listen: Celebrate their choice',
            '',
            '  "What do you want to learn MORE about next quarter?"',
            '  Listen: Informs Quarter 2 focus',
            '',
            'Parent reflection sharing:',
            '  "Can I tell you what I\'m proud of?"',
            '  Share specific growth you\'ve observed',
            '  "I remember Week 1 when CAT felt hard."',
            '  "Today you read FROG and SKIP easily!"',
            '  "That\'s INCREDIBLE growth!"',
            '',
            'Create reflection certificate (simple):',
            '  Write on paper:',
            '  "Word Master Challenge Complete!"',
            '  "[Name] earned ___ stars!"',
            '  "Quarter 1 Skills Mastered:"',
            '  "✓ CVC Words"',
            '  "✓ Consonant Blends"',
            '  "✓ Word Families"',
            '  Sign and date',
            '',
            'Display:',
            '  Hang certificate',
            '  Keep star chart visible',
            '  "This shows your amazing work!"',
            '',
            'Forward-looking:',
            '  "Tomorrow we celebrate even more!"',
            '  "Then Quarter 2 brings NEW adventures!"',
            '  "You\'re ready for ANYTHING!"'
          ],
          examples: [
            'Child: "Blends were hardest!" You: "Yes, they\'re tricky! But you got CLIP and SKIP right! You\'re learning them!"'
          ],
          watchFor: [
            'Accurate self-assessment.',
            'Pride in accomplishments.',
            'Realistic about struggles.',
            'Excitement for continued learning.',
            'Metacognitive awareness developing.'
          ],
          parentTip: 'Their reflections are as valuable as their performance — listen deeply!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Activity Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '⭐ Word Master Challenge Complete!',
            'You practiced:',
            '✓ Reading 12 quarter-review words',
            '✓ Generating new words',
            '✓ Reflecting on your learning',
            '✓ Celebrating your growth'
          ],
          reviewQuestions: [
            'How many words did your child read independently?',
            'Which skill areas are strongest?',
            'Which need more support in Quarter 2?',
            'Does your child demonstrate reading confidence?'
          ],
          parentTakeaway: {
            title: 'Why This Matters',
            content: 'Today was assessment AND celebration combined! You now have clear data about your child\'s Quarter 1 mastery. The 12-word challenge sampled across all major skill areas: CVCs, blends, and word families. Their performance reveals exactly where they\'re solid and where they need continued support. More importantly, their RESPONSE to challenge matters as much as their accuracy. Did they persist? Problem-solve? Self-correct? These metacognitive skills predict long-term reading success even more than current accuracy. The bonus round tested deeper mastery — generative skills show true internalization. The reflection piece built self-awareness: they\'re learning to think ABOUT their thinking. Use today\'s observations to guide Quarter 2: celebrate strengths, gently strengthen gaps. Remember: Quarter 1 is FOUNDATION. Mastery takes time. Every child progresses at their own pace. What matters most is they\'re engaged, trying, and growing. Today proved that\'s happening!'
          },
          nextSteps: [
            'Review struggle areas casually this week.',
            'Tomorrow: Quarter 1 Certificate Ceremony!',
            'Use performance data to guide Quarter 2 focus.',
            'Celebrate the journey, not just the destination!'
          ]
        }
      }
    ]
  },

  // ========= LESSON 65 =========
  {
    id: 65,
    phase: '4',
    quarter: 1,
    week: 13,
    weekIndex: 12,
    dayInWeek: 5,
    title: 'Lesson 65: Quarter 1 Certificate Ceremony',
    theme: 'Quarter 1 Review: "Word Builder Party!"',
    skillRecurrenceTags: ['quarter-review', 'celebration', 'milestone-recognition'],
    relatedToPreviousSkills: ['all-quarter-1-skills', 'metacognitive-reflection'],
    duration: '10-15 min',
    steps: [
      {
        id: 0,
        type: 'intro',
        title: 'Welcome to Your Celebration!',
        duration: '1 min',
        content: {
          goal: 'Celebrate Quarter 1 completion with ceremony, reflection, and forward-looking excitement.',
          parentGuide: [
            'This is PURE celebration — no new teaching!',
            'Honor the 13-week journey with ceremony.',
            'Create tangible memory of achievement.',
            'Build excitement for Quarter 2.',
            'This matters for motivation and identity!'
          ],
          materials: ['Paper and colorful drawing tools', 'Stickers/stamps (optional)', 'All prior certificates from this week', 'Your biggest smile!'],
          tip: 'Make this SPECIAL — this is a milestone moment in their literacy journey!',
          researchNote: 'Vygotsky (1978) - Formal recognition of learning milestones strengthens self-concept as competent learner and builds intrinsic motivation for continued growth.'
        }
      },
      {
        id: 1,
        type: 'activity',
        title: 'Step 1: The Quarter 1 Journey Map',
        duration: '3 min',
        focus: 'Visual Journey Reflection',
        researchBasis: 'Bruner (1983) - Narrative reflection on learning journey promotes metacognitive awareness and deeper integration of knowledge.',
        content: {
          goal: 'Create visual timeline of Quarter 1 showing progression from Week 1 to Week 13.',
          whatToSay: [
            '"Let\'s look at your AMAZING journey!"',
            '"13 weeks of learning — let\'s map it!"',
            '"You\'ve come SO far!"'
          ],
          whatToDo: [
            'Create Journey Map on paper (landscape orientation):',
            '',
            'Draw simple timeline/path across page:',
            '  Starting point (left): "Week 1"',
            '  Ending point (right): "Week 13 — TODAY!"',
            '  Winding path connecting them',
            '',
            'Mark major milestones along the path:',
            '',
            'WEEK 1-2: "CVC Words Start!"',
            '  Draw: Simple cat picture',
            '  Label: "CAT, MAP, JAM"',
            '  Reflection: "Remember when these felt hard?"',
            '',
            'WEEK 3-4: "Blending Power!"',
            '  Draw: Three sounds coming together',
            '  Label: "/c/ /a/ /t/ = CAT!"',
            '  Reflection: "You learned to blend sounds!"',
            '',
            'WEEK 5-7: "Blend Adventure!"',
            '  Draw: Three blend badges (L, R, S)',
            '  Label: "CLIP, FROG, SKIP"',
            '  Reflection: "TWO sounds at once!"',
            '',
            'WEEK 8: "Review Victory!"',
            '  Draw: Trophy or star',
            '  Label: "You remembered everything!"',
            '',
            'WEEK 9-11: "Word Family Party!"',
            '  Draw: Three houses labeled -AT, -IP, -EN',
            '  Label: "9 families mastered!"',
            '  Reflection: "You found the patterns!"',
            '',
            'WEEK 12: "Mixed Mastery!"',
            '  Draw: Puzzle pieces coming together',
            '  Label: "All skills combined!"',
            '',
            'WEEK 13: "CELEBRATION!" (add stars, confetti)',
            '  Draw: Big star with child\'s name',
            '  Label: "WORD MASTER!"',
            '',
            'Count achievements together:',
            '  "You learned 3 vowel sounds deeply!"',
            '  "You mastered 20+ consonant blends!"',
            '  "You know 9 word families!"',
            '  "You can read 50+ words independently!"',
            '  "You completed 65 lessons!"',
            '',
            'Visual impact:',
            '  Child colors the path',
            '  Adds decorations',
            '  Sees tangible progress',
            '',
            'Emotional connection:',
            '  "Look how far you\'ve traveled!"',
            '  "From Week 1 to now — WOW!"',
            '  "You worked hard EVERY day!"'
          ],
          examples: [
            'You point to Week 1. Child: "I remember CAT! That was my first word!" You: "Now you read TWIST! Look at that growth!"'
          ],
          watchFor: [
            'Recognition of progress.',
            'Memory of early lessons.',
            'Pride in journey.',
            'Understanding of growth.',
            'Excitement about achievements.'
          ],
          parentTip: 'Visual timelines make abstract progress concrete — powerful for young minds!'
        }
      },
      {
        id: 2,
        type: 'activity',
        title: 'Step 2: Create the Master Certificate',
        duration: '4 min',
        focus: 'Official Recognition',
        researchBasis: 'Dweck (2006) - Recognition of effort and progress (not just ability) builds growth mindset and resilience.',
        content: {
          goal: 'Design and award comprehensive Quarter 1 completion certificate.',
          whatToSay: [
            '"Time for your official certificate!"',
            '"This shows the WORLD what you achieved!"',
            '"Let\'s make it beautiful together!"'
          ],
          whatToDo: [
            'Create certificate on paper (portrait, decorated border):',
            '',
            '  ═══════════════════════════════════',
            '  ✨ QUARTER 1 COMPLETION CERTIFICATE ✨',
            '  ═══════════════════════════════════',
            '',
            '  This certifies that',
            '',
            '  [CHILD\'S FULL NAME]',
            '  (child writes or parent writes in fancy letters)',
            '',
            '  has successfully completed',
            '  PHASE 4 • QUARTER 1',
            '  "Sound + Sight = Reading"',
            '',
            '  13 Weeks • 65 Lessons',
            '  91-182 Hours of Learning',
            '',
            '  ═══════════════════════════════════',
            '  SKILLS MASTERED:',
            '  ═══════════════════════════════════',
            '',
            '  ✓ CVC Word Decoding (Short A, E, I)',
            '  ✓ Sound Blending & Segmentation',
            '  ✓ L-Blends (bl, cl, fl, gl, pl, sl)',
            '  ✓ R-Blends (br, cr, dr, fr, gr, pr, tr)',
            '  ✓ S-Blends (sc, sk, sl, sm, sn, sp, st, sw)',
            '  ✓ Word Families (-at, -an, -ap, -et, -en, -eg, -ig, -in, -ip)',
            '  ✓ Reading Fluency & Automaticity',
            '  ✓ Story Comprehension & Retelling',
            '',
            '  ═══════════════════════════════════',
            '  NOTABLE ACHIEVEMENTS:',
            '  ═══════════════════════════════════',
            '',
            '  • Reads 50+ words independently',
            '  • Decodes new words using phonics',
            '  • Recognizes word patterns',
            '  • Retells stories with confidence',
            '  • Demonstrates reading joy!',
            '',
            '  ═══════════════════════════════════',
            '',
            '  Awarded with pride on:',
            '  [DATE]',
            '',
            '  ________________________',
            '  Parent Signature',
            '',
            '  "You are a READER!"',
            '',
            '  Ready for Quarter 2: Building Sentences!',
            '',
            '  ═══════════════════════════════════',
            '',
            'Decoration time (child participates):',
            '  Border: Child draws stars, books, letters',
            '  Colors: Use favorite colors',
            '  Stickers: Add celebration stickers',
            '  Photo spot: Small drawn frame for future photo',
            '',
            'Personalization additions:',
            '  "Favorite word learned: _______" (child fills in)',
            '  "Proudest moment: _______"',
            '  "Can\'t wait to learn: _______"',
            '',
            'Quality time:',
            '  Take 4 minutes to make this BEAUTIFUL',
            '  This is a keepsake',
            '  Work together joyfully',
            '  Talk about memories while decorating'
          ],
          examples: [
            'Child adds rainbow border. You: "Perfect! Your certificate is as colorful as your reading skills!"'
          ],
          watchFor: [
            'Pride in creation.',
            'Careful decoration effort.',
            'Understanding of achievement.',
            'Excitement about certificate.',
            'Sense of accomplishment.'
          ],
          parentTip: 'This certificate is tangible proof of intangible growth — treasure it!'
        }
      },
      {
        id: 3,
        type: 'activity',
        title: 'Step 3: The Award Ceremony',
        duration: '3 min',
        focus: 'Formal Recognition',
        researchBasis: 'National Early Literacy Panel (2008) - Celebration of literacy milestones strengthens reading identity and intrinsic motivation.',
        content: {
          goal: 'Conduct formal ceremony presenting certificate with gravitas and joy.',
          whatToSay: [
            '"Time for your official award ceremony!"',
            '"Please stand for the presentation!"',
            '"This is a VERY important moment!"'
          ],
          whatToDo: [
            'Ceremony setup:',
            '  Child stands',
            '  Parent holds certificate formally',
            '  Optional: Play triumphant music or hum fanfare',
            '  Create special moment',
            '',
            'Opening speech (parent delivers):',
            '  "Today we celebrate an extraordinary achievement."',
            '  "[Child\'s name], 13 weeks ago, you began a journey."',
            '  "You couldn\'t read CAT. Today you read TWIST."',
            '  "You didn\'t know what a blend was. Today you\'re a Blend Champion."',
            '  "You struggled with sounds. Today you blend them perfectly."',
            '',
            'Specific recognitions:',
            '  "I watched you:"',
            '  "  • Work hard when things were tricky"',
            '  "  • Practice even when you were tired"',
            '  "  • Celebrate small wins"',
            '  "  • Never give up"',
            '  "  • Grow braver every week"',
            '',
            'The presentation:',
            '  "It is my honor and privilege..."',
            '  "To present you with..."',
            '  "The Quarter 1 Completion Certificate!"',
            '  Hand certificate formally',
            '  Shake hands or high-five',
            '',
            'Child response:',
            '  Child holds certificate',
            '  "How does it feel to be a Quarter 1 graduate?"',
            '  Listen to their reflection',
            '',
            'Celebration actions:',
            '  Standing ovation (parent claps)',
            '  "Speech! Speech!" (child says anything)',
            '  Photo opportunity:',
            '    • Hold certificate',
            '    • Big smile',
            '    • Take photo for memory',
            '    • Optional: Share with family',
            '',
            'Recognition extension:',
            '  "Tonight, show this to [family member]!"',
            '  "Tell them what you learned!"',
            '  "They\'ll be SO proud!"',
            '',
            'Display planning:',
            '  "Where should we hang this?"',
            '  "Bedroom wall? Refrigerator? Special spot?"',
            '  Child chooses placement',
            '  Hang it together immediately',
            '',
            'Emotional moment:',
            '  "I am so proud of you."',
            '  "Not just for what you learned..."',
            '  "But for HOW you learned."',
            '  "You showed up. You tried. You grew."',
            '  "That\'s what makes you amazing."'
          ],
          examples: [
            'You hand certificate formally. Child beams. You: "Congratulations, Reader! You earned this!"'
          ],
          watchFor: [
            'Emotional response.',
            'Pride in achievement.',
            'Understanding of significance.',
            'Confidence boost visible.',
            'Identity shift: "I\'m a reader!"'
          ],
          parentTip: 'This ceremony matters MORE than you think — it\'s identity-forming!'
        }
      },
      {
        id: 4,
        type: 'activity',
        title: 'Step 4: Looking Forward to Quarter 2',
        duration: '3 min',
        focus: 'Future Excitement',
        researchBasis: 'Dweck (2006) - Forward-looking goal setting maintains growth mindset and sustained motivation.',
        content: {
          goal: 'Build anticipation and excitement for Quarter 2 learning adventures.',
          whatToSay: [
            '"Quarter 1 was amazing — but there\'s MORE!"',
            '"Want to know what\'s next?"',
            '"Quarter 2 is even MORE exciting!"'
          ],
          whatToDo: [
            'Quarter 2 preview (make it enticing!):',
            '',
            'Announcement:',
            '  "Quarter 1: You learned to read WORDS!"',
            '  "Quarter 2: You\'ll read SENTENCES!"',
            '  "That\'s right — whole sentences!"',
            '',
            'Quarter 2 sneak peek:',
            '  "Starting next week, you\'ll learn:"',
            '  "  • Sight words (words you know instantly!)"',
            '  "  • How to build sentences (The cat can jump!)"',
            '  "  • Reading whole books BY YOURSELF!"',
            '  "  • Writing your own stories!"',
            '  "  • Even MORE word patterns!"',
            '',
            'Excitement building:',
            '  "Imagine reading a WHOLE book to me!"',
            '  "Imagine writing a story and reading it to Grandma!"',
            '  "That\'s where we\'re going!"',
            '',
            'Bridge message:',
            '  "Everything you learned in Quarter 1..."',
            '  "...is the foundation for Quarter 2!"',
            '  "You needed to read words FIRST..."',
            '  "...before you could read sentences!"',
            '  "You\'re READY!"',
            '',
            'Goal setting together:',
            '  "What do YOU want to learn in Quarter 2?"',
            '  Listen to child\'s goals',
            '  Validate and connect to curriculum',
            '  "Yes! We\'ll learn that in Week ___!"',
            '',
            'Create Q2 preview poster (quick sketch):',
            '  Title: "Quarter 2: I\'m Coming!"',
            '  Draw: Book, pencil, sentence example',
            '  Child decorates',
            '  Hang next to Q1 certificate',
            '',
            'Continuity message:',
            '  "We\'ll still practice Quarter 1 skills!"',
            '  "But we\'ll ADD new superpowers!"',
            '  "You\'re leveling up!"',
            '',
            'Rest and readiness:',
            '  "Take this weekend to celebrate!"',
            '  "Rest your learning brain!"',
            '  "Monday, Quarter 2 begins!"',
            '',
            'Confidence affirmation:',
            '  "If you could learn all of Quarter 1..."',
            '  "...you can DEFINITELY learn Quarter 2!"',
            '  "I believe in you!"',
            '  "Do you believe in yourself?"',
            '  Wait for child\'s affirmation'
          ],
          examples: [
            'Child: "I want to read a WHOLE book!" You: "Yes! By the end of Quarter 2, you WILL!"'
          ],
          watchFor: [
            'Excitement about future.',
            'No anxiety about next steps.',
            'Confidence in ability.',
            'Clear understanding of progression.',
            'Maintained enthusiasm.'
          ],
          parentTip: 'Ending with forward momentum prevents "achievement slump" — keep them excited!'
        }
      },
      {
        id: 5,
        type: 'conclusion',
        title: 'Quarter 1 Complete!',
        duration: '1 min',
        content: {
          celebration: [
            '🎉 QUARTER 1 GRADUATE!',
            'You completed:',
            '✓ 13 weeks of learning',
            '✓ 65 amazing lessons',
            '✓ Hundreds of words mastered',
            '✓ A complete literacy foundation',
            '✓ Becoming a READER!'
          ],
          reviewQuestions: [
            'Does your child recognize their growth?',
            'Do they show pride in achievements?',
            'Are they excited for Quarter 2?',
            'Do they identify as a reader now?'
          ],
          parentTakeaway: {
            title: 'Why This Matters — The Big Picture',
            content: 'Today wasn\'t just about a certificate — it was about IDENTITY. Over 13 weeks, your child transformed from a non-reader to a reader. That\'s not just a skill shift; it\'s an identity shift. They now see themselves differently. When asked "Can you read?" they can confidently say YES. That self-concept matters more than any single lesson. The journey map showed them their OWN growth — not compared to peers, but compared to their past self. That\'s the foundation of growth mindset. The ceremony formalized what they\'ve accomplished. In our culture, we formalize important transitions: graduations, ceremonies, awards. Even at age 4-5, children understand ceremony means IMPORTANT. Today told them: "You did something important. You earned recognition." The forward look to Quarter 2 maintained momentum. Celebrations can sometimes feel like endings, creating post-achievement slumps. By immediately building excitement for what\'s next, you prevented that slump. Your child ends today not with "I\'m done" but with "I can\'t wait for more!" That\'s the attitude that creates lifelong learners. As parent-teacher, YOU orchestrated this entire 13-week journey. You showed up daily. You celebrated small wins. You supported through struggles. You believed in them when they doubted themselves. THAT is why they succeeded. Quarter 1 is complete. Quarter 2 awaits. But today? Today you both deserve to celebrate this magnificent achievement. Well done, both of you!'
          },
          nextSteps: [
            'Display certificate prominently!',
            'Share achievement with family!',
            'Rest and celebrate this weekend!',
            'Monday: Quarter 2 begins with Lesson 66!',
            'Review Quarter 1 skills casually during break!',
            'Read books together — notice how much they can decode now!',
            'Document this moment — photo, journal entry, memory!'
          ],
          finalMessage: '🌟 You did it! Quarter 1 complete! You\'re officially a READER! Rest, celebrate, and get ready for Quarter 2\'s amazing adventures! 🌟'
        }
      }
    ]
  }

  ]
};